import { useState, useEffect, useMemo, useRef, lazy, Suspense } from "react";
import { createPortal } from "react-dom";
import { BRAND } from '../lib/brand-config.js';
import { wordmarkSvg, fullLogoSvg, fullLogoSvgOnDark } from '../lib/brand-logo.js';

// Lazy Tier 2 chunks (2026-05-19i) — public-path code-split.
// JournalPages: only on /blog and /blog/[slug] navigation.
// LegalPages: only on /privacy, /terms, /cookies, /prohibited-items.
const BlogPage = lazy(() => import("./journal/JournalPages.jsx"));
const LegalPages = lazy(() => import("./legal/LegalPages.jsx"));
const BookingOverlay = lazy(() => import("./funnel/Funnel.jsx")); // funnel chunk (code-split)
const CharterServicePage = lazy(() => import("./pages/Pages.jsx").then(m => ({ default: m.CharterServicePage })));
const RoutesPage = lazy(() => import("./pages/Pages.jsx").then(m => ({ default: m.RoutesPage })));
const AircraftDetailPage = lazy(() => import("./pages/Pages.jsx").then(m => ({ default: m.AircraftDetailPage })));
const ContactPage = lazy(() => import("./pages/Pages.jsx").then(m => ({ default: m.ContactPage })));
const HotelsPage = lazy(() => import("./pages/Pages.jsx").then(m => ({ default: m.HotelsPage })));
const TourDetailPage = lazy(() => import("./pages/Pages.jsx").then(m => ({ default: m.TourDetailPage })));
const DestinationsPage = lazy(() => import("./pages/Pages.jsx").then(m => ({ default: m.DestinationsPage })));
const SafetyPage = lazy(() => import("./pages/Pages.jsx").then(m => ({ default: m.SafetyPage })));
const AboutPage = lazy(() => import("./pages/Pages.jsx").then(m => ({ default: m.AboutPage })));
const HowItWorksPage = lazy(() => import("./pages/Pages.jsx").then(m => ({ default: m.HowItWorksPage })));
const FleetPage = lazy(() => import("./pages/Pages.jsx").then(m => ({ default: m.FleetPage })));
const PaymentReturnPage = lazy(() => import("./pages/Pages.jsx").then(m => ({ default: m.PaymentReturnPage })));
const NotFoundPage = lazy(() => import("./pages/Pages.jsx").then(m => ({ default: m.NotFoundPage })));
// DOMPurify is lazy-loaded via getDOMPurify() below (2026-05-19d) — the
// `isomorphic-dompurify` chunk was sitting in the homepage critical-
// request chain at 1,495ms even though only blog articles + prohibited-
// items page need it. Dynamic import drops it out of the initial bundle
// and the <SafeHtml> component below loads it on demand.
import {
  ChevronDown, ChevronRight, ArrowRight, ArrowLeftRight, X, Check, Search,
  Users, Shield, Star, MapPin, Plane, Info, Plus, Minus,
  CreditCard, Phone, Instagram, Facebook, Linkedin,
  Wind, Anchor, Camera, Zap, Route, Package, Gauge, Briefcase, AlertTriangle, Clock
} from "lucide-react";
import { isDirectHelipad, helipadLabel } from "../lib/hotel.js";
import { isAircraftUnavailableOn, resolveBaseForDate } from "../lib/fleet-availability.js";
import { feeClassOf } from "../lib/pricing/fee-class.js";
import { routeCardFromLabel, dedupeRouteCards, routeFromPriceMinLabel, routeFromPriceAmountLabel, routeFromPriceMin, routesFromPriceMin, priceValueForLabel, isStartingPriceKeyFact } from "../lib/fleet-card-price.js";
import { sanitizeSeoContentDoc } from "../lib/seo-price-sanitizer.js";
import { buildRouteSystemKeyFacts, buildDestSystemKeyFacts, buildHotelSystemKeyFacts, mergeKeyFacts } from "../lib/system-key-facts.js";
import { buildMetaTags } from "../lib/seo-meta.js";
import { JOURNEYS, journeyLead } from "../lib/homepage-journeys.js";
import { safetySection, howItWorksSteps, HOW_IT_WORKS_HEADING, pricingPromise } from "../lib/homepage-sections.js";
import { resolveEntity, publishedReviewsFor, homepageReviews } from "../lib/jsonld.js";
import { CHARTER_SERVICES_FALLBACK } from "../lib/charter-services-fallback.js";
import { haversineKm, parseSpeed, flightTimeLabel, buildHubFlightContext, distLabel } from "../lib/flightTime.js";
import { getValidPolicyTiers } from "../lib/cancellation-policy-display.js";
import { fireReturnConversionOnce } from "../lib/payment-return-tracking.js";
import { adsPageLocationOverride } from "../lib/landing-ads-params.js";
import { availablePayload as _availablePayload, crewWeight as _crewWeight, requiresMandatorySecondPilot, luggageWeight as _luggageWeight, baggageFits } from "../lib/feasibility.js";
import { prepareIdFile } from "../lib/image-compression.js";
import { getReturnPrice, brokerToCustomer, getEffectiveCommission, estimateAircraftPrice, effectiveComputeNet, applyBrokerSpecial } from "../lib/pricing.js";
// Per-pad pad-fee model — SAME pure functions the server uses (zero drift).
import { routePadFeesNet, routePaxFeesNet, buildPadTouches } from "../lib/pricing/helipad-fee.js";
import { getCustomerHelipadFee } from "../lib/pricing/helipad-fee.js";
// Pad eligibility — SAME pure union the server (bookings-create) uses, so a pad
// the customer can pick is always one the server accepts (no eligibility split).
import { isOperatorServingPad } from "../lib/pricing/eligibility.js";
import { canBookPilotExtra, isExtraAvailableForItinerary } from "../lib/extras-availability.js";
import { activeOperatorIdSet, buildPadServedPredicate, customerVisiblePads, servedCities, sightseeingServedCities } from "../lib/pad-visibility.js";
import { buildResponsiveUrl, buildSrcset, SIZE_PRESETS } from "../lib/responsive-image.js";
import { MarkdownBody } from "../lib/markdown-body.jsx";
import { safeMdLink } from "../lib/markdown-tokenizer.js";
// Route-hero resolvers — pure, shared with the SSR template so both render the
// SAME image URLs / price-min (zero drift). Stage 1 extraction; the wrappers
// below bind App's runtime DESTS/SEO_ROUTES so all call sites stay unchanged.
import { IMG, destImg as _destImg, destForRoute as _destForRoute, getRoutePriceMin as _getRoutePriceMin } from "../lib/route-resolvers.js";
import { getApproxFlightMin as _getApproxFlightMin, destFlightLabel as _destFlightLabel } from "../lib/dest-resolvers.js";

// ============================================
// CMS DATA LOADER — reads Sanity data injected by Astro
// Falls back to hardcoded data if Sanity is not available
// ============================================
export const __CMS = (() => {
  if (typeof window === 'undefined' || !window.__SANITY_DATA__) return null;
  const s = window.__SANITY_DATA__;
  const mapFleet = (f) => ({ id:f.id, engine:f.engine, svgType:f.svgType, fl3xxTailNumber:f.fl3xxTailNumber||'', fl3xxAircraftType:f.fl3xxAircraftType||'', name:f.name, type:f.type, seats:f.seats, minCrew:f.minCrew||1, cabinSeats:f.cabinSeats, cockpitPaxSeats:f.cockpitPaxSeats, requiresTwoPilots:!!f.requiresTwoPilots, needsSeatVerification:!!f.needsSeatVerification, maxPayload:f.maxPayload, maxLuggage: (f.maxLuggage != null && f.maxLuggage > 0) ? f.maxLuggage : ((f.compartmentVol && f.compartmentVol > 0) ? Math.floor((f.compartmentVol * 0.70) / 0.08) : 0), _manualLuggage: (f.maxLuggage != null && f.maxLuggage > 0) ? f.maxLuggage : null, compartmentVol:f.compartmentVol, cabinable:f.cabinable||0, range:f.range, speed:f.speed, price:f.price, hourlyRate:f.hourlyRate||null, img:f.image||f.seoCardImage, illustrationUrl:null, heroImg:f.heroImage||f.image||f.seoHeroImage, tags:f.tags||[], desc:f.desc||'', gallery:f.gallery||[], cabinImage:f.cabinImage||null, longDesc:f.longDesc||'', highlights:f.highlights||[], faqs:f.faqs||[], metaTitle:f.metaTitle||'', metaDesc:f.metaDesc||'', policy:f.policy||null, homeBase:f.homeBase||null, basePeriods:f.basePeriods||[], currentBase: resolveBaseForDate(f, s.serverDate), unavailabilityWindows:f.unavailabilityWindows||[], feeClass:f.feeClass||null, hideFromFleetPage:!!f.hideFromFleetPage });
  // op-pricing P6b — aircraft base coords + numeric speed, keyed by aircraft
  // id, for the LIVE automatic customer-price computation below. Pure in-memory
  // (no per-aircraft Sanity read) — fed from the same __SANITY_DATA__.
  const fleetById = {};
  (s.fleet || []).forEach(f => {
    // Feature B1 — seasonal base: the route-card "from" price positions from the
    // base in effect on the SSR-render date (s.serverDate, baked server-side), via
    // the SHARED resolver — never the browser's own "today" — so the card base
    // matches the SSR card-precompute by construction.
    const hb = resolveBaseForDate(f, s.serverDate);
    fleetById[f.id] = {
      base: (hb && hb.lat != null && hb.lng != null) ? { _id: hb.id || f.id, name: hb.name, type: hb.type, lat: hb.lat, lng: hb.lng } : null,
      speedKmh: Number(f.speedKmh) || 0,
    };
  });
  return {
    FLEET: (s.fleet||[]).map(mapFleet),
    DESTS: (s.destinations||[]).map(d => { const si = (s.seoImages||[]).find(x => x.pageType==="dest" && x.slug===(d.name || '').toLowerCase().replace(/\s+/g,"-")); return { name:d.name, slug:d.slug||null, tag:d.tag, region:d.region, time:d.time, lat:d.lat, lng:d.lng, img:d.image||si?.cardImage, heroImg:d.heroImage||si?.heroImage||null, highlights:Array.isArray(d.highlights)?d.highlights:[] }; }),
    SEO_ROUTES: (s.routes||[]).map(r => {
      // Stage B (C1 security) — the client route-card PRICE-BUILDER is REMOVED.
      // Operator pricing (brokerPrice / acPricing / commissionRate /
      // priceRoundingStep / buffer / routeOverrides) is no longer shipped in the
      // public payload (stripped by sanitisePublicSiteData) and the old client
      // compute is dead: real prices come from the server (/api/aircraft/quote +
      // the SSR route.cardTotals[]). With those fields gone the compute produced an
      // empty map anyway, so route-list / detail show "Price on request" exactly
      // as before. The customer-facing (non-confidential) indicativePricing is
      // still surfaced below.
      const prices = {};
      const returnPrices = {};
      const winningEntry = {};
      const indPrices = {};
      (r.indicativePricing||[]).forEach(ip => { if (ip.aircraft && ip.price) indPrices[ip.aircraft] = ip.price; });
      const si = (s.seoImages||[]).find(x => x.pageType==="route" && x.slug===(r.slug || (r.from+'-'+r.to).toLowerCase().replace(/\s+/g,'-'))); return { from:r.from, to:r.to, flightMin:r.flightMin, slug:r.slug || (r.from+'-'+r.to).toLowerCase().replace(/\s+/g,'-'), prices, returnPrices, indicativePrices:indPrices, winningEntryByAircraft: winningEntry, featured:r.featured||false, cardImg:r.cardImage||si?.cardImage||null, heroImg:r.heroImage||si?.heroImage||null, cardFromPrice:r.cardFromPrice||{}, cardTotals:r.cardTotals||[] };
    }),
    HOTELS: (s.hotels||[]).map(h => ({ id:h.id, lat:h.lat, lng:h.lng, city:h.city, name:h.name, cat:h.cat, stars:h.stars, rooms:h.rooms, helipadType:h.helipadType, helipads:h.helipads||[], transferMin:h.transferMin, img:h.image||h.seoCardImage, heroImg:h.image||h.seoHeroImage, desc:h.desc, notable:h.notable })),
    HUBS: (s.hubs||[]).map(h => ({ _id:h._id, name:h.name, lat:h.lat, lng:h.lng, iata:h.iata||'', order:h.order||0 })),
    LOCATION_DB: (() => {
      // Active-operator id set = the SAME active, non-test operator bundle the fleet
      // availability filter uses (operatorFleet is already status=="active"). A pad
      // is customer-visible (marker + list) only when ≥1 of its operators is active
      // (hasActiveOperator) — see pad-visibility.js. Display-only; data untouched.
      const activeOpIds = activeOperatorIdSet(s.operatorFleet);
      // CANONICAL served gate (display == bookability): a pad shows on customer
      // surfaces only when ≥1 ACTIVE operator SERVES it per isOperatorServingPad
      // (admin-linked OR self-assigned, minus unserved). hasActiveOperator (operatorIds
      // ∩ active) is kept for back-compat but no longer gates display — it over-counted
      // unserved bases (e.g. ZZZZHEL). Built once via the shared predicate.
      const padServed = buildPadServedPredicate(s.operatorFleet);
      return (s.locations||[]).map(l => ({ _id:l._id, id:l.id, city:l.city, name:l.name, addr:l.addr||'', note:l.note||'', lat:l.lat, lng:l.lng, type:l.type, icao:l.icao||'', iata:l.iata||'', fee:l.fee||0, feeNote:l.feeNote||'', padFees:l.padFees||null, enabled:l.enabled!==false, operatorIds:l.operatorIds||[], hasActiveOperator:(l.operatorIds||[]).some(id => activeOpIds.has(id)), hasServingActiveOperator:padServed({ _id:l._id, operatorIds:l.operatorIds||[], adminAssignedOnly:!!l.adminAssignedOnly }), adminAssignedOnly:!!l.adminAssignedOnly }));
    })(),
    REVIEWS: (s.reviews||[]).map(r => ({ _id:r._id, author:r.author, rating:r.rating, text:r.text, date:r.date, routeSlug:r.routeSlug, destName:r.destName, aircraftLabel:r.aircraftLabel })),
    SIGHTSEEING_TOURS: (s.tours||[]).map(t => {
      const flatPricing = (t.pricing||[]).flatMap(op => (op.prices||[]).map(p => ({ aircraft:p.aircraft, price:p.price, brokerPrice:p.brokerPrice, operatorName:op.operatorName, operatorId:op.operatorId })));
      const COMM_T = (s.settings?.defaultCommissionPct ?? 10) / 100;
      const resolvedPricing = flatPricing.map(p => ({
        ...p,
        price: p.price || (p.brokerPrice ? Math.ceil(p.brokerPrice / (1 - COMM_T) / 10) * 10 : null)
      }));
      const operatorPriceFrom = (() => {
        const valid = resolvedPricing.map(p => p.price).filter(p => typeof p === 'number' && p > 0 && Number.isFinite(p));
        return valid.length > 0 ? Math.min(...valid) : null;
      })();
      // Backward-compat: if no `variants`, synthesize from legacy duration/durations + tour-level highlights/desc.
      const legacyDurs = (t.durations?.length ? t.durations : t.duration ? [t.duration] : []).sort((a,b)=>a-b);
      const variants = (t.variants && t.variants.length)
        ? t.variants.slice().sort((a,b)=>(a.duration||0)-(b.duration||0)).map(v => ({
            duration: v.duration,
            price: v.price ?? null,
            highlights: (v.highlights && v.highlights.length) ? v.highlights : (t.highlights || []),
            description: v.description || t.desc || '',
          }))
        : legacyDurs.map(d => ({
            duration: d,
            price: null,
            highlights: t.highlights || [],
            description: t.desc || '',
          }));
      // Backward-compat: if no `departureLocations`, fall back to legacy `base`.
      const departures = (t.departureLocations && t.departureLocations.length)
        ? t.departureLocations
        : (t.base ? [t.base] : []);
      const variantPrices = variants.map(v => v.price).filter(Boolean);
      const variantPriceFrom = variantPrices.length ? Math.min(...variantPrices) : null;
      return {
        id: t.id, name: t.name,
        departures, variants,
        // Legacy fields (still consumed by some code paths during migration):
        base: t.base, duration: t.duration, durations: legacyDurs,
        desc: t.desc, highlights: t.highlights || [],
        image: t.image, pricing: resolvedPricing,
        priceFrom: variantPriceFrom || operatorPriceFrom,
      };
    }),
    EXTRAS: (s.extras||[]).map(e => ({ id:e.id, name:e.name, desc:e.desc, price:e.price, locationIds:e.locationIds||[], perLeg:!!e.perLeg, requiresDetails:!!e.requiresDetails, detailsLabel:e.detailsLabel||'' })),
    BLOGS: (s.blogs||[]).map(b => ({ slug:b.slug, cat:b.category, title:b.title, exc:b.excerpt, img:b.image, date:b.publishedAt?new Date(b.publishedAt).toLocaleDateString('en-US',{month:'short',year:'numeric'}):'', readTime:b.readTime||5, author:b.author, bodyMarkdown:b.bodyMarkdown, metaTitle:b.metaTitle, metaDesc:b.metaDesc })),
    OPERATOR_FLEET: (s.operatorFleet||[]).map(op => ({
      _id: op._id, name: op.name, status: op.status || 'active',
      customerRating: typeof op.customerRating === 'number' ? op.customerRating : 0,
      customerRatingCount: typeof op.customerRatingCount === 'number' ? op.customerRatingCount : 0,
      avgResponseMinutes: typeof op.avgResponseMinutes === 'number' ? op.avgResponseMinutes : 0,
      commissionRate: typeof op.commissionRate === 'number' ? op.commissionRate : null,
      // Operator-wide opt-in: charge landing fees on empty positioning/ferry legs.
      // Read by buildPadTouches via f._operator (customerTotalFor/padFeeContribution).
      chargeFerryLandings: !!op.chargeFerryLandings,
      helipadOverrides: Array.isArray(op.helipadOverrides) ? op.helipadOverrides : [],
      // Brief A — operator pad-service exclusion list (Brief B foundation).
      // ref = location._id the operator opted OUT of. Drives customer pad
      // eligibility (opServesPad) in the Helipads + Aircraft steps.
      unservedHelipads: Array.isArray(op.unservedHelipads) ? op.unservedHelipads : [],
      // Schritt C — operator self-assign allowlist (open pads). Union'd with
      // operators[] in isOperatorServingPad; ignored for adminAssignedOnly pads.
      servedHelipads: Array.isArray(op.servedHelipads) ? op.servedHelipads : [],
      // Sightseeing eligibility (Phase 2): which pads the operator offers
      // sightseeing from + the per-aircraft sightseeing-on flag. STRIPPED shape
      // only ({aircraft, sightseeingEnabled}) — the operator-confidential cost
      // model NEVER reaches the client; the sightseeing PRICE is computed
      // server-side (/api/aircraft/sightseeing-quote). Consumed by the Step-3
      // eligibility gate + the depPads sightseeingServes filter.
      sightseeingHelipads: Array.isArray(op.sightseeingHelipads) ? op.sightseeingHelipads : [],
      acPricing: Array.isArray(op.acPricing) ? op.acPricing.map(a => ({ aircraft: a && a.aircraft, sightseeingEnabled: !!(a && a.sightseeingEnabled) })) : [],
      fleet: op.fleet||[],
      // P2 #9 phase 3 — operator extras catalog (kind/perLeg/applies/
      // locationIds). Consumed by the inline aircraft-card extras UI in
      // Step 2 via getAircraftExtras(). Active filtering happens there.
      extras: Array.isArray(op.extras) ? op.extras.map(e => ({
        id: e.id, name: e.name, description: e.description || '',
        price: Number(e.price) || 0, active: e.active !== false,
        kind: e.kind || 'service', perLeg: !!e.perLeg, applies: e.applies || 'either',
        locationIds: Array.isArray(e.locationIds) ? e.locationIds : [],
        // Server-authoritative customer price (public-dto enriched at the operator's
        // real commission; commissionRate itself stays stripped). null → fall back
        // to the local getEffectiveCommission calc in getAircraftExtras.
        customerPrice: typeof e.customerPrice === 'number' ? e.customerPrice : null,
      })) : [],
    })),
    CHARTER_SERVICES: Object.fromEntries((s.charterServices||[]).map(cs => [cs.slug, { title:cs.title, subtitle:cs.subtitle, hero:cs.hero, aircraft:cs.aircraft||[], intro:cs.intro, highlights:(cs.highlights||[]).map(h=>({...h})), popularRoutes:cs.popularRoutes||[], features:(cs.features||[]).map(f=>({...f})) }])),
    HOMEPAGE: s.homepage||{},
    PAGES: Object.fromEntries((s.pages||[]).map(p => [p.slug, p])),
    LEGAL: Object.fromEntries((s.legalPages||[]).map(p => [p.slug, p])),
    HOW_IT_WORKS: s.howItWorks||{},
    SETTINGS: s.settings||{},
    // Brief A — per-aircraft base coords + speed, for the pad-exact automatic
    // customer-price computation at the aircraft step (mirrors the server).
    fleetById,
  };
})();

// Exported (additive — homepage render path unchanged) so the standalone
// chrome-island entry can inject the IDENTICAL fonts + CSS when it mounts the
// real Nav/Footer on token-accessed SSR pages. See src/components/chrome-island.jsx.
// Self-hosted Instrument Serif (italic only — the sole cut rendered; see --serif
// usages, all font-style:italic). Injected document-level here AND by
// chrome-island.jsx (a url @font-face must be at document level to resolve inside
// its shadow-DOM Nav/Footer, hence FONTS, not CSS). No Google Fonts request: the
// old @import only ever delivered Instrument Serif (the --sans display family was
// silently ignored by Google → its CG-Fallback system stack, unchanged). latin + latin-ext subsets,
// unicode-range mirrors Google's split so latin-only pages fetch one file.
export const FONTS = "@font-face{font-family:'Instrument Serif';font-style:italic;font-weight:400;font-display:swap;src:url(/fonts/instrument-serif-italic-latin.woff2) format('woff2');unicode-range:U+0000-00FF,U+0131,U+0152-0153,U+02BB-02BC,U+02C6,U+02DA,U+02DC,U+0304,U+0308,U+0329,U+2000-206F,U+20AC,U+2122,U+2191,U+2193,U+2212,U+2215,U+FEFF,U+FFFD;}@font-face{font-family:'Instrument Serif';font-style:italic;font-weight:400;font-display:swap;src:url(/fonts/instrument-serif-italic-latin-ext.woff2) format('woff2');unicode-range:U+0100-02BA,U+02BD-02C5,U+02C7-02CC,U+02CE-02D7,U+02DD-02FF,U+0304,U+0308,U+0329,U+1D00-1DBF,U+1E00-1E9F,U+1EF2-1EFF,U+2020,U+20A0-20AB,U+20AD-20C0,U+2113,U+2C60-2C7F,U+A720-A7FF;}";

/* IMAGES */
// Priority chain per render: Sanity field FIRST → IMG.* Unsplash
// SECOND → themed FALLBACK_SVG (cream gradient + helicopter silhouette,
// substituted by <Img> when src is null) as the LAST resort. Each
// destination/page has a curated stock photo here so empty-Sanity
// previews still look meaningful; production sites should set the
// Sanity field to override.
// IMG moved verbatim to src/lib/route-resolvers.js (Stage 1) and imported at
// the top of this file — every IMG.* reference below is unchanged.

// Branded local fallback rendered when both the primary src (Sanity/Unsplash)
// fails to load. Was a per-seed Picsum URL keyed off a PICSUM map; swapped for
// an inline SVG data-URI because (a) picsum.photos itself was flaky during PSI
// audits, (b) two CSP allowlist entries (picsum.photos + fastly.picsum.photos)
// disappear, (c) zero network on the fallback path. SVG is cream gradient with
// a subtle helicopter silhouette + small caption.
const FALLBACK_SVG = 'data:image/svg+xml;utf8,' + encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 800 600" preserveAspectRatio="xMidYMid slice">' +
    '<defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1">' +
      '<stop offset="0" stop-color="#F9F8F5"/><stop offset="1" stop-color="#EEEBE4"/>' +
    '</linearGradient></defs>' +
    '<rect width="800" height="600" fill="url(#g)"/>' +
    '<g transform="translate(400 300)" fill="#0D56A6" opacity="0.22">' +
      '<line x1="-200" y1="-30" x2="200" y2="-30" stroke="#0D56A6" stroke-width="3" stroke-linecap="round"/>' +
      '<rect x="-72" y="-14" width="144" height="28" rx="14"/>' +
      '<rect x="60" y="-3" width="44" height="6" rx="3"/>' +
      '<rect x="-30" y="14" width="60" height="6" rx="3"/>' +
    '</g>' +
    '<text x="400" y="380" font-family="system-ui,sans-serif" font-size="16" font-weight="600" fill="#605E5B" text-anchor="middle" opacity="0.7">Image unavailable</text>' +
  '</svg>'
);

// Apply CDN transforms (Sanity / Unsplash) to an image URL. Used by the Img
// wrapper below and by the few raw <img> tags that can't use Img directly.
// `width` is the rendered CSS width — we double it for retina, capping at 1600.
function optSrc(src, width = 800) {
  if (!src || typeof src !== 'string') return src;
  const w = Math.min(width * 2, 1600);
  if (src.includes("cdn.sanity.io") && !src.includes("?")) {
    return src + "?w=" + w + "&q=80&fm=webp&fit=crop";
  }
  if (src.includes("unsplash.com") && !src.includes("fm=webp")) {
    const sep = src.includes("?") ? "&" : "?";
    return src + sep + "fm=webp";
  }
  return src;
}

export function Img({ src, alt="", style={}, width, preset, sizes, loading, fetchpriority, ...rest }) {
  const [failed, setFailed] = useState(false);

  // Auto-detect numeric pixel widths (number, "320", "320px"). Skip
  // non-numeric values like "100%" or "auto" — those mean "fill the parent",
  // which doesn't tell us the rendered pixel size, so fall back to the 800
  // default. Previously parseInt("100%") returned 100, causing optSrc() to
  // request a 200px-wide image that was then stretched across the full
  // container — the source of the blurry galleries / route cards.
  const styleW = (typeof style.width === 'number')
    ? style.width
    : (typeof style.width === 'string' && /^\d+(\.\d+)?(px)?$/.test(style.width.trim()) ? parseFloat(style.width) : null);

  // 2026-05-19c — preset-driven responsive rendering. When a preset is
  // supplied, emit srcset + sizes + loading/fetchpriority from the
  // preset. Without preset, fall back to the legacy optSrc behaviour
  // so existing 44 call sites keep working unchanged.
  const presetCfg = preset && SIZE_PRESETS[preset] ? SIZE_PRESETS[preset] : null;

  if (!src || failed) {
    return <img src={FALLBACK_SVG} alt={alt} style={style} loading={loading || 'lazy'} onError={() => !failed && setFailed(true)} {...rest} />;
  }

  if (presetCfg) {
    const fallbackWidth = width || styleW || presetCfg.defaultWidth;
    const responsiveSrc = buildResponsiveUrl(src, { width: fallbackWidth }) || src;
    const responsiveSrcset = buildSrcset(src, presetCfg.widths);
    // CLS: presets whose containers enforce a known ratio (card 3:2,
    // thumbnail 3:2, avatar 1:1) emit intrinsic width/height ATTRIBUTES so
    // the browser reserves the box pre-load. Container CSS (width/height
    // 100%, object-fit, absolute inset) still fully controls the rendered
    // size — the attributes only supply the pre-load aspect ratio. hero and
    // inline presets deliberately carry no aspect (hero mechanics untouched;
    // inline images have unknown ratios).
    // inline preset (unknown design ratio): derive the EXACT intrinsic ratio
    // from the Sanity asset filename ("...-6553x2634.webp") so gallery/cabin
    // images reserve their true box too (SEO follow-up 2026-07-08). hero stays
    // attribute-free by design (LCP mechanics untouched).
    let aspect = presetCfg.aspect;
    if (!aspect && preset !== 'hero' && typeof src === 'string') {
      const dim = src.match(/-([1-9]\d*)x([1-9]\d*)\.(?:webp|jpe?g|png|avif)/i);
      if (dim) aspect = Number(dim[1]) / Number(dim[2]);
    }
    const attrW = aspect ? Math.round(fallbackWidth) : undefined;
    const attrH = aspect ? Math.round(fallbackWidth / aspect) : undefined;
    return (
      <img
        src={responsiveSrc}
        srcSet={responsiveSrcset || undefined}
        sizes={sizes || presetCfg.sizes}
        alt={alt}
        style={style}
        width={attrW}
        height={attrH}
        loading={loading || presetCfg.loading}
        fetchpriority={fetchpriority || presetCfg.fetchpriority}
        onError={() => !failed && setFailed(true)}
        {...rest}
      />
    );
  }

  const optimized = optSrc(src, width || styleW || 800);
  return (
    <img src={optimized} alt={alt} style={style} loading={loading || 'lazy'} onError={() => !failed && setFailed(true)} {...rest} />
  );
}

// Bag-capacity disclaimer reused on every "X bags" cell. The volume-based
// computation assumes standard hold bags; rigid cases or oversized luggage
// reduce capacity. Surfaced via native `title=` for hover; the small Info
// icon makes the disclaimer discoverable on touch as well.
const BAGS_TOOLTIP = "Based on standard hold bags up to 70x50x30 cm. Larger or rigid cases may reduce capacity. You can specify exact bag dimensions during booking.";

export function BagsLine({ children }) {
  // The info was previously a hover-only native `title` — invisible on touch.
  // Now a tap toggles a portalled, position:fixed popover anchored to the
  // icon's rect so it escapes the card's overflow:hidden + the spec row's
  // overflow-x:auto clipping (it would be cut off if rendered inline). The
  // native `title` is kept so desktop hover keeps working unchanged. Shared
  // by every BagsLine caller (cards, popup, fleet, comparison table).
  const [open, setOpen] = useState(false);
  const [pos, setPos]   = useState(null);
  const btnRef = useRef(null);

  const place = () => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const half = 130, m = 12; // popover ≤260px wide, translateX(-50%)
    const cx = Math.min(Math.max(r.left + r.width / 2, half + m), window.innerWidth - half - m);
    setPos({ top: r.bottom + 8, left: cx });
  };

  const toggle = (e) => {
    e.stopPropagation(); // never let the tap select the underlying aircraft card
    setOpen(o => { const next = !o; if (next) place(); return next; });
  };

  // While open: dismiss on any outside pointer, on scroll (capture catches the
  // spec row's horizontal scroll + the sheet's vertical scroll), and on resize.
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    document.addEventListener("pointerdown", close);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
      document.removeEventListener("pointerdown", close);
    };
  }, [open]);

  return (
    <span style={{ display:"inline-flex", alignItems:"center", gap:4 }}>
      {children}
      <button
        ref={btnRef}
        type="button"
        title={BAGS_TOOLTIP}
        aria-label="Luggage details"
        aria-expanded={open}
        onPointerDown={e => e.stopPropagation()}
        onClick={toggle}
        style={{ background:"none", border:"none", padding:10, margin:-10, cursor:"help", display:"inline-flex", alignItems:"center", lineHeight:0, color:"inherit", WebkitTapHighlightColor:"transparent", touchAction:"manipulation" }}
      >
        <Info size={11} style={{ opacity:.55, flexShrink:0 }}/>
      </button>
      {open && pos && createPortal(
        <div role="tooltip" style={{ position:"fixed", top:pos.top, left:pos.left, transform:"translateX(-50%)", width:"max-content", maxWidth:260, zIndex:9999, background:"var(--ink)", color:"white", fontSize:12, lineHeight:1.5, fontWeight:400, padding:"10px 12px", borderRadius:10, boxShadow:"0 8px 24px rgba(0,0,0,.22)", pointerEvents:"none" }}>
          {BAGS_TOOLTIP}
        </div>,
        document.body
      )}
    </span>
  );
}

/* CSS */
export const CSS = `
/* Metric-matched fallback fonts (CLS) — self-contained in the React-app CSS so
   the Cabinet Grotesk / Instrument Serif web-font swap reflows minimally even on
   the React-rendered charter/dest pages, not only where a .astro shell head
   happens to declare them. 'CG Fallback' MIRRORS the .astro @font-face EXACTLY
   (index.astro:289 / [...slug].astro:466); --sans already references it. 'IS
   Fallback' is a serif metric-match for Instrument Serif (none existed anywhere):
   size-adjust:100% keeps Georgia's width so line-wrapping is unchanged (no
   horizontal CLS risk), ascent/descent overrides nudge the line box toward
   Instrument Serif. font-display:swap is carried by the self-hosted @font-face
   in FONTS (App.jsx:~228). */
@font-face {
  font-family: 'CG Fallback';
  src: local('Arial');
  size-adjust: 93%;
  ascent-override: 91%;
  descent-override: 21%;
  line-gap-override: 0%;
}
@font-face {
  font-family: 'IS Fallback';
  src: local('Georgia');
  size-adjust: 100%;
  ascent-override: 88%;
  descent-override: 25%;
  line-gap-override: 0%;
}
.rpad{padding:0 36px;}

*,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
html{overflow-x:hidden;}
body{overflow-x:hidden;max-width:100%;position:relative;}
img,video,svg{max-width:100%;}
#root,#app{overflow-x:hidden;}
:root{
  --w:#FFFFFF; --off:#F9F8F5; --off2:#F2F0EB;
  --bd:#E6E3DC; --bd2:#CCCAC2;
  --ink:#18191A; --ink2:#2D2F33;
  --mu:#605E5B; --mu2:#797672;
  --bl:#0D56A6; --bl2:#0A4080;
  --blbg:#EAF1FB; --blmid:#B8D0F0;
  --gr:#0A6640; --grbg:#E6F4ED;
  --serif:'Instrument Serif','IS Fallback',Georgia,serif;
  /* 'CG Fallback' is the metric-matched Arial overlay declared in
     src/pages/index.astro <head> so the swap from fallback to Cabinet
     Grotesk causes minimal CLS. Keep system-ui as a final fallback
     for environments where Arial is unavailable. */
  --sans:'Cabinet Grotesk','CG Fallback',system-ui,sans-serif;
  --nav:68px; --max:1160px;
}
html{scroll-behavior:auto;}
body{font-family:var(--sans);background:var(--w);color:var(--ink);line-height:1.55;-webkit-font-smoothing:antialiased;overflow-x:hidden;}
button,input,select,textarea{font-family:inherit;}
img{display:block;}

.wrap{max-width:var(--max);margin:0 auto;padding:0 36px;width:100%;}
.sec{padding:88px 0;background:white;}
.sec-sm{padding:56px 0;background:white;}

/* Nav */
.nav{position:fixed;top:0;left:0;right:0;z-index:200;height:var(--nav);background:rgba(255,255,255,.94);backdrop-filter:blur(16px);border-bottom:1px solid var(--bd);display:flex;align-items:center;transition:box-shadow .25s;}
.leaflet-container{position:relative;z-index:0;}
/* Leaflet map controls (2026-05-19o) — unified 30x30 footprint for
   the zoom (+/-) buttons AND the custom layers toggle so all three
   controls feel proportional to the map view. Earlier override
   (455f197) sized the layers toggle at 40x40 which looked oversized
   next to the default 30x30 zoom buttons; this pass shrinks the
   layers toggle to match. Layers icon is the Lucide "Layers" inline
   SVG data URI (no external fetch — zero risk of the "big white
   square" sprite-load failure the original 455f197 commit fixed).
   Hover state matches the operator-portal KPI-card vocabulary
   (#EAF1FB background + #0D56A6 inset shadow). Functionality
   unchanged — still toggles CARTO Voyager / Esri World Imagery. */
.leaflet-control-zoom a,
.leaflet-control-layers-toggle{
  width:30px !important;
  height:30px !important;
  line-height:30px !important;
  border-radius:6px;
  transition:background-color .12s, box-shadow .12s;
}
.leaflet-control-zoom a{font-size:18px;font-weight:700;color:#18191A}
.leaflet-control-layers-toggle{
  background-image:url("data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%230D56A6' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M12 2L2 7l10 5 10-5-10-5z'/%3E%3Cpath d='M2 17l10 5 10-5'/%3E%3Cpath d='M2 12l10 5 10-5'/%3E%3C/svg%3E") !important;
  background-size:16px 16px;
  background-position:center;
  background-repeat:no-repeat;
}
.leaflet-control-zoom a:hover,
.leaflet-control-layers-toggle:hover{
  background-color:#EAF1FB;
  box-shadow:0 0 0 1px #0D56A6 inset;
}
.leaflet-control-layers-expanded .leaflet-control-layers-toggle{display:none}
.leaflet-control-layers-expanded{padding:8px 12px;border-radius:8px;font-size:13px;line-height:1.6}
.leaflet-control-layers-base label{cursor:pointer;display:flex;align-items:center;gap:8px;padding:2px 0}
.hp-pulse-wrap{position:relative;width:50px;height:50px;}
.hp-pulse-dot{position:absolute;top:50%;left:50%;width:18px;height:18px;background:#0D56A6;border:3px solid white;border-radius:50%;transform:translate(-50%,-50%);box-shadow:0 2px 8px rgba(0,0,0,.4);z-index:2;}
.hp-pulse-ring{position:absolute;top:50%;left:50%;width:18px;height:18px;border:2px solid #0D56A6;border-radius:50%;transform:translate(-50%,-50%);animation:hp-pulse 2s ease-out infinite;will-change:transform,opacity;}
@keyframes hp-pulse{0%{transform:translate(-50%,-50%) scale(1);opacity:1;}100%{transform:translate(-50%,-50%) scale(2.8);opacity:0;}}
.hotel-pin-label{background:white;color:var(--ink,#1f2937);border:none;font-size:13px;font-weight:600;padding:6px 14px;border-radius:999px;box-shadow:0 2px 8px rgba(0,0,0,.15);white-space:nowrap;}
.hotel-pin-label::before{display:none !important;}
.nav.up{box-shadow:0 2px 24px rgba(0,0,0,.07);}
.nav-in{width:100%;max-width:var(--max);margin:0 auto;padding:0 36px;display:flex;align-items:center;}
.logo{font-size:17px;font-weight:800;color:var(--ink);cursor:pointer;letter-spacing:-.03em;margin-right:28px;}
.logo span{color:var(--bl);}
.nls{display:flex;align-items:center;gap:2px;flex:1;}
.ham{display:none;flex-direction:column;gap:5px;cursor:pointer;padding:6px;background:none;border:none;}
.ham span{display:block;width:22px;height:2px;background:var(--ink);border-radius:2px;transition:all .25s;}
.mob-menu{position:fixed;inset:0;z-index:490;display:flex;flex-direction:column;}
.mob-backdrop{position:absolute;inset:0;background:rgba(0,0,0,.4);}
.mob-sheet{position:absolute;top:0;left:0;right:0;max-height:100vh;background:white;border-radius:0 0 24px 24px;padding:0 0 24px;box-shadow:0 8px 40px rgba(0,0,0,.15);transform:translateY(-100%);transition:transform .35s cubic-bezier(.32,0,.15,1);display:flex;flex-direction:column;}
.mob-sheet.open{transform:translateY(0);}
.mob-nav-header{display:flex;align-items:center;justify-content:space-between;padding:14px 20px;border-bottom:1px solid var(--bd);flex-shrink:0;}
.mob-scroll{overflow-y:auto;-webkit-overflow-scrolling:touch;flex:1;max-height:calc(100vh - 130px);}
.mob-link{display:block;padding:14px 20px;font-size:16px;font-weight:600;color:var(--ink);border:none;background:none;width:100%;text-align:left;cursor:pointer;border-bottom:1px solid var(--bd);text-decoration:none;}
.mob-link:last-child{border-bottom:none;}
.mob-link:active{background:var(--off);}
.mob-parent{display:flex;align-items:center;justify-content:space-between;padding:14px 20px;font-size:16px;font-weight:700;color:var(--ink);border:none;background:none;width:100%;text-align:left;cursor:pointer;border-bottom:1px solid var(--bd);}
.mob-parent:active{background:var(--off);}
.mob-child{display:block;padding:11px 20px 11px 36px;font-size:15px;font-weight:500;color:var(--mu);border:none;background:none;width:100%;text-align:left;cursor:pointer;text-decoration:none;}
.mob-child:active{background:var(--off);color:var(--bl);}
.nl{font-size:13.5px;font-weight:500;color:var(--mu);cursor:pointer;padding:8px 12px;border-radius:8px;transition:all .15s;display:flex;align-items:center;gap:4px;position:relative;white-space:nowrap;text-decoration:none;}
.nl:hover,.nl.on{color:var(--ink);background:var(--off);}
.dd{position:absolute;top:100%;left:0;background:white;border:1px solid var(--bd);border-radius:18px;box-shadow:0 16px 48px rgba(0,0,0,.1);min-width:210px;padding:8px;opacity:0;transform:translateY(4px);pointer-events:none;transition:opacity .18s,transform .18s;z-index:300;}
.dd-wrap{position:relative;padding-bottom:0;}
.dd-wrap:hover .dd{opacity:1;transform:translateY(0);pointer-events:all;}
.ddi{display:block;width:100%;padding:9px 13px;border-radius:8px;font-size:13.5px;color:var(--ink2);cursor:pointer;transition:background .12s;text-align:left;border:none;background:transparent;font-family:inherit;text-decoration:none;}
.ddi:hover{background:var(--off);}
.nav-r{display:flex;align-items:center;gap:8px;margin-left:auto;}
.nbtn{padding:10px 22px;border-radius:10px;font-size:13.5px;font-weight:600;cursor:pointer;border:none;transition:all .15s;letter-spacing:-.01em;white-space:nowrap;}
.nbtn-g{background:transparent;color:var(--mu);border:1.5px solid var(--bd);}
.nbtn-g:hover{border-color:var(--ink);color:var(--ink);}
.nbtn-p{background:var(--bl);color:white;}
.nbtn-p:hover{background:var(--bl2);}

/* Buttons */
.btn{display:inline-flex;align-items:center;justify-content:center;gap:8px;padding:11px 22px;border-radius:10px;font-size:14px;font-weight:600;border:none;cursor:pointer;transition:all .15s;white-space:nowrap;letter-spacing:-.01em;}
.btn-bl{background:var(--bl);color:white;}
.btn-bl:hover{background:var(--bl2);transform:translateY(-1px);box-shadow:0 6px 20px rgba(13,86,166,.3);}
.btn-bl:disabled{opacity:.45;cursor:not-allowed;transform:none;box-shadow:none;}
.btn-wh{background:white;color:var(--ink);}
.btn-wh:hover{background:var(--off);}
.btn-ol{background:transparent;color:var(--ink);border:1.5px solid var(--bd);}
.btn-ol:hover{border-color:var(--ink);}
.btn-lg{padding:15px 30px;font-size:15px;border-radius:12px;}
.btn-sm{padding:8px 16px;font-size:13px;border-radius:8px;}

/* Tags */
.tag{display:inline-flex;align-items:center;gap:5px;padding:4px 10px;border-radius:100px;font-size:11.5px;font-weight:600;}
.tag-bl{background:var(--blbg);color:var(--bl);}
.tag-gr{background:var(--grbg);color:var(--gr);}

/* Cards */
.card{background:white;border-radius:18px;border:1px solid var(--bd);}
.card-sh{box-shadow:0 4px 24px rgba(0,0,0,.06);}
/* Review gallery (2026-08-15) — CSS scroll-snap only, no carousel library and
   no JS: the container scrolls with touch, trackpad, scrollbar and keyboard.
   tabIndex on the container is what makes arrow keys work; :focus-visible
   gives that the visible ring the rest of the site uses (var(--bl)).
   Existing tokens only — --bd, --bl and the 12px gap match the grid it
   replaces, so one and six reviews sit on the same rhythm. */
.rev-gallery{display:flex;gap:12px;overflow-x:auto;scroll-snap-type:x mandatory;-webkit-overflow-scrolling:touch;padding:2px 2px 10px;scrollbar-width:thin;overscroll-behavior-x:contain;}
.rev-gallery>.rev-card{scroll-snap-align:start;flex:0 0 300px;max-width:88vw;}
.rev-gallery:focus-visible{outline:2px solid var(--bl);outline-offset:3px;border-radius:18px;}
.rev-gallery::-webkit-scrollbar{height:8px;}
.rev-gallery::-webkit-scrollbar-thumb{background:var(--bd2);border-radius:4px;}
@media (prefers-reduced-motion:reduce){.rev-gallery,.jt-grid{scroll-behavior:auto;}}
.card-hv{transition:all .22s;cursor:pointer;}
.card-hv:hover{transform:translateY(-4px);box-shadow:0 16px 48px rgba(0,0,0,.1);border-color:var(--bd2);}
/* Aircraft selection card — keyboard focus ring (hover handled inline in SearchPage) */
.ac-card:focus{outline:none;}
.ac-card:focus-visible{outline:2.5px solid var(--bl);outline-offset:2px;}

/* Forms */
.field{display:flex;flex-direction:column;gap:5px;}
.fl{font-size:11.5px;font-weight:700;letter-spacing:.07em;text-transform:uppercase;color:var(--mu);}
.fi{background:var(--off);border:1.5px solid transparent;border-radius:10px;padding:11px 14px;font-size:16px;color:var(--ink2);outline:none;transition:all .15s;width:100%;}
input[type=number]::-webkit-inner-spin-button,input[type=number]::-webkit-outer-spin-button{-webkit-appearance:none;margin:0;}
input[type=number]{-moz-appearance:textfield;}
.fi:focus{border-color:var(--bl);background:var(--off);box-shadow:0 0 0 3px rgba(13,86,166,.08);}
.fi::placeholder{color:var(--mu2);}
select.fi{cursor:pointer;}
textarea.fi{resize:none;}

/* Heads */
.eyebrow{font-size:11.5px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:var(--bl);margin-bottom:10px;display:flex;align-items:center;gap:8px;}
.eyebrow::before{content:'';width:16px;height:2px;background:var(--bl);border-radius:2px;}
.h2{font-size:clamp(26px,3vw,42px);font-weight:800;letter-spacing:-.045em;color:var(--ink);line-height:1.05;}
.h2 em{font-family:var(--serif);font-style:italic;font-weight:400;}
.h3{font-size:20px;font-weight:700;letter-spacing:-.03em;color:var(--ink);}

/* Steps */
.steps{display:flex;align-items:center;gap:0;padding:0 8px;}
.st-it{display:flex;flex-direction:column;align-items:center;gap:5px;padding:14px 8px;cursor:default;position:relative;}
.st-it.clickable{cursor:pointer;}
.st-c{width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:800;flex-shrink:0;transition:all .2s;font-family:var(--sans);}
.st-c.done{background:var(--gr);color:white;box-shadow:0 0 0 3px var(--grbg);}
.st-c.active{background:var(--bl);color:white;box-shadow:0 0 0 3px var(--blbg);}
.st-c.pend{background:white;color:var(--mu2);border:2px solid var(--bd);}
.st-l{font-size:11px;font-weight:600;white-space:nowrap;}
.st-l.done{color:var(--gr);}
.st-l.active{color:var(--bl);}
.st-l.pend{color:var(--mu2);}
.st-sep{flex:1;height:2px;margin:0 2px;margin-bottom:20px;border-radius:2px;transition:background .3s;}
.st-sep.done{background:var(--gr);}
.st-sep.pend{background:var(--bd);}

/* Sidebar */
.sb{background:var(--off);border-radius:18px;padding:22px;border:1px solid var(--bd);}
.sb-t{font-size:14px;font-weight:700;color:var(--ink);margin-bottom:14px;}
.sb-r{display:flex;justify-content:space-between;align-items:baseline;padding:7px 0;border-bottom:1px solid var(--bd);}
.sb-r:last-of-type{border-bottom:none;}
.sb-l{font-size:13px;color:var(--mu);}
.sb-v{font-size:13px;font-weight:600;color:var(--ink);}
.sb-tot{display:flex;justify-content:space-between;align-items:baseline;padding-top:12px;margin-top:4px;border-top:2px solid var(--bd2);}
.sb-tl{font-size:14px;font-weight:700;}
.sb-tv{font-size:22px;font-weight:800;letter-spacing:-.04em;}

/* Pax */
.overlay{position:fixed;inset:0;background:rgba(0,0,0,.45);z-index:400;display:flex;align-items:flex-end;justify-content:center;}
.bk-overlay{position:fixed;inset:0;z-index:500;display:flex;align-items:center;justify-content:center;pointer-events:none;}
.bk-backdrop{position:absolute;inset:0;background:rgba(6,16,38,.6);backdrop-filter:blur(6px);cursor:pointer;opacity:0;transition:opacity .35s;}
.bk-backdrop.open{opacity:1;}
.bk-sheet{position:absolute;top:0;bottom:0;left:50%;width:100%;max-width:1100px;background:var(--off);transform:translateX(-50%) translateY(100%);transition:transform .4s cubic-bezier(.32,0,.15,1);box-shadow:0 0 80px rgba(0,0,0,.25);display:grid;grid-template-columns:1fr 320px;grid-template-rows:auto 1fr;overflow:hidden;}
.bk-sheet.open{transform:translateX(-50%) translateY(0);}
.bk-header{grid-column:1/-1;display:flex;align-items:center;padding:0 20px;background:white;border-bottom:1px solid var(--bd);height:48px;flex-shrink:0;touch-action:none;user-select:none;position:relative;}
.bk-handle{width:40px;height:4px;background:var(--bd2);border-radius:2px;position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);display:none;}
.bk-close{width:34px;height:34px;border-radius:50%;background:var(--off);border:1px solid var(--bd);cursor:pointer;display:flex;align-items:center;justify-content:center;transition:background .15s;flex-shrink:0;margin-left:auto;}
.bk-close:hover{background:var(--bd);}
.bk-content{overflow-y:auto;overflow-x:clip;overscroll-behavior:contain;position:relative;display:flex;flex-direction:column;font-size:14px;}
.bk-content p,.bk-content .card p{font-size:14px;line-height:1.7;}
.bk-content .field .fl{font-size:12px;}
.bk-sidebar-panel{background:white;border-left:1px solid var(--bd);overflow-y:auto;display:flex;flex-direction:column;}
.pax-pop{background:white;border-radius:18px 18px 0 0;padding:24px;width:100%;max-width:420px;}
.pax-row{display:flex;align-items:center;justify-content:space-between;padding:12px 0;border-bottom:1px solid var(--bd);}
.pax-row:last-child{border-bottom:none;}
.pax-nm{font-size:14px;font-weight:600;}
.pax-hint{font-size:12px;color:var(--mu2);margin-top:2px;}
.pax-ctrl{display:flex;align-items:center;gap:12px;}
.pax-n{font-size:18px;font-weight:700;min-width:24px;text-align:center;}
.pax-btn{width:32px;height:32px;border-radius:50%;border:1.5px solid var(--bd2);background:white;display:flex;align-items:center;justify-content:center;cursor:pointer;transition:all .15s;}
.pax-btn:hover:not(:disabled){border-color:var(--bl);background:var(--blbg);}
.pax-btn:disabled{opacity:.35;cursor:not-allowed;}

/* Hero */
.hero{position:relative;min-height:100vh;padding-top:var(--nav);display:flex;flex-direction:column;justify-content:center;overflow:hidden;}
.hero-bg{position:absolute;inset:0;z-index:0;}
.hero-bg img{object-fit:cover;width:100%;height:100%;filter:brightness(.95) saturate(1.05);}
.hero-bg::after{content:'';position:absolute;inset:0;background:linear-gradient(105deg,rgba(6,16,38,.92) 0%,rgba(6,16,38,.7) 48%,rgba(6,16,38,.32) 100%);}
.hero-c{position:relative;z-index:2;padding:80px 0 60px;}
/* heroFadeUp 2026-05-19c — element is fully visible from frame 0 so the
   LCP timer doesn't wait for the opacity ramp. Only translateY is
   animated; the visual effect is identical (subtle settle from below)
   without delaying the largest-contentful-paint measurement. */
@keyframes heroFadeUp{from{transform:translateY(10px);}to{transform:translateY(0);}}
.hero-anim{animation:heroFadeUp .7s cubic-bezier(.22,1,.36,1) both;will-change:transform,opacity;}
.hero-anim-d1{animation-delay:.1s;}.hero-anim-d2{animation-delay:.25s;}.hero-anim-d3{animation-delay:.4s;}.hero-anim-d4{animation-delay:.55s;}.hero-anim-d5{animation-delay:.7s;}
.hero-trust-row{display:flex;gap:20px;flex-wrap:wrap;margin-top:32px;}
.hero-trust-item{display:flex;align-items:center;gap:7px;font-size:12px;color:rgba(255,255,255,.65);letter-spacing:.01em;}
.hero-trust-item svg{opacity:.5;}
.hero-cta-row{display:flex;gap:12px;flex-wrap:wrap;margin-bottom:32px;}
.dest-pill-btn{transition:all .2s!important;}
.dest-pill-btn:hover{background:rgba(255,255,255,.22)!important;border-color:rgba(255,255,255,.4)!important;transform:translateY(-1px);}

/* Search widget */
.sw{background:white;border-radius:22px;box-shadow:0 20px 64px rgba(0,0,0,.13),0 4px 16px rgba(0,0,0,.05);border:1px solid var(--bd);overflow:visible;position:relative;}
.sw-tabs{display:flex;border-bottom:1px solid var(--bd);}
.sw-tab{flex:1;display:flex;align-items:center;justify-content:center;gap:5px;padding:14px 10px;font-size:12.5px;font-weight:600;border:none;cursor:pointer;transition:all .15s;border-bottom:2.5px solid transparent;color:var(--mu);background:transparent;white-space:nowrap;}
.sw-tab.on{border-bottom-color:var(--bl);color:var(--bl);}
.sw-b{padding:20px 24px 22px;}
.sw-row{display:grid;grid-template-columns:1fr 36px 1fr;gap:8px;align-items:end;margin-bottom:16px;}
.sw-swap{width:36px;height:44px;border-radius:8px;background:var(--off);border:1.5px solid var(--bd);display:flex;align-items:center;justify-content:center;cursor:pointer;transition:all .15s;flex-shrink:0;}
.sw-swap:hover{background:var(--blbg);border-color:var(--blmid);}
.sw-cols2{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:16px;}
.sw-cols3{display:grid;grid-template-columns:1fr 1fr 1fr;gap:12px;margin-bottom:16px;}
.sw-cols4{display:grid;grid-template-columns:1fr 1fr 1fr 1fr;gap:12px;margin-bottom:16px;}
.sw-divider{display:flex;align-items:center;gap:10px;margin-bottom:10px;font-size:11px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:var(--mu2);}
.sw-divider::before,.sw-divider::after{content:'';flex:1;height:1px;background:var(--bd);}
.sw-foot{display:flex;align-items:center;justify-content:space-between;padding-top:16px;border-top:1px solid var(--bd);margin-top:4px;}
.sw-hint{font-size:11.5px;color:var(--mu2);display:flex;align-items:center;gap:5px;}

/* Fleet card */
.fc{background:white;border-radius:18px;border:1px solid var(--bd);overflow:hidden;transition:border-color .2s,box-shadow .2s;cursor:pointer;display:flex;flex-direction:column;}
.fc:hover{border-color:var(--blmid);box-shadow:0 8px 32px rgba(13,86,166,.1);}
.fc-img{aspect-ratio:3/2;position:relative;overflow:hidden;background:linear-gradient(135deg,#F8F9FB 0%,#EEF1F6 100%);border-bottom:1px solid var(--bd);}
.fc-img>div{position:absolute!important;inset:0;height:100%!important;}
.fc-img img{width:100%;height:100%;object-fit:cover;transition:transform .45s ease;}
.fc:hover .fc-img img{transform:scale(1.05);}
.fc-body{padding:20px 22px;flex:1;}
.fc-type{font-size:11px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:var(--mu);margin-bottom:5px;}
.fc-name{font-size:19px;font-weight:800;letter-spacing:-.04em;margin-bottom:8px;}
.fc-desc{font-size:13px;color:var(--mu);line-height:1.6;margin-bottom:14px;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;min-height:3.2em;}
.fc-specs{display:flex;gap:14px;flex-wrap:wrap;}
.fc-spec{display:flex;align-items:center;gap:5px;font-size:12px;color:var(--mu);font-weight:500;}
.fc-foot{padding:13px 22px;background:var(--off);border-top:1px solid var(--bd);display:flex;align-items:center;justify-content:space-between;}
.fc-p{font-size:20px;font-weight:800;letter-spacing:-.04em;}
.fc-pl{font-size:11px;color:var(--mu2);margin-top:1px;}

/* Destination grid */
.dg{display:grid;grid-template-columns:2fr 1fr 1fr;grid-template-rows:260px 260px;gap:12px;}
.dc{border-radius:18px;overflow:hidden;position:relative;cursor:pointer;}
.dc:first-child{grid-row:1/3;}
.dc-in{width:100%;height:100%;transition:transform .45s ease;}
.dc:hover .dc-in{transform:scale(1.05);}
.dc-in img{width:100%;height:100%;object-fit:cover;}
.dc-ov{position:absolute;inset:0;background:linear-gradient(0deg,rgba(6,16,38,.75) 0%,rgba(6,16,38,.05) 55%,transparent 100%);z-index:1;}
.dc-inf{position:absolute;bottom:0;left:0;right:0;padding:20px;z-index:2;}
.dc-nm{font-size:22px;font-weight:800;color:white;display:block;letter-spacing:-.03em;}
.dc:first-child .dc-nm{font-size:34px;}
.dc-meta{font-size:11px;color:rgba(255,255,255,.65);margin-top:5px;display:flex;gap:8px;align-items:center;}
.dc-pill{background:rgba(255,255,255,.16);padding:2px 9px;border-radius:100px;font-weight:600;backdrop-filter:blur(4px);}

/* Service cards */
.sg{display:grid;grid-template-columns:repeat(3,1fr);gap:16px;}
.sc{border-radius:18px;overflow:hidden;position:relative;cursor:pointer;height:clamp(200px,35vw,280px);}
.sc img{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;transition:transform .45s ease;}
.sc:hover img{transform:scale(1.07);}
.sc-ov{position:absolute;inset:0;background:linear-gradient(0deg,rgba(6,16,38,.92) 0%,rgba(6,16,38,.35) 60%,rgba(6,16,38,.1) 100%);z-index:1;}
.sc-c{position:absolute;bottom:0;left:0;right:0;padding:22px;z-index:2;}
.sc-icon{width:40px;height:40px;border-radius:10px;background:rgba(255,255,255,.15);backdrop-filter:blur(6px);display:flex;align-items:center;justify-content:center;margin-bottom:10px;}
.sc-title{font-size:18px;font-weight:800;color:white;letter-spacing:-.03em;margin-bottom:4px;}
.sc-sub{font-size:12px;color:rgba(255,255,255,.92);}

/* Homepage journeys ("How our clients fly") — desktop grid; on narrow
   viewports the SAME scroll-snap pattern as .rev-gallery (identical snap,
   card sizing and scrollbar values — responsive application of the one
   existing gallery mechanism, not a second variant). */
.jt-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:20px;}
.jt-card{padding:28px;display:flex;flex-direction:column;}

/* Blog */
.bg{display:grid;grid-template-columns:repeat(3,1fr);gap:20px;}
.bc{border-radius:18px;overflow:hidden;border:1px solid var(--bd);background:white;cursor:pointer;transition:all .2s;}
.bc:hover{transform:translateY(-3px);box-shadow:0 12px 36px rgba(0,0,0,.09);}
.bc-img{height:190px;overflow:hidden;}
.bc-img img{width:100%;height:100%;object-fit:cover;transition:transform .45s ease;}
.bc:hover .bc-img img{transform:scale(1.05);}
.bc-body{padding:20px;}
.bc-cat{font-size:11px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:var(--bl);margin-bottom:8px;}
.bc-title{font-size:16px;font-weight:700;color:var(--ink);margin-bottom:8px;letter-spacing:-.02em;line-height:1.3;}
.bc-exc{font-size:13px;color:var(--mu);line-height:1.6;}

/* Extras */
.ex-it{display:flex;justify-content:space-between;align-items:center;padding:14px;border:1.5px solid var(--bd);border-radius:10px;cursor:pointer;transition:all .15s;}
.ex-it:hover{border-color:var(--blmid);background:var(--blbg);}
.ex-it.sel{border-color:var(--bl);background:var(--blbg);}
.ex-chk{width:22px;height:22px;border-radius:50%;border:1.5px solid var(--bd2);background:white;display:flex;align-items:center;justify-content:center;transition:all .15s;flex-shrink:0;}
.ex-it.sel .ex-chk{background:var(--bl);border-color:var(--bl);}

/* Payment */
.pm{display:flex;align-items:center;gap:14px;padding:15px 17px;border:1.5px solid var(--bd);border-radius:12px;cursor:pointer;transition:all .15s;background:white;font:inherit;color:inherit;text-align:left;width:100%;}
.pm:hover{border-color:var(--blmid);}
.pm.sel{border-color:var(--bl);background:var(--blbg);}
.pm-r{width:18px;height:18px;border-radius:50%;border:1.5px solid var(--bd2);flex-shrink:0;position:relative;transition:all .15s;}
.pm.sel .pm-r{border-color:var(--bl);}
.pm.sel .pm-r::after{content:'';position:absolute;inset:0;margin:auto;width:8px;height:8px;border-radius:50%;background:var(--bl);}

/* Page hero */
.ph{position:relative;overflow:hidden;min-height:520px;display:flex;align-items:flex-end;}
.ph-hero-rich{position:relative;min-height:520px;overflow:hidden;display:flex;align-items:flex-end;}
.ph-bg{position:absolute;inset:0;z-index:0;background:linear-gradient(135deg,#0a3d7a 0%,#061026 100%);}
.ph-bg img{width:100%;height:100%;object-fit:cover;}
/* Charter LCP-flip guard: the SSR #charter-static-hero img paints the LCP early
   (~load-bound), but CharterServicePage is a lazy chunk, so when it hydrates and
   reveals this byte-matched React hero, Chrome would register the React img as a
   new, equal-or-marginally-larger LCP candidate that supersedes the SSR one and
   re-anchors LCP on the late (hydration-gated) paint (~5-8s, render-bound). Making
   the React hero img 6px shorter keeps its area strictly below the SSR img, so
   Chrome retains the early SSR LCP. The 6px sit at the hero's dark bottom edge,
   under the navy gradient (.ph-bg base + ::after) — visually identical to the
   full image (verified). Charter-scoped only; dest/hotel/route React heroes are
   eager (paint at App-mount) and unaffected. */
.ph-charter-lcp .ph-bg img{height:calc(100% - 6px);}
.ph-bg::after{content:'';position:absolute;inset:0;background:linear-gradient(105deg,rgba(6,16,38,.9) 0%,rgba(6,16,38,.65) 60%,rgba(6,16,38,.4) 100%);}
.ph-c{position:relative;z-index:2;padding:calc(var(--nav) + 48px) 0 52px;width:100%;}
.ph-c h1,.ph-c .ph-h1{fontFamily:var(--sans);font-weight:800;font-size:clamp(28px,4vw,48px);letter-spacing:-.05em;color:white;line-height:1.08;margin-bottom:14px;max-width:680px;}
.ph-c p{font-size:15px;color:rgba(255,255,255,.7);max-width:520px;line-height:1.75;}
/* Canonical detail-article body text (typography wave 2026-07-08) — ONE
   definition for every route/dest/hotel/blog article paragraph and FAQ
   answer. Margins/max-width stay per-site (layout, not typography). */
.body-p{font-size:15px;line-height:1.85;color:var(--mu);}

/* Confirm */
.ci{width:72px;height:72px;border-radius:50%;background:var(--grbg);display:flex;align-items:center;justify-content:center;margin:0 auto 22px;}

/* Footer */
footer{background:var(--ink);color:white;padding:0;}
.f-cta{padding:56px 0;border-bottom:1px solid rgba(255,255,255,.08);}
.f-main{padding:64px 0 40px;}
.fg{display:grid;grid-template-columns:1.4fr 1fr 1fr 1fr 1fr;gap:40px;}
.flogo{font-size:17px;font-weight:800;letter-spacing:-.03em;margin-bottom:12px;display:block;}
.flogo span{color:var(--blmid);}
.fdesc{font-size:13px;color:rgba(255,255,255,.6);line-height:1.75;max-width:260px;}
.f-contact{margin-top:20px;display:flex;flex-direction:column;gap:8px;}
.f-contact a,.f-contact span{font-size:13px;color:rgba(255,255,255,.65);text-decoration:none;display:flex;align-items:center;gap:8px;transition:color .15s;cursor:pointer;}
.f-contact a:hover,.f-contact span:hover{color:white;}
.fch{font-size:10.5px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:rgba(255,255,255,.55);margin-bottom:16px;}
.flinks{display:flex;flex-direction:column;gap:10px;}
.flink{font-size:13.5px;color:rgba(255,255,255,.45);cursor:pointer;transition:color .15s;background:none;border:none;text-align:left;padding:0;display:block;font-family:inherit;}
.flink:hover{color:white;}
.fsocs{display:flex;gap:8px;margin-top:20px;}
.fsoc{width:36px;height:36px;border-radius:10px;background:rgba(255,255,255,.06);display:flex;align-items:center;justify-content:center;cursor:pointer;transition:all .2s;border:1px solid rgba(255,255,255,.06);}
.fsoc:hover{background:rgba(255,255,255,.12);border-color:rgba(255,255,255,.12);}
.f-trust{display:flex;gap:24px;align-items:center;flex-wrap:wrap;padding:24px 0;border-top:1px solid rgba(255,255,255,.06);border-bottom:1px solid rgba(255,255,255,.06);margin-top:40px;}
.f-trust-badge{display:flex;align-items:center;gap:8px;font-size:11.5px;color:rgba(255,255,255,.6);letter-spacing:.01em;}
.f-trust-badge svg{opacity:.65;}
.fbot{display:flex;justify-content:space-between;align-items:center;padding:24px 0 0;}
.fcopy{font-size:11.5px;color:rgba(255,255,255,.5);}
.fbl{display:flex;gap:20px;flex-wrap:wrap;}
.fbll{font-size:11.5px;color:rgba(255,255,255,.6);cursor:pointer;transition:color .15s;background:none;border:none;padding:0;font-family:inherit;}
.fbll:hover{color:rgba(255,255,255,.92);}
.fleet-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:20px;}
/* Public /fleet aircraft cards — fluid auto-fill grid (1/2/3 cols, no hard breakpoints). Dedicated class so the shared .fleet-grid (portal/booking) rules + !important media overrides do not apply here. */
.ac-cards-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(300px,1fr));gap:24px;}
/* Booking Step-2 - hoper-style horizontal aircraft rows. Image stays a 3:2
   cover box (HeliGallery pipeline unchanged); content + price/CTA flow right;
   the row expands the policy + extras panel beneath it. */
.ac-rows{display:flex;flex-direction:column;gap:14px;}
/* Base card chrome — light 1px border + radius + clip. /fleet/carousel rely on
   this for their outline; Step-2's inline border (2px, selectable states) wins
   via specificity so it is unaffected. */
.ac-card{background:white;border:1px solid var(--bd);border-radius:18px;overflow:hidden;}
.ac-card.card-hv:hover{border-color:var(--blmid);box-shadow:0 8px 32px rgba(13,86,166,.1);}
.ac-row-top{display:flex;align-items:stretch;}
/* Image column is a STRICT 3:2 box (aspect-ratio on the column, fc-img fills it)
   so the format never drifts regardless of row/content height. align-self
   flex-start keeps it 3:2 even if the content column ends up taller. */
.ac-row-img{position:relative;width:300px;flex-shrink:0;aspect-ratio:3/2;align-self:flex-start;}
.ac-row-img .fc-img{height:100%;aspect-ratio:auto;border-bottom:none;}
/* Funnel-only image disclaimer — a dezent caption pinned to the bottom of the
   aircraft photo (Step-3 charter + sightseeing cards). Absolute so it never alters
   the 3:2 image box or pushes the specs/price/CTA; pointer-events:none keeps the
   whole card clickable. Sits below the most-booked badge (z4) / unavailable veil (z5).
   Works in both layouts: desktop (left image column) + mobile (full-width stacked). */
.ac-img-disclaimer{position:absolute;left:0;right:0;bottom:0;z-index:3;padding:3px 8px;font-size:9.5px;line-height:1.3;letter-spacing:.01em;color:var(--mu);text-align:center;background:rgba(255,255,255,.86);border-top:1px solid rgba(0,0,0,.04);pointer-events:none;}
/* Top-align eyebrow + name with the image's top edge (frees vertical space).
   Padding trimmed (was 13/14) to give the two Step-2 spec rows vertical room
   within the 200px image height. */
.ac-row-mid{flex:1;min-width:0;padding:11px 12px;display:flex;flex-direction:column;justify-content:flex-start;gap:6px;overflow:hidden;}
/* Price column narrowed to 130 so the middle stat column is wide enough for two
   3-chip rows whose chips each stay on one line (verified at widest plausible
   values). Price block content unchanged + verified non-clipping at this width
   ("EUR 4,250" / "one way · incl. VAT"). */
.ac-row-right{flex-shrink:0;width:130px;padding:13px 12px;display:flex;flex-direction:column;justify-content:center;gap:8px;border-left:1px solid var(--bd);background:var(--off);overflow:hidden;}
.ac-chips{display:flex;flex-wrap:wrap;gap:6px;}
/* /fleet + carousel have a wide content column (~632px): distribute the chips
   as equal-width boxes spanning the full width (repeat(auto-fit,1fr)) so they look
   intentionally placed across the card, not clumped left. minmax floor holds the
   widest value ("260 km/h") on one line; on narrow/mobile the row wraps to a tidy
   3-up grid. Scoped to .ac-chips-roomy — Step-2 (.ac-chip-rows) is unaffected. */
.ac-chips-roomy{display:grid;grid-template-columns:repeat(auto-fit,minmax(84px,1fr));gap:12px;}
/* Step-2: an equal 3-column grid so all 6 boxes are identical width and row 2
   (Speed/Range/Duration) sits exactly under row 1 (Seats/Bags/Payload). */
.ac-chip-rows{display:grid;grid-template-columns:repeat(3,1fr);gap:5px;}
/* Hoper-style stat chips: subtle fill, no hard border, content centred both axes.
   min-width:0 lets the grid columns equalise the box widths; sized so two 3-chip
   rows + eyebrow + name + base fit within the 200px image height on Step-2 while
   the value still carries its unit where the unit differs from the label. */
.ac-chip{display:flex;flex-direction:column;align-items:center;justify-content:center;gap:1px;padding:5px 6px 6px;background:var(--off);border:1px solid transparent;border-radius:10px;min-width:0;text-align:center;}
/* nowrap keeps every value on one line so all grid boxes stay equal height. */
.ac-chip-v{font-size:13.5px;font-weight:800;letter-spacing:-.02em;color:var(--ink);line-height:1.15;white-space:nowrap;}
.ac-chip-c{font-size:9.5px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:var(--mu);}
/* Class eyebrow (Sanity type field, e.g. Light Single-Engine) above the name. */
.ac-eyebrow{font-size:10px;font-weight:700;letter-spacing:.07em;text-transform:uppercase;color:var(--mu);margin-bottom:3px;line-height:1.2;}
.ac-row-expand{background:none;border:none;padding:4px;cursor:pointer;color:var(--mu);display:flex;align-items:center;justify-content:center;gap:4px;font-family:inherit;font-size:11.5px;font-weight:600;-webkit-tap-highlight-color:transparent;}
.ac-row-expand:hover{color:var(--bl);}
@media(max-width:760px){
  .ac-row-top{flex-direction:column;}
  .ac-row-img{width:100%;}
  .ac-row-mid{padding:14px 16px;}
  .ac-row-right{width:100%;border-left:none;border-top:1px solid var(--bd);}
}
.dest-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:16px;}

/* Newsletter */
.nl-row{display:flex;gap:10px;}
.nl-in{flex:1;background:rgba(255,255,255,.07);border:1.5px solid rgba(255,255,255,.1);border-radius:10px;padding:11px 16px;font-size:16px;color:white;outline:none;transition:border-color .15s;font-family:inherit;}
.nl-in:focus{border-color:rgba(255,255,255,.28);}
.nl-in::placeholder{color:rgba(255,255,255,.6);}

/* Responsive booking layout utilities */
.bk-main-grid{display:grid;}
.bk-sidebar{}
.ac-grid{display:grid;}
.hp-grid{display:grid;}
.form-grid-2{display:grid;}
.form-grid-3{display:grid;}

@media(max-width:1024px){
  .wrap{padding:0 24px;}
  .fg{grid-template-columns:1fr 1fr;}
  .fleet-grid{grid-template-columns:1fr 1fr;}
  .jt-grid{grid-template-columns:repeat(2,1fr);}
  .dest-grid{grid-template-columns:repeat(2,1fr);}
  .sec{padding:64px 0;}
  .hg{grid-template-columns:1fr!important;}
}
@media(max-width:768px){
  :root{--nav:58px;}
  /* Prevent iOS Safari auto-zoom on input focus */
  input,select,textarea{font-size:16px!important;}
  .wrap{padding:0 16px;}
  .rpad{padding:0 16px!important;}
  .wrap[style]{padding-left:16px!important;padding-right:16px!important;}
  .wrap[style*="36px"]{padding-left:16px!important;padding-right:16px!important;}
  .sec{padding:48px 0;}.sec-sm{padding:36px 0;}
  .ph{min-height:auto;}
  .ph-hero-rich{min-height:auto;}
  .ph-c{padding:calc(var(--nav) + 32px) 0 36px;}
  .ph-c h1,.ph-c .ph-h1{font-size:clamp(24px,6vw,36px)!important;margin-bottom:10px!important;}
  /* Nav */
  .nls{display:none;}
  .ham{display:flex;}
  .nav-in{padding:0 16px;}
  /* Section grids → single col */
  .hg,.sg,.bg{grid-template-columns:1fr!important;}
  .fg{grid-template-columns:1fr 1fr!important;}
  /* Journeys → the reviews scroll-snap gallery pattern (same values as
     .rev-gallery/.rev-card at App.jsx ~587) */
  .jt-grid{display:flex;gap:12px;overflow-x:auto;scroll-snap-type:x mandatory;-webkit-overflow-scrolling:touch;padding:2px 2px 10px;scrollbar-width:thin;overscroll-behavior-x:contain;}
  .jt-grid>.jt-card{scroll-snap-align:start;flex:0 0 300px;max-width:88vw;}
  .jt-grid:focus-visible{outline:2px solid var(--bl);outline-offset:3px;border-radius:18px;}
  .jt-grid::-webkit-scrollbar{height:8px;}
  .jt-grid::-webkit-scrollbar-thumb{background:var(--bd2);border-radius:4px;}
  .f-cta{padding:36px 0;}
  .f-cta .wrap{flex-direction:column;align-items:flex-start!important;}
  .f-trust{flex-wrap:wrap;gap:16px;}
  .fbot{flex-direction:column;gap:12px;align-items:flex-start;}
  .dg{grid-template-columns:1fr 1fr;grid-template-rows:auto;}
  .dc:first-child{grid-column:1/3;grid-row:auto;}
  /* Stats strips */
  .ac-grid{grid-template-columns:1fr 1fr!important;}
  /* Search widget */
  .sw-tab{font-size:11.5px;gap:4px;padding:12px 6px;}
  .sw-row{grid-template-columns:1fr 32px 1fr;}
  .sw-cols3,.sw-cols4{grid-template-columns:1fr 1fr;}
  .sw-cols2{grid-template-columns:1fr 1fr;}
  .sw-foot{flex-direction:column;gap:12px;align-items:stretch;}
  .sw-b{padding:16px;}
  /* Prevent iOS Safari auto-zoom on focus (requires font-size >= 16px) */
  input.fi, select.fi, textarea.fi, button.fi { font-size:16px; }
  .fi:focus { font-size:16px; }
  /* Step bar */
  .steps{overflow-x:auto;-webkit-overflow-scrolling:touch;padding-bottom:2px;flex-wrap:nowrap;scrollbar-width:none;}
  .steps::-webkit-scrollbar{display:none;}
  .st-sep{min-width:16px;margin-bottom:16px;}
  /* Hero */
  .hero-c{padding:32px 0 24px;}
  .hero-cta-row{flex-direction:column;gap:10px;}
  .hero-cta-row .btn{width:100%;justify-content:center;}
  .hero-trust-row{gap:12px;}
  .hero-trust-item{font-size:11px;}
  /* Typography scale-down */
  .h-xl{font-size:clamp(26px,6vw,38px)!important;}
  .hg{gap:28px!important;}
  .h-lg{font-size:clamp(22px,5vw,32px)!important;}
  .h-md{font-size:clamp(20px,4.5vw,26px)!important;}
  /* Section padding */
  .sec{padding:40px 0;}
  .sec-sm{padding:28px 0;}
  .sec-hero{padding:32px 0;}
  /* Override large inline paddings on sections */

  .p56{padding-top:36px!important;padding-bottom:36px!important;}
  .p80{padding-top:48px!important;padding-bottom:48px!important;}
  /* Stat numbers in hero */
  .hero-stat-num{font-size:20px!important;}
  .hero-stats{gap:20px!important;}
  /* Quick-dest pills wrap */
  .dest-pills{flex-wrap:wrap;}
  /* Contact page */
  .contact-grid{grid-template-columns:1fr!important;}
  /* Booking layout — sidebar collapses */
  .bk-main-grid{grid-template-columns:1fr!important;}
  .pet-grid{grid-template-columns:1fr!important;}
  .bk-sidebar{display:none!important;}
  /* Aircraft card */
  .ac-img{height:140px!important;border-radius:12px 12px 0 0!important;}
  .ac-price{border-left:none!important;border-top:1px solid var(--bd);flex-direction:row!important;justify-content:space-between;align-items:center;padding:12px 16px!important;}
  /* Helipad panels */
  .hp-grid{grid-template-columns:1fr!important;}
  /* Form grids */
  .form-grid-2{grid-template-columns:1fr!important;}
  .form-grid-3{grid-template-columns:1fr 1fr!important;}
  /* Aircraft detail page — stats row + full-width specs grid collapse */
  .ac-stats-row{grid-template-columns:repeat(2,1fr)!important;}
  .ac-specs-grid{grid-template-columns:1fr!important;}
  .ac-specs-grid>div{border-right:none!important;border-top:1px solid var(--bd)!important;}
  .ac-specs-grid>div:first-child{border-top:none!important;}
  /* Fleet grid */
  .fleet-grid{grid-template-columns:1fr 1fr!important;}
  /* Destination grid */
  .dest-grid{grid-template-columns:1fr 1fr!important;}
  /* Hotel horizontal card */
  .hotel-card-grid{grid-template-columns:1fr!important;}
  .hotel-card-grid>div:first-child{height:200px!important;}
}
@media(max-width:480px){
  /* Search widget */
  .sw-row{grid-template-columns:1fr;}
  .sw-swap{display:none;}
  .sw-cols2,.sw-cols3,.sw-cols4{grid-template-columns:1fr;}
  /* Destination grid */
  .dg{grid-template-columns:1fr;}
  .dc:first-child{grid-column:auto;}
  /* Footer */
  .fg{grid-template-columns:1fr!important;}
  .f-cta .wrap>div:last-child{flex-direction:column;width:100%;}
  .f-cta .btn{width:100%;justify-content:center;}
  /* Fleet + all small grids */
  .fleet-grid{grid-template-columns:1fr!important;}
  .ac-grid{grid-template-columns:1fr 1fr!important;}
  .dest-grid{grid-template-columns:repeat(2,1fr)!important;}
  /* Fleet card */
  .fc-body{padding:16px!important;}
  .fc-foot{flex-direction:column;align-items:flex-start;gap:10px;padding:14px 16px!important;}
  .fc-foot .btn{width:100%;justify-content:center;}
  /* Nav */
  .nav-r{gap:6px;}
  .nbtn{padding:8px 14px;font-size:12.5px;}
  /* Form grids */
  .form-grid-2,.form-grid-3{grid-template-columns:1fr!important;}
  /* Booking overlay full screen */
  .bk-content{font-size:15px;}
  .bk-content .fl{font-size:12px;}
  .bk-sheet{left:0!important;right:0!important;width:100%!important;max-width:100%!important;transform:translateY(100%)!important;top:auto!important;grid-template-columns:1fr!important;border-radius:20px 20px 0 0!important;grid-template-rows:auto 1fr!important;min-height:92vh!important;min-height:92dvh!important;}
  .bk-sheet.open{transform:translateY(var(--bk-drag,0px))!important;transition:transform .4s cubic-bezier(.32,0,.15,1);}
  .bk-header{height:52px;padding:0 16px;}
  .bk-close{width:38px!important;height:38px!important;}
  .bk-handle{display:block!important;}
  .bk-content{max-height:calc(100vh - 52px);max-height:calc(100dvh - 52px);overflow-y:auto;}
  .bk-content .wrap{padding-bottom:32px!important;padding-top:16px!important;}
  .bk-sidebar-panel{display:none!important;}
  /* Stats strip — 2 col on mobile */
  .stats-strip-4{grid-template-columns:1fr 1fr!important;}
  .stats-strip-4>div{border-right:none!important;border-bottom:1px solid rgba(255,255,255,.1);}
  /* Utility spacing overrides */
  .gap-mob{gap:24px!important;}
  .p-sec{padding:32px 0!important;}
  .p-hero{padding:36px 0!important;}
  /* How-it-works 3-col → 1-col */
  .hiw-grid{grid-template-columns:1fr!important;gap:28px!important;}
  /* Transparent pricing 2-col → 1-col */
  .pricing-grid{grid-template-columns:1fr!important;gap:24px!important;}
  /* About/Safety 2-col → 1-col */
  .two-col-text{grid-template-columns:1fr!important;gap:24px!important;}
  [style*="1fr 360px"]{grid-template-columns:1fr!important;}
  [style*="1fr 320px"]{grid-template-columns:1fr!important;}
  /* CTA section padding */
  .cta-pad{padding:36px 20px!important;}
  /* Newsletter / CTA strip */
  .nl-grid{grid-template-columns:1fr!important;text-align:center;}
  /* Typography */
  .h-xl{font-size:clamp(24px,7vw,32px)!important;}
  .h-lg{font-size:clamp(20px,6vw,26px)!important;}
  .h-md{font-size:clamp(18px,5vw,22px)!important;}
  /* Contact sidebar stacks below form */
  .contact-grid>div:last-child{order:2;}
  /* Section padding tighter */
  .sec{padding:32px 0;}
}

/* Animations */
@keyframes spin{to{transform:rotate(360deg)}}
@keyframes fadeInUp{from{opacity:0;transform:translateY(24px);}to{opacity:1;transform:translateY(0);}}
@keyframes fadeIn{from{opacity:0;}to{opacity:1;}}
@keyframes shimmer{0%{background-position:-400px 0;}100%{background-position:400px 0;}}
@keyframes pulse{0%,100%{opacity:1;}50%{opacity:.5;}}
@keyframes slideUp{from{opacity:0;transform:translateY(100%);}to{opacity:1;transform:translateY(0);}}

/* Fade-in on scroll */
.fade-section{opacity:0;transform:translateY(24px);transition:opacity .65s cubic-bezier(.22,1,.36,1),transform .65s cubic-bezier(.22,1,.36,1);}
.fade-section.visible{opacity:1;transform:translateY(0);}

/* Skeleton */
.skel{background:linear-gradient(90deg,var(--off) 25%,var(--off2) 50%,var(--off) 75%);background-size:800px 100%;animation:shimmer 1.5s infinite linear;border-radius:8px;}
.skel-line{height:14px;margin-bottom:10px;border-radius:6px;}
.skel-line.w80{width:80%;}.skel-line.w60{width:60%;}.skel-line.w40{width:40%;}.skel-line.w100{width:100%;}
.skel-block{height:180px;border-radius:14px;margin-bottom:16px;}
.skel-circle{width:48px;height:48px;border-radius:50%;}

/* Back to top */
.btt{position:fixed;bottom:24px;right:24px;z-index:180;width:44px;height:44px;border-radius:50%;background:var(--ink);color:white;border:none;cursor:pointer;display:flex;align-items:center;justify-content:center;box-shadow:0 4px 20px rgba(0,0,0,.2);opacity:0;transform:translateY(16px);transition:opacity .3s,transform .3s;pointer-events:none;}
.btt.show{opacity:1;transform:translateY(0);pointer-events:all;}
.btt:hover{background:var(--bl);transform:translateY(-2px);}
@media(max-width:768px){.btt{bottom:16px;right:16px;width:40px;height:40px;}}
`;

/* FADE-IN ON SCROLL — wraps any section to animate it on viewport entry */
export function FadeIn({ children, delay = 0, className = "" }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.unobserve(el); } },
      { threshold: 0.08, rootMargin: "0px 0px -40px 0px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return (
    <div ref={ref} className={"fade-section" + (visible ? " visible" : "") + (className ? " " + className : "")} style={{ transitionDelay: delay + "s" }}>
      {children}
    </div>
  );
}

/* BACK TO TOP BUTTON */
function BackToTop() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const fn = () => setShow(window.scrollY > 600);
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);
  return (
    <button className={"btt" + (show ? " show" : "")} onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} aria-label="Back to top">
      <ChevronDown size={20} style={{ transform: "rotate(180deg)" }}/>
    </button>
  );
}

/* SKELETON LOADING SCREENS */
function SkeletonPage() {
  return (
    <div style={{ paddingTop: "var(--nav)", minHeight: "60vh" }}>
      <div style={{ background: "var(--off)", padding: "64px 0 48px" }}>
        <div className="wrap">
          <div className="skel skel-line w40" style={{ height: 12, marginBottom: 14 }}/>
          <div className="skel skel-line w80" style={{ height: 36, marginBottom: 12 }}/>
          <div className="skel skel-line w60" style={{ height: 16 }}/>
        </div>
      </div>
      <div className="wrap" style={{ paddingTop: 40, paddingBottom: 60 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 320px", gap: 48 }} className="bk-main-grid">
          <div>
            <div className="skel skel-line w100" style={{ height: 16 }}/>
            <div className="skel skel-line w100" style={{ height: 16 }}/>
            <div className="skel skel-line w80" style={{ height: 16, marginBottom: 28 }}/>
            <div className="skel skel-block"/>
            <div className="skel skel-line w100" style={{ height: 16 }}/>
            <div className="skel skel-line w100" style={{ height: 16 }}/>
            <div className="skel skel-line w60" style={{ height: 16, marginBottom: 28 }}/>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div className="skel skel-block" style={{ height: 120 }}/>
              <div className="skel skel-block" style={{ height: 120 }}/>
            </div>
          </div>
          <div>
            <div className="skel skel-block" style={{ height: 200, marginBottom: 16 }}/>
            <div className="skel skel-block" style={{ height: 140, marginBottom: 16 }}/>
            <div className="skel skel-block" style={{ height: 100 }}/>
          </div>
        </div>
      </div>
    </div>
  );
}


/* DATA */
// ===========================================================================
// LOCATION MASTER DATABASE
// ===========================================================================
// In Sanity CMS: `location` document type with fields:
//   id (slug), name, city, addr, note, lat, lng, type, iata, enabled
// Set enabled:false to hide from booking UI without deleting the record.
// ===========================================================================
export let LOCATION_DB = [];
export let OPERATOR_FLEET = [];
export let REVIEWS = [];
// Aggregated, anonymised platform trust signal — populated after CMS merge.
// PLATFORM_AVG_RESPONSE_HOURS = null forces the static "2-4 hours" copy.
let PLATFORM_AVG_RESPONSE_HOURS = null;
// All location data comes from Sanity CMS. Import via FL3XX in Content Lab.

// Derived lookups (recomputed after CMS merge)
let ACTIVE_LOCATIONS = [];
let LOCS = [];
let HELIPADS = {};
// Serving-filtered city lists — a city shows in the funnel dropdown only when ≥1
// ACTIVE operator SERVES ≥1 pad there (charter: opServesPad semantics; sightseeing:
// sightseeingHelipads). Derived from the SAME bundle (LOCATION_DB + OPERATOR_FLEET)
// as the Helipad-step predicate, so they never disagree. Empty until OPERATOR_FLEET
// is populated (recomputed at CMS init — see line ~2550), exactly like LOCS.
let SERVED_CITIES = [];
let SIGHTSEEING_SERVED_CITIES = [];

function recomputeLocationLookups() {
  ACTIVE_LOCATIONS = LOCATION_DB.filter(l => l.enabled);
  const cmpCity = (a, b) => {
    if (a === "Athens") return -1;
    if (b === "Athens") return 1;
    return a.localeCompare(b);
  };
  LOCS = [...new Set(ACTIVE_LOCATIONS.map(l => l.city))].filter(Boolean).sort(cmpCity);
  HELIPADS = LOCS.reduce((acc, city) => {
    acc[city] = ACTIVE_LOCATIONS.filter(l => l.city === city);
    return acc;
  }, {});
  // Charter + sightseeing serving-filtered city lists (shared pad-visibility helpers
  // → SAME isOperatorServingPad / sightseeingHelipads truth as the Helipad step).
  SERVED_CITIES = [...servedCities(ACTIVE_LOCATIONS, OPERATOR_FLEET)].sort(cmpCity);
  SIGHTSEEING_SERVED_CITIES = [...sightseeingServedCities(ACTIVE_LOCATIONS, OPERATOR_FLEET)].sort(cmpCity);
}
recomputeLocationLookups();

export function getHelipads(city) {
  const raw = HELIPADS[city] || [];
  if (!raw.length) return [{ id:((city||"X").slice(0,3).toUpperCase())+"-M", name:(city)+" Helipad", addr:city, note:"Main helipad", lat:37.5, lng:25.0, type:"helipad", enabled:true }];
  return [...raw].sort((a, b) => {
    // Airports first, then helipads
    const typeDiff = (a.type === "airport" ? 0 : 1) - (b.type === "airport" ? 0 : 1);
    if (typeDiff !== 0) return typeDiff;
    // Within same type: cheapest first
    const feeDiff = (a.fee || 0) - (b.fee || 0);
    if (feeDiff !== 0) return feeDiff;
    return (a.name||"").localeCompare(b.name||"");
  });
}

/* HELI GALLERY */
function handleImgError(e) { e.target.style.display="none"; if(e.target.nextSibling) e.target.nextSibling.style.display="flex"; }

// Keyboard-activate handler for role="button" divs: Enter and Space synthesize a
// click on the same element, matching native button behavior. Preventing default
// on Space also stops page-scroll.
export const onKbdActivate = (e) => {
  if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.currentTarget.click(); }
};

// Generate a _key for Sanity array-of-object items (pets, extras, alternativeQueue).
// Kept client-local — mirrors the server helper in src/pages/api/_helpers.js.
export const newKey = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID().replace(/-/g, '').slice(0, 12);
  return Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4);
};

// Marketing attribution: first-touch UTM + click-ID capture with 30-day TTL.
// Stored in localStorage so it survives across sessions. Not consent-gated —
// persists business-attribution data, not ad-storage. Legitimate-interest
// under Art. 6(1)(f) GDPR; privacy policy must mention it.
const ATTRIBUTION_KEY = "gh_attribution";
const ATTRIBUTION_TTL_MS = 30 * 24 * 60 * 60 * 1000;
function captureAttribution() {
  if (typeof window === "undefined") return;
  try {
    const raw = localStorage.getItem(ATTRIBUTION_KEY);
    if (raw) {
      const existing = JSON.parse(raw);
      if (existing && existing.capturedAt && Date.now() - existing.capturedAt < ATTRIBUTION_TTL_MS) return;
    }
    const params = new URLSearchParams(window.location.search);
    const clip = (v, max = 255) => (typeof v === "string" && v.length > 0) ? v.slice(0, max) : null;
    const attr = {
      utm_source:   clip(params.get("utm_source")),
      utm_medium:   clip(params.get("utm_medium")),
      utm_campaign: clip(params.get("utm_campaign")),
      utm_term:     clip(params.get("utm_term")),
      utm_content:  clip(params.get("utm_content")),
      gclid:        clip(params.get("gclid")),
      fbclid:       clip(params.get("fbclid")),
      referrer:     clip(document.referrer || "", 500),
      landingPage:  clip((window.location.pathname || "/") + (window.location.search || ""), 500),
      capturedAt:   Date.now(),
    };
    const hasAny = attr.utm_source || attr.utm_medium || attr.utm_campaign || attr.gclid || attr.fbclid || attr.referrer;
    if (!hasAny) return;
    localStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(attr));
  } catch {}
}
export function getAttribution() {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(ATTRIBUTION_KEY);
    if (!raw) return null;
    const a = JSON.parse(raw);
    if (!a || !a.capturedAt) return null;
    if (Date.now() - a.capturedAt > ATTRIBUTION_TTL_MS) { localStorage.removeItem(ATTRIBUTION_KEY); return null; }
    const { capturedAt, ...rest } = a;
    const hasAny = Object.values(rest).some(v => v);
    return hasAny ? rest : null;
  } catch { return null; }
}
if (typeof window !== "undefined") captureAttribution();

// Universal icon renderer — handles SVG icon names AND emoji fallback
const EMOJI_TO_ICON = {
  "\u23F1\uFE0F":"stopwatch","\u23F1":"stopwatch","\uD83D\uDD12":"lock","\uD83E\uDDF3":"luggage",
  "\uD83D\uDCCD":"mappin","\u2693":"anchor","\uD83C\uDFD6\uFE0F":"sun","\u26A1":"zap",
  "\uD83D\uDDFA\uFE0F":"globe","\uD83D\uDCF7":"camera","\u23F3":"hourglass","\uD83C\uDF89":"gift",
  "\u2728":"compass","\uD83D\uDCBA":"seat","\uD83E\uDD1D":"handshake","\uD83D\uDCDE":"phone",
  "\uD83C\uDF0D":"globe","\u2708\uFE0F":"plane","\uD83D\uDE4B":"greet","\uD83D\uDD0D":"search",
  "\uD83D\uDCF1":"phone","\uD83D\uDC3E":"paw","\uD83D\uDC76":"greet","\u2695\uFE0F":"medical",
  "\uD83C\uDFC4":"surf","\uD83C\uDF19":"moon","\uD83D\uDCF8":"camera","\uD83D\uDD12":"lock",
  "\uD83D\uDEE1\uFE0F":"shield","\uD83C\uDF24\uFE0F":"sun","\u2708":"plane",
};
export function renderIcon(name, size = 24, color = "var(--bl)", iconImage) {
  // Decorative CMS icon: alt="" is correct; lazy (never LCP) + a small Sanity
  // width transform so a full-size upload is not fetched for a 24px icon.
  if (iconImage) {
    const iconSrc = (typeof iconImage === 'string' && iconImage.includes('cdn.sanity.io') && !iconImage.includes('?'))
      ? iconImage + '?w=96&auto=format&q=75'
      : iconImage;
    return <img src={iconSrc} alt="" loading="lazy" style={{ width:size, height:size, objectFit:"contain" }}/>;
  }
  if (!name) return null;
  name = EMOJI_TO_ICON[name] || name;
  const SVG_PATHS = {
    search: '<circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>',
    clock: '<circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>',
    card: '<rect x="2" y="5" width="20" height="14" rx="2"/><path d="M2 10h20"/>',
    check: '<path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>',
    shield: '<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>',
    users: '<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>',
    mappin: '<path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>',
    plane: '<path d="M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z"/>',
    camera: '<path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/>',
    wind: '<path d="M17.7 7.7a2.5 2.5 0 1 1 1.8 4.3H2"/><path d="M9.6 4.6A2 2 0 1 1 11 8H2"/><path d="M12.6 19.4A2 2 0 1 0 14 16H2"/>',
    zap: '<polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>',
    package: '<path d="m7.5 4.27 9 5.15"/><path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z"/><path d="m3.3 7 8.7 5 8.7-5"/><path d="M12 22V12"/>',
    phone: '<path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>',
    anchor: '<circle cx="12" cy="5" r="3"/><line x1="12" y1="22" x2="12" y2="8"/><path d="M5 12H2a10 10 0 0 0 20 0h-3"/>',
    briefcase: '<rect x="2" y="7" width="20" height="14" rx="2" ry="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>',
    star: '<polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>',
    info: '<circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/>',
    arrow: '<line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>',
    route: '<circle cx="6" cy="19" r="3"/><path d="M9 19h8.5a3.5 3.5 0 0 0 0-7h-11a3.5 3.5 0 0 1 0-7H15"/><circle cx="18" cy="5" r="3"/>',
    gauge: '<path d="m12 14 4-4"/><path d="M3.34 19a10 10 0 1 1 17.32 0"/>',
    lock: '<rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>',
    stopwatch: '<circle cx="12" cy="13" r="8"/><path d="M12 9v4l2 2"/><path d="M10 2h4"/><path d="M12 2v3"/>',
    luggage: '<rect x="6" y="7" width="12" height="14" rx="2"/><path d="M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"/><path d="M9 21v1M15 21v1"/>',
    globe: '<circle cx="12" cy="12" r="10"/><path d="M2 12h20"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>',
    sun: '<circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>',
    hourglass: '<path d="M5 22h14"/><path d="M5 2h14"/><path d="M17 22v-4.172a2 2 0 0 0-.586-1.414L12 12l-4.414 4.414A2 2 0 0 0 7 17.828V22"/><path d="M7 2v4.172a2 2 0 0 0 .586 1.414L12 12l4.414-4.414A2 2 0 0 0 17 6.172V2"/>',
    gift: '<polyline points="20 12 20 22 4 22 4 12"/><rect x="2" y="7" width="20" height="5"/><line x1="12" y1="22" x2="12" y2="7"/><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/>',
    compass: '<circle cx="12" cy="12" r="10"/><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"/>',
    seat: '<path d="M7 18v-2a4 4 0 0 1 4-4h0a4 4 0 0 1 4 4v2"/><path d="M5 18h14v2a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-2z"/><path d="M9 4a3 3 0 0 1 6 0v4H9V4z"/>',
    handshake: '<path d="m11 17 2 2a1 1 0 1 0 3-3"/><path d="m14 14 2.5 2.5a1 1 0 1 0 3-3l-3.88-3.88a3 3 0 0 0-4.24 0l-.88.88"/><path d="m10 13-2-2a1 1 0 1 0-3 3l3.88 3.88"/><path d="M3 7c0-1.1.9-2 2-2h2l7-3 3 3h2c1.1 0 2 .9 2 2v3"/>',
    greet: '<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M20 8v6"/><path d="M23 11h-6"/>',
    moon: '<path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>',
    paw: '<circle cx="11" cy="4" r="2"/><circle cx="18" cy="8" r="2"/><circle cx="4" cy="8" r="2"/><path d="M12 22c-4 0-5-3-5-6 0-2 1.5-4 5-4s5 2 5 4c0 3-1 6-5 6z"/>',
    medical: '<path d="M8 2v4M16 2v4M3 10h18"/><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M12 12v4M10 14h4"/>',
    surf: '<path d="M2 22c4-4 8-3 12-6s4-6 4-10"/><path d="M6 18c2-1 4-3 6-6"/><circle cx="18" cy="4" r="2"/>',
  };
  if (SVG_PATHS[name]) {
    return <span dangerouslySetInnerHTML={{ __html: '<svg width="' + size + '" height="' + size + '" viewBox="0 0 24 24" fill="none" stroke="' + color + '" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">' + SVG_PATHS[name] + '</svg>' }}/>;
  }
  return <span style={{ fontSize:size }}>{name}</span>;
}

export function HeliGallery({ fleet, height = 220, fit = "cover", aspectRatio = null, chrome = "full", coverPhotos = false, maxHeight = null, liftDots = false }) {
  // If generated/uploaded image exists, show only that + gallery. No illustration.
  const hasGenerated = fleet.img && fleet.img !== fleet.illustrationUrl;
  const firstSlide = hasGenerated
    ? { type:"img", url: fleet.img }
    : fleet.illustrationUrl
      ? { type:"img", url: fleet.illustrationUrl, placeholder:true }
      : { type:"svg" };
  const slides = [firstSlide, ...(fleet.gallery || []).map(url => ({ type:"img", url }))];
  const [idx, setIdx]   = useState(0);
  const [drag, setDrag] = useState(null); // { startX, currentX }
  const total = slides.length;

  const prev = (e) => { e?.stopPropagation(); setIdx(i => (i - 1 + total) % total); };
  const next = (e) => { e?.stopPropagation(); setIdx(i => (i + 1) % total); };

  const onTouchStart = e => setDrag({ startX: e.touches[0].clientX, currentX: e.touches[0].clientX });
  const onTouchMove  = e => drag && setDrag(d => ({ ...d, currentX: e.touches[0].clientX }));
  const onTouchEnd   = () => {
    if (!drag) return;
    const dx = drag.currentX - drag.startX;
    if (Math.abs(dx) > 40) dx < 0 ? next() : prev();
    setDrag(null);
  };

  const slide = slides[idx];
  const offset = drag ? drag.currentX - drag.startX : 0;
  // coverPhotos: real photos fill the frame (cover); illustration/SVG
  // placeholders stay contained so vector art isn't cropped. When off, the
  // uniform `fit` prop applies to every slide (legacy callers unchanged).
  const slideFit = coverPhotos ? (slide.placeholder ? "contain" : "cover") : fit;
  // "dots-arrows" = the dots-only chrome PLUS always-visible dezent prev/next
  // arrows (used by /fleet + homepage MarketingAircraftRow). Plain "dots"
  // (Step-2 ineligible card) keeps swipe + dots only — behaviour unchanged.
  const dotArrows = chrome === "dots-arrows";
  const dotsOnly = chrome === "dots" || dotArrows;

  return (
    <div style={{ position:"relative", width:"100%", ...(aspectRatio ? { aspectRatio } : { height }), ...(maxHeight ? { maxHeight } : {}), overflow:"hidden", background:"var(--off)", userSelect:"none" }} onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}>

      {/* Slide */}
      <div style={{ width:"100%", height:"100%", transform:"translateX("+(offset)+"px)", transition: drag ? "none" : "transform .15s" }}>
        {slide.type === "svg"
          ? <HeliSVG type={fleet.svgType} height={height}/>
          : <Img preset="card" src={slide.url} alt={fleet.name + " helicopter"} style={{ width:"100%", height:"100%", objectFit:slideFit, display:"block" }} onError={handleImgError}/>
        }
      </div>

      {/* Bottom scrim for dot contrast over bright photos (dots-only chrome) */}
      {total > 1 && dotsOnly && (
        <div style={{ position:"absolute", bottom:0, left:0, right:0, height:48, background:"linear-gradient(to top, rgba(0,0,0,.28), rgba(0,0,0,0))", pointerEvents:"none", zIndex:1 }}/>
      )}

      {/* Arrows -- only if >1 slide (hidden in dots-only chrome) */}
      {total > 1 && !dotsOnly && (
        <>
          <button onClick={prev} aria-label="Previous image" style={{ position:"absolute", left:8, top:"50%", transform:"translateY(-50%)", width:30, height:30, borderRadius:"50%", background:"rgba(255,255,255,.88)", border:"none", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", boxShadow:"0 2px 8px rgba(0,0,0,.15)", zIndex:2 }}>
            <ChevronDown size={14} color="var(--ink)" style={{ transform:"rotate(90deg)" }}/>
          </button>
          <button onClick={next} aria-label="Next image" style={{ position:"absolute", right:8, top:"50%", transform:"translateY(-50%)", width:30, height:30, borderRadius:"50%", background:"rgba(255,255,255,.88)", border:"none", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", boxShadow:"0 2px 8px rgba(0,0,0,.15)", zIndex:2 }}>
            <ChevronDown size={14} color="var(--ink)" style={{ transform:"rotate(-90deg)" }}/>
          </button>
        </>
      )}

      {/* Dezent always-visible prev/next arrows (/fleet + carousel): subtle dark
          translucent circle so they stay legible on any photo without dominating;
          share idx with the dots, so each control updates the active dot. Tap
          works (onClick fires on touch); swipe + dots remain intact. */}
      {total > 1 && dotArrows && (
        <>
          <button onClick={prev} aria-label="Previous image" style={{ position:"absolute", left:6, top:"50%", transform:"translateY(-50%)", width:26, height:26, borderRadius:"50%", background:"rgba(0,0,0,.34)", border:"none", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", zIndex:2, WebkitTapHighlightColor:"transparent", backdropFilter:"blur(1px)" }}>
            <ChevronDown size={15} color="white" style={{ transform:"rotate(90deg)" }}/>
          </button>
          <button onClick={next} aria-label="Next image" style={{ position:"absolute", right:6, top:"50%", transform:"translateY(-50%)", width:26, height:26, borderRadius:"50%", background:"rgba(0,0,0,.34)", border:"none", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", zIndex:2, WebkitTapHighlightColor:"transparent", backdropFilter:"blur(1px)" }}>
            <ChevronDown size={15} color="white" style={{ transform:"rotate(-90deg)" }}/>
          </button>
        </>
      )}

      {/* Dots -- padded transparent buttons give a ≥~28px tap target while the
          inner pill stays visually small. Active dot elongates. */}
      {total > 1 && (
        // liftDots: funnel cards pin a ~19px "representative image" disclaimer at the
        // photo bottom (z-index 3); raise the dots clear above that band so it never
        // covers them. Off (bottom 4) everywhere else (/fleet + carousel — no disclaimer).
        <div style={{ position:"absolute", bottom: liftDots ? 24 : 4, left:0, right:0, display:"flex", justifyContent:"center", gap:2, zIndex: liftDots ? 4 : 2 }}>
          {slides.map((_, i) => (
            <button key={i} onClick={e => { e.stopPropagation(); setIdx(i); }} aria-label={"Go to image " + (i + 1) + " of " + total} aria-current={i === idx ? "true" : undefined} style={{ background:"none", border:"none", cursor:"pointer", padding: liftDots ? "4px 6px" : "11px 6px", display:"flex", alignItems:"center", WebkitTapHighlightColor:"transparent" }}>
              <span style={{ display:"block", width: i === idx ? 16 : 6, height:6, borderRadius:3, transition:"all .2s",
                background: i === idx ? "var(--bl)" : "rgba(255,255,255,.85)",
                boxShadow:"0 1px 3px rgba(0,0,0,.35)" }}/>
            </button>
          ))}
        </div>
      )}

      {/* Slide counter badge (hidden in dots-only chrome) */}
      {total > 1 && !dotsOnly && (
        <div style={{ position:"absolute", top:8, right:8, background:"rgba(0,0,0,.45)", color:"white", fontSize:11, fontWeight:600, padding:"2px 8px", borderRadius:100 }}>
          {idx + 1} / {total}
        </div>
      )}
    </div>
  );
}

/* HELICOPTER SVG ILLUSTRATIONS */
// Each heli has a distinct silhouette matching the real model profile
function svgRotor(cx, col, n = 3, len = 110, y = 24) {
  return Array.from({ length: n }, (_, i) => (
    <line key={i} x1={cx} y1={y} x2={cx + Math.cos((i*360/n)*Math.PI/180)*len} y2={y + Math.sin((i*360/n)*Math.PI/180)*len*0.18} stroke={col} strokeWidth="5" strokeLinecap="round"/>
  ));
}
function svgTailRotor(x, cy, col, n = 3, r = 18) {
  return Array.from({ length: n }, (_, i) => (
    <line key={i} x1={x} y1={cy} x2={x + Math.cos((i*120)*Math.PI/180)*r} y2={cy + Math.sin((i*120)*Math.PI/180)*r} stroke={col} strokeWidth="3.5" strokeLinecap="round"/>
  ));
}
function svgSkids(x, sw, col, y = 138) {
  return (
    <>
      <line x1={x} y1={y - 10} x2={x + 14} y2={y} stroke={col} strokeWidth="3.5" strokeLinecap="round"/>
      <line x1={x + sw - 14} y1={y - 10} x2={x + sw} y2={y} stroke={col} strokeWidth="3.5" strokeLinecap="round"/>
      <line x1={x} y1={y} x2={x + sw} y2={y} stroke={col} strokeWidth="4" strokeLinecap="round"/>
    </>
  );
}

function HeliSVG({ type, color = "#0D56A6", bg = "#EAF1FB", height = 160 }) {
  const w = 480, h = 160;
  const col = color;

  // Shared drawing helpers
  // (helpers: svgRotor, svgTailRotor, svgSkids defined above)

  if (type === "r66") return (
    <svg viewBox={"0 0 "+w+" "+h} style={{ width:"100%", height, display:"block", background:bg }}>
      {svgRotor(200, col,  3,  105,  26)}
      <circle cx={200} cy={26} r={7} fill={col}/>
      {/* Fuselage - compact teardrop */}
      <path d="M100,95 Q105,60 145,52 L260,52 Q295,52 305,72 Q315,90 305,108 Q295,122 260,124 L145,124 Q110,122 100,95Z" fill="white" stroke={col} strokeWidth="3"/>

      {/* Windshield */}
      <path d="M148,56 L148,76 Q148,82 154,82 L230,82 Q236,82 238,76 L240,56Z" fill={col+"30"} stroke={col} strokeWidth="2"/>

      {/* Door line */}
      <line x1="190" y1="56" x2="190" y2="123" stroke={col} strokeWidth="1.5" strokeDasharray="3,2" opacity="0.4"/>
      {/* Tail boom */}
      <path d="M305,88 L380,78 L385,88 L305,100Z" fill="white" stroke={col} strokeWidth="2.5"/>
      {/* Tail fin */}
      <path d="M376,78 L390,52 L392,80" fill="white" stroke={col} strokeWidth="2.5"/>
      {svgTailRotor(392,  64, col,  3,  16)}
      <circle cx={392} cy={64} r={4} fill={col}/>
      {svgSkids(118,  165, col,  140)}
      {/* Engine exhaust */}
      <path d="M258,52 Q265,38 272,50" fill="none" stroke={col} strokeWidth="3" strokeLinecap="round"/>
    </svg>
  );

  if (type === "h125") return (
    <svg viewBox={"0 0 "+w+" "+h} style={{ width:"100%", height, display:"block", background:bg }}>
      {svgRotor(210, col,  3,  115,  24)}
      <circle cx={210} cy={24} r={8} fill={col}/>
      {/* Fuselage - taller, squarer */}
      <path d="M95,100 Q98,58 142,48 L275,48 Q315,48 322,70 Q330,92 320,112 Q310,126 275,128 L142,128 Q105,126 95,100Z" fill="white" stroke={col} strokeWidth="3"/>

      {/* Large windshield - H125 signature */}
      <path d="M144,52 L144,82 Q144,90 152,90 L240,90 Q250,90 254,82 L258,52Z" fill={col+"28"} stroke={col} strokeWidth="2.2"/>

      {/* Chin window */}
      <path d="M144,90 L152,110 L230,110 L240,90Z" fill={col+"18"} stroke={col} strokeWidth="1.5"/>
      {/* Door */}
      <rect x="192" y="52" width="2" height="76" fill={col} opacity="0.3"/>
      {/* Tail boom - longer */}
      <path d="M322,90 L400,76 L404,88 L322,106Z" fill="white" stroke={col} strokeWidth="2.5"/>
      {/* Tail fin - taller */}
      <path d="M396,76 L412,44 L414,80" fill="white" stroke={col} strokeWidth="2.5"/>
      {svgTailRotor(414,  58, col,  3,  18)}
      <circle cx={414} cy={58} r={5} fill={col}/>
      {svgSkids(112,  178, col,  142)}
      {/* Engine top */}
      <path d="M265,48 Q275,30 290,46" fill="none" stroke={col} strokeWidth="3.5" strokeLinecap="round"/>
    </svg>
  );

  if (type === "aw109") return (
    <svg viewBox={"0 0 "+w+" "+h} style={{ width:"100%", height, display:"block", background:bg }}>
      {/* Twin engines top - AW109 signature */}
      <rect x="175" y="28" width="35" height="20" rx="4" fill="white" stroke={col} strokeWidth="2.5"/>
      <rect x="220" y="28" width="35" height="20" rx="4" fill="white" stroke={col} strokeWidth="2.5"/>
      {svgRotor(215, col,  4,  112,  28)}
      <circle cx={215} cy={28} r={8} fill={col}/>
      {/* Sleek executive fuselage */}
      <path d="M90,98 Q92,55 138,44 L278,44 Q322,44 332,65 Q342,86 330,110 Q320,126 278,130 L138,130 Q100,128 90,98Z" fill="white" stroke={col} strokeWidth="3"/>

      {/* Raked windshield - AW109 look */}
      <path d="M140,48 L148,74 Q149,84 158,84 L242,84 Q252,84 255,74 L268,48Z" fill={col+"28"} stroke={col} strokeWidth="2.2"/>

      {/* Executive windows */}
      <rect x="160" y="90" width="35" height="28" rx="5" fill={col+"22"} stroke={col} strokeWidth="1.8"/>
      <rect x="205" y="90" width="35" height="28" rx="5" fill={col+"22"} stroke={col} strokeWidth="1.8"/>
      <rect x="250" y="90" width="28" height="28" rx="5" fill={col+"22"} stroke={col} strokeWidth="1.8"/>
      {/* Retractable skids (AW109 has retractable gear) - shown extended */}
      <line x1="148" y1="120" x2="148" y2="144" stroke={col} strokeWidth="3.5" strokeLinecap="round"/>
      <line x1="280" y1="120" x2="280" y2="144" stroke={col} strokeWidth="3.5" strokeLinecap="round"/>
      <line x1="128" y1="144" x2="168" y2="144" stroke={col} strokeWidth="4" strokeLinecap="round"/>
      <line x1="260" y1="144" x2="300" y2="144" stroke={col} strokeWidth="4" strokeLinecap="round"/>
      {/* Tail boom */}
      <path d="M330,88 L408,72 L412,85 L330,105Z" fill="white" stroke={col} strokeWidth="2.5"/>
      {/* Swept tail fin */}
      <path d="M403,72 L422,40 L425,75" fill="white" stroke={col} strokeWidth="2.5"/>
      {svgTailRotor(425,  54, col,  3,  18)}
      <circle cx={425} cy={54} r={5} fill={col}/>
    </svg>
  );

  // h145 -- wide twin, 8-pax
  return (
    <svg viewBox={"0 0 "+w+" "+h} style={{ width:"100%", height, display:"block", background:bg }}>
      {/* Twin engines - wider spacing */}
      <rect x="162" y="26" width="40" height="22" rx="5" fill="white" stroke={col} strokeWidth="2.5"/>
      <rect x="215" y="26" width="40" height="22" rx="5" fill="white" stroke={col} strokeWidth="2.5"/>
      {svgRotor(217, col,  4,  124,  26)}
      <circle cx={217} cy={26} r={9} fill={col}/>
      {/* Wide fuselage - H145 is wider */}
      <path d="M82,100 Q84,54 132,42 L290,42 Q336,42 348,64 Q360,88 348,114 Q336,130 290,134 L132,134 Q92,130 82,100Z" fill="white" stroke={col} strokeWidth="3"/>

      {/* Wide windshield */}
      <path d="M135,46 L140,80 Q141,90 150,90 L250,90 Q260,90 263,80 L278,46Z" fill={col+"28"} stroke={col} strokeWidth="2.2"/>

      {/* 8-pax windows -- 4 pairs */}
      {[152, 192, 232, 272].map((x, i) => (
        <rect key={i} x={x} y={96} width={30} height={26} rx={5} fill={col+"22"} stroke={col} strokeWidth="1.8"/>

      ))}
      {/* Tail rotor on fenestron (H145 has fenestron = enclosed tail rotor) */}
      <path d="M348,88 L424,70 L428,84 L348,108Z" fill="white" stroke={col} strokeWidth="2.5"/>
      {/* Fenestron -- enclosed disk */}
      <path d="M422,70 L438,44 L440,78" fill="white" stroke={col} strokeWidth="2.5"/>
      <circle cx={432} cy={62} r={14} fill="white" stroke={col} strokeWidth="2.5"/>
      <circle cx={432} cy={62} r={6} fill={col}/>
      {[0,60,120,180,240,300].map((a,i) => <line key={i} x1={432} y1={62} x2={432 + Math.cos(a*Math.PI/180)*10} y2={62 + Math.sin(a*Math.PI/180)*10} stroke={col} strokeWidth="2" strokeLinecap="round"/>)} {svgSkids(100,  200, col,  148)} </svg>
  );
}

/* LUGGAGE WEIGHTS */
export let LUG = { carryOn: 5, small: 8, standard: 23, large: 32 }; // overridden by CMS

let LUG_VOL = { carryOn: 0.010, small: 0.020, standard: 0.080, large: 0.130 }; // overridden by CMS

// Helicopter compartments aren't rectangular — curved fuselage, safety gear, gaps between bags
// 70% packing efficiency is standard for irregularly-shaped cargo spaces
let PACKING_EFFICIENCY = 0.70;

// Cabin stowage: each spare passenger seat can hold ~0.05 m³ (footwell + under seat)
let CABIN_VOL_PER_SEAT = 0.050;

// Standard crew masses for the payload feasibility check (overridden by CMS
// siteSettings: pilotWeightKg / secondPilotWeight). Crew is folded into the
// payload sum on purpose — maxPayload is the manufacturer pax+bags+cargo
// figure (crew NOT included), so counting crew makes the pre-filter
// conservative and buffers the deliberately-unmodelled fuel. The operator
// still confirms final weight & balance.
let PILOT_WEIGHT_KG = 90;        // always-present pilot
let SECOND_PILOT_WEIGHT_KG = 85; // second pilot (mandatory or added as extra)

// Thin wrappers over the shared feasibility lib (src/lib/feasibility.js) so the
// funnel prefilter and the server backstop (bookings-create) run IDENTICAL
// weight/baggage math — there is no second copy of the formula to drift. They
// close over the CMS-overridable PILOT/SECOND_PILOT weights + LUG so per-site
// config still applies. requiresMandatorySecondPilot is imported from the lib.
export function luggageWeight(lug) { return _luggageWeight(lug, LUG); }
export function crewWeight(f, secondPilotSelected) {
  return _crewWeight(f, secondPilotSelected, { pilotWeightKg: PILOT_WEIGHT_KG, secondPilotWeightKg: SECOND_PILOT_WEIGHT_KG });
}
// kg available for pax + luggage after crew (see availablePayload + the FUTURE
// FUEL-AWARE SEAM in src/lib/feasibility.js — flightMin is the upgrade hook).
export function availablePayload(f, opts) {
  return _availablePayload(f, { ...opts, pilotWeightKg: PILOT_WEIGHT_KG, secondPilotWeightKg: SECOND_PILOT_WEIGHT_KG });
}

export function totalFlightWeight(paxData) {
  return paxData.reduce((sum, p) => {
    const bw = parseFloat(p.bodyWeight) || 0;   // 0 when not entered yet
    const lw = luggageWeight(p.luggage);
    return sum + bw + lw;
  }, 0);
}

export function totalBags(paxData) {
  return paxData.reduce((sum, p) => {
    return sum + (p.luggage?.carryOn||0) + (p.luggage?.small||0) + (p.luggage?.standard||0) + (p.luggage?.large||0);
  }, 0);
}

// Volume in m³
function totalLugVol(paxData) {
  return paxData.reduce((sum, p) => {
    return sum
      + (p.luggage?.small||0)    * LUG_VOL.small
      + (p.luggage?.standard||0) * LUG_VOL.standard
      + (p.luggage?.large||0)    * LUG_VOL.large;
  }, 0);
}
export function luggageCompartmentStatus(paxData, aircraft, nPax) {
  const vol       = totalLugVol(paxData);
  const usableHold = +((aircraft.compartmentVol||0) * PACKING_EFFICIENCY).toFixed(3);
  const spareSeats  = Math.max(0, aircraft.seats - nPax);
  const cabinCapacity = spareSeats * CABIN_VOL_PER_SEAT;
  if (vol <= usableHold) return { status:"ok",      overflowVol:0,        spareSeats, useCabin:false };
  const overflowVol = vol - usableHold;
  if (overflowVol <= cabinCapacity) return { status:"cabin",  overflowVol, spareSeats, useCabin:true  };
  return { status:"over",  overflowVol, spareSeats, useCabin:false };
}


export let FLEET = [];
// Marketing-visibility helper: the SAME render-layer filter MarketingAircraftRow
// uses — excludes aircraft flagged hideFromFleetPage from PUBLIC marketing surfaces
// (fleet grid, carousel, dest/route/charter aircraft lists, headline counts). The
// FLEET array itself stays COMPLETE (detail pages + FLEET.find lookups + the booking
// funnel keep all aircraft). Never use this in the funnel / pricing / availability.
export function visibleMarketingFleet() { return FLEET.filter(f => f.hideFromFleetPage !== true); }
// All fleet data from Sanity CMS

// Engine type is now a required field on each FLEET entry: engine:"single" or engine:"twin"

// ===========================================================================
// INTEGRATION CONFIG -- fill in before going live
// ===========================================================================
// Site contact — used for mailto fallbacks
export const CONTACT_EMAIL = (__CMS?.SETTINGS?.charterEmail) || "charter@heligreece.com";

export let DESTS = [];
// All destination data from Sanity CMS

export let HOTELS = [];
// All hotel data from Sanity CMS

let HUBS = [];
// All hub data from Sanity CMS (Phase 3 Track A)

// Derived
export let HOTEL_CITIES = [...new Set(HOTELS.map(h => h.city))].filter(Boolean).sort();
const getHotelsForCity = (city) => HOTELS.filter(h => h.city === city);
const getHotelsForRoute = (routeTo) => HOTELS.filter(h => h.city === routeTo).slice(0, 3);

let SVCS = [
  { title:"Island Transfers",    sub:"Hotel to helipad - resort to resort",     img:IMG.santorini, icon:<Plane size={18} color="white"/>    },
  { title:"Yacht Transfers",     sub:"Sea to shore - superyacht coordination",  img:IMG.yacht,     icon:<Anchor size={18} color="white"/>   },
  { title:"Sightseeing Flights", sub:"Caldera - Acropolis - Meteora from above",img:IMG.heliAir,   icon:<Camera size={18} color="white"/>   },
  { title:"VIP Charter",         sub:"Fully private - discreet - flexible",     img:IMG.luxury,    icon:<Star size={18} color="white"/>     },
  { title:"Airport Connections", sub:"Direct from airport to any island",       img:IMG.heliLand,  icon:<Wind size={18} color="white"/>     },
  { title:"Custom Itineraries",  sub:"Multi-stop - proposals - events",         img:IMG.island,    icon:<Zap size={18} color="white"/>      },
];

export const fmtPrice = (n) => Number(n).toLocaleString("de-DE");

// Generate booking reference: GH/XXXXX (5 uppercase letters)
// If collisions become likely, extends to GH/XXXXXY (5 letters + 1 digit)
export const generateBookingRef = () => {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  const bytes = new Uint8Array(7);
  crypto.getRandomValues(bytes);
  let ref = "";
  for (let i = 0; i < 7; i++) ref += chars[bytes[i] % chars.length];
  return "GH" + ref;
};
const getRoutePrice = (from, to, aircraftId) => {
  const route = SEO_ROUTES.find(r => r.from === from && r.to === to);
  if (!route?.prices) return null;
  return route.prices[aircraftId] || null;
};
// Route-card "from EUR X" price — the SSR-precomputed CHEAPEST customer total for an
// aircraft on a route across ALL valid (depPad, arrPad, operator) constellations
// (route.cardFromPrice, built by precomputeRouteCardTotals via the SAME
// estimateAircraftPrice → customerBaseTotal chain the funnel + /api/aircraft/quote
// use). READ-ONLY; a min over real quotable constellations, so never below what the
// funnel would actually quote. null (missing/0/NaN) → the card shows "Price on request".
const getRouteCardFromPrice = (from, to, aircraftId) => {
  const route = SEO_ROUTES.find(r => r.from === from && r.to === to);
  const v = route?.cardFromPrice?.[aircraftId];
  return (typeof v === 'number' && Number.isFinite(v) && v > 0) ? v : null;
};
// Overall route "from EUR X" = the cheapest across all aircraft on the route.
const getRouteFromPriceMin = (from, to) => {
  const route = SEO_ROUTES.find(r => r.from === from && r.to === to);
  return routeFromPriceMin(route?.cardFromPrice);
};
// Destination "from EUR X" = the cheapest from-Athens route group-min for that
// destination. A destination has no single cardFromPrice, so min over the
// cardFromPrice of EVERY Athens->destination route (read-only; reuses the existing
// per-route mins, never recomputes a price). null when no such route has a min.
const getDestFromAthensMin = (destName) =>
  routesFromPriceMin(SEO_ROUTES.filter(r => r.from === 'Athens' && r.to === destName));
// Return-trip customer price for an exact route — built by the __CMS loader
// via getReturnPrice() (explicit returnBrokerPrice or one-way × 2).
const getRouteReturnPrice = (from, to, aircraftId) => {
  const route = SEO_ROUTES.find(r => r.from === from && r.to === to);
  if (!route?.returnPrices) return null;
  return route.returnPrices[aircraftId] || null;
};
// Customer-facing helipad fee for the picked (pad, aircraft) under Option Y.
// Single source of truth: src/lib/pricing/helipad-fee.js. When no operator
// is pinned (custom-route flow, or interpolated/hourly-fallback pricing
// with no winning operator), the helper falls back to the global
// location.fee. The runtime kill-switch FEATURE_HELIPAD_OVERRIDES = false
// short-circuits this to 0 inside the helper (full rollback to legacy
// "landing fees informational only" behaviour).
function resolvedPadFee(pad, aircraft) {
  if (!pad) return 0;
  const op = aircraft && aircraft._operator ? aircraft._operator : null;
  // P2 #9 phase 4 — effective commission rate (markup); getCustomerHelipadFee
  // applies brokerToCustomer internally.
  const rate = getEffectiveCommission(op, __CMS?.SETTINGS);
  // Brief 2 — location.fee applies for route-price modes (manual/auto/override);
  // a pure cost-model compute pins _feeFromLocation=false at selection so its
  // pad fee comes from the cost-model chain instead (no location.fee). Default
  // true (undefined) so legacy/estimated prices keep the additive fee.
  const allowLocationFee = aircraft ? (aircraft._feeFromLocation !== false) : true;
  return getCustomerHelipadFee(pad, op, rate, allowLocationFee);
}

// SINGLE source for the customer total at EVERY client site (display / submit /
// emails) — mirrors the server computeExpectedTotal exactly: NET base + per-pad
// NET fees (routePadFeesNet over buildPadTouches along the real legs) → ONE
// brokerToCustomer (markup + VAT 13% + round once) + extras. Uses the SAME pure
// functions as the server, so client == server by construction (no second math,
// no per-site touch divergence). 0 pad fees → base price unchanged (prod today).
// Pads + home base are resolved from LOCATION_DB so their Sanity _id matches
// (base==pickup detection in buildPadTouches), and they carry padFees.
export function customerTotalFor(f, data) {
  if (!f) return 0;
  const isRT = data?.tripType === 'roundtrip';
  if (!f.resolvedPrice || f.priceType === 'indicative') return 0; // quote request → 0
  const exTotal = data?.exTotal || 0;
  // Served-but-unpriced CANDIDATE operators: the cost model is NOT in the client
  // bundle, so the broker special can't be applied client-side. The /api/aircraft/quote
  // endpoint already returned the authoritative customer base total (special applied
  // server-side via customerBaseTotal == the booking money-gate). Use it verbatim so the
  // submitted total == the server expectedTotal (no overcharge, no 400). Priced operators
  // have _candidateBaseTotal null → fall through to the local chain (special via bundle).
  const candBase = (data?.tripType === 'roundtrip') ? f._candidateRtBaseTotal : f._candidateBaseTotal;
  if (candBase != null && candBase > 0) return candBase + exTotal;
  const fallbackBase = (isRT ? (f.resolvedReturnPrice ?? f.resolvedPrice * 2) : f.resolvedPrice) || (isRT ? f.price * 2 : f.price) || 0;
  const padLoc = (pad) => pad ? (LOCATION_DB.find(l => (pad._id && l._id === pad._id) || (pad.id && l.id === pad.id)) || (pad.padFees !== undefined ? pad : null)) : null;
  // Feature B1 — seasonal base: the positioning-departure pad fee touch uses the
  // base in effect on the flight date (basePeriods[] over homeBase), via the SHARED
  // resolver — the SAME base the server's customerBaseTotal uses for booking.date.
  const rbBase = resolveBaseForDate(f, data?.date);
  const baseLoc = rbBase ? (LOCATION_DB.find(l => l.id === (rbBase.id || rbBase._id) || l._id === (rbBase._id || rbBase.id)) || null) : null;
  const fc = feeClassOf(f);
  const touches = buildPadTouches({
    baseLocation: baseLoc, depLocation: padLoc(data?.depPad), arrLocation: padLoc(data?.arrPad),
    operator: f._operator || null, isRoundTrip: isRT,
  });
  const padNet = routePadFeesNet(touches, fc);
  const netBase = isRT ? f.resolvedRtBrokerNet : f.resolvedBrokerPrice;
  const rate = (f._rate != null) ? f._rate : getEffectiveCommission(f._operator, __CMS?.SETTINGS);
  const step = f._roundStep || 10;
  // D + B (compute modes): the operator Final incl VAT (over the COMBINED net = flight
  // + pad + per-pax airport fees) is rounded UP to €100 at the END, then the markup
  // computes on it — SAME as the server customerBaseTotal (effectiveComputeNet). Manual
  // is byte-identical to before (markup on net+pad, no pax, no €100 rounding).
  const round100 = !!f._roundFinal100;
  const pax = (Array.isArray(data?.passengers) ? data.passengers.length : 0) || Number(data?.pax) || 1;
  const paxNet = round100 ? routePaxFeesNet(touches, fc, pax) : 0;
  // HELISTAR broker special discount (cost-model only) — the LAST NET-stage op on the
  // COMBINED net (flight + pad + per-pax), EXACTLY as the server customerBaseTotal. null
  // (manual/auto/override) → no-op. Without this the client total dropped the special and
  // diverged from the operator payout + the server price-gate (overcharge / 400 at submit).
  const special = (round100 && f._brokerDiscount) || null;
  const custBase = (netBase != null && (round100 || padNet > 0))
    ? brokerToCustomer(round100 ? effectiveComputeNet(applyBrokerSpecial(netBase + padNet + paxNet, special)) : (netBase + padNet), rate, undefined, step)
    : fallbackBase;
  return custBase + exTotal;
}

// Charter pad-exact price resolution — the SINGLE source shared by the funnel
// primary list (Funnel _rawDisplayPrice) AND the flexible-pads alternatives
// section, so a card's price for (aircraft, depLoc→arrLoc) is IDENTICAL by
// construction to what the funnel shows after switching to that pad pair.
// padExactPrice (client route.pricing) FIRST — instant, no server call; only a
// served-but-unpriced cost-model candidate falls back to the server candidateQuote
// (pass it in). Returns the SAME shape _rawDisplayPrice returned, or a null price
// when neither the client engine nor the (provided) quote can price it.
export function resolveCharterPadPrice({ aircraft, from, to, depLoc, arrLoc, operatorFilter, flightMin, date, isRoundTrip, candidateQuote, settingsRate, priceData }) {
  if (!aircraft || !depLoc || !arrLoc || !operatorFilter) return { price: null, type: null, oneway: null, returnPrice: null, operatorId: null };
  const rate0 = (typeof settingsRate === 'number') ? settingsRate : ((typeof window !== 'undefined' && window.__SANITY_DATA__?.settings?.commissionRate) ?? 10);
  let est = padExactPrice(aircraft.id, from || '', to || '', depLoc, arrLoc, operatorFilter, flightMin, date || null);
  // served-but-unpriced candidate: no instant EXACT route.pricing price → use the
  // server candidateQuote's pad-exact broker-NET (client applies markup+VAT; the
  // booking money-gate re-derives the SAME number). Byte-identical to _rawDisplayPrice.
  if (!est || !(est.price > 0) || est.type !== 'exact') {
    const q = candidateQuote;
    if (q && q.brokerNet > 0) {
      const r = q.rate ?? rate0, st = q.roundStep || 10;
      const r100 = q.roundFinal100 === true;
      const effF = (n) => (r100 && n > 0) ? effectiveComputeNet(n) : n;
      est = {
        price: brokerToCustomer(effF(q.brokerNet), r, undefined, st), type: 'exact',
        brokerPrice: q.brokerNet, roundTripBrokerNet: (q.rtBrokerNet > 0 ? q.rtBrokerNet : null),
        roundTripPrice: (q.rtBrokerNet > 0 ? brokerToCustomer(effF(q.rtBrokerNet), r, undefined, st) : null),
        operator: { _id: q.operatorId || null }, rate: r, roundStep: st, feeFromLocation: q.feeFromLocation !== false, roundFinal100: r100,
        customerTotal: q.customerTotal ?? null, customerRtTotal: q.customerRtTotal ?? null,
      };
    }
  }
  if (!est || !est.price) return { price: null, type: null, oneway: null, returnPrice: null, operatorId: null };
  const returnPrice = est.type === 'exact' ? getReturnPrice(est, est.price, est.rate ?? rate0, est.roundStep || 10, est.roundTripPrice) : est.price * 2;
  const charterDisplay = isRoundTrip ? returnPrice : est.price;
  const _foldOp = est.operator?._id ? (OPERATOR_FLEET.find(o => o._id === est.operator._id) || null) : null;
  const _foldF = { ...aircraft, resolvedPrice: est.price, resolvedReturnPrice: returnPrice, priceType: est.type, _operator: _foldOp, _feeFromLocation: est.feeFromLocation, resolvedBrokerPrice: est.brokerPrice ?? null, resolvedRtBrokerNet: est.roundTripBrokerNet ?? null, _rate: est.rate ?? null, _roundStep: est.roundStep ?? null, _roundFinal100: est.roundFinal100 === true, _brokerDiscount: est.brokerDiscount ?? null, _candidateBaseTotal: est.customerTotal ?? null, _candidateRtBaseTotal: est.customerRtTotal ?? null };
  const p = customerTotalFor(_foldF, priceData) || charterDisplay;
  return { price: p, type: est.type, oneway: est.price, returnPrice, operatorId: est.operator?._id || null, feeFromLocation: est.feeFromLocation,
    oneBrokerNet: est.brokerPrice ?? null, rtBrokerNet: est.roundTripBrokerNet ?? null, rate: est.rate ?? null, roundStep: est.roundStep ?? null, roundFinal100: est.roundFinal100 === true, brokerDiscount: est.brokerDiscount ?? null, candidateBaseTotal: est.customerTotal ?? null, candidateRtBaseTotal: est.customerRtTotal ?? null };
}

// Per-pad customer contribution under the UNIFIED markup. The markup ceils the
// TOTAL once, so the authoritative landing-fees number is the TOTAL contribution
// = brokerToCustomer(netBase + allPadNet) − brokerToCustomer(netBase). A pad's
// own contribution is the MARGINAL delta (total with all − total without that
// pad's touches); marginal deltas may not sum to the total when the shared ceil
// splits unevenly, so the DISPLAY uses the total as the single "Landing fees"
// line (which sums exactly with Charter + Extras), and perPad marginals are
// stored for reference only. Returns { total, perPad:{ [locId]: delta }, exact }.
export function padFeeContribution(f, data) {
  const empty = { total: 0, perPad: {}, exact: true };
  if (!f || !f.resolvedPrice || f.priceType === 'indicative') return empty;
  const isRT = data?.tripType === 'roundtrip';
  const netBase = isRT ? f.resolvedRtBrokerNet : f.resolvedBrokerPrice;
  if (netBase == null) return empty;
  const rate = (f._rate != null) ? f._rate : getEffectiveCommission(f._operator, __CMS?.SETTINGS);
  const step = f._roundStep || 10;
  const padLoc = (pad) => pad ? (LOCATION_DB.find(l => (pad._id && l._id === pad._id) || (pad.id && l.id === pad.id)) || (pad.padFees !== undefined ? pad : null)) : null;
  // Feature B1 — seasonal base for the flight date (see customerTotalFor).
  const rbBase = resolveBaseForDate(f, data?.date);
  const baseLoc = rbBase ? (LOCATION_DB.find(l => l.id === (rbBase.id || rbBase._id) || l._id === (rbBase._id || rbBase.id)) || null) : null;
  const touches = buildPadTouches({ baseLocation: baseLoc, depLocation: padLoc(data?.depPad), arrLocation: padLoc(data?.arrPad), operator: f._operator || null, isRoundTrip: isRT });
  const fc = feeClassOf(f); // per-aircraft-class pad fees (client == server)
  // D: round-at-end for compute modes; B: per-pax airport fees fold into the fees total.
  const round100 = !!f._roundFinal100;
  const pax = (Array.isArray(data?.passengers) ? data.passengers.length : 0) || Number(data?.pax) || 1;
  const paxNet = round100 ? routePaxFeesNet(touches, fc, pax) : 0;
  // Broker special discount on the COMBINED net (SAME as customerTotalFor) so the folded
  // Charter + Landing-fees display sums to the corrected total. null → no-op.
  const special = (round100 && f._brokerDiscount) || null;
  const c = (extra) => brokerToCustomer(round100 ? effectiveComputeNet(applyBrokerSpecial(netBase + extra, special)) : (netBase + extra), rate, undefined, step);
  const base0 = c(0);
  const total = c(routePadFeesNet(touches, fc) + paxNet) - base0;
  const padIds = [...new Set(touches.filter(t => t.location && t.location._id).map(t => t.location._id))];
  const perPad = {}; let sum = 0;
  for (const pid of padIds) {
    const without = touches.filter(t => !(t.location && t.location._id === pid));
    const delta = c(routePadFeesNet(touches, fc)) - c(routePadFeesNet(without, fc));
    if (delta !== 0) { perPad[pid] = delta; sum += delta; }
  }
  return { total, perPad, exact: sum === total };
}

// One pad's marginal customer contribution (for the booking snapshot depPad.fee /
// arrPad.fee — informational; the authoritative total is booking.landingFees).
export function padContribFor(f, data, pad) {
  if (!pad) return 0;
  const loc = LOCATION_DB.find(l => (pad._id && l._id === pad._id) || (pad.id && l.id === pad.id));
  if (!loc) return 0;
  return padFeeContribution(f, data).perPad[loc._id] || 0;
}

// Thin wrapper over the shared pure resolver — binds App's runtime SEO_ROUTES.
export const getRoutePriceMin = (from, to) => _getRoutePriceMin(from, to, SEO_ROUTES);

// ═══════════════════════════════════════════════════════════════════════════
// Brief A — pad-service eligibility + pad-exact pricing (helipads-before-aircraft)
//
// The booking flow is Route → Helipads → Aircraft. Both the Helipads step and
// the Aircraft step gate on which operators actually SERVE the chosen pads, and
// the displayed aircraft price is computed pad-exact (base→pad→pad→base) by the
// SAME exported engine the server runs in bookings-create.js — so the price the
// customer sees is the price the server validates. See
// src/lib/pricing.js getRoutePriceFromDoc / estimateAircraftPrice.
// ═══════════════════════════════════════════════════════════════════════════

// opId -> Set(location._id) the operator opted OUT of (operator.unservedHelipads).
// Built at call time so it reads the CMS-populated OPERATOR_FLEET (a `let`
// reassigned at module init), never the pre-init empty array.
export function buildUnservedByOp() {
  const m = {};
  for (const o of OPERATOR_FLEET) {
    m[o._id] = new Set((o.unservedHelipads || []).map(u => u && u.ref).filter(Boolean));
  }
  return m;
}

// Schritt C — opId -> Set(location._id) the operator SELF-assigned (operator.
// servedHelipads). Built like buildUnservedByOp; union'd in isOperatorServingPad.
export function buildServedByOp() {
  const m = {};
  for (const o of OPERATOR_FLEET) {
    m[o._id] = new Set((o.servedHelipads || []).map(s => s && (s.ref || s)).filter(Boolean));
  }
  return m;
}

// An operator serves a pad — thin wrapper over the SHARED isOperatorServingPad
// (the SAME union the server `servedBy` in bookings-create.js uses, so client ==
// server). `padLoc` is a LOCATION_DB entry (_id + operatorIds + adminAssignedOnly).
export function opServesPad(opId, padLoc, unservedByOp, servedByOp) {
  if (!padLoc) return false;
  return isOperatorServingPad({
    operatorIds: padLoc.operatorIds,
    adminAssignedOnly: !!padLoc.adminAssignedOnly,
    opId, padId: padLoc._id,
    servedSet: servedByOp && servedByOp[opId],
    unservedSet: unservedByOp && unservedByOp[opId],
  });
}

// Operators that PRICE this route (either direction). Drives dead-end-free pad
// filtering at the Helipads step: a pad is offered only if a route-pricing
// operator serves it, so every selectable pad leads to ≥1 priced aircraft.
// Falls back to all active operators when the route is entirely unpriced, so
// quote-request flows (price on request) still let the customer pick pads.
export function routePricingOperatorIds(from, to) {
  const direct = SEO_ROUTES.find(r => r.from === from && r.to === to);
  const reverse = SEO_ROUTES.find(r => r.from === to && r.to === from);
  const ids = [...new Set([...(direct?.pricing || []), ...(reverse?.pricing || [])].map(p => p && p.operatorId).filter(Boolean))];
  if (ids.length) return ids;
  return OPERATOR_FLEET.filter(o => o.status === 'active').map(o => o._id);
}

// Pad-exact aircraft price for the aircraft step. Calls the SHARED exported
// engine against the raw __SANITY_DATA__ route doc with the chosen pad
// coordinates + a both-pads-served operator filter — identical inputs to the
// server's anti-tampering check, so client price == server price by
// construction. Returns the getRoutePriceFromDoc result shape
// ({ price, type, operator, rate, returnBrokerPrice, brokerPrice }) or
// { price:null, type:null }.
export function padExactPrice(aircraftId, from, to, depLoc, arrLoc, operatorFilter, flightMin, flightDate = null) {
  const raw = (typeof window !== 'undefined') ? window.__SANITY_DATA__ : null;
  if (!raw) return { price: null, type: null };
  const settingsRate = raw.settings?.commissionRate ?? 10;
  const directRoute = (raw.routes || []).find(r => r.from === from && r.to === to) || null;
  const reverseRoute = (raw.routes || []).find(r => r.from === to && r.to === from) || null;
  const aircraftDoc = (raw.fleet || []).find(f => f.id === aircraftId) || null;
  const depCoords = (depLoc && depLoc.lat != null && depLoc.lng != null) ? { lat: depLoc.lat, lng: depLoc.lng } : null;
  const arrCoords = (arrLoc && arrLoc.lat != null && arrLoc.lng != null) ? { lat: arrLoc.lat, lng: arrLoc.lng } : null;
  return estimateAircraftPrice({
    aircraftId, from, to,
    flightMin: flightMin || directRoute?.flightMin || reverseRoute?.flightMin || 0,
    directRoute, reverseRoute, allRoutes: raw.routes || [], aircraftDoc,
    commPct: settingsRate, locations: raw.locations || [],
    depCoords, arrCoords, operatorFilter,
    depId: depLoc?._id || null, arrId: arrLoc?._id || null, // route-override pad match
    flightDate, // seasonal base resolved for the chosen flight date (client == server)
  });
}

// Find primary location (helipad/airport) for a city name.
// Generic haversine/parseSpeed helpers live in src/lib/flightTime.js.
function cityLocation(cityName) {
  const locs = LOCATION_DB.filter(l => l.enabled !== false && l.city === cityName);
  return locs.find(l => l.type === "helipad") || locs.find(l => l.type === "airport") || locs[0] || null;
}

// Calculate flight time in minutes for a specific aircraft between two cities.
// City-aware wrapper around lib helpers — uses cityLocation + fleet aircraft speed.
export function calcFlightMin(fromCity, toCity, aircraftId) {
  const fromLoc = cityLocation(fromCity);
  const toLoc = cityLocation(toCity);
  if (!fromLoc?.lat || !toLoc?.lat) return null;
  const ac = FLEET.find(f => f.id === aircraftId);
  const speed = ac ? parseSpeed(ac.speed) : 0;
  if (!speed) return null;
  const dist = haversineKm(fromLoc, toLoc);
  if (dist == null) return null;
  // Pure cruise — no hardcoded taxi/approach overhead (removed 2026-06-05).
  // Operator-defined per-aircraft overhead does not reach this generic,
  // cross-operator customer flight-time estimate (see report).
  return Math.round((dist / speed) * 60);
}

// Get approximate flight time for a route — fastest aircraft, or CMS fallback.
// Thin wrapper over the shared pure resolver (src/lib/dest-resolvers.js); passes
// App's runtime FLEET/LOCATION_DB/SEO_ROUTES so SSR can resolve the same value.
function getApproxFlightMin(fromCity, toCity) {
  return _getApproxFlightMin(fromCity, toCity, { fleet: FLEET, locations: LOCATION_DB, routes: SEO_ROUTES });
}

// Route flight-time — manual override wins, fleet calc is fallback (Phase 3F)
export function routeFlightMin(r) { return r?.flightMin || getApproxFlightMin(r?.from, r?.to) || null; }

// Unified flight-time label: "~X min" or "—" (Phase 3F)
export function fmtFlightShort(min) { return min > 0 ? "~" + min + " min" : "—"; }

// Stat-chip unit guards — the card chips must never show a bare number. Most
// Sanity speed/range values already carry their unit ("251 km/h", "633 km"),
// but defend against unit-less data by appending the unit when the value is
// purely numeric. Display-only; the underlying field is untouched.
export function withSpeedUnit(v) { const s = String(v == null ? "" : v).trim(); return /^\d+(\.\d+)?$/.test(s) ? s + " km/h" : s; }
export function withRangeUnit(v) { const s = String(v == null ? "" : v).trim(); return /^\d+(\.\d+)?$/.test(s) ? s + " km" : s; }

// Destination flight-time label — manual > calc > dash, with Hub edge-case (Phase 3F).
// Thin wrapper over the shared pure resolver (src/lib/dest-resolvers.js); passes
// App's runtime FLEET/LOCATION_DB/SEO_ROUTES so the dest SSR hero shows the same label.
export function destFlightLabel(dest) {
  return _destFlightLabel(dest, { fleet: FLEET, locations: LOCATION_DB, routes: SEO_ROUTES });
}

// Estimate price when no route-specific price exists
// Returns { price, type, returnPrice } where type = "exact" | "indicative" |
// "estimated" | null. `returnPrice` is the round-trip customer price: for
// exact routes it honours an explicit returnBrokerPrice (via the __CMS
// loader's returnPrices map), otherwise it is one-way × 2. Mirrors the
// server's estimateAircraftPrice + getReturnPrice chain.
export function estimatePrice(aircraftId, from, to, flightMin) {
  // 1. Exact route price from CMS
  const exact = getRoutePrice(from, to, aircraftId);
  if (exact) return { price: exact, type: "exact", returnPrice: getRouteReturnPrice(from, to, aircraftId) || exact * 2 };

  // 2. Reverse route (if ATH→MYK exists but MYK→ATH doesn't)
  const reverse = getRoutePrice(to, from, aircraftId);
  if (reverse) return { price: reverse, type: "exact", returnPrice: getRouteReturnPrice(to, from, aircraftId) || reverse * 2 };

  // 3. Indicative price from past operator quotes
  const route = SEO_ROUTES.find(r => r.from === from && r.to === to);
  const indPrice = route?.indicativePrices?.[aircraftId];
  if (indPrice) return { price: indPrice, type: "indicative", returnPrice: indPrice * 2 };
  const reverseRoute = SEO_ROUTES.find(r => r.from === to && r.to === from);
  const indReverse = reverseRoute?.indicativePrices?.[aircraftId];
  if (indReverse) return { price: indReverse, type: "indicative", returnPrice: indReverse * 2 };

  // 4. Interpolation: calculate EUR/min from all routes with price for this aircraft
  const routesWithPrice = SEO_ROUTES.filter(r => r.prices?.[aircraftId] && r.flightMin > 0);
  if (routesWithPrice.length >= 2 && flightMin > 0) {
    // Weighted by similarity in flight time (closer routes matter more)
    let totalWeight = 0, weightedSum = 0;
    for (const r of routesWithPrice) {
      const ratePerMin = r.prices[aircraftId] / r.flightMin;
      const diff = Math.abs(r.flightMin - flightMin);
      const w = 1 / (1 + diff / 10); // closer flight times = higher weight
      weightedSum += ratePerMin * w;
      totalWeight += w;
    }
    const estimatedRate = weightedSum / totalWeight;
    const estimated = Math.ceil(estimatedRate * flightMin / 10) * 10; // round up to nearest 10
    return { price: estimated, type: "estimated", returnPrice: estimated * 2 };
  }

  // 4. Hourly rate fallback
  const aircraft = FLEET.find(f => f.id === aircraftId);
  if (aircraft?.hourlyRate && flightMin > 0) {
    const estimated = Math.ceil((aircraft.hourlyRate * flightMin / 60) / 10) * 10;
    return { price: estimated, type: "estimated", returnPrice: estimated * 2 };
  }

  // 5. No price available
  return { price: null, type: null, returnPrice: null };
}

// -- VAT CONFIG -- set in backend/CMS -------------------------------------
// Greece applies 13% VAT on domestic helicopter passenger transport.
// Set vatIncluded:true if prices shown are already VAT-inclusive (gross),
// or false if prices are net and VAT should be added on top.
// Set vatRate:0 to mark flights as VAT-exempt (e.g. international routes).
const VAT_CONFIG = {
  vatRate:     0.13,   // 13% -- Greek domestic helicopter transport (FPA 13%)
  vatIncluded: true,   // prices shown are gross (VAT already included)
  vatLabel:    "FPA / VAT 13%",
  vatNote:     "Greek domestic helicopter transport is subject to 13% VAT (FPA) per Greek tax law. Prices shown are VAT-inclusive.",
};

export let EXTRAS = [];
// All extras from Sanity CMS

let BLOGS = [];
// All blog posts from Sanity CMS

export let SEO_ROUTES = [];
// All routes + prices from Sanity CMS

/* PAX POPUP */
function PaxDropdown({ adults, setAdults, children, setChildren, infants, setInfants, label }) {
  const [open, setOpen] = useState(false);
  const [isMobile, setMobile] = useState(false);
  const ref = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    const check = () => setMobile(window.innerWidth <= 640);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  useEffect(() => {
    const fn = (e) => {
      if (ref.current && !ref.current.contains(e.target) && panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);

  const total = adults + children + infants;
  const paxLabel = adults + " adult" + (adults>1?"s":"") + (children>0 ? ", "+children+" child"+(children>1?"ren":"") : "") + (infants>0 ? ", "+infants+" infant"+(infants>1?"s":"") : "");

  const maxSeats = FLEET.reduce((m,f) => Math.max(m,f.seats), 0); // 8
  const seatPax = adults + children; // infants are lap

  const rows = [
    { label:"Adults",   hint:"Age 12+",            n:adults,   set:setAdults,   min:1, max:Math.min(maxSeats, maxSeats - children) },
    { label:"Children", hint:"Age 2–11",            n:children, set:setChildren, min:0, max:Math.max(0, maxSeats - adults) },
    { label:"Infants",  hint:"Under 2, lap infant", n:infants,  set:setInfants,  min:0, max:Math.min(adults, 3) },
  ];

  const PaxRows = ({ large }) => (
    <div>
      {rows.map(r => (
        <div key={r.label} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding: large ? "16px 0" : "12px 0", borderBottom:"1px solid var(--bd)" }}>
          <div>
            <div style={{ fontSize: large ? 16 : 14, fontWeight:600 }}>{r.label}</div>
            <div style={{ fontSize: large ? 13 : 12, color:"var(--mu2)", marginTop:2 }}>{r.hint}</div>
          </div>
          <div style={{ display:"flex", alignItems:"center", gap: large ? 16 : 12 }}>
            <button type="button" onClick={() => r.set(Math.max(r.min, r.n-1))} disabled={r.n<=r.min} style={{ width: large ? 40 : 32, height: large ? 40 : 32, borderRadius:"50%", border:"1.5px solid var(--bd2)", background:"white", display:"flex", alignItems:"center", justifyContent:"center", cursor: r.n<=r.min?"not-allowed":"pointer", opacity: r.n<=r.min?0.35:1, transition:"all .15s" }}><Minus size={large ? 16 : 14}/></button>
            <span style={{ fontSize: large ? 22 : 18, fontWeight:700, minWidth: large ? 28 : 24, textAlign:"center" }}>{r.n}</span>
            <button type="button" onClick={() => r.set(Math.min(r.max, r.n+1))} disabled={r.n>=r.max} style={{ width: large ? 40 : 32, height: large ? 40 : 32, borderRadius:"50%", border:"1.5px solid var(--bd2)", background:"white", display:"flex", alignItems:"center", justifyContent:"center", cursor: r.n>=r.max?"not-allowed":"pointer", opacity: r.n>=r.max?0.35:1, transition:"all .15s" }}><Plus size={large ? 16 : 14}/></button>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div ref={ref} style={{ position:"relative" }}>
      {/* Trigger — matches DatePicker/TimePicker */}
      <button type="button" onClick={() => setOpen(o => !o)} style={{ width:"100%", padding:"11px 14px", borderRadius:10, fontSize:14, fontFamily:"inherit", border:"1.5px solid " + (open ? "var(--bl)" : "transparent"), background:"var(--off)", boxShadow: open ? "0 0 0 3px rgba(13,86,166,.08)" : "none", cursor:"pointer", textAlign:"left", display:"flex", alignItems:"center", justifyContent:"space-between", color:"var(--ink2)", transition:"all .15s" }}>
        <span style={{ fontWeight:500 }}>{paxLabel}</span>
        <ChevronDown size={13} color="var(--mu2)" style={{ transform:"rotate("+(open?180:0)+"deg)", transition:"transform .2s" }}/>
      </button>

      {/* Mobile: PickerSheet */}
      {open && isMobile && (
        <PickerSheet onClose={() => setOpen(false)} title={total + " passenger" + (total>1?"s":"")}>
          <PaxRows large/>
          <button className="btn btn-bl" style={{ width:"100%", marginTop:20, justifyContent:"center", padding:"14px 0", fontSize:16 }} onClick={() => setOpen(false)}>Done</button>
        </PickerSheet>
      )}

      {/* Desktop: dropdown */}
      {open && !isMobile && (
        <div ref={panelRef} onMouseDown={e => e.stopPropagation()} style={{ position:"absolute", top:"calc(100% + 6px)", left:"50%", transform:"translateX(-50%)", width:"max(100%, 280px)", maxWidth:"calc(100vw - 32px)", zIndex:1200, background:"white", border:"1px solid var(--bd)", borderRadius:12, boxShadow:"0 16px 48px rgba(0,0,0,.13)", padding:"12px 14px" }}>
          <PaxRows/>
          <button className="btn btn-bl btn-sm" style={{ width:"100%", marginTop:12, justifyContent:"center" }} onClick={() => setOpen(false)}>Done</button>
        </div>
      )}
    </div>
  );
}

// 15-minute interval time slots 06:00 - 20:00
const TIME_SLOTS = Array.from({ length: (20-6)*4+1 }, (_, i) => {
  const h = Math.floor(i/4) + 6;
  const m = (i % 4) * 15;
  return String(h).padStart(2,"0")+":"+String(m).padStart(2,"0");
});

// Time picker hours range 06-20
let DEP_HOUR_MIN = 6, DEP_HOUR_MAX = 20;
export let ARRIVAL_BEFORE_MIN = 20;
// System-wide sightseeing loop durations (minutes) — shown as the Duration choice for
// EVERY sightseeing-served city, independent of any tour doc. Overridable via
// siteSettings.sightseeingDurations; built-in default [30,45,60]. Set in the __CMS block.
let SIGHTSEEING_DURATIONS = [30, 45, 60];
const HOURS   = Array.from({ length: 24 }, (_, i) => i);
const MINUTES = ["00", "15", "30", "45"];

/* TIME PICKER */
function TimeHourBtn({ h, selHour, pickHour }) {
  const active = selHour === h;
  return (
    <button type="button" onMouseDown={() => pickHour(h)} style={{ minWidth:36, padding:"8px 0", borderRadius:8, border:"1.5px solid "+(active?"var(--bl)":"var(--bd)"), background:active?"var(--bl)":"white", color:active?"white":"var(--ink)", fontFamily:"var(--sans)", fontWeight:active?700:500, fontSize:14, cursor:"pointer", textAlign:"center", transition:"all .1s" }}>
      {String(h).padStart(2,"0")}
    </button>
  );
}
function TimeMinBtn({ m, selHour, value, pickMin, compact }) {
  const t = String(selHour).padStart(2,"0") + ":" + m;
  const active = value === t;
  return compact ? (
    <button type="button" onMouseDown={() => pickMin(m)} style={{ padding:"8px 0", borderRadius:8, border:"1.5px solid "+(active?"var(--bl)":"var(--bd)"), background:active?"var(--bl)":"white", color:active?"white":"var(--ink)", fontFamily:"var(--sans)", fontWeight:active?700:500, fontSize:14, cursor:"pointer", textAlign:"center", transition:"all .1s" }}>
      {String(selHour).padStart(2,"0")}:{m}
    </button>
  ) : (
    <button type="button" onMouseDown={() => pickMin(m)} style={{ padding:"16px 0", borderRadius:12, border:"1.5px solid "+(active?"var(--bl)":"var(--bd)"), background:active?"var(--bl)":"white", color:active?"white":"var(--ink)", fontFamily:"var(--sans)", fontWeight:700, fontSize:18, letterSpacing:"-.03em", cursor:"pointer" }}>
      {String(selHour).padStart(2,"0")}:{m}
    </button>
  );
}
function FlexPicker({ value, onChange, label = "Flexible" }) {
  const parsed = typeof value === "object" && value !== null ? value : (value > 0 ? { minus: value, plus: value } : { minus: 0, plus: 0 });
  const isActive = parsed.minus > 0 || parsed.plus > 0;
  const [open, setOpen] = useState(false);
  const [minus, setMinus] = useState(parsed.minus || 0);
  const [plus, setPlus]   = useState(parsed.plus || 0);
  const ref = useRef(null);
  const panelRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => { const c = () => setIsMobile(window.innerWidth <= 640); c(); window.addEventListener("resize", c); return () => window.removeEventListener("resize", c); }, []);
  useEffect(() => { const fn = e => { if (ref.current && !ref.current.contains(e.target) && panelRef.current && !panelRef.current.contains(e.target)) setOpen(false); }; document.addEventListener("mousedown", fn); return () => document.removeEventListener("mousedown", fn); }, []);

  const pickMinus = (h) => { setMinus(minus === h ? 0 : h); };
  const pickPlus  = (h) => { setPlus(plus === h ? 0 : h); };
  const confirm = () => { onChange({ minus, plus }); setOpen(false); };
  const clear = (e) => { e.stopPropagation(); onChange({ minus: 0, plus: 0 }); setMinus(0); setPlus(0); };

  const display = isActive
    ? (parsed.minus > 0 && parsed.plus > 0 && parsed.minus === parsed.plus
        ? "\u00B1 " + parsed.minus + "h"
        : (parsed.minus > 0 ? "-" + parsed.minus + "h" : "") + (parsed.minus > 0 && parsed.plus > 0 ? " / " : "") + (parsed.plus > 0 ? "+" + parsed.plus + "h" : ""))
    : null;

  const hours = [1,2,3,4,5,6];

  const pickerContent = (
    <div style={{ fontFamily:"var(--sans)" }}>
      <div style={{ fontSize:12, fontWeight:700, color:"var(--mu)", textTransform:"uppercase", letterSpacing:".06em", marginBottom:10 }}>Can depart earlier by</div>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(6,1fr)", gap:6, marginBottom:18 }}>
        {hours.map(h => (
          <button key={h} type="button" onMouseDown={e => e.preventDefault()} onClick={() => pickMinus(h)} style={{ padding: isMobile ? "14px 0" : "10px 0", borderRadius:8, border:"1.5px solid " + (minus===h ? "var(--bl)" : "var(--bd)"), background: minus===h ? "var(--blbg)" : "white", color: minus===h ? "var(--bl)" : "var(--ink2)", fontWeight:700, fontSize: isMobile ? 15 : 13, cursor:"pointer", fontFamily:"var(--sans)" }}>
            -{h}h
          </button>
        ))}
      </div>
      <div style={{ fontSize:12, fontWeight:700, color:"var(--mu)", textTransform:"uppercase", letterSpacing:".06em", marginBottom:10 }}>Can depart later by</div>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(6,1fr)", gap:6 }}>
        {hours.map(h => (
          <button key={h} type="button" onMouseDown={e => e.preventDefault()} onClick={() => pickPlus(h)} style={{ padding: isMobile ? "14px 0" : "10px 0", borderRadius:8, border:"1.5px solid " + (plus===h ? "var(--bl)" : "var(--bd)"), background: plus===h ? "var(--blbg)" : "white", color: plus===h ? "var(--bl)" : "var(--ink2)", fontWeight:700, fontSize: isMobile ? 15 : 13, cursor:"pointer", fontFamily:"var(--sans)" }}>
            +{h}h
          </button>
        ))}
      </div>
      <button type="button" className="btn btn-bl" onClick={confirm} style={{ width:"100%", padding:"11px 0", fontSize:14, marginTop:16 }}>
        {(minus > 0 || plus > 0) ? "Confirm" : "Not flexible"}
      </button>
    </div>
  );

  return (
    <div ref={ref} style={{ position:"relative" }}>
      <div className="fl" style={{ marginBottom:5 }}>{label}</div>
      <button type="button" onClick={() => setOpen(o => !o)} style={{ width:"100%", padding:"11px 14px", borderRadius:10, fontSize:14, fontFamily:"inherit", border:"1.5px solid " + (open ? "var(--bl)" : "transparent"), background:"var(--off)", boxShadow: open ? "0 0 0 3px rgba(13,86,166,.08)" : "none", cursor:"pointer", textAlign:"left", display:"flex", alignItems:"center", justifyContent:"space-between", color: display ? "var(--ink2)" : "var(--mu)", transition:"all .15s" }}>
        <span style={{ fontWeight: display ? 500 : 400 }}>{display || "Not flexible"}</span>
        <span style={{ display:"flex", alignItems:"center", gap:4 }}>
          {display && <span onMouseDown={clear} style={{ color:"var(--mu2)", display:"flex", lineHeight:1 }}><X size={12}/></span>}
          <ChevronDown size={13} color="var(--mu2)" style={{ transform:"rotate(" + (open?180:0) + "deg)", transition:"transform .2s" }}/>
        </span>
      </button>
      {open && isMobile && (
        <PickerSheet onClose={() => setOpen(false)} title="Time flexibility">
          {pickerContent}
        </PickerSheet>
      )}
      {open && !isMobile && (
        <div ref={panelRef} onMouseDown={e => e.stopPropagation()} style={{ position:"absolute", top:"calc(100% + 6px)", right:0, width:320, zIndex:1200, background:"white", border:"1px solid var(--bd)", borderRadius:12, boxShadow:"0 16px 48px rgba(0,0,0,.13)", padding:"16px" }}>
          {pickerContent}
        </div>
      )}
    </div>
  );
}

function TimePicker({ value, onChange, label, placeholder = "Any time", date, required, minTime, error = false }) {
  const [open,    setOpen]    = useState(false);
  const [selHour, setSelHour] = useState(value ? parseInt(value.split(":")[0]) : null);
  const ref        = useRef(null);
  const panelRef   = useRef(null);
  const pickingRef = useRef(false); // flag: suppress close while picking hour

  // When date = today, only allow hours >= current hour + 1
  const todayMinHour = !date ? 6 : (greekToday() !== date ? 6 : Math.min(greekHour() + 1, 22));
  // When minTime is set (e.g. return on same day), only allow hours >= departure hour + 1
  const mtHour = minTime ? parseInt(minTime.split(":")[0]) : 0;
  const mtMin  = minTime ? parseInt(minTime.split(":")[1]) : 0;
  const minHour = Math.max(todayMinHour, minTime ? mtHour + 1 : DEP_HOUR_MIN);
  const availableHours = HOURS.filter(h => h >= minHour && h <= DEP_HOUR_MAX);

  useEffect(() => {
    setSelHour(value ? parseInt(value.split(":")[0]) : null);
  }, [value]);

  // Clear selection if chosen hour is now in the past (date changed to today) or below minTime
  useEffect(() => {
    if (selHour !== null && selHour < minHour) {
      setSelHour(null);
      onChange("");
    }
  }, [date, minTime]);

  useEffect(() => {
    const fn = e => {
      if (pickingRef.current) { pickingRef.current = false; return; }
      const inTrigger = ref.current    && ref.current.contains(e.target);
      const inPanel   = panelRef.current && panelRef.current.contains(e.target);
      if (!inTrigger && !inPanel) setOpen(false);
    };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);

  const openPicker = () => {
    setOpen(o => !o);
  };

  const pickHour = (h) => {
    pickingRef.current = true; // suppress the document listener this cycle
    setSelHour(h);
  };
  const pickMin = (m) => { onChange(String(selHour).padStart(2,"0") + ":" + m); setOpen(false); };
  const clear   = (e) => { e.stopPropagation(); onChange(""); setSelHour(null); };

  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 640);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  return (
    <div ref={ref} style={{ position:"relative" }}>
      {label && <div className="fl" style={{ marginBottom:5 }}>{label}{required && <span style={{color:"#C0392B",marginLeft:3}}>*</span>}</div>}

      {/* Trigger */}
      <button type="button" onClick={openPicker} style={{ width:"100%", padding:"11px 14px", borderRadius:10, fontSize:14, fontFamily:"inherit", border:"1.5px solid " + (error ? "#C0392B" : open ? "var(--bl)" : "transparent"), background: "var(--off)", boxShadow: open ? "0 0 0 3px rgba(13,86,166,.08)" : "none", cursor:"pointer", textAlign:"left", display:"flex", alignItems:"center", justifyContent:"space-between", color: value ? "var(--ink2)" : "var(--mu)", transition:"all .15s", }}>

        <span style={{ fontWeight: value ? 500 : 400 }}>{value || placeholder}</span>
        <span style={{ display:"flex", alignItems:"center", gap:4 }}>
          {value && <span onMouseDown={clear} style={{ color:"var(--mu2)", display:"flex", lineHeight:1 }}><X size={12}/></span>} <ChevronDown size={13} color="var(--mu2)" style={{ transform:"rotate(" + (open?180:0) + "deg)", transition:"transform .2s" }}/>
        </span>
      </button>

      {/* Mobile: swipe-to-dismiss bottom sheet */}
      {open && isMobile && (
        <PickerSheet onClose={() => setOpen(false)} title={selHour === null ? "Select hour" : String(selHour).padStart(2,"0") + " : ?"}>

          <div style={{ minHeight:300 }}>
            {selHour === null && (
              <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10 }}>
                {availableHours.map(h => (
                  <button key={h} type="button" onMouseDown={() => pickHour(h)} style={{ padding:"16px 0", borderRadius:12, border:"1.5px solid var(--bd)", background:"white", color:"var(--ink)", fontFamily:"var(--sans)", fontWeight:700, fontSize:18, cursor:"pointer", letterSpacing:"-.02em" }}>
                    {String(h).padStart(2,"0")}
                  </button>
                ))}
              </div>
            )}
            {selHour !== null && (
              <>
                <button type="button" onMouseDown={() => setSelHour(null)} style={{ fontSize:13, color:"var(--bl)", background:"none", border:"none", cursor:"pointer", marginBottom:16, display:"block", fontFamily:"var(--sans)", fontWeight:600 }}>
                  ← Change hour
                </button>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                  {MINUTES.filter(m => selHour < 20 || m === "00").map(m => <TimeMinBtn key={m} m={m} selHour={selHour} value={value} pickMin={pickMin}/>)}
                </div>
              </>
            )}
          </div>
        </PickerSheet>
      )}

      {/* Desktop: absolute dropdown anchored to trigger */}
      {open && !isMobile && (
        <div ref={panelRef} onMouseDown={e => e.stopPropagation()} style={{ position:"absolute", top:"calc(100% + 6px)", left:"50%", transform:"translateX(-50%)", width:"max(100%, 280px)", maxWidth:"calc(100vw - 32px)", zIndex:1200, background:"white", border:"1px solid var(--bd)", borderRadius:12, boxShadow:"0 16px 48px rgba(0,0,0,.13)", padding:"12px 12px 10px", }}>
          <div style={{ fontSize:11, fontWeight:700, letterSpacing:".06em", textTransform:"uppercase", color:"var(--mu2)", marginBottom:6 }}>Hour</div>
          <div style={{ display:"flex", flexWrap:"wrap", gap:4, marginBottom: selHour !== null ? 10 : 4 }}>
            {availableHours.map(h => <TimeHourBtn key={h} h={h} selHour={selHour} pickHour={pickHour}/>)}
          </div>
          {selHour !== null && (
            <>
              <div style={{ fontSize:11, fontWeight:700, letterSpacing:".06em", textTransform:"uppercase", color:"var(--mu2)", marginBottom:6 }}>Minute</div>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(4, 1fr)", gap:4 }}>
                {MINUTES.filter(m => selHour < 20 || m === "00").map(m => <TimeMinBtn key={m} m={m} selHour={selHour} value={value} pickMin={pickMin} compact/>)}
              </div>
            </>
          )}
          {selHour === null && (
            <div style={{ fontSize:11, color:"var(--mu2)", textAlign:"center", paddingTop:3 }}>Pick an hour above</div>
          )}
        </div>
      )}
    </div>
  );
}

/* PRIVATE HELIPAD TOGGLE */

/* CLICKABLE PIN MAP (for custom route) */
/* LOCATION SEARCH INPUT (custom route) */

/* LEAFLET MAP LOADER */
const _leaflet = { promise: null, L: null };
export function loadLeaflet() {
  if (_leaflet.L) return Promise.resolve(_leaflet.L);
  if (_leaflet.promise) return _leaflet.promise;
  _leaflet.promise = new Promise((resolve) => {
    if (!document.getElementById("leaflet-css")) {
      const link = document.createElement("link"); link.id = "leaflet-css"; link.rel = "stylesheet";
      link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css"; document.head.appendChild(link);
    }
    if (window.L) { _leaflet.L = window.L; resolve(window.L); return; }
    const s = document.createElement("script"); s.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
    s.onload = () => { _leaflet.L = window.L; resolve(window.L); }; document.head.appendChild(s);
  });
  return _leaflet.promise;
}

// Returns true once `ref`'s element is within `rootMargin` of the viewport, then
// stays true (latches). Used to DEFER Leaflet init + its carto basemap tiles
// (~70-210 KiB) until a map is about to scroll into view, instead of eagerly on
// mount while it sits below the fold. rootMargin pre-warms ~200px before entry so
// the map is ready by the time it is visible; if IntersectionObserver is absent
// or the element is already in/near view on load, it fires immediately (maps
// above the fold / deep-links init right away). The observed element is the
// reserved-size placeholder container, which always renders, so the box (and the
// CLS reservation) is unchanged whether or not the map has initialized.
function useNearViewport(ref, rootMargin = "200px") {
  const [near, setNear] = useState(false);
  useEffect(() => {
    if (near) return;
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") { setNear(true); return; }
    const io = new IntersectionObserver((entries) => {
      if (entries.some(e => e.isIntersecting)) { setNear(true); io.disconnect(); }
    }, { rootMargin });
    io.observe(el);
    return () => io.disconnect();
  }, [near, rootMargin]);
  return near;
}
const TILE_CARTO = "https://basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png";
const TILE_SAT   = "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}";

// Attach CARTO + Esri Satellite base layers with collapsed layer-toggle control (Track 4B)
export function attachMapLayers(map, L) {
  const carto = L.tileLayer(TILE_CARTO, { maxZoom: 18 });
  const sat   = L.tileLayer(TILE_SAT,   { maxZoom: 18, attribution: 'Tiles © Esri' });
  carto.addTo(map);
  L.control.layers({ 'Map': carto, 'Satellite': sat }, {}, { position: 'topleft' }).addTo(map);
}

function makeIcon(L, html, size = [28, 28]) {
  return L.divIcon({ className: "", html, iconSize: size, iconAnchor: [size[0]/2, size[1]/2] });
}
export function helipadIcon(L, selected, col = "#0D56A6") {
  const bg = selected ? col : "white", stroke = selected ? "white" : col;
  return makeIcon(L, '<svg width="28" height="28" viewBox="0 0 40 40"><circle cx="20" cy="20" r="18" fill="'+bg+'" stroke="'+col+'" stroke-width="2.5"/><circle cx="20" cy="20" r="12" fill="none" stroke="'+stroke+'" stroke-width="1.8"/><line x1="14" y1="12" x2="14" y2="28" stroke="'+stroke+'" stroke-width="2.8" stroke-linecap="round"/><line x1="26" y1="12" x2="26" y2="28" stroke="'+stroke+'" stroke-width="2.8" stroke-linecap="round"/><line x1="14" y1="20" x2="26" y2="20" stroke="'+stroke+'" stroke-width="2.8" stroke-linecap="round"/></svg>');
}
export function airportIcon(L, selected, col = "#0D56A6") {
  const bg = selected ? col : "white", fill = selected ? "white" : col;
  return makeIcon(L, '<svg width="28" height="28" viewBox="0 0 28 28"><circle cx="14" cy="14" r="12" fill="'+bg+'" stroke="'+col+'" stroke-width="2"/><svg x="5" y="5" width="18" height="18" viewBox="1 1 22 22"><path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z" fill="'+fill+'"/></svg></svg>');
}
function hotelIcon(L) {
  // Track 4E: pulsing brand-blue dot ("here you land") — no image, no label, radar-style
  const html = '<div class="hp-pulse-wrap"><div class="hp-pulse-ring"></div><div class="hp-pulse-dot"></div></div>';
  return L.divIcon({ className: '', html, iconSize: [50, 50], iconAnchor: [25, 25] });
}
function pinIcon(L, col = "#0D56A6") {
  return L.divIcon({ className: "", html: '<svg width="28" height="36" viewBox="0 0 28 36"><path d="M14 0C6.27 0 0 6.27 0 14C0 24.5 14 36 14 36S28 24.5 28 14C28 6.27 21.73 0 14 0Z" fill="'+col+'"/><circle cx="14" cy="14" r="6" fill="white"/></svg>', iconSize: [28, 36], iconAnchor: [14, 36] });
}



/* CANONICAL REQUIRED-FIELD ERROR STYLE (owner, 2026-07-06): red border/ring +
   inline text below the field on the NORMAL field background — never a red
   fill. Single definition; booking-flow fields derive their error state from
   these two exports. */
export const FIELD_ERROR_BORDER = "1.5px solid #C0392B";
export function FieldError({ id = undefined, children }) {
  if (!children) return null;
  return (
    <div id={id} style={{ fontSize:13, color:"#C0392B", marginTop:4, display:"flex", alignItems:"center", gap:4 }}>
      <Info size={12} style={{ flexShrink:0 }}/>{children}
    </div>
  );
}

/* LOCATION PICKER */
function LocationPicker({ value, onChange, exclude, placeholder = "Search location...", label, error = false, required = false, cities = null, id = undefined, errorText = null }) {
  const [query,   setQuery]   = useState(value || "");
  const [open,    setOpen]    = useState(false);
  const [focused, setFocused] = useState(false);
  const ref = useRef(null);
  // When `cities` is passed, restrict the picker to that subset (used for
  // sightseeing where only departure-cities-of-tours are valid). Otherwise
  // fall back to the global LOCS list.
  const SOURCE = (cities && cities.length) ? cities : LOCS;

  // Sync external value changes (e.g. swap button, back-navigation)
  useEffect(() => { setQuery(value || ""); }, [value]);

  // Close on outside click
  useEffect(() => {
    const fn = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);

  const filtered = query.length === 0
    ? SOURCE.filter(l => l !== exclude)
    : SOURCE.filter(l => l !== exclude && l.toLowerCase().includes(query.toLowerCase()));

  const select = (loc) => {
    setQuery(loc);
    onChange(loc);
    setOpen(false);
  };

  const handleInput = (e) => {
    setQuery(e.target.value);
    setOpen(true);
    // If cleared, also clear the value
    if (!e.target.value) onChange("");
  };

  const handleFocus = () => { setFocused(true); setOpen(true); };
  const handleBlur  = () => { setFocused(false); };

  const isValid = SOURCE.includes(query);

  return (
    <div ref={ref} style={{ position:"relative" }}>
      {label && <div className="fl" style={{ marginBottom:5 }}>{label}{required && <span style={{color:"#C0392B",marginLeft:3}}>*</span>}</div>} <div style={{ position:"relative" }}>
        <input id={id} className="fi" value={query} onChange={handleInput} onFocus={handleFocus} onBlur={handleBlur} placeholder={placeholder} autoComplete="off" aria-invalid={error ? true : undefined} style={{ paddingRight:32, borderColor: focused ? "var(--bl)" : error ? "#C0392B" : isValid && value ? "var(--bd)" : "transparent", background: "var(--off)", boxShadow:   focused ? "0 0 0 3px rgba(13,86,166,.08)" : "none", }} />
        {/* Clear / chevron icon */}
        {query ? (
          <button type="button" onClick={() => { setQuery(""); onChange(""); setOpen(true); }} style={{ position:"absolute", right:10, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", cursor:"pointer", color:"var(--mu2)", padding:2, display:"flex" }}>
            <X size={13}/>
          </button>
        ) : (
          <ChevronDown size={13} color="var(--mu2)" style={{ position:"absolute", right:10, top:"50%", transform:"translateY(-50%) rotate("+(open?180:0)+"deg)", transition:"transform .2s", pointerEvents:"none" }}/>
        )}
      </div>
      {error && errorText && <FieldError>{errorText}</FieldError>}

      {/* Dropdown */}
      {open && (
        <div style={{ position:"absolute", top:"calc(100% + 4px)", left:0, right:0, zIndex:600, background:"white", border:"1px solid var(--bd)", borderRadius:12, boxShadow:"0 12px 40px rgba(0,0,0,.12)", overflow:"hidden", maxHeight:240, overflowY:"auto" }}>

          {filtered.length === 0 ? (
            <div style={{ padding:"12px 14px", fontSize:13, color:"var(--mu2)" }}>No results for "{query}"</div>
          ) : (
            filtered.map(loc => (
              <div key={loc} onMouseDown={() => select(loc)} style={{ padding:"10px 14px", fontSize:14, cursor:"pointer", background: loc === value ? "var(--blbg)" : "white", color:      loc === value ? "var(--bl)"  : "var(--ink)", fontWeight: loc === value ? 600 : 400, display:"flex", alignItems:"center", gap:8, transition:"background .1s", }} >
                <MapPin size={11} color={loc === value ? "var(--bl)" : "var(--mu2)"}/>
                {loc}
                {loc === value && <Check size={12} color="var(--bl)" style={{ marginLeft:"auto" }}/>} </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

/* SEARCH WIDGET */
export let SIGHTSEEING_TOURS = [];
// All sightseeing tours from Sanity CMS
let SIGHTSEEING_NAMES = SIGHTSEEING_TOURS.map(t => t.name);

// ============================================
// CMS OVERRIDE — replace hardcoded data with Sanity CMS content
// ============================================
if (__CMS) {
  FLEET = (__CMS.FLEET || []).filter((x, i, a) => a.findIndex(y => y.id === x.id) === i).sort((a, b) => (a.name||"").localeCompare(b.name||""));
  DESTS = (__CMS.DESTS || []).filter((x, i, a) => a.findIndex(y => y.name === x.name) === i);
  HOTELS = (__CMS.HOTELS || []).filter((x, i, a) => a.findIndex(y => y.id === x.id) === i);
  HUBS = (__CMS.HUBS || []).filter((x, i, a) => a.findIndex(y => y._id === x._id) === i).sort((a, b) => (a.order || 0) - (b.order || 0));
  OPERATOR_FLEET = __CMS.OPERATOR_FLEET || [];
  // Aggregated trust signal from active operators. Customer-facing only —
  // never expose individual operator identities here; response-time is a
  // smoothed average to avoid leaking per-operator behaviour.
  const _responseOps = OPERATOR_FLEET.filter(o => o.status === 'active' && o.avgResponseMinutes > 0);
  if (_responseOps.length > 0) {
    const _avgMin = _responseOps.reduce((s, o) => s + o.avgResponseMinutes, 0) / _responseOps.length;
    PLATFORM_AVG_RESPONSE_HOURS = Math.round((_avgMin / 60) * 10) / 10;
  }
  LOCATION_DB = (__CMS.LOCATION_DB || []).filter((l, i, a) => a.findIndex(x => x.id === l.id) === i);
  recomputeLocationLookups();
  EXTRAS = (__CMS.EXTRAS || []).filter((x, i, a) => a.findIndex(y => y.id === x.id) === i);
  BLOGS = (__CMS.BLOGS || []).filter((x, i, a) => a.findIndex(y => y.slug === x.slug) === i);
  SIGHTSEEING_TOURS = (__CMS.SIGHTSEEING_TOURS || []).filter((x, i, a) => a.findIndex(y => y.name === x.name) === i);
  SEO_ROUTES = (__CMS.SEO_ROUTES || []).filter((x, i, a) => a.findIndex(y => y.slug === x.slug) === i);
  REVIEWS = __CMS.REVIEWS || [];
  HOTEL_CITIES = [...new Set(HOTELS.map(h => h.city))].filter(Boolean).sort();
  SIGHTSEEING_NAMES = SIGHTSEEING_TOURS.map(t => t.name);
  // Luggage weights/volumes overrides
  if (__CMS.SETTINGS.luggageWeights) {
    const lw = __CMS.SETTINGS.luggageWeights;
    if (lw.carryOn) LUG.carryOn = lw.carryOn;
    if (lw.small) LUG.small = lw.small;
    if (lw.standard) LUG.standard = lw.standard;
    if (lw.large) LUG.large = lw.large;
  }
  if (__CMS.SETTINGS.luggageVolumes) {
    const lv = __CMS.SETTINGS.luggageVolumes;
    if (lv.carryOn) LUG_VOL.carryOn = lv.carryOn;
    if (lv.small) LUG_VOL.small = lv.small;
    if (lv.standard) LUG_VOL.standard = lv.standard;
    if (lv.large) LUG_VOL.large = lv.large;
  }
  // Departure hours + packing settings
  if (__CMS.SETTINGS.departureHourMin != null) DEP_HOUR_MIN = __CMS.SETTINGS.departureHourMin;
  if (__CMS.SETTINGS.departureHourMax != null) DEP_HOUR_MAX = __CMS.SETTINGS.departureHourMax;
  if (__CMS.SETTINGS.packingEfficiency != null) PACKING_EFFICIENCY = __CMS.SETTINGS.packingEfficiency;
  if (__CMS.SETTINGS.cabinVolPerSeat != null) CABIN_VOL_PER_SEAT = __CMS.SETTINGS.cabinVolPerSeat;
  if (__CMS.SETTINGS.arrivalBeforeMin != null) ARRIVAL_BEFORE_MIN = __CMS.SETTINGS.arrivalBeforeMin;
  if (Array.isArray(__CMS.SETTINGS.sightseeingDurations) && __CMS.SETTINGS.sightseeingDurations.filter(n => Number(n) > 0).length) {
    SIGHTSEEING_DURATIONS = __CMS.SETTINGS.sightseeingDurations.map(Number).filter(n => n > 0).sort((a, b) => a - b);
  }
  if (__CMS.SETTINGS.pilotWeightKg != null && __CMS.SETTINGS.pilotWeightKg > 0) PILOT_WEIGHT_KG = __CMS.SETTINGS.pilotWeightKg;
  if (__CMS.SETTINGS.secondPilotWeight != null && __CMS.SETTINGS.secondPilotWeight > 0) SECOND_PILOT_WEIGHT_KG = __CMS.SETTINGS.secondPilotWeight;
  // Recompute maxLuggage for all aircraft using CMS-configured values.
  // _manualLuggage is the raw Sanity-pflegt value; if present we honour it.
  // Without this guard the volume-formula silently overrides the operator's
  // manual entry — which is how Bell 429 ended up advertising 18 bags.
  FLEET.forEach(f => {
    if (f._manualLuggage != null && f._manualLuggage > 0) {
      f.maxLuggage = f._manualLuggage;
    } else if (f.compartmentVol && f.compartmentVol > 0) {
      f.maxLuggage = Math.floor((f.compartmentVol * PACKING_EFFICIENCY) / LUG_VOL.standard);
    }
  });
}

// Homepage content — from CMS with hardcoded fallbacks
const HP = {
  heroTitle:       __CMS?.HOMEPAGE?.heroTitle       || "Private helicopter",
  heroItalic:      __CMS?.HOMEPAGE?.heroTitleItalic  || "charter across Greece.",
  heroSub:         __CMS?.HOMEPAGE?.heroSubtitle     || "Full aircraft charters from HCAA-licensed Greek operators — island transfers, sightseeing flights, yacht connections, and VIP travel. The price you see is the price you pay.",
  heroCta:         __CMS?.HOMEPAGE?.heroCta          || "Request a flight",
  // 2026-05-19c — added heroImage so the homepage hero <Img> reads from
  // Sanity homepage.heroImage. Was missing entirely; hero was hardcoded
  // to IMG.hero (Unsplash) so Sanity uploads never surfaced.
  heroImage:       __CMS?.HOMEPAGE?.heroImage       || null,
  ctaTitle:        __CMS?.HOMEPAGE?.ctaTitle         || "Ready to fly?",
  ctaSub:          __CMS?.HOMEPAGE?.ctaSubtitle      || "Request a quote in 60 seconds. Our team will get back to you as soon as possible.",
  steps:           __CMS?.HOMEPAGE?.howItWorksSteps  || null,
};

// Business timezone — configurable in Sanity → Site Settings → Business Timezone
export const SITE_TZ = __CMS?.SETTINGS?.timezone || "Europe/Athens";

// Timezone helpers — all business logic uses SITE_TZ
function greekToday() {
  return new Intl.DateTimeFormat("en-CA", { timeZone: SITE_TZ }).format(new Date());
}
function greekHour() {
  return parseInt(new Intl.DateTimeFormat("en-GB", { hour: "2-digit", hour12: false, timeZone: SITE_TZ }).format(new Date()));
}
export const TODAY     = greekToday();
export const YESTERDAY = new Intl.DateTimeFormat("en-CA", { timeZone: SITE_TZ }).format(new Date(Date.now()-86400000));
const MAX_DOB   = new Date(Date.now()-365*24*3600*1000*18).toISOString().split("T")[0]; // 18 yrs ago

// Shared swipe-to-dismiss bottom sheet for DatePicker and TimePicker
export function PickerSheet({ onClose, title, children }) {
  const sheetRef = useRef(null);
  const startY   = useRef(null);
  const lastY    = useRef(0);
  const active   = useRef(false);

  const move = (clientY) => {
    if (!active.current) return;
    const dy = clientY - startY.current;
    if (dy < 0) return;
    lastY.current = dy;
    if (sheetRef.current) sheetRef.current.style.transform = "translateY(" + dy + "px)";
  };

  const end = () => {
    if (!active.current) return;
    active.current = false;
    const sheet = sheetRef.current;
    if (!sheet) return;
    if (lastY.current > 100) {
      sheet.style.transition = "transform .3s cubic-bezier(.4,0,1,1)";
      sheet.style.transform  = "translateY(" + window.innerHeight + "px)";
      setTimeout(onClose, 300);
    } else {
      sheet.style.transition = "transform .35s cubic-bezier(.32,0,.15,1)";
      sheet.style.transform  = "translateY(0)";
      setTimeout(() => { if (sheet) sheet.style.transition = ""; }, 350);
    }
    lastY.current = 0;
  };

  // Touch handlers attached to the whole overlay -- start only from handle zone
  const onTouchStart = (e) => {
    const sheet = sheetRef.current;
    if (!sheet) return;
    const sheetTop = sheet.getBoundingClientRect().top;
    const touchY   = e.touches[0].clientY;
    // Only initiate drag if touch starts within 60px from top of sheet (handle area)
    if (touchY - sheetTop > 60) return;
    active.current = true;
    startY.current = touchY;
    sheet.style.transition = "none";
  };
  const onTouchMove  = (e) => { if (active.current) { e.preventDefault(); move(e.touches[0].clientY); } };
  const onTouchEnd   = () => end();

  return (
    <div onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd} style={{ position:"fixed", inset:0, zIndex:900, display:"flex", flexDirection:"column", justifyContent:"flex-end" }}>
      <div onMouseDown={onClose} style={{ position:"absolute", inset:0, background:"rgba(0,0,0,.4)" }}/>
      <div ref={sheetRef} style={{ position:"relative", background:"white", borderRadius:"20px 20px 0 0", padding:"0 20px 36px", boxShadow:"0 -8px 40px rgba(0,0,0,.18)" }}>
        <div style={{ padding:"14px 0 4px" }}>
          <div style={{ width:40, height:4, background:"var(--bd2)", borderRadius:2, margin:"0 auto" }}/>
        </div>
        <div style={{ fontFamily:"var(--sans)", fontWeight:800, fontSize:18, letterSpacing:"-.03em", color:"var(--ink)", textAlign:"center", padding:"14px 0 20px" }}>{title}</div>
        {children}
      </div>
    </div>
  );
}

/* CUSTOM DATE PICKER */
function DayCell({ d, idx, value, TODAY, isDisabled, selectDate, large }) {
  if (!d) return <div key={"e"+idx}/>;
  if (isDisabled(d)) return <div key={idx}/>;
  const dStr = d.getFullYear() + "-" + String(d.getMonth()+1).padStart(2,"0") + "-" + String(d.getDate()).padStart(2,"0");
  const isSel   = value === dStr;
  const isToday = dStr === TODAY;
  return (
    <button type="button" onMouseDown={() => selectDate(d)} style={{ padding: large ? "14px 0" : "8px 0", borderRadius: large ? 12 : 8, border:"1.5px solid " + (isSel ? "var(--bl)" : isToday ? "var(--blmid)" : "var(--bd)"), background: isSel ? "var(--bl)" : "white", color: isSel ? "white" : isToday ? "var(--bl)" : "var(--ink)", fontFamily:"var(--sans)", fontWeight: isSel||isToday ? 700 : 500, fontSize: large ? 18 : 14, textAlign:"center", cursor:"pointer", transition:"all .1s", letterSpacing: large ? "-.02em" : "0" }}>
      {d.getDate()}
    </button>
  );
}

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const DAYS   = ["Mo","Tu","We","Th","Fr","Sa","Su"];

function CalendarGrid({ viewYear, viewMonth, cells, canPrev, prevMonth, canNext, nextMonth, selectDate, value, TODAY, isDisabled, large, setYear, setMon, minYear, maxYear, maxDate }) {
  const canJump = !!setYear;
  const [jumpMode, setJumpMode] = useState(canJump && !value ? "year" : null); // null | "year" | "month"
  const yearMin = minYear || 1920;
  const yearMax = maxYear || new Date().getFullYear();
  const maxMonth = (maxDate && viewYear === yearMax) ? maxDate.getMonth() : 11;

  const [yearQuery, setYearQuery] = useState("");

  if (jumpMode === "year" && canJump) {
    const years = [];
    for (let y = yearMax; y >= yearMin; y--) years.push(y);
    const filteredYears = yearQuery ? years.filter(y => String(y).includes(yearQuery)) : years;
    return (
      <div onMouseDown={e => e.stopPropagation()}>
        <div style={{ fontWeight:700, fontSize: large ? 18 : 14, textAlign:"center", marginBottom: large ? 8 : 6, fontFamily:"var(--sans)" }}>Select year</div>
        <div style={{ marginBottom: large ? 8 : 6 }}>
          <input className="fi" autoFocus value={yearQuery} onChange={e => setYearQuery(e.target.value.replace(/[^\d]/g, "").slice(0,4))} placeholder="Type year, e.g. 1988" inputMode="numeric" style={{ fontSize: large ? 16 : 13, padding: large ? "10px 14px" : "7px 10px", background:"var(--off)", textAlign:"center" }}/>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap: large ? 6 : 4, maxHeight: large ? "45vh" : 180, overflowY:"auto" }}>
          {filteredYears.map(y => (
            <button key={y} type="button" onClick={e => { e.stopPropagation(); setYear(y); setJumpMode("month"); setYearQuery(""); }} style={{ padding: large ? "10px 0" : "7px 0", borderRadius:8, border:"1.5px solid "+(y===viewYear?"var(--bl)":"var(--bd)"), background:y===viewYear?"var(--bl)":"white", color:y===viewYear?"white":"var(--ink2)", fontFamily:"var(--sans)", fontWeight:y===viewYear?700:500, fontSize: large ? 16 : 13, cursor:"pointer" }}>
              {y}
            </button>
          ))}
          {filteredYears.length === 0 && <div style={{ gridColumn:"1/-1", textAlign:"center", padding:12, fontSize:12, color:"var(--mu2)" }}>No match</div>}
        </div>
      </div>
    );
  }

  if (jumpMode === "month" && canJump) {
    return (
      <div onMouseDown={e => e.stopPropagation()}>
        <div style={{ fontWeight:700, fontSize: large ? 18 : 14, textAlign:"center", marginBottom: large ? 12 : 8, fontFamily:"var(--sans)" }}>{viewYear}</div>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap: large ? 8 : 5 }}>
          {MONTHS.map((m, i) => {
            const disabled = i > maxMonth;
            return (
            <button key={m} type="button" disabled={disabled} onClick={e => { e.stopPropagation(); if (!disabled) { setMon(i); setJumpMode(null); } }} style={{ padding: large ? "12px 0" : "8px 0", borderRadius:8, border:"1.5px solid "+(i===viewMonth?"var(--bl)":"var(--bd)"), background:i===viewMonth?"var(--bl)":disabled?"var(--off)":"white", color:i===viewMonth?"white":disabled?"var(--mu2)":"var(--ink2)", fontFamily:"var(--sans)", fontWeight:i===viewMonth?700:500, fontSize: large ? 15 : 13, cursor:disabled?"not-allowed":"pointer", opacity:disabled?.4:1 }}>
              {m.slice(0,3)}
            </button>
            );
          })}
        </div>
        <button type="button" onClick={e => { e.stopPropagation(); setJumpMode("year"); }} style={{ width:"100%", marginTop:8, padding:"6px 0", fontSize:12, color:"var(--bl)", fontWeight:600, background:"none", border:"none", cursor:"pointer", fontFamily:"var(--sans)" }}>← Change year</button>
      </div>
    );
  }

  return (
    <>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom: large ? 12 : 8 }}>
        <button type="button" onMouseDown={e=>{e.stopPropagation();prevMonth();}} disabled={!canPrev()} style={{ width: large ? 36 : 28, height: large ? 36 : 28, borderRadius: large ? 10 : 7, border:"1.5px solid var(--bd)", background:"white",
            cursor:canPrev()?"pointer":"default", opacity:canPrev()?1:0.25,
            display:"flex", alignItems:"center", justifyContent:"center" }}>
          <ChevronDown size={large ? 16 : 12} style={{ transform:"rotate(90deg)" }}/>
        </button>
        {canJump ? (
          <button type="button" onClick={e => { e.stopPropagation(); setJumpMode("year"); }} style={{ fontWeight:700, fontSize: large ? 18 : 14, letterSpacing:"-.02em", fontFamily:"var(--sans)", background:"none", border:"none", cursor:"pointer", color:"var(--bl)", padding:"4px 8px", borderRadius:6 }}>
            {MONTHS[viewMonth]} {viewYear}
          </button>
        ) : (
          <div style={{ fontWeight:700, fontSize: large ? 18 : 14, letterSpacing:"-.02em", fontFamily:"var(--sans)" }}>{MONTHS[viewMonth]} {viewYear}</div>
        )}
        <button type="button" onMouseDown={e=>{e.stopPropagation();nextMonth();}} disabled={!canNext()} style={{ width: large ? 36 : 28, height: large ? 36 : 28, borderRadius: large ? 10 : 7, border:"1.5px solid var(--bd)", background:"white",
            cursor:canNext()?"pointer":"default", opacity:canNext()?1:0.25,
            display:"flex", alignItems:"center", justifyContent:"center" }}>
          <ChevronDown size={large ? 16 : 12} style={{ transform:"rotate(-90deg)" }}/>
        </button>
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", marginBottom: large ? 6 : 4 }}>
        {DAYS.map(d => (
          <div key={d} style={{ textAlign:"center", fontSize: large ? 12 : 11, fontWeight:700, letterSpacing:".06em", textTransform:"uppercase", color:"var(--mu2)", fontFamily:"var(--sans)", padding:"3px 0" }}>{d}</div>
        ))}
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap: large ? 6 : 4 }}>
        {cells.map((d, idx) => <DayCell key={idx} d={d} idx={idx} value={value} TODAY={TODAY} isDisabled={isDisabled} selectDate={selectDate} large={large}/>)} </div>
    </>
  );
}

export function DatePicker({ value, onChange, label, required, error, min, max, placeholder = "Select date", canJump = false }) {
  const [open,     setOpen]   = useState(false);
  const [viewYear, setYear]   = useState(null);
  const [viewMonth, setMon]   = useState(null);
  const [isMobile, setMobile] = useState(false);
  const ref      = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    const check = () => setMobile(window.innerWidth <= 640);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  const minDate = min ? new Date(min + "T00:00:00") : null;
  const maxDate = max ? new Date(max + "T00:00:00") : null;

  const initView = (v) => {
    let base;
    if (v) { base = new Date(v + "T00:00:00"); }
    else if (canJump) { base = new Date(); base.setFullYear(base.getFullYear() - 30); } // DOB: start ~30 years ago
    else { base = new Date(); }
    if (minDate && base < minDate) base = new Date(minDate);
    if (maxDate && base > maxDate) base = new Date(maxDate);
    setYear(base.getFullYear());
    setMon(base.getMonth());
  };

  useEffect(() => {
    const fn = e => {
      const inRef   = ref.current    && ref.current.contains(e.target);
      const inPanel = panelRef.current && panelRef.current.contains(e.target);
      if (!inRef && !inPanel) setOpen(false);
    };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);

  const openPicker = () => {
    initView(value);
    setOpen(o => !o);
  };

  const isDisabled = (d) => {
    if (minDate && d < minDate) return true;
    if (maxDate && d > maxDate) return true;
    return false;
  };

  const selectDate = (d) => {
    if (isDisabled(d)) return;
    const str = d.getFullYear() + "-" + String(d.getMonth()+1).padStart(2,"0") + "-" + String(d.getDate()).padStart(2,"0");
    onChange(str);
    setOpen(false);
  };

  const prevMonth = () => { if (viewMonth === 0) { setYear(y=>y-1); setMon(11); } else setMon(m=>m-1); };
  const nextMonth = () => { if (viewMonth === 11) { setYear(y=>y+1); setMon(0); } else setMon(m=>m+1); };

  const canPrev = () => !minDate || new Date(viewYear, viewMonth, 1) > new Date(minDate.getFullYear(), minDate.getMonth(), 1);
  const canNext = () => !maxDate || new Date(viewYear, viewMonth, 1) < new Date(maxDate.getFullYear(), maxDate.getMonth(), 1);

  const buildGrid = () => {
    if (viewYear === null) return [];
    const firstDay = (new Date(viewYear, viewMonth, 1).getDay() + 6) % 7;
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
    const cells = Array(firstDay).fill(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(viewYear, viewMonth, d));
    return cells;
  };

  const formatDisplay = (v) => {
    if (!v) return null;
    const [y,m,d] = v.split("-");
    return d + " " + MONTHS[parseInt(m)-1].slice(0,3) + " " + y;
  };

  const clear  = (e) => { e.stopPropagation(); onChange(""); };
  const cells  = buildGrid();

  return (
    <div ref={ref} style={{ position:"relative" }}>
      {label && <div className="fl" style={{ marginBottom:5 }}>{label}{required && <span style={{ color:"#C0392B", marginLeft:3 }}>*</span>}</div>}

      {/* Trigger -- same as TimePicker trigger */}
      <button type="button" onClick={openPicker} style={{ width:"100%", padding:"11px 14px", borderRadius:10, fontSize:14, fontFamily:"inherit", border:"1.5px solid " + (error ? "#C0392B" : open ? "var(--bl)" : "transparent"), background: "var(--off)", boxShadow: open ? "0 0 0 3px rgba(13,86,166,.08)" : "none", cursor:"pointer", textAlign:"left", display:"flex", alignItems:"center", justifyContent:"space-between", color: value ? "var(--ink2)" : "var(--mu)", transition:"all .15s", }}>

        <span style={{ fontWeight: value ? 500 : 400 }}>{formatDisplay(value) || placeholder}</span>
        <span style={{ display:"flex", alignItems:"center", gap:4 }}>
          {value && <span onMouseDown={clear} style={{ color:"var(--mu2)", display:"flex", lineHeight:1 }}><X size={12}/></span>} <ChevronDown size={13} color="var(--mu2)" style={{ transform:"rotate("+(open?180:0)+"deg)", transition:"transform .2s" }}/>
        </span>
      </button>

      {/* Mobile: swipe-to-dismiss bottom sheet */}
      {open && isMobile && (
        <PickerSheet onClose={() => setOpen(false)} title={value ? formatDisplay(value) : "Select date"}>
          <div ref={panelRef}>{<CalendarGrid viewYear={viewYear} viewMonth={viewMonth} cells={cells} canPrev={canPrev} prevMonth={prevMonth} canNext={canNext} nextMonth={nextMonth} selectDate={selectDate} value={value} TODAY={TODAY} isDisabled={isDisabled} large setYear={canJump?setYear:undefined} setMon={canJump?setMon:undefined} minYear={1920} maxYear={maxDate ? maxDate.getFullYear() : new Date().getFullYear()} maxDate={maxDate}/>}</div>
        </PickerSheet>
      )}

      {/* Desktop: absolute dropdown anchored to trigger */}
      {open && !isMobile && (
        <div ref={panelRef} onMouseDown={e => e.stopPropagation()} style={{ position:"absolute", top:"calc(100% + 6px)", left:"50%", transform:"translateX(-50%)", width:"max(100%, 280px)", maxWidth:"calc(100vw - 32px)", zIndex:1200, background:"white", border:"1px solid var(--bd)", borderRadius:12, boxShadow:"0 16px 48px rgba(0,0,0,.13)", padding:"12px 12px 10px", }}>

          {<CalendarGrid viewYear={viewYear} viewMonth={viewMonth} cells={cells} canPrev={canPrev} prevMonth={prevMonth} canNext={canNext} nextMonth={nextMonth} selectDate={selectDate} value={value} TODAY={TODAY} isDisabled={isDisabled} setYear={canJump?setYear:undefined} setMon={canJump?setMon:undefined} minYear={1920} maxYear={maxDate ? maxDate.getFullYear() : new Date().getFullYear()} maxDate={maxDate}/>} </div>
      )}
    </div>
  );
}

export function SearchWidget({ onSearch, onSync = null, init = {}, overlayMode = false, onMobileTap = null }) {
  const [tripType, setTripType] = useState(init.tripType || "oneway");
  const [from, setFrom]         = useState(init.from     || "");
  const [to, setTo]             = useState(init.to       || "");
  const [date, setDate]         = useState(init.date     || "");
  const [time, setTime]         = useState(init.time       || "");
  const [returnTime, setRetTime]  = useState(init.returnTime  || "");
  const [returnDate, setReturn] = useState(init.returnDate || "");
  const [sgRoute, setSgRoute]   = useState(init.sgRoute  || "");
  const [sgBase, setSgBase]     = useState(init.sgBase   || "");
  const [sgDate, setSgDate]     = useState(init.sgDate   || "");
  const [duration, setDuration] = useState(init.duration || "30");
  const [adults, setAdults]     = useState(init.adults   || 2);
  const [children, setChildren] = useState(init.children || 0);
  const [infants, setInfants]   = useState(init.infants  || 0);

  // Clear capacity warning when passengers change
  useEffect(() => { setCapacityWarning(false); }, [adults, children, infants]);
  const [customRouteData, setCustomRouteData] = useState(init.customRouteData || {
    depDesc: "", depPin: null,
    arrDesc: "", arrPin: null,
    note: init.customHelipad?.note || "",
  });
  const updCR = (k, v) => setCustomRouteData(p => ({ ...p, [k]: v }));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [capacityWarning, setCapacityWarning] = useState(false);
  const [customHelipad, setCustomHelipad] = useState(init.customHelipad || { enabled:false, note:"" });

  // Mobile detection
  const [timeFlexible, setTimeFlex] = useState(init.timeFlexible || false);
  const [flexHours, setFlexHours]   = useState(init.flexHours || 2);
  const [flexMinus, setFlexMinus]   = useState(init.flexMinus || 0);
  const [flexPlus, setFlexPlus]     = useState(init.flexPlus || 0);
  const [returnFlexible, setRetFlex] = useState(init.returnFlexible || false);
  const [returnFlexHours, setRetFlexH] = useState(init.returnFlexHours || 2);
  const [retFlexMinus, setRetFlexMinus] = useState(init.retFlexMinus || 0);
  const [retFlexPlus, setRetFlexPlus]   = useState(init.retFlexPlus || 0);
  // Connecting flights (Phase 2B-V2, 2026-05-03) — only shown in the
  // overlay/booking-flow render, suppressed on the homepage SearchWidget
  // (gated on overlayMode below). State seeds from init so back-nav from
  // step 2 → step 1 preserves what the user already typed.
  const [cfAttempted, setCfAttempted] = useState(false);
  const [cf, setCf] = useState(() => {
    const arr = init?.connectingFlights || [];
    const ob = arr.find(c => c.leg === 'outbound') || null;
    const rt = arr.find(c => c.leg === 'return') || null;
    return {
      obOn: !!ob,
      rtOn: !!rt,
      ob: { direction: ob?.direction || 'inbound', airline: ob?.airline || '', flightNumber: ob?.flightNumber || '', airport: ob?.airport || '', date: ob?.date || '', time: ob?.time || '' },
      rt: { direction: rt?.direction || 'outbound', airline: rt?.airline || '', flightNumber: rt?.flightNumber || '', airport: rt?.airport || '', date: rt?.date || '', time: rt?.time || '' },
    };
  });
  const setCfFor = (slot) => (key, val) => setCf(p => ({ ...p, [slot]: { ...p[slot], [key]: val } }));
  const setCfOn  = (slot) => (val) => setCf(p => ({ ...p, [slot + 'On']: val }));
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.innerWidth <= 768);
  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  const tabs = [
    { id:"oneway",      label:"One-Way",       icon:<ArrowRight size={13}/> },
    { id:"roundtrip",   label:"Round Trip",    icon:<ArrowLeftRight size={13}/> },
    { id:"sightseeing", label:"Sightseeing",   icon:<Camera size={13}/> },
    { id:"customroute", label:"Custom Route",  icon:<MapPin size={13}/> },
  ];

  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (tripType === "oneway" || tripType === "roundtrip") {
      if (!from) e.from = "Required";
      if (!to)   e.to   = "Required";
      if (!date)             e.date = "Please select a departure date";
      else if (date < TODAY) e.date = "Departure date must be today or in the future";
      if (!time)             e.time = "Please select a departure time";
      if (tripType === "roundtrip") {
        if (!returnDate)                     e.returnDate = "Please select a return date";
        else if (returnDate < (date||TODAY)) e.returnDate = "Return date must be after departure";
        if (returnDate && returnDate === date && time && returnTime && returnTime <= time) e.returnTime = "Return time must be after departure time";
      }
    }
    if (tripType === "sightseeing") {
      if (!sgBase) e.sgBase = "Required";
      // Track B (generic): a tour is NO LONGER required — durations are system-wide so a
      // served city (e.g. Crete) is bookable without a tour doc. The optional Tour dropdown
      // only records a display name. Duration must be a positive number.
      if (!(Number(duration) > 0)) e.duration = "Please select a duration";
      if (!sgDate)             e.sgDate = "Please select a date";
      else if (sgDate < TODAY) e.sgDate = "Date must be today or in the future";
      if (!time)               e.time = "Please select a start time";
    }
    if (tripType === "customroute") {
      if (!customRouteData.note || customRouteData.note.trim().length < 10) e.note = "Please describe your request";
      if (!date)             e.date = "Please select a preferred date";
      else if (date < TODAY) e.date = "Date must be today or in the future";
      if (!time)             e.time = "Please select a preferred time";
    }
    return e;
  };

  // Connecting-flights validation — only relevant in overlay mode
  const showCF = overlayMode && (tripType === 'oneway' || tripType === 'roundtrip');
  const isRT = tripType === 'roundtrip';
  const cfMissing = (slot) => {
    const v = cf[slot];
    return !String(v.airline || '').trim() || !String(v.flightNumber || '').trim() || !String(v.airport || '').trim() || !v.direction || !v.date || !v.time;
  };
  const cfHasErrors = (showCF && cf.obOn && cfMissing('ob')) || (showCF && isRT && cf.rtOn && cfMissing('rt'));

  // SINGLE source for the step-1 selection payload — used by BOTH the advance
  // button (handleSearch → onSearch) and the overlay live-sync effect (onSync), so
  // the two can never produce a different shape.
  const buildSelection = () => {
    const finalCustom = tripType === "customroute"
      ? { ...customHelipad, enabled: true, ...customRouteData }
      : customHelipad;
    const connectingFlights = [];
    if (showCF && cf.obOn) connectingFlights.push({ leg: 'outbound', ...cf.ob });
    if (showCF && isRT && cf.rtOn) connectingFlights.push({ leg: 'return', ...cf.rt });
    return { tripType, from, to, date, time, timeFlexible, flexHours, flexMinus, flexPlus, returnDate, returnTime, returnFlexible, returnFlexHours, retFlexMinus, retFlexPlus, sgRoute, sgBase, sgDate, duration, adults, children, infants, customHelipad: finalCustom, connectingFlights };
  };

  // OVERLAY MODE: SearchWidget is the live step-1 editor inside the booking wizard.
  // Mirror EVERY edit straight into the shared funnel `data` (the single source of
  // truth) so a step-bar forward jump — which navigates with a bare setPage and does
  // NOT call the advance button / handleSearch — still carries the latest edits.
  // The parent's onSync runs the SAME downstream invalidation handleSearch does
  // (it diffs the patch vs prev and wipes the stale aircraft/price + resets maxStep
  // on a route/date/pax change), so a back-edit can never leave a stale priced
  // aircraft. Skip the first run: local state was just seeded from init=data, so the
  // mount echo would be a redundant no-op (and must not trip the downstream wipe).
  // Non-overlay (homepage) passes no onSync → this never fires → unchanged behaviour.
  const _firstSync = useRef(true);
  useEffect(() => {
    if (!overlayMode || typeof onSync !== 'function') return;
    if (_firstSync.current) { _firstSync.current = false; return; }
    onSync(buildSelection());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overlayMode, tripType, from, to, date, time, timeFlexible, flexHours, flexMinus, flexPlus, returnDate, returnTime, returnFlexible, returnFlexHours, retFlexMinus, retFlexPlus, sgRoute, sgBase, sgDate, duration, adults, children, infants, cf, customHelipad, customRouteData]);

  const handleSearch = () => {
    const e = validate();
    if (Object.keys(e).length > 0 || cfHasErrors) {
      setErrors(e);
      if (cfHasErrors) setCfAttempted(true);
      return;
    }
    setErrors({});
    setCfAttempted(false);
    // Check if passengers exceed max fleet capacity
    const seatPax = adults + children; // infants are lap, don't need seats
    const maxSeats = FLEET.reduce((m,f) => Math.max(m,f.seats), 0);
    if (seatPax > maxSeats) {
      setCapacityWarning(true);
      track("capacity_warning", { passengers: seatPax, max_seats: maxSeats, from, to });
      return;
    }
    setCapacityWarning(false);
    setMobileOpen(false);
    onSearch(buildSelection());
  };

  // Summary line for mobile bar
  const routeLabel = tripType === "sightseeing" ? sgBase || "Select location"
    : tripType === "customroute" ? (customRouteData.depDesc || "Custom location")
    : (from||"From")+" → "+(to||"To");
  const dateLabel = (tripType === "sightseeing" ? sgDate : date) || "Any date";
  const timeLabel = time || "";
  const paxShort  = (adults+children+infants)+" pax";

  // The actual form -- shared between desktop and mobile sheet
  const renderFormContent = () => { return (
    <>
      {/* Trip type tabs */}
      <div className="sw-tabs">
        {tabs.map(t => (
          <button key={t.id} onClick={() => { setTripType(t.id); if (t.id === "sightseeing" && date && !sgDate) setSgDate(date); if (t.id !== "sightseeing" && sgDate && !date) setDate(sgDate); }} className={"sw-tab"+(tripType===t.id?" on":"")+""}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      <div className="sw-b">
        {/* ONE-WAY */}
        {tripType === "oneway" && (
          <>
            <div className="sw-row">
              <LocationPicker id="sw-from" errorText="Please select a departure location." label="From" value={from} cities={SERVED_CITIES} onChange={v=>{setFrom(v);setErrors(p=>({...p,from:false}))}} exclude={to} placeholder="Departure city..." error={errors.from} required/>
              <button className="sw-swap" aria-label="Swap departure and arrival" onClick={() => { const t=from; setFrom(to); setTo(t); }}><ArrowLeftRight size={14}/></button>
              <LocationPicker id="sw-to" errorText="Please select a destination." label="To" value={to} cities={SERVED_CITIES} onChange={v=>{setTo(v);setErrors(p=>({...p,to:false}))}} exclude={from} placeholder="Arrival city..." error={errors.to} required/>
            </div>
            <div className="sw-cols2">
              <div className="field" id="sw-date"><div className="fl">Date <span style={{color:"#C0392B",marginLeft:3}}>*</span></div>
                <DatePicker value={date} onChange={v=>{setDate(v);setErrors(p=>({...p,date:false}))}} min={TODAY} max={new Date(Date.now()+2*365*24*3600*1000).toISOString().split("T")[0]} error={errors.date} placeholder="Departure date"/>
                {errors.date && typeof errors.date === "string" && <FieldError>{errors.date}</FieldError>} </div>
              <div className="field" id="sw-time"><TimePicker label="Departure time" required error={!!errors.time} value={time} onChange={v=>{setTime(v);setErrors(p=>({...p,time:false}))}} date={date}/>
                {errors.time && <FieldError>{typeof errors.time === "string" ? errors.time : "Please select a departure time"}</FieldError>}</div>
            </div>
            <div className="sw-cols2">
              <FlexPicker value={{ minus: flexMinus, plus: flexPlus }} onChange={v => { setFlexMinus(v.minus); setFlexPlus(v.plus); const active = v.minus > 0 || v.plus > 0; setTimeFlex(active); setFlexHours(Math.max(v.minus, v.plus) || 2); }}/>
              <div className="field"><div className="fl">Passengers <span style={{color:"#C0392B",marginLeft:3}}>*</span></div>
                <PaxDropdown adults={adults} setAdults={setAdults} children={children} setChildren={setChildren} infants={infants} setInfants={setInfants}/>
              </div>
            </div>
          </>
        )}

        {/* ROUND TRIP */}
        {tripType === "roundtrip" && (
          <>
            <div className="sw-row">
              <LocationPicker id="sw-from" errorText="Please select a departure location." label="From" value={from} cities={SERVED_CITIES} onChange={v=>{setFrom(v);setErrors(p=>({...p,from:false}))}} exclude={to} placeholder="Departure city..." error={errors.from} required/>
              <button className="sw-swap" aria-label="Swap departure and arrival" onClick={() => { const t=from; setFrom(to); setTo(t); }}><ArrowLeftRight size={14}/></button>
              <LocationPicker id="sw-to" errorText="Please select a destination." label="To" value={to} cities={SERVED_CITIES} onChange={v=>{setTo(v);setErrors(p=>({...p,to:false}))}} exclude={from} placeholder="Arrival city..." error={errors.to} required/>
            </div>
            <div className="sw-cols3">
              <div className="field" id="sw-date"><div className="fl">Departure date <span style={{color:"#C0392B",marginLeft:3}}>*</span></div>
                <DatePicker value={date} onChange={v=>{setDate(v);setErrors(p=>({...p,date:false}))}} min={TODAY} max={new Date(Date.now()+2*365*24*3600*1000).toISOString().split("T")[0]} error={errors.date} placeholder="Departure date"/>
                {errors.date && typeof errors.date === "string" && <FieldError>{errors.date}</FieldError>} </div>
              <div className="field" id="sw-time"><TimePicker label="Departure time" required error={!!errors.time} value={time} onChange={v=>{setTime(v);setErrors(p=>({...p,time:false}))}} date={date}/>
                {errors.time && <FieldError>{typeof errors.time === "string" ? errors.time : "Please select a departure time"}</FieldError>}</div>
              <FlexPicker value={{ minus: flexMinus, plus: flexPlus }} onChange={v => { setFlexMinus(v.minus); setFlexPlus(v.plus); const active = v.minus > 0 || v.plus > 0; setTimeFlex(active); setFlexHours(Math.max(v.minus, v.plus) || 2); }}/>
            </div>
            <div className="sw-cols3">
              <div className="field" id="sw-returnDate"><div className="fl">Return date <span style={{color:"#C0392B",marginLeft:3}}>*</span></div>
                <DatePicker value={returnDate} onChange={v=>{setReturn(v);setErrors(p=>({...p,returnDate:false}))}} min={date||TODAY} max={new Date(Date.now()+2*365*24*3600*1000).toISOString().split("T")[0]} error={errors.returnDate} placeholder="Return date"/>
                {errors.returnDate && typeof errors.returnDate === "string" && <FieldError>{errors.returnDate}</FieldError>} </div>
              <div className="field" id="sw-returnTime"><TimePicker label="Return time" required error={!!errors.returnTime} value={returnTime} onChange={setRetTime} date={returnDate} minTime={returnDate && returnDate === date && time ? time : undefined}/>
                {errors.returnTime && typeof errors.returnTime === "string" && <FieldError>{errors.returnTime}</FieldError>}</div>
              <FlexPicker label="Flexible" value={{ minus: retFlexMinus, plus: retFlexPlus }} onChange={v => { setRetFlexMinus(v.minus); setRetFlexPlus(v.plus); const active = v.minus > 0 || v.plus > 0; setRetFlex(active); setRetFlexH(Math.max(v.minus, v.plus) || 2); }}/>
            </div>
            <div className="field" style={{ marginBottom:16 }}><div className="fl">Passengers <span style={{color:"#C0392B",marginLeft:3}}>*</span></div>
              <PaxDropdown adults={adults} setAdults={setAdults} children={children} setChildren={setChildren} infants={infants} setInfants={setInfants}/>
            </div>
          </>
        )}

        {/* SIGHTSEEING */}
        {tripType === "sightseeing" && (() => {
          // Departure picker = cities where >=1 ACTIVE operator offers SIGHTSEEING from
          // >=1 pad (sightseeingServedCities, operator-toggle-driven). Track B (generic,
          // 2026-06-17): DURATIONS are system-wide (SIGHTSEEING_DURATIONS), so EVERY
          // served city is bookable WITHOUT a tour doc. The Tour dropdown is now OPTIONAL
          // CONTENT only — shown only when the city has tour docs (records a display name
          // + shows its description); it NEVER gates bookability or drives the duration.
          // HIGHLIGHTS pills come from the per-city destination.highlights (decoupled from
          // tours), length-gated so a city without a destination doc (e.g. Crete) is a no-op.
          const sightseeingDepartureCities = SIGHTSEEING_SERVED_CITIES;
          const matchingTours = sgBase
            ? SIGHTSEEING_TOURS.filter(t => (t.departures || []).includes(sgBase))
            : [];
          const hasTours = matchingTours.length > 0;
          const selectedTour = SIGHTSEEING_TOURS.find(t => t.name === sgRoute);
          const durList = (SIGHTSEEING_DURATIONS && SIGHTSEEING_DURATIONS.length) ? SIGHTSEEING_DURATIONS : [30, 45, 60];
          const tourDesc = selectedTour?.desc || ''; // optional content only
          const cityHighlights = (DESTS.find(d => d.name === sgBase)?.highlights) || []; // per-city pills
          return (
          <>
            <div className="sw-cols2">
              <LocationPicker label="Departure location" value={sgBase} cities={sightseeingDepartureCities} onChange={v=>{
                setSgBase(v);
                setErrors(p=>({...p,sgBase:false,sgRoute:false}));
                // Drop the optional tour if its departures no longer include the new city.
                const currentTour = SIGHTSEEING_TOURS.find(t => t.name === sgRoute);
                if (currentTour && (!v || !(currentTour.departures || []).includes(v))) setSgRoute('');
                // Keep the chosen duration valid against the system-wide list.
                if (!durList.map(String).includes(String(duration))) setDuration(String(durList[0]));
              }} placeholder="Select location..." error={errors.sgBase} errorText="Please select a departure location." required/>
              <div className="field">
                <div className="fl">Duration <span style={{color:"#C0392B",marginLeft:3}}>*</span></div>
                <select className="fi" value={String(duration)} disabled={!sgBase} onChange={e => { setDuration(e.target.value); }} style={{ color: sgBase ? 'inherit' : '#999' }}>
                  {durList.map(d => <option key={d} value={String(d)}>{d} min</option>)}
                </select>
              </div>
            </div>
            {/* Optional tour (content only) — shown ONLY when the chosen city has tour docs.
                Never gates booking; duration stays system-wide. Tourless cities skip this. */}
            {hasTours && (
              <div className="field">
                <div className="fl">Tour <span style={{ color:"var(--mu)", fontWeight:400, marginLeft:3 }}>(optional)</span></div>
                <select className="fi" value={sgRoute} onChange={e => { setSgRoute(e.target.value); }} style={{ color: sgRoute === '' ? '#999' : 'inherit' }}>
                  <option value="" style={{ color: '#999' }}>No specific tour</option>
                  {matchingTours.map(t => <option key={t.id} value={t.name} style={{ color: 'inherit' }}>{t.name}</option>)}
                </select>
              </div>
            )}
            {/* Per-city highlights (destination.highlights) + optional tour description.
                No-op when both empty (e.g. a city with no destination doc). */}
            {(cityHighlights.length > 0 || tourDesc) && (
              <div style={{ padding:"12px 16px", background:"var(--off)", borderRadius:10, marginBottom:12 }}>
                {tourDesc && <div style={{ fontSize:13, color:"var(--mu)", lineHeight:1.65, marginBottom: cityHighlights.length > 0 ? 8 : 0 }}>{tourDesc}</div>}
                {cityHighlights.length > 0 && (
                  <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                    {cityHighlights.map(h => (
                      <span key={h} style={{ fontSize:11, fontWeight:600, color:"var(--bl)", background:"var(--blbg)", padding:"2px 8px", borderRadius:100 }}>{h}</span>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div className="sw-cols3">
              <div className="field"><div className="fl">Date <span style={{color:"#C0392B"}}>*</span></div>
                <DatePicker value={sgDate} onChange={v=>{setSgDate(v);setErrors(p=>({...p,sgDate:false}))}} min={TODAY} max={new Date(Date.now()+2*365*24*3600*1000).toISOString().split("T")[0]} error={errors.sgDate} placeholder="Flight date"/>
                {errors.sgDate && typeof errors.sgDate === "string" && <FieldError>{errors.sgDate}</FieldError>} </div>
              <div className="field"><TimePicker label="Start time" required error={!!errors.time} value={time} onChange={v=>{setTime(v);setErrors(p=>({...p,time:false}))}} date={sgDate}/>
                {errors.time && <FieldError>{typeof errors.time === "string" ? errors.time : "Please select a start time"}</FieldError>}</div>
              <FlexPicker value={{ minus: flexMinus, plus: flexPlus }} onChange={v => { setFlexMinus(v.minus); setFlexPlus(v.plus); const active = v.minus > 0 || v.plus > 0; setTimeFlex(active); setFlexHours(Math.max(v.minus, v.plus) || 2); }}/>
            </div>
            <div className="field" style={{ marginBottom:14 }}>
              <div className="fl">Passengers <span style={{color:"#C0392B",marginLeft:3}}>*</span></div>
              <PaxDropdown adults={adults} setAdults={setAdults} children={children} setChildren={setChildren} infants={infants} setInfants={setInfants}/>
            </div>
            <div style={{ display:"flex", alignItems:"center", gap:6, padding:"8px 12px", background:"var(--blbg)", borderRadius:8, marginBottom:14, fontSize:12, color:"var(--bl)" }}>
              <Info size={12}/> Departs and lands at the same helipad — full aircraft rental </div>
          </>
          );
        })()}

        {/* CUSTOM ROUTE */}
        {tripType === "customroute" && (
          <>
            <div style={{ padding:"10px 14px", background:"var(--blbg)", border:"1px solid var(--blmid)", borderRadius:10, marginBottom:16, fontSize:13, color:"var(--bl)", display:"flex", gap:8, alignItems:"flex-start" }}>
              <Info size={14} style={{ flexShrink:0, marginTop:1 }}/>
              <span>Describe your charter request below. Our team will review it and send you a personalised quote.</span>
            </div>
            <div className="sw-cols3">
              <div className="field"><div className="fl">Date <span style={{color:"#C0392B",marginLeft:3}}>*</span></div>
                <DatePicker value={date} onChange={v=>{setDate(v);setErrors(p=>({...p,date:false}))}} min={TODAY} max={new Date(Date.now()+2*365*24*3600*1000).toISOString().split("T")[0]} error={errors.date} placeholder="Preferred date"/>
                {errors.date && typeof errors.date === "string" && <FieldError>{errors.date}</FieldError>} </div>
              <div className="field"><TimePicker label="Preferred time" required error={!!errors.time} value={time} onChange={v=>{setTime(v);setErrors(p=>({...p,time:false}))}} date={date}/>
                {errors.time && <FieldError>Please select a time</FieldError>}
              </div>
              <FlexPicker value={{ minus: flexMinus, plus: flexPlus }} onChange={v => { setFlexMinus(v.minus); setFlexPlus(v.plus); const active = v.minus > 0 || v.plus > 0; setTimeFlex(active); setFlexHours(Math.max(v.minus, v.plus) || 2); }}/>
            </div>
            <div className="field"><div className="fl">Passengers <span style={{color:"#C0392B",marginLeft:3}}>*</span></div>
              <PaxDropdown adults={adults} setAdults={setAdults} children={children} setChildren={setChildren} infants={infants} setInfants={setInfants}/>
            </div>
            <div className="field" style={{ marginTop:14 }}>
              <div className="fl">Your request <span style={{color:"#C0392B",marginLeft:3}}>*</span></div>
              <textarea className="fi" rows={3} style={{ border: errors.note ? FIELD_ERROR_BORDER : "1.5px solid transparent", background: "var(--off)" }} placeholder="e.g. Helicopter from Athens to a private villa in Mykonos, return 3 days later. VIP transfer for a wedding party of 6. Need luggage space for 8 large bags..." value={customRouteData.note || ""} onChange={e => { updCR("note", e.target.value); setErrors(p=>({...p,note:false})); }}/>
              {errors.note && <FieldError>Please describe your request (min. 10 characters)</FieldError>}
            </div>
          </>
        )}

        {/* Capacity warning */}
        {capacityWarning && (
          <div style={{ padding:"16px 18px", background:"#FFF7ED", border:"1.5px solid #F59E0B", borderRadius:14, marginBottom:4 }}>
            <div style={{ display:"flex", alignItems:"flex-start", gap:10 }}>
              <Users size={18} color="#92400E" style={{ flexShrink:0, marginTop:1 }}/>
              <div>
                <div style={{ fontWeight:700, fontSize:14, color:"#92400E", marginBottom:4 }}>Too many passengers for a single aircraft</div>
                <p style={{ fontSize:13, color:"#92400E", lineHeight:1.6, margin:0 }}>
                  Your group of {adults+children} passengers exceeds the capacity of our largest helicopter ({FLEET.reduce((m,f) => Math.max(m,f.seats), 0)} seats). We can arrange multiple aircraft for your group.
                </p>
                <div style={{ display:"flex", gap:8, marginTop:12 }}>
                  <button className="btn btn-ol btn-sm" onClick={() => setCapacityWarning(false)} style={{ fontSize:12 }}>Adjust passengers</button>
                  <button className="btn btn-bl btn-sm" onClick={() => { setCapacityWarning(false); onSearch({ tripType, from, to, date, time, timeFlexible, flexHours, flexMinus, flexPlus, returnDate, returnTime, returnFlexible, returnFlexHours, retFlexMinus, retFlexPlus, sgRoute, sgBase, sgDate, duration, adults, children, infants, customHelipad: { enabled:true }, forceCustomQuote:true }); }} style={{ fontSize:12 }}>
                    Request multi-aircraft quote <ArrowRight size={11}/>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Connecting flights — overlay mode only, oneway/roundtrip only */}
        {showCF && (
          <div id="sw-cf" style={{ background:'white', border:'1px solid var(--bd)', borderRadius:14, marginBottom:14 }}>
            <div style={{ padding:'14px 16px' }}>
              <div style={{ fontWeight:700, fontSize:13, marginBottom:4 }}>Connecting flights (optional)</div>
              <div style={{ fontSize:12, color:'var(--mu)', lineHeight:1.5 }}>
                Tell us about any airline transfers tied to this charter so the operator can plan timing. The operator may suggest an alternative heli time if there's a conflict — you can accept or reject from your booking link.
              </div>
            </div>
            <ConnectingFlightCard
              on={cf.obOn} setOn={setCfOn('ob')}
              cf={cf.ob} setCfField={setCfFor('ob')}
              label={isRT ? 'Arriving by airplane before the outbound helicopter departs' : 'I have a connecting airline flight'}
              subtitle={null}
              showDirection={!isRT}
              attempted={cfAttempted}
            />
            {isRT && (
              <ConnectingFlightCard
                on={cf.rtOn} setOn={setCfOn('rt')}
                cf={cf.rt} setCfField={setCfFor('rt')}
                label="Departing by airplane after the return helicopter arrives"
                subtitle={null}
                showDirection={false}
                attempted={cfAttempted}
              />
            )}
          </div>
        )}

        <div className="sw-foot">
          {Object.values(errors).some(Boolean) || (cfAttempted && cfHasErrors) ? (
            <div style={{ fontSize:12, color:"#C0392B", display:"flex", alignItems:"center", gap:5, fontWeight:600 }}>
              <Info size={12}/>
              {Object.values(errors).find(v => typeof v === "string" && v !== "Required") || (cfAttempted && cfHasErrors ? "Please complete all connecting-flight fields" : "Please fill in all required fields")}
            </div>
          ) : (
            <div className="sw-hint"><Shield size={12}/>HCAA-licensed operators - Secure booking</div>
          )}
          {/* Inline CTA lives on the homepage SearchWidget. In the booking
              overlay (overlayMode) the CTA is moved into the sticky
              BookingFooter below — not duplicated — so the hint row stays
              inline (mirrors the inline-hint + sticky-button split of
              booking steps 2–5). */}
          {!overlayMode && (
          <button className="btn btn-bl btn-lg" onClick={handleSearch} style={{ opacity: Object.values(errors).some(Boolean) || capacityWarning || (cfAttempted && cfHasErrors) ? 0.5 : 1, cursor: Object.values(errors).some(Boolean) || capacityWarning || (cfAttempted && cfHasErrors) ? "not-allowed" : "pointer" }}>
            <Search size={15}/> {tripType === "customroute" ? "Request custom quote" : "Search helicopters"} </button>
          )}
        </div>
      </div>
    </>
  ); };

  if (isMobile && !overlayMode) return (
    <>
      {/* Compact summary bar */}
      <div onClick={() => onMobileTap ? onMobileTap() : setMobileOpen(true)} style={{ background:"white", borderRadius:18, border:"1px solid var(--bd)", padding:"14px 18px", cursor:"pointer", boxShadow:"0 8px 32px rgba(0,0,0,.1)", display:"flex", alignItems:"center", gap:12 }}>
        <div style={{ width:36, height:36, borderRadius:"50%", background:"var(--blbg)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
          <Search size={16} color="var(--bl)"/>
        </div>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontWeight:700, fontSize:15, color:"var(--ink)", marginBottom:2, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
            {routeLabel}
          </div>
          <div style={{ fontSize:12, color:"var(--mu)", display:"flex", gap:8 }}>
            <span>{dateLabel}</span>
            {timeLabel && <><span>-</span><span>{timeLabel}</span></>} <span>-</span>
            <span>{paxShort}</span>
          </div>
        </div>
        <div style={{ fontSize:12, fontWeight:600, color:"var(--bl)", background:"var(--blbg)", padding:"6px 14px", borderRadius:100, flexShrink:0 }}>Search</div>
      </div>

      {/* Mobile bottom sheet */}
      {mobileOpen && (
        <div style={{ position:"fixed", inset:0, zIndex:600, display:"flex", flexDirection:"column", justifyContent:"flex-end" }}>
          <div onClick={() => setMobileOpen(false)} style={{ position:"absolute", inset:0, background:"rgba(0,0,0,.5)" }}/>
          <div style={{ position:"relative", background:"var(--off)", borderRadius:"24px 24px 0 0", maxHeight:"92vh", display:"flex", flexDirection:"column", boxShadow:"0 -8px 48px rgba(0,0,0,.2)" }}>
            <div style={{ padding:"12px 20px 0", flexShrink:0 }}>
              <div style={{ width:40, height:4, background:"var(--bd2)", borderRadius:2, margin:"0 auto 14px" }}/>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12 }}>
                <div style={{ fontWeight:700, fontSize:17 }}>Plan your charter</div>
                <button onClick={() => setMobileOpen(false)} style={{ width:32, height:32, borderRadius:"50%", border:"none", background:"var(--off2)", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>
                  <X size={14} color="var(--mu)"/>
                </button>
              </div>
            </div>
            <div style={{ overflowY:"auto", flex:1, WebkitOverflowScrolling:"touch" }}>
              <div className="sw" style={{ margin:"0 12px 12px", overflow:"visible", boxShadow:"none", border:"none", borderRadius:16, background:"white" }}>
                {renderFormContent()}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );

  // Step-1 sticky CTA gate — live-evaluates the SAME validate() predicate
  // (plus cfHasErrors) that handleSearch enforces on submit, so there is no
  // second/divergent validation. Covers all four trip types. Only meaningful
  // in the booking overlay; the homepage SearchWidget keeps its inline button.
  const step1CanAdvance = overlayMode && Object.keys(validate()).length === 0 && !cfHasErrors;
  // Gated-CTA feedback for the charter route step (oneway/roundtrip only —
  // sightseeing/custom keep the previous silent-gated behaviour). Marking
  // reuses the EXISTING errors state that handleSearch/validate drive; the
  // gate itself (step1CanAdvance) is unchanged.
  const step1TrackedRef = useRef(false);
  const STEP1_FIELD_ORDER = ["from","to","date","time","returnDate","returnTime"];
  const STEP1_HINTS = { from:"Select a departure location to continue.", to:"Select a destination to continue.", date:"Choose a travel date to continue.", time:"Choose a departure time to continue.", returnDate:"Choose a return date to continue.", returnTime:"Choose a return time to continue.", connectingFlights:"Complete the connecting flight details to continue." };
  const step1IsCharter = tripType === "oneway" || tripType === "roundtrip";
  const step1MissingKey = (() => {
    if (!overlayMode || step1CanAdvance || !step1IsCharter) return null;
    const e = validate();
    return STEP1_FIELD_ORDER.find(k => e[k]) || (cfHasErrors ? "connectingFlights" : null);
  })();
  const handleGatedStep1 = () => {
    if (!step1IsCharter) return; // out-of-scope trip types: no-op, like the old disabled button
    const e = validate();
    setErrors(e);
    if (cfHasErrors) setCfAttempted(true);
    gatedCtaFeedback("route", STEP1_FIELD_ORDER.map(k => ({ key: k, isValid: !e[k], anchorId: "sw-" + k })).concat([{ key: "connectingFlights", isValid: !cfHasErrors, anchorId: "sw-cf" }]), step1TrackedRef);
  };
  return (
    <>
      <div className="sw" style={overlayMode ? { overflow:"visible", boxShadow:"none", border:"none", borderRadius:0 } : {}}>
        {/* In the overlay, wrap form + footer in the same minHeight:100vh
            flex column the later steps use, so BookingFooter's marginTop:auto
            pins the CTA to the viewport bottom even when the form is shorter
            than the screen — identical pinning mechanism across all steps.
            The form itself stays in a plain block div (layout unchanged). */}
        {overlayMode ? (
          <div style={{ minHeight:"100vh", display:"flex", flexDirection:"column" }}>
            <div>{renderFormContent()}</div>
            {/* Sticky bottom CTA — reuses the BookingFooter shape of steps 2–5
                (sticky bottom:0 + safe-area padding inherited). Disabled/greyed
                until every required field for the active trip type is filled. */}
            <BookingFooter hint={step1MissingKey ? (
              <div id="sw-gate-hint" style={{ fontSize:13, color:"var(--mu)", textAlign:"center", marginBottom:10 }}>{STEP1_HINTS[step1MissingKey]}</div>
            ) : null}>
              <button className="btn btn-bl btn-sm" aria-disabled={!step1CanAdvance} aria-describedby={step1MissingKey ? "sw-gate-hint" : undefined} onClick={() => { if (!step1CanAdvance) { handleGatedStep1(); return; } handleSearch(); }} style={{ padding:"12px 20px", fontSize:14, flex:1, minHeight:44, opacity: step1CanAdvance ? 1 : 0.5, cursor: step1CanAdvance ? "pointer" : "not-allowed" }}>
                <Search size={15}/> {tripType === "customroute" ? "Request custom quote" : "Search helicopters"} <ArrowRight size={12}/>
              </button>
            </BookingFooter>
          </div>
        ) : (
          renderFormContent()
        )}
      </div>
    </>
  );
}
/* NAV */
/* MOBILE MENU — scrollable, accordion sub-menus */
function MobileMenu({ links, go, closeMob, mobOpen, setMobOpen, openBooking }) {
  const [openSub, setOpenSub] = useState(null); // which parent label is expanded

  // Progressive-enhancement anchor click: soft-navigate via go() on a plain
  // left-click; let modified / non-primary clicks use the native <a href>.
  const navClick = (e, p) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    e.preventDefault();
    go(p);
  };

  const handleParent = (l) => {
    if (l.ch) {
      // Toggle accordion
      setOpenSub(prev => prev === l.label ? null : l.label);
      // If it also has a page, navigate on second tap
      // (first tap opens children)
    } else if (l.page) {
      go(l.page);
    }
  };

  return (
    <div className="mob-menu">
      <div className="mob-backdrop" onClick={closeMob}/>
      <div className={"mob-sheet" + (mobOpen ? " open" : "")}>
        <div className="mob-nav-header">
          <div className="logo" dangerouslySetInnerHTML={{ __html: wordmarkSvg({ height: 20 }) }} />
          <button onClick={() => setMobOpen(false)} style={{ width:32, height:32, borderRadius:"50%", border:"none", background:"var(--off2)", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>
            <X size={15} color="var(--mu)"/>
          </button>
        </div>

        <div className="mob-scroll">
          {links.map(l => (
            <div key={l.label}>
              {l.ch ? (
                <>
                  <button className="mob-parent" onClick={() => handleParent(l)}>
                    <span>{l.label}</span>
                    <ChevronDown size={16} color="var(--mu)" style={{ transform: openSub === l.label ? "rotate(180deg)" : "none", transition:"transform .2s" }}/>
                  </button>
                  {openSub === l.label && (
                    <div style={{ background:"var(--off)", borderBottom:"1px solid var(--bd)" }}>
                      {l.page && (
                        <a className="mob-child" href={pageToPath(l.page)} style={{ fontWeight:600, color:"var(--bl)" }} onClick={(e) => navClick(e, l.page)}>
                          All {l.label} →
                        </a>
                      )}
                      {l.ch.map(c => (
                        <a key={c.label} className="mob-child" href={pageToPath(c.page)} onClick={(e) => navClick(e, c.page)}>
                          {c.label}
                        </a>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <a className="mob-link" href={pageToPath(l.page)} onClick={(e) => navClick(e, l.page)}>
                  {l.label}
                </a>
              )}
            </div>
          ))}
        </div>

        <div style={{ padding:"16px 20px 0", flexShrink:0 }}>
          <button className="btn btn-bl" style={{ width:"100%", justifyContent:"center" }} onClick={() => { setMobOpen(false); openBooking(); }}>
            Book a charter
          </button>
        </div>
      </div>
    </div>
  );
}

export function Nav({ page, setPage, openBooking }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobOpen,  setMobOpen]  = useState(false);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  // Close mob menu on page change
  const go = (p) => { setPage(p); setMobOpen(false); document.getElementById("top")?.scrollIntoView(); };
  // Progressive-enhancement anchor click: soft-navigate via go() for a plain
  // left-click, but let modified / non-primary clicks fall through to the native
  // <a href> so cmd/ctrl/shift-click (and middle-click) open a new tab/window.
  const navClick = (e, p) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    e.preventDefault();
    go(p);
  };

  const links = [
    { label:"How It Works", page:"how-it-works" },
    { label:"Charter",      ch:[
        { label:"Island Transfers",   page:"charter-island"    },
        { label:"Yacht Transfers",    page:"charter-yacht"     },
        { label:"Sightseeing Flights",page:"charter-sightseeing"},
        { label:"VIP & Corporate",    page:"charter-vip"       },
        { label:"Airport Connections",page:"charter-airport"   },
        { label:"Custom Itineraries", page:"charter-custom"    },
      ]
    },
    { label:"Destinations", ch:[
        { label:"Islands & Regions",  page:"destinations" },
        { label:"Hotels",             page:"hotels"       },
      ]
    },
    { label:"Routes",  page:"routes"  },
    { label:"Fleet",   page:"fleet"   },
    { label:"Safety",  page:"safety"  },
    { label:"Journal", page:"blog"    },
    { label:"Contact", page:"contact" },
  ];

  const closeMob = () => setMobOpen(false);
  return (
    <>
      <nav className={"nav"+(scrolled?" up":"")+""}>
        <div className="nav-in">
          <a href="/" onClick={(e) => navClick(e, "home")} style={{ cursor:"pointer", textDecoration:"none", color:"inherit" }}>
            <div className="logo" dangerouslySetInnerHTML={{ __html: fullLogoSvg({ height: 36 }) }} />
          </a>

          {/* Desktop links */}
          <div className="nls">
            {links.map(l => (
              <div key={l.label} className="dd-wrap nl">
                {l.page ? (
                  // Real anchor → soft-nav via navClick, but a real /path href so
                  // modified clicks open a new tab and crawlers see the link.
                  <a href={pageToPath(l.page)} className={"nl"+(page===l.page?" on":"")+""} onClick={(e) => navClick(e, l.page)} style={{ cursor:"pointer" }}>
                    {l.label} {l.ch && <ChevronDown size={13}/>} </a>
                ) : (
                  // Pure dropdown parent (no target page) — stays a non-anchor,
                  // no bogus href; the submenu opens on hover as before.
                  <div className={"nl"+(page===l.page?" on":"")+""} style={{ cursor:"default" }}>
                    {l.label} {l.ch && <ChevronDown size={13}/>} </div>
                )}
                {l.ch && (
                  <div className="dd">
                    {l.ch.map(c => { const cp = typeof c==="string"?"home":c.page; return (
                      <a key={typeof c==="string"?c:c.label} href={pageToPath(cp)} className="ddi" onClick={(e) => navClick(e, cp)}>{typeof c==="string"?c:c.label}</a>
                    ); })} </div>
                )}
              </div>
            ))}
          </div>

          <div className="nav-r">
            <button className="nbtn nbtn-p" onClick={() => openBooking()}>Book a charter</button>
            {/* Hamburger */}
            <button className="ham" onClick={() => setMobOpen(o => !o)} aria-label="Menu">
              <span style={{ transform: mobOpen ? "rotate(45deg) translate(5px,5px)" : "none" }}/>
              <span style={{ opacity: mobOpen ? 0 : 1 }}/>
              <span style={{ transform: mobOpen ? "rotate(-45deg) translate(5px,-5px)" : "none" }}/>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile menu */}
      {mobOpen && (
        <MobileMenu links={links} go={go} closeMob={closeMob} mobOpen={mobOpen} setMobOpen={setMobOpen} openBooking={openBooking}/>
      )}
    </>
  );
}

/* TOP DESTINATIONS (dynamic) */
function TopDestinations({ go, setPage }) {
  const [dests, setDests]   = useState(DESTS.filter(d => d.name !== "Athens").slice(0, 5));
  const [hasData, setHasData] = useState(false);

  const reload = () => {
    loadDestScores().then(scores => {
      const anyBooked = scores.some(s => s.score > 0);
      if (anyBooked) {
        setHasData(true);
        const scoreMap = {};
        scores.forEach(s => { scoreMap[s.city] = s.score; });
        const ranked = DESTS
          .filter(d => d.name !== "Athens")
          .slice()
          .sort((a, b) => (scoreMap[b.name] || 0) - (scoreMap[a.name] || 0));
        setDests(ranked.slice(0, 5));
      }
    }).catch(() => {});
  };

  useEffect(() => {
    reload();
    const t = setInterval(reload, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, []);

  return (
    <div>
      {hasData && (
        <div style={{ marginBottom:16, display:"flex", alignItems:"center", gap:6, fontSize:12, color:"var(--mu)" }}>
          <div style={{ width:6, height:6, borderRadius:"50%", background:"var(--gr)" }}/>
          Ranked by bookings
        </div>
      )}
      <div className="dg">
        {dests.map((d, i) => (
          <div key={d.name} className="dc" onClick={() => { const slug = (d.name || '').toLowerCase().replace(/\s+/g,"-"); setPage("dest:" + slug); document.getElementById("top")?.scrollIntoView(); }}>
            <div className="dc-in"><Img preset="card" src={d.img} alt={d.name + " — helicopter charter destination"}/></div>
            <div className="dc-ov"/>
            {hasData && i === 0 && (
              <div style={{ position:"absolute", top:0, left:0, right:0, zIndex:4, display:"flex", alignItems:"center", gap:5, padding:"6px 12px", background:"linear-gradient(135deg, #F59E0B, #D97706)", color:"white", fontSize:10, fontWeight:700, letterSpacing:".03em" }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                Most popular
              </div>
            )}
            {hasData && i === 1 && (
              <div style={{ position:"absolute", top:0, left:0, right:0, zIndex:4, display:"flex", alignItems:"center", gap:5, padding:"5px 12px", background:"linear-gradient(135deg, #D97706, #B45309)", color:"white", fontSize:10, fontWeight:700, letterSpacing:".03em" }}>
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                Popular
              </div>
            )}
            {hasData && i === 2 && (
              <div style={{ position:"absolute", top:0, left:0, right:0, zIndex:4, display:"flex", alignItems:"center", gap:5, padding:"5px 12px", background:"linear-gradient(135deg, #92400E, #78350F)", color:"white", fontSize:10, fontWeight:700, letterSpacing:".03em" }}>
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>
                Trending
              </div>
            )}
            <div className="dc-inf">
              <span className="dc-nm">{d.name}</span>
              <div className="dc-meta">
                <span>{d.tag}</span>
                {d.time !== "Hub" && <span className="dc-pill">{destFlightLabel(d)} from Athens</span>} </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* POPULAR ROUTES (dynamic) */
function PopularRoutes({ setPage, openBooking }) {
  const [routes, setRoutes] = useState(SEO_ROUTES.slice(0, 6));
  const [hasData, setHasData] = useState(false);

  useEffect(() => {
    loadRouteScores().then(scores => {
      // If at least one route has been booked, sort by score
      const anyBooked = scores.some(s => s.score > 0);
      if (anyBooked) {
        setHasData(true);
        const scoreMap = {};
        scores.forEach(s => { scoreMap[s.slug] = s.score; });
        // Sort all routes by score, fallback to original index
        const ranked = [...SEO_ROUTES]
          .sort((a, b) => (scoreMap[b.slug] || 0) - (scoreMap[a.slug] || 0));
        setRoutes(ranked.slice(0, 6));
      }
    }).catch(() => {});
    // Refresh every 5 minutes
    const interval = setInterval(() => {
      loadRouteScores().then(scores => {
        const anyBooked = scores.some(s => s.score > 0);
        if (anyBooked) {
          const scoreMap = {};
          scores.forEach(s => { scoreMap[s.slug] = s.score; });
          const ranked = [...SEO_ROUTES]
            .sort((a, b) => (scoreMap[b.slug] || 0) - (scoreMap[a.slug] || 0));
          setRoutes(ranked.slice(0, 6));
        }
      }).catch(() => {});
    }, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div>
      {hasData && (
        <div style={{ marginBottom:16, display:"flex", alignItems:"center", gap:6, fontSize:12, color:"var(--mu)" }}>
          <div style={{ width:6, height:6, borderRadius:"50%", background:"var(--gr)", animation:"pulse 2s infinite" }}/>
          Ranked by bookings
        </div>
      )}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(2,1fr)", gap:14, paddingTop:8 }} className="form-grid-2">
        {routes.map((r, i) => <RouteCard key={r.slug} r={r} i={i} hasData={hasData} setPage={setPage} openBooking={openBooking}/>)}
      </div>
    </div>
  );
}

export function FleetFilterBtn({ id, label, filter, setFilter }) {
  return (
    <button onClick={() => setFilter(id)} style={{ padding:"7px 16px", borderRadius:100, fontSize:13, fontWeight:600, cursor:"pointer", transition:"all .15s", border:"1.5px solid " + (filter===id ? "var(--bl)" : "var(--bd)"), background: filter===id ? "var(--blbg)" : "white", color: filter===id ? "var(--bl)" : "var(--mu)" }}>
      {label}
      <span style={{ marginLeft:5, fontSize:11, opacity:.6 }}>
        ({id==="all" ? visibleMarketingFleet().length : visibleMarketingFleet().filter(f=>f.type===id||f.engine===id).length})
      </span>
    </button>
  );
}

/* HOME FLEET SECTION */
// Marketing aircraft row (hoper-style) shared by the homepage carousel and
// the /fleet grid. Booking-only bits are intentionally absent: no Select, no
// extras, no price (no route context here) - the CTA is "Get a quote". Image
// keeps the exact 3:2 cover pipeline (HeliGallery aspectRatio 3/2 + coverPhotos)
// so the format never changes. `topAcId` shows the most-booked badge overlay.
export function MarketingAircraftRow({ f, setPage, openBooking, topAcId }) {
  return (
    <a href={"/fleet/"+f.id} className="ac-card card-hv" onClick={(e) => { if (e.metaKey||e.ctrlKey||e.shiftKey||e.altKey||e.button!==0) return; e.preventDefault(); setPage("fleet:"+f.id); }} style={{ position:"relative", cursor:"pointer", textDecoration:"none", color:"inherit", display:"block" }}>
      <div className="ac-row-top">
        <div className="ac-row-img">
          {topAcId === f.id && (
            <div style={{ position:"absolute", top:0, left:0, right:0, zIndex:4, display:"flex", alignItems:"center", gap:5, padding:"5px 12px", background:"linear-gradient(135deg, #F59E0B, #D97706)", color:"white", fontSize:10, fontWeight:700, letterSpacing:".03em" }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>Most booked
            </div>
          )}
          <div className="fc-img" style={{ borderBottom:"none" }}>
            <HeliGallery fleet={f} aspectRatio="3/2" coverPhotos chrome="dots-arrows"/>
          </div>
        </div>
        <div className="ac-row-mid">
          <div>
            {/* Class eyebrow (Sanity `type`) above the name; omitted when empty. */}
            {f.type ? <div className="ac-eyebrow">{f.type}</div> : null}
            <div className="fc-name" style={{ fontSize:18, marginBottom:6 }}>{f.name}</div>
            {(() => {
              // Seasonal base: show the base in effect on the SSR-render date (currentBase,
              // resolved via the SHARED resolver from s.serverDate — the SAME base the
              // pricing engine positions from), NOT the year-round homeBase. Falls back to
              // homeBase if currentBase is absent. Display-only.
              const bo = f.currentBase || resolveBaseForDate(f, null);
              const city = bo && (bo.city || bo.name || bo.id);
              return city ? (
              <span style={{ display:"inline-flex", alignItems:"center", gap:4, fontSize:12, color:"var(--mu)" }}><MapPin size={11}/> Based in {city}</span>
            ) : null; })()}
          </div>
          {/* Full stat row distributed across the card width — units always present
             (never a bare number); count-nouns (Seats/Bags) bare with the noun in
             the label. "Hold" = luggage-compartment volume (Sanity compartmentVol,
             m³) — a real per-aircraft spec, complementary to the Bags count and
             already surfaced on the aircraft detail page. Omitted where absent. */}
          <div className="ac-chips ac-chips-roomy">
            {[
              ["Seats", f.seats],
              ["Speed", withSpeedUnit(f.speed)],
              f.range ? ["Range", withRangeUnit(f.range)] : null,
              f.maxPayload ? ["Payload", f.maxPayload + " kg"] : null,
              f.maxLuggage != null ? ["Bags", <BagsLine>{f.maxLuggage}</BagsLine>] : null,
              f.compartmentVol ? ["Hold", (+f.compartmentVol.toFixed(2)) + " m³"] : null,
            ].filter(Boolean).map(([cap, val]) => (
              <div key={cap} className="ac-chip"><span className="ac-chip-v">{val}</span><span className="ac-chip-c">{cap}</span></div>
            ))}
          </div>
          {f.tags && f.tags.length > 0 && (
            <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
              {f.tags.map(t => <span key={t} className="tag tag-bl">{t}</span>)}
            </div>
          )}
        </div>
        <div className="ac-row-right">
          <div className="fc-p" style={{ color:"var(--bl)", fontSize:16 }}>Full aircraft charter</div>
          <button className="btn btn-bl btn-sm" style={{ width:"100%", justifyContent:"center" }} onClick={e => { e.stopPropagation(); openBooking({ aircraft:f }); }}>Get a quote</button>
          <button type="button" className="ac-row-expand" onClick={e => { e.stopPropagation(); setPage("fleet:"+f.id); }}>View aircraft <ArrowRight size={11}/></button>
        </div>
      </div>
    </a>
  );
}

function HomeFleetSection({ setPage, openBooking }) {
  const [filter, setFilter] = useState("all");
  const [topAcId, setTopAcId] = useState(null);

  useEffect(() => {
    _loadPopularity().then(pop => {
      const ac = pop.aircraft || [];
      // honest threshold: the "Most booked" star needs >=2 REAL captured
      // bookings (count missing on stale cached responses => no badge)
      if (ac.length > 0 && ac[0].score > 0 && (ac[0].count || 0) >= 2) setTopAcId(ac[0].id);
    }).catch(() => {});
  }, []);

  // Marketing-display visibility (opt-out): hide hideFromFleetPage aircraft from the
  // homepage fleet carousel ONLY. FLEET stays complete (funnel/detail unaffected).
  const _visibleFleet = FLEET.filter(f => f.hideFromFleetPage !== true);
  const shown = (filter === "all" ? _visibleFleet : _visibleFleet.filter(f => f.engine === filter)).slice().sort((a, b) => (a.id === topAcId ? -1 : b.id === topAcId ? 1 : 0));

  // Carousel state
  const [idx, setIdx]      = useState(0);
  const [isMob, setIsMob]  = useState(false);
  const trackRef           = useRef(null);
  const touchDivRef        = useRef(null);
  const startX             = useRef(null);
  const startY             = useRef(null);
  const lockAxis           = useRef(null);

  // Native touch listeners on the carousel container (passive:false for preventDefault)
  useEffect(() => {
    const el = touchDivRef.current;
    if (!el) return;
    el.addEventListener("touchstart", onTouchStart, { passive:true });
    el.addEventListener("touchmove",  onTouchMove,  { passive:false });
    el.addEventListener("touchend",   onTouchEnd,   { passive:true });
    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove",  onTouchMove);
      el.removeEventListener("touchend",   onTouchEnd);
    };
  }); // "h" | "v" | null

  useEffect(() => {
    const check = () => setIsMob(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // Reset index when filter changes
  useEffect(() => { setIdx(0); }, [filter]);

  const total = shown.length;
  const canPrev = idx > 0;
  const canNext = idx < total - 1;

  const onTouchStart = e => {
    startX.current = e.touches[0].clientX;
    startY.current = e.touches[0].clientY;
    lockAxis.current = null;
  };
  const onTouchMove = e => {
    if (startX.current === null) return;
    const dx = e.touches[0].clientX - startX.current;
    const dy = e.touches[0].clientY - startY.current;
    // Determine axis on first significant move
    if (!lockAxis.current) {
      if (Math.abs(dx) > Math.abs(dy) + 5) lockAxis.current = "h";
      else if (Math.abs(dy) > Math.abs(dx) + 5) lockAxis.current = "v";
    }
    // Only block browser scroll when swiping horizontally
    if (lockAxis.current === "h") e.preventDefault();
  };
  const onTouchEnd   = e => {
    if (startX.current === null || lockAxis.current !== "h") { startX.current = null; lockAxis.current = null; return; }
    const dx = e.changedTouches[0].clientX - startX.current;
    if (dx < -40 && canNext) setIdx(i => i + 1);
    if (dx >  40 && canPrev) setIdx(i => i - 1);
    startX.current = null;
    startY.current = null;
    lockAxis.current = null;
  };

  return (
    <div>
      <div style={{ display:"flex", gap:8, marginBottom:24, flexWrap:"wrap" }}>
        <FleetFilterBtn id="all"    label="All"    filter={filter} setFilter={setFilter}/>
        <FleetFilterBtn id="single" label="Single-engine" filter={filter} setFilter={setFilter}/>
        <FleetFilterBtn id="twin"   label="Twin-engine"   filter={filter} setFilter={setFilter}/>
      </div>

      {isMob ? (
        <div>
        <div style={{ overflow:"hidden" }} ref={touchDivRef}>
            <div ref={trackRef} style={{ display:"flex", transform:"translateX(" + (-idx * 100) + "%)", transition:"transform .3s cubic-bezier(.32,0,.15,1)" }}>
              {shown.map(f => (
                <div key={f.id} style={{ minWidth:"100%", paddingRight:0 }}>
                  <MarketingAircraftRow f={f} setPage={setPage} openBooking={openBooking} topAcId={topAcId}/>
                </div>
              ))}
            </div>
          </div>
          {/* Nav arrows + dots */}
          <div style={{ display:"flex", justifyContent:"center", alignItems:"center", gap:12, marginTop:16 }}>
            <button onClick={() => setIdx(i => Math.max(0, i-1))} disabled={!canPrev} aria-label="Previous aircraft" style={{ width:36, height:36, borderRadius:"50%", border:"1.5px solid var(--bd)", background:"white", cursor:canPrev?"pointer":"default", opacity:canPrev?1:.3, display:"flex", alignItems:"center", justifyContent:"center" }}>
              <ChevronDown size={16} style={{ transform:"rotate(90deg)" }}/>
            </button>
            <div style={{ display:"flex", gap:6 }}>
              {shown.map((_, i) => (
                <div key={i} onClick={() => setIdx(i)} style={{ width: i===idx ? 20 : 6, height:6, borderRadius:3, background: i===idx ? "var(--bl)" : "var(--bd)", cursor:"pointer", transition:"all .25s" }}/>
              ))}
            </div>
            <button onClick={() => setIdx(i => Math.min(total-1, i+1))} disabled={!canNext} aria-label="Next aircraft" style={{ width:36, height:36, borderRadius:"50%", border:"1.5px solid var(--bd)", background:"white", cursor:canNext?"pointer":"default", opacity:canNext?1:.3, display:"flex", alignItems:"center", justifyContent:"center" }}>
              <ChevronDown size={16} style={{ transform:"rotate(-90deg)" }}/>
            </button>
          </div>
        </div>
      ) : (
        <div style={{ position:"relative" }}>
          <div style={{ overflow:"hidden" }}>
            <div style={{ display:"flex", transition:"transform .35s cubic-bezier(.32,0,.15,1)", transform:"translateX(" + (-idx * 100) + "%)" }}>
              {shown.map((f, i) => (
                <div key={f.id} style={{ minWidth:"100%", flexShrink:0 }}>
                  <MarketingAircraftRow f={f} setPage={setPage} openBooking={openBooking} topAcId={topAcId}/>
                </div>
              ))}
            </div>
          </div>
          {total > 1 && (
            <div style={{ display:"flex", justifyContent:"center", alignItems:"center", gap:12, marginTop:20 }}>
              <button onClick={() => setIdx(i => Math.max(0, i-1))} disabled={idx===0} aria-label="Previous aircraft" style={{ width:40, height:40, borderRadius:"50%", border:"1.5px solid var(--bd)", background:"white", cursor:idx>0?"pointer":"default", opacity:idx>0?1:.3, display:"flex", alignItems:"center", justifyContent:"center" }}>
                <ChevronDown size={16} style={{ transform:"rotate(90deg)" }}/>
              </button>
              <div style={{ display:"flex", gap:6 }}>
                {Array.from({ length: total }, (_, i) => (
                  <div key={i} onClick={() => setIdx(i)} style={{ width: i===idx ? 20 : 6, height:6, borderRadius:3, background: i===idx ? "var(--bl)" : "var(--bd)", cursor:"pointer", transition:"all .25s" }}/>
                ))}
              </div>
              <button onClick={() => setIdx(i => Math.min(total-1, i+1))} disabled={idx>=total-1} aria-label="Next aircraft" style={{ width:40, height:40, borderRadius:"50%", border:"1.5px solid var(--bd)", background:"white", cursor:idx<total-1?"pointer":"default", opacity:idx<total-1?1:.3, display:"flex", alignItems:"center", justifyContent:"center" }}>
                <ChevronDown size={16} style={{ transform:"rotate(-90deg)" }}/>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* HOME */
// Destination image lookup: CMS → IMG fallback → generic. Thin wrapper over
// the shared pure resolver — binds App's runtime DESTS (and shared IMG).
function destImg(name) {
  return _destImg(name, DESTS, IMG);
}

// Resolve the TARGET destination for a route so its image (card) / heroImage (hero)
// can drive the route card + hero instead of a stock placeholder. from/to are free
// text + slug "athens-<dest>", with NO reference — match by slug-endteil first, then
// by name (case-insensitive); always the destination (to), never the origin (from).
// Returns null when no destination matches; callers fall back to the prior behaviour.
// Thin wrapper over the shared pure resolver — binds App's runtime DESTS.
function destForRoute(route) {
  return _destForRoute(route, DESTS);
}

// Shared split-image for route surfaces — origin (from) on the left, destination
// (to) on the right, plane icon in the middle, bottom gradient + city labels. ONE
// source for the route CARD (variant="card", 160px) and the route HERO
// (variant="hero", fills its container). Image sources are IDENTICAL for both:
// origin = destImg(from); destination = destForRoute(to).img → route stock → island
// fallback (display-only; same resolution the card already used). Hero-specific:
// taller + larger icon/labels only; the hero keeps its own dark gradient + title
// overlay on top (rendered by the caller).
export function RouteSplitImage({ r, variant = "card" }) {
  const hero = variant === "hero";
  const toSrc = destForRoute(r)?.img || r.cardImg || destImg(r.to);
  const iconBox = hero ? 46 : 32;
  const iconSvg = hero ? 20 : 14;
  const labelSize = hero ? 14 : 12;
  return (
    <div style={{ height: hero ? "100%" : 160, position:"relative", overflow:"hidden", display:"flex" }}>
      <div style={{ flex:1, overflow:"hidden" }}><Img preset={hero ? "hero" : "card"} sizes={hero ? "50vw" : undefined} src={destImg(r.from)} alt={r.from + " — helicopter departure point from the air"} style={{ width:"100%", height:"100%", objectFit:"cover" }}/></div>
      <div style={{ flex:1, overflow:"hidden" }}><Img preset={hero ? "hero" : "card"} sizes={hero ? "50vw" : undefined} fetchpriority={hero ? "low" : undefined} src={toSrc} alt={r.to + " — helicopter arrival from the air"} style={{ width:"100%", height:"100%", objectFit:"cover" }}/></div>
      {!hero && (
      <div style={{ position:"absolute", top:"50%", left:"50%", transform:"translate(-50%,-50%)", width:iconBox, height:iconBox, borderRadius:"50%", background:"white", display:"flex", alignItems:"center", justifyContent:"center", boxShadow:"0 2px 8px rgba(0,0,0,.2)", zIndex:3 }}>
        <svg width={iconSvg} height={iconSvg} viewBox="0 0 24 24" fill="none" stroke="var(--bl)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.8 19.2L16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.4-.1.9.3 1.1L11 12l-2 3H6l-1 1 3 2 2 3 1-1v-3l3-2 3.7 7.3c.3.4.7.5 1.1.3l.5-.3c.4-.2.6-.6.5-1.1z"/></svg>
      </div>
      )}
      <div style={{ position:"absolute", inset:0, background:"linear-gradient(to top,rgba(0,0,0,.5) 0%,transparent 40%)", pointerEvents:"none" }}/>
      <div style={{ position:"absolute", bottom: hero ? 18 : 10, left:0, right:0, display:"flex", justifyContent:"space-between", padding: hero ? "0 24px" : "0 14px", color:"white", pointerEvents:"none", zIndex:2 }}>
        <span style={{ fontSize:labelSize, fontWeight:700, opacity:.9, textShadow:"0 1px 4px rgba(0,0,0,.6)" }}>{r.from}</span>
        <span style={{ fontSize:labelSize, fontWeight:700, textShadow:"0 1px 4px rgba(0,0,0,.6)" }}>{r.to}</span>
      </div>
    </div>
  );
}

// Helicopter thumbnail: CMS image or SVG fallback
export function HeliThumb({ src, alt, h = 48 }) {
  if (src) return <div style={{ height:h, display:"flex", alignItems:"center" }}><Img preset="thumbnail" src={src} alt={alt ? alt + " helicopter side view" : ""} style={{ height:"100%", maxWidth:"100%", objectFit:"contain" }}/></div>;
  return (
    <div style={{ height:h, display:"flex", alignItems:"center", opacity:0.18 }}>
      <svg viewBox="0 0 120 40" style={{ height:"100%" }} fill="var(--ink)"><path d="M12 28h22l6-8h28l4-4h16l2-2h12v2h-10l-2 2H76l-4 4H44l-6 8H12zm38-14h30v2H50zm-6 4h8v2h-8z"/><circle cx="20" cy="30" r="3"/><circle cx="60" cy="30" r="3"/><path d="M40 10h2v8h-2z"/><path d="M28 10h26v2H28z"/></svg>
    </div>
  );
}

// Key-facts grid — shared renderer for the SYSTEM-truth key facts
// (system-key-facts.js mergeKeyFacts output: { label, items[] }). Multi-value
// facts (e.g. several arrival pads) render as a compact bullet list inside the
// existing card look; single values keep the one-line style. Used by the
// route/dest/hotel SEO pages (2026-07-08).
export function KeyFactsGrid({ facts }) {
  if (!Array.isArray(facts) || !facts.length) return null;
  return (
    <div style={{ marginBottom:48 }}>
      <h2 style={{ fontWeight:800, fontSize:20, letterSpacing:"-.04em", marginBottom:20 }}>Key facts</h2>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(180px,1fr))", gap:10 }}>
        {facts.map((k, i) => (
          <div key={i} className="card" style={{ padding:"14px 16px" }}>
            <div style={{ fontSize:11, fontWeight:700, letterSpacing:".06em", textTransform:"uppercase", color:"var(--mu)", marginBottom:6 }}>{k.label}</div>
            {k.items.length > 1
              ? <ul style={{ margin:0, paddingLeft:16, fontSize:14, fontWeight:700, color:"var(--ink)", letterSpacing:"-.02em", lineHeight:1.5 }}>
                  {k.items.map((it, j) => <li key={j}>{it}</li>)}
                </ul>
              : <div style={{ fontSize:15, fontWeight:700, color:"var(--ink)", letterSpacing:"-.02em" }}>{k.items[0]}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

// "What our clients say" — STRICTLY CMS-gated social proof (2026-07-08):
// renders ONLY when >=1 PUBLISHED review exists for the entity (shared
// matching rule publishedReviewsFor — same list that activates the schema
// rating). 0 reviews -> null: no placeholder, no empty header, no markup.
// 2026-08-15, additive: `limit` (route/dest pages keep their 6-card cap, the
// homepage passes null to show all) and `gallery` (horizontal scroll-snap).
// Route + helicopter lines render whenever the review carries them, on every
// surface — a review that has them is not more or less true on a route page.
export function ReviewsSection({ reviews, limit = 6, gallery = false, headingClassName }) {
  if (!Array.isArray(reviews) || !reviews.length) return null;
  const stars = (n) => "★".repeat(Math.max(1, Math.min(5, Math.round(n)))) + "☆".repeat(5 - Math.max(1, Math.min(5, Math.round(n))));
  const shown = limit == null ? reviews : reviews.slice(0, limit);
  // GALLERY THRESHOLD — 3. Below that the auto-fill grid already fills its row
  // on every breakpoint, so a scroll container would show a scrollbar with
  // nothing to scroll: the "broken carousel" look the brief rules out.
  const isGallery = gallery && shown.length >= 3;
  return (
    <div style={{ marginBottom:48 }}>
      <h2 className={headingClassName} style={headingClassName ? { marginBottom:20 } : { fontWeight:800, fontSize:20, letterSpacing:"-.04em", marginBottom:20 }}>What our clients say</h2>
      <div
        className={isGallery ? "rev-gallery" : undefined}
        style={isGallery ? undefined : { display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))", gap:12 }}
        {...(isGallery ? { tabIndex:0, role:"region", "aria-label":"Customer reviews, scroll horizontally" } : {})}
      >
        {shown.map((r, i) => (
          <div key={r._id || i} className={"card" + (isGallery ? " rev-card" : "")} style={{ padding:"16px 18px" }}>
            <div style={{ color:"#E8B54D", fontSize:14, letterSpacing:2, marginBottom:8 }} aria-label={r.rating + " out of 5 stars"}>{stars(r.rating)}</div>
            {r.text && <p className="body-p" style={{ marginBottom:10 }}>{r.text}</p>}
            <div style={{ fontSize:12.5, fontWeight:700, color:"var(--ink)" }}>{r.author}</div>
            {r.routeLabel && <div style={{ fontSize:11.5, color:"var(--mu2)", marginTop:2 }}>{r.routeLabel}</div>}
            {r.aircraftLabel && <div style={{ fontSize:11.5, color:"var(--mu2)", marginTop:2 }}>{r.aircraftLabel}</div>}
            {r.date && <div style={{ fontSize:11.5, color:"var(--mu2)", marginTop:2 }}>{String(r.date).slice(0, 10)}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

// "Featured in / Trusted by" strip — CMS-fed (siteSettings.featuredIn:
// name + optional logo + url). Renders NOTHING while the array is empty
// (today: 0 entries) — no fake logos, ever (content wave 2026-07-08).
export function FeaturedInStrip() {
  const entries = (__CMS?.SETTINGS?.featuredIn || []).filter(f => f && f.name);
  if (!entries.length) return null;
  return (
    <section className="sec" style={{ padding:"36px 0", background:"white", borderTop:"1px solid var(--bd)" }}>
      <div className="wrap">
        <div className="eyebrow" style={{ textAlign:"center", marginBottom:18 }}>Featured in</div>
        <div style={{ display:"flex", gap:36, alignItems:"center", justifyContent:"center", flexWrap:"wrap" }}>
          {entries.map((f, i) => {
            const inner = f.logoUrl
              ? <img src={f.logoUrl} alt={f.name} loading="lazy" style={{ height:28, width:"auto", objectFit:"contain", opacity:.75 }}/>
              : <span style={{ fontSize:14, fontWeight:700, color:"var(--mu)" }}>{f.name}</span>;
            return f.url
              ? <a key={f._key || i} href={f.url} rel="noopener" target="_blank" aria-label={f.name}>{inner}</a>
              : <span key={f._key || i}>{inner}</span>;
          })}
        </div>
      </div>
    </section>
  );
}

// Reusable route card for subpages (destinations, hotels, routes)
export function SubRouteCard({ r, setPage, openBooking, extra, compact }) {
  const minPrice = routeFromPriceMin(r.cardFromPrice);
  const flMin = routeFlightMin(r);
  return (
    <div className="card card-hv" style={{ padding: compact ? "10px 14px" : "12px 16px", cursor:"pointer", display:"flex", alignItems:"center", gap:12 }} onClick={() => setPage ? setPage("route:"+r.slug) : openBooking?.({ from:r.from, to:r.to })}>
      <div style={{ display:"flex", alignItems:"center", flexShrink:0 }}>
        <div style={{ width:36, height:36, borderRadius:"50%", overflow:"hidden", border:"2px solid white", boxShadow:"0 0 0 1px var(--bd)", zIndex:1 }}>
          <Img preset="avatar" src={destImg(r.from)} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }}/>
        </div>
        <div style={{ width:36, height:36, borderRadius:"50%", overflow:"hidden", border:"2px solid white", boxShadow:"0 0 0 1px var(--bd)", marginLeft:-10 }}>
          <Img preset="avatar" src={destImg(r.to)} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }}/>
        </div>
      </div>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ display:"flex", alignItems:"center", gap:4 }}>
          <span style={{ fontWeight:700, fontSize:13 }}>{r.from}</span>
          <svg width="12" height="12" viewBox="0 0 16 16" style={{ flexShrink:0 }}><path d="M3 8h10M9 4l4 4-4 4" stroke="var(--mu)" strokeWidth="1.3" fill="none" strokeLinecap="round" strokeLinejoin="round"/></svg>
          <span style={{ fontWeight:700, fontSize:13 }}>{r.to}</span>
        </div>
        <div style={{ fontSize:11, color:"var(--mu)", marginTop:1 }}>{fmtFlightShort(flMin)}{" \u00b7 "+routeCardFromLabel(minPrice)}{extra || ""}</div>
      </div>
      <div style={{ flexShrink:0, padding:"5px 12px", borderRadius:100, background:"var(--blbg)", color:"var(--bl)", fontSize:11, fontWeight:600, display:"flex", alignItems:"center", gap:3 }} onClick={e => { e.stopPropagation(); openBooking?.({ from:r.from, to:r.to }); }}>
        Book <ArrowRight size={10}/>
      </div>
    </div>
  );
}

function RouteCard({ r, i, hasData, setPage, openBooking }) {
  const minPrice = routeFromPriceMin(r.cardFromPrice);
  const rank = hasData && i <= 2;
  const rankColor = i===0?"#F59E0B":i===1?"#D97706":"#92400E";
  const rankLabel = i===0?"#1 Most popular":i===1?"#2 Popular":"#3 Trending";
  return (
    <div className="card card-hv" style={{ padding:"14px 16px", cursor:"pointer", display:"flex", alignItems:"center", gap:12, borderLeft: rank ? "3px solid "+rankColor : undefined }} onClick={() => setPage("route:"+r.slug)}>
      {/* City images */}
      <div style={{ display:"flex", alignItems:"center", flexShrink:0 }}>
        <div style={{ width:40, height:40, borderRadius:"50%", overflow:"hidden", border:"2px solid white", boxShadow:"0 0 0 1px var(--bd)", zIndex:1 }}>
          <Img preset="avatar" src={destImg(r.from)} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }}/>
        </div>
        <div style={{ width:40, height:40, borderRadius:"50%", overflow:"hidden", border:"2px solid white", boxShadow:"0 0 0 1px var(--bd)", marginLeft:-12 }}>
          <Img preset="avatar" src={destImg(r.to)} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }}/>
        </div>
      </div>
      {/* Route info */}
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ display:"flex", alignItems:"center", gap:5 }}>
          <span style={{ fontWeight:700, fontSize:14 }}>{r.from}</span>
          <svg width="12" height="12" viewBox="0 0 16 16" style={{ flexShrink:0 }}><path d="M3 8h10M9 4l4 4-4 4" stroke="var(--mu)" strokeWidth="1.3" fill="none" strokeLinecap="round" strokeLinejoin="round"/></svg>
          <span style={{ fontWeight:700, fontSize:14 }}>{r.to}</span>
        </div>
        <div style={{ fontSize:12, color:"var(--mu)", marginTop:2, display:"flex", alignItems:"center", gap:6, flexWrap:"wrap" }}>
          <span>{fmtFlightShort(routeFlightMin(r))}{" \u00b7 "+routeCardFromLabel(minPrice)}</span>
          {rank && <span style={{ display:"inline-flex", alignItems:"center", gap:3, fontSize:10, fontWeight:700, color:"white", background:rankColor, padding:"2px 8px", borderRadius:100 }}><svg width="8" height="8" viewBox="0 0 24 24" fill="white" stroke="none"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>{rankLabel}</span>}
        </div>
      </div>
      {/* Book CTA */}
      <div style={{ flexShrink:0, padding:"6px 14px", borderRadius:100, background:"var(--blbg)", color:"var(--bl)", fontSize:12, fontWeight:600, display:"flex", alignItems:"center", gap:4 }} onClick={e => { e.stopPropagation(); openBooking({ from:r.from, to:r.to }); }}>
        Book <ArrowRight size={11}/>
      </div>
    </div>
  );
}


/* NEWSLETTER FORM */
function NewsletterForm({ source }) {
  const [email, setEmail]   = useState("");
  const [hp, setHp]         = useState(""); // honeypot — bots auto-fill
  const [status, setStatus] = useState("idle"); // idle | sending | success | duplicate | error
  const [error, setError]   = useState("");

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const subscribe = async () => {
    // Silent honeypot: bots fill the hidden field. Fake a "success" state so
    // automated systems can't tell the submission was blocked.
    if (hp) { setStatus("success"); return; }
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) { setError("Please enter your email"); return; }
    if (!EMAIL_RE.test(trimmed)) { setError("Please enter a valid email"); return; }
    setError("");
    setStatus("sending");

    try {
      // In production: POST /api/newsletter/subscribe
      // Stores subscriber in Sanity + sends welcome email via Resend.
      // Source: explicit prop (e.g. "homepage-hero" / "footer") wins over
      // pathname fallback for landing-page segmentation.
      const sourceValue = source || (typeof window !== "undefined" ? window.location.pathname : "homepage");
      const res = await fetch("/api/newsletter/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: trimmed, source: sourceValue }),
      });

      if (res.ok) {
        const data = await res.json();
        setStatus(data.duplicate ? "duplicate" : "success");
        if (!data.duplicate) {
          track("newsletter_signup", { source: typeof window !== "undefined" ? window.location.pathname : "/" });
        }
      } else {
        // Non-2xx — surface honestly so the user can retry instead of
        // thinking they subscribed when the server rejected it.
        setStatus("error");
        setError("Subscription failed. Please try again in a moment.");
      }
    } catch (e) {
      setStatus("error");
      setError("Could not reach the server. Please check your connection and try again.");
    }
  };

  if (status === "success") {
    return (
      <div style={{ textAlign:"center", padding:"8px 0", animation:"fadeIn .4s ease" }}>
        <div style={{ width:44, height:44, borderRadius:"50%", background:"rgba(16,185,129,.15)", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 12px" }}>
          <Check size={22} color="#10B981"/>
        </div>
        <div style={{ fontWeight:700, fontSize:15, color:"white", marginBottom:4 }}>You're subscribed!</div>
        <div style={{ fontSize:13, color:"rgba(255,255,255,.45)" }}>You will receive new routes, seasonal packages, and travel guides.</div>
      </div>
    );
  }

  if (status === "duplicate") {
    return (
      <div style={{ textAlign:"center", padding:"8px 0", animation:"fadeIn .4s ease" }}>
        <div style={{ fontWeight:700, fontSize:15, color:"white", marginBottom:4 }}>Already subscribed!</div>
        <div style={{ fontSize:13, color:"rgba(255,255,255,.45)", marginBottom:12 }}>This email is already on our list. Check your inbox for previous offers.</div>
        <button className="btn btn-sm" style={{ background:"rgba(255,255,255,.1)", color:"white", border:"1px solid rgba(255,255,255,.15)" }} onClick={() => { setStatus("idle"); setEmail(""); }}>
          Try a different email
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="nl-row">
        <input
          className="nl-in"
          placeholder="Your email address"
          type="email"
          value={email}
          onChange={e => { setEmail(e.target.value); setError(""); }}
          onKeyDown={e => { if (e.key === "Enter") subscribe(); }}
          style={{ borderColor: error ? "rgba(239,68,68,.5)" : undefined }}
        />
        <button
          className="btn btn-bl"
          onClick={subscribe}
          style={{ opacity: status === "sending" ? 0.7 : 1, cursor: status === "sending" ? "wait" : "pointer", minWidth:110 }}
        >
          {status === "sending"
            ? <div style={{ width:16, height:16, border:"2px solid white", borderTopColor:"transparent", borderRadius:"50%", animation:"spin .7s linear infinite" }}/>
            : "Subscribe"}
        </button>
      </div>
      {/* Honeypot — hidden from humans, bots auto-fill */}
      <div style={{ display:"none" }} aria-hidden="true" tabIndex={-1}>
        <label>Leave empty</label>
        <input type="text" name="website" tabIndex={-1} autoComplete="off" value={hp} onChange={e => setHp(e.target.value)}/>
      </div>
      {error
        ? <div style={{ fontSize:11, color:"#EF4444", marginTop:8 }}>{error}</div>
        : <div style={{ fontSize:11, color:"rgba(255,255,255,.6)", marginTop:8 }}>No spam. Unsubscribe any time.</div>
      }
    </div>
  );
}


function HeroDestPills({ setPage }) {
  const fallback = DESTS.filter(d => d.name !== "Athens" && d.time !== "Hub").slice(0, 5).map(d => d.name);
  const [dests, setDests] = useState(fallback.length ? fallback : ["Mykonos","Santorini","Paros","Corfu","Rhodes"]);
  useEffect(() => {
    loadDestScores().then(scores => {
      const ranked = [...scores].sort((a, b) => b.score - a.score).slice(0, 5).map(s => s.city);
      if (ranked.length >= 3) setDests(ranked);
    }).catch(() => {});
  }, []);
  if (!dests.length) return null;
  return (
    <div className="dest-pills hero-anim hero-anim-d4" style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:0 }}>

      {dests.map(d => (
        <button key={d} onClick={() => { setPage("dest:" + (d || '').toLowerCase().replace(/\s+/g, "-")); document.getElementById("top")?.scrollIntoView(); }} style={{ padding:"6px 14px", borderRadius:100, fontSize:12.5, fontWeight:600, border:"1px solid rgba(255,255,255,.15)", background:"rgba(255,255,255,.06)", color:"rgba(255,255,255,.75)", cursor:"pointer", backdropFilter:"blur(6px)", transition:"all .15s" }} className="dest-pill-btn">
          {d}
        </button>
      ))}
    </div>
  );
}

function Home({ setPage, setData, data, openBooking, onSearch, swReset }) {
  const go = d => { if (onSearch) { onSearch(d); } else { setData(prev => ({ ...d, paxData: prev.paxData })); setPage((d.tripType === "customroute" || d.forceCustomQuote) ? "booking-custom-quote" : "helipads"); } };
  return (
    <>
      {/* Hero */}
      <section className="hero">
        <div className="hero-bg"><Img preset="hero" src={HP.heroImage || IMG.hero} alt="Helicopter over Greek islands"/></div>
        <div className="hero-c">
          <div className="wrap">
            <div className="hg" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:56, alignItems:"center" }}>
              <div>
                {/* Trust badge */}
                <div className="hero-anim hero-anim-d1" style={{ display:"inline-flex", flexWrap:"wrap", alignItems:"center", gap:8, background:"rgba(255,255,255,.08)", backdropFilter:"blur(12px)", WebkitBackdropFilter:"blur(12px)", color:"white", padding:"5px 16px 5px 6px", borderRadius:100, fontSize:11.5, fontWeight:600, marginBottom:28, border:"1px solid rgba(255,255,255,.1)" }}>
                  <span style={{ background:"var(--bl)", color:"white", padding:"3px 10px", borderRadius:100, fontSize:10, letterSpacing:".04em" }}>CHARTER BROKER</span>
                  HCAA-licensed operators &middot; EASA-compliant fleet
                </div>

                {/* Headline */}
                <h2 className="hero-anim hero-anim-d2" style={{ fontFamily:"var(--sans)", fontSize:"clamp(34px,4.8vw,60px)", fontWeight:800, letterSpacing:"-.05em", lineHeight:1.0, color:"white", marginBottom:20 }}>
                  {HP.heroTitle}<br/>
                  <span style={{ fontFamily:"var(--serif)", fontStyle:"italic", fontWeight:400, color:"#90C0F8" }}>{HP.heroItalic}</span>
                </h2>

                {/* Subline */}
                <p className="hero-anim hero-anim-d3" style={{ fontSize:"clamp(14px,1.8vw,17px)", color:"rgba(255,255,255,.65)", maxWidth:440, lineHeight:1.8, marginBottom:28 }}>
                  {HP.heroSub}
                </p>

                {/* Primary + Secondary CTA */}
                <div className="hero-cta-row hero-anim hero-anim-d3">
                  <button className="btn btn-bl btn-lg" onClick={() => openBooking()} style={{ fontSize:15 }}>
                    {HP.heroCta} <ArrowRight size={16}/>
                  </button>
                  <button className="btn btn-lg" style={{ background:"rgba(255,255,255,.1)", color:"white", border:"1.5px solid rgba(255,255,255,.18)", backdropFilter:"blur(6px)" }} onClick={() => { setPage("how-it-works"); }}>
                    How it works
                  </button>
                </div>

                {/* Quick destination pills — ranked by bookings */}
                <HeroDestPills setPage={setPage}/>

                {/* Inline trust indicators */}
                <div className="hero-trust-row hero-anim hero-anim-d5">
                  <div className="hero-trust-item"><Shield size={13}/> HCAA-licensed operators</div>
                  <div className="hero-trust-item"><Check size={13}/> No shared seats</div>
                  <div className="hero-trust-item"><Zap size={13}/> Fast response</div>
                  <div className="hero-trust-item"><CreditCard size={13}/> Fixed pricing</div>
                </div>
              </div>

              {/* Search Widget */}
              <div className="hero-anim hero-anim-d4">
                <SearchWidget key={swReset} onSearch={go} init={{}} onMobileTap={openBooking}/>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div style={{ position:"absolute", bottom:24, left:"50%", transform:"translateX(-50%)", zIndex:3, display:"flex", flexDirection:"column", alignItems:"center", gap:6, opacity:.4 }}>
          <div style={{ fontSize:10, color:"white", letterSpacing:".1em", textTransform:"uppercase" }}>Scroll</div>
          <div style={{ width:1, height:24, background:"linear-gradient(to bottom, white, transparent)" }}/>
        </div>
      </section>

      {/* Services */}
      <FadeIn>
      <section className="sec">
        <div className="wrap">
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-end", marginBottom:36 }}>
            <div><div className="eyebrow">What we offer</div><h2 className="h2">Every kind of private charter, <em>arranged.</em></h2></div>
          </div>
          <div className="sg">
            {[
              { slug:"charter-island",      icon:<Plane size={18} color="white"/>,  fallback:SVCS[0] },
              { slug:"charter-yacht",       icon:<Anchor size={18} color="white"/>, fallback:SVCS[1] },
              { slug:"charter-sightseeing", icon:<Camera size={18} color="white"/>, fallback:SVCS[2] },
              { slug:"charter-vip",         icon:<Star size={18} color="white"/>,   fallback:SVCS[3] },
              { slug:"charter-airport",     icon:<Wind size={18} color="white"/>,   fallback:SVCS[4] },
              { slug:"charter-custom",      icon:<Zap size={18} color="white"/>,    fallback:SVCS[5] },
            ].map(s => {
              const cms = CHARTER_SERVICES?.[s.slug];
              return { title: cms?.title || s.fallback?.title || s.slug, sub: cms?.subtitle || s.fallback?.sub || '', img: cms?.hero || s.fallback?.img, icon: s.icon, page: s.slug };
            }).map(s => (
              <div key={s.title} className="sc" onClick={() => setPage(s.page)}>
                <Img preset="inline" src={s.img} alt={s.title}/>
                <div className="sc-ov"/>
                <div className="sc-c">
                  <div className="sc-icon">{s.icon}</div>
                  <div className="sc-title">{s.title}</div>
                  <div className="sc-sub">{s.sub}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      </FadeIn>

      {/* Destinations */}
      <FadeIn>
      <section className="sec" style={{ background:"var(--off)", paddingTop:72, paddingBottom:72 }}>
        <div className="wrap">
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-end", marginBottom:36 }}>
            <div><div className="eyebrow">Where we fly</div><h2 className="h2">Top destinations</h2></div>
            <button className="btn btn-ol btn-sm" onClick={() => setPage("destinations")}>All destinations <ArrowRight size={13}/></button>
          </div>
          <TopDestinations go={go} setPage={setPage}/>
        </div>
      </section>
      </FadeIn>

      {/* Fleet preview */}
      <FadeIn>
      <section className="sec">
        <div className="wrap">
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-end", marginBottom:24 }}>
            <div><div className="eyebrow">Our aircraft</div><h2 className="h2">{visibleMarketingFleet().length} helicopters, <em>every need covered.</em></h2></div>
            <button className="btn btn-ol btn-sm" onClick={() => setPage("fleet")}>Full fleet <ArrowRight size={13}/></button>
          </div>
          <HomeFleetSection setPage={setPage} openBooking={openBooking}/>
        </div>
      </section>
      </FadeIn>

      {/* Mid-page CTA */}
      <section style={{ background:"var(--bl)", padding:"44px 0" }}>
        <div className="wrap" style={{ display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:20 }}>
          <div>
            <h2 style={{ fontFamily:"var(--sans)", fontWeight:800, fontSize:"clamp(20px,3vw,26px)", letterSpacing:"-.04em", color:"white", lineHeight:1.2, marginBottom:6 }}>
              Know where you want to go?
            </h2>
            <p style={{ fontSize:14, color:"rgba(255,255,255,.92)", maxWidth:400 }}>Get an instant price. No enquiry forms, no waiting.</p>
          </div>
          <button className="btn btn-lg" style={{ background:"white", color:"var(--bl)", fontWeight:700 }} onClick={() => openBooking()}>
            Request a flight <ArrowRight size={16}/>
          </button>
        </div>
      </section>

      {/* Safety */}
      <FadeIn>
      <section className="sec">
        <div className="wrap">
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:64, alignItems:"center" }} className="bk-main-grid">
            <div>
              <div className="eyebrow">Why trust us</div>
              <h2 className="h2" style={{ marginBottom:18 }}>{safetySection(BRAND.name).heading}</h2>
              <p style={{ fontSize:15, color:"var(--mu)", lineHeight:1.75, marginBottom:28 }}>{safetySection(BRAND.name).intro}</p>
              {safetySection(BRAND.name).points.map(item => (
                <div key={item} style={{ display:"flex", alignItems:"center", gap:10, marginBottom:10 }}>
                  <div style={{ width:22, height:22, borderRadius:"50%", background:"var(--grbg)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}><Check size={12} color="var(--gr)"/></div>
                  <span style={{ fontSize:14, fontWeight:500 }}>{item}</span>
                </div>
              ))}
            </div>
            <div style={{ borderRadius:24, overflow:"hidden", height:"min(460px,55vw)" }}>
              <Img preset="inline" src={IMG.heliCockpit} alt="Cockpit" style={{ width:"100%", height:"100%", objectFit:"cover" }}/>
            </div>
          </div>
        </div>
      </section>
      </FadeIn>

      {/* Featured in / trusted by — CMS-fed, invisible while empty */}
      <FeaturedInStrip/>

      {/* Customer reviews (2026-08-15) — ALL published reviews, newest first,
          as a scroll-snap gallery from 3 cards up. Same list and same order as
          the #ssr-content block in index.astro (both call homepageReviews), so
          the crawler and the visitor see identical data. Renders NOTHING when
          no review is published — no placeholder, no empty header. Visible
          text only: no aggregateRating and no Review markup is emitted here,
          deliberately (self-serving reviews are not eligible for stars). */}
      {homepageReviews(REVIEWS, SEO_ROUTES).length > 0 && (
        <FadeIn>
        <section className="sec-sm" style={{ background:"var(--off)" }}>
          <div className="wrap">
            <div className="eyebrow">In their words</div>
            <ReviewsSection
              reviews={homepageReviews(REVIEWS, SEO_ROUTES)}
              limit={null}
              gallery
              headingClassName="h2"
            />
          </div>
        </section>
        </FadeIn>
      )}

      {/* Blog */}
      <FadeIn>
      <section className="sec" style={{ background:"var(--off)" }}>
        <div className="wrap">
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-end", marginBottom:32 }}>
            <div><div className="eyebrow">The {BRAND.name} journal</div><h2 className="h2">Stories &amp; travel guides</h2></div>
            <button className="btn btn-ol btn-sm" onClick={() => setPage("blog")}>All articles <ArrowRight size={13}/></button>
          </div>
          <div className="bg">
            {BLOGS.slice(0,3).map(b => (
              <div key={b.slug} className="bc" style={{ cursor:"pointer" }} onClick={() => setPage("blog:" + b.slug)}>
                <div className="bc-img"><Img preset="card" src={b.img} alt={b.title}/></div>
                <div className="bc-body">
                  <div className="bc-cat">{b.cat}</div>
                  <div className="bc-title">{b.title}</div>
                  <div className="bc-exc">{b.exc}</div>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginTop:12 }}>
                    <span style={{ fontSize:12, color:"var(--mu2)" }}>{b.date}{b.readTime ? " · " + b.readTime + " min" : ""}</span>
                    <span style={{ fontSize:12, color:"var(--bl)", fontWeight:600 }}>Read more</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      </FadeIn>

      {/* HOW IT WORKS */}
      <FadeIn>
      <section className="sec-sm" style={{ background:"var(--off)" }}>
        <div className="wrap">
          <div style={{ textAlign:"center", marginBottom:40 }}>
            <div className="eyebrow">Simple by design</div>
            <h2 className="h2">{HOW_IT_WORKS_HEADING}</h2>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:20, position:"relative" }} className="ac-grid stats-strip-4">
            {howItWorksSteps(HP.steps, ARRIVAL_BEFORE_MIN).map((s, i) => {
              const icons = [<Search size={20}/>, <Check size={20}/>, <Shield size={20}/>, <Plane size={20}/>];
              return (
              <div key={s.title} style={{ textAlign:"center", position:"relative" }}>
                <div style={{ width:56, height:56, borderRadius:"50%", background:"var(--bl)", color:"white", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 16px", position:"relative" }}>
                  {icons[i] || icons[0]}
                  <div style={{ position:"absolute", top:-4, right:-4, width:22, height:22, borderRadius:"50%", background:"var(--ink)", color:"white", fontSize:11, fontWeight:800, display:"flex", alignItems:"center", justifyContent:"center", border:"2px solid var(--off)" }}>{i+1}</div>
                </div>
                <div style={{ fontWeight:700, fontSize:15, marginBottom:8 }}>{s.title}</div>
                <p style={{ fontSize:13, color:"var(--mu)", lineHeight:1.7 }}>{s.body}</p>
              </div>
              );
            })}
          </div>
          <div style={{ textAlign:"center", marginTop:36 }}>
            <button className="btn btn-bl btn-lg" onClick={() => openBooking()}>Start your booking <ArrowRight size={16}/></button>
          </div>
        </div>
      </section>
      </FadeIn>

      {/* Popular routes section follows */}
      <FadeIn>
      <section className="sec">
        <div className="wrap">
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-end", marginBottom:28, flexWrap:"wrap", gap:12 }}>
            <div>
              <div className="eyebrow">Direct connections</div>
              <h2 className="h2">Popular routes</h2>
            </div>
            <button className="btn btn-ol btn-sm" onClick={() => setPage("routes")}>All routes</button>
          </div>
          <PopularRoutes setPage={setPage} openBooking={openBooking}/>
        </div>
      </section>
      </FadeIn>

      {/* TRANSPARENT PRICING */}
      <FadeIn>
      <section className="sec-sm" style={{ background:"var(--off)" }}>
        <div className="wrap">
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:32, alignItems:"center" }} className="bk-main-grid">
            <div>
              <div className="eyebrow" style={{ marginBottom:14 }}>Transparent pricing</div>
              <h2 className="h2" style={{ marginBottom:16 }}>{pricingPromise(__CMS?.SETTINGS?.fullPaymentOnlyDays).heading}</h2>
              <p style={{ fontSize:15, color:"var(--mu)", lineHeight:1.8, marginBottom:24 }}>
                {pricingPromise(__CMS?.SETTINGS?.fullPaymentOnlyDays).body}
              </p>
              <div style={{ display:"flex", flexDirection:"column", gap:10, marginBottom:24 }}>
                {visibleMarketingFleet().map(f => (
                  <div key={f.id} style={{ display:"flex", alignItems:"center", gap:12, padding:"10px 16px", background:"white", borderRadius:10, border:"1px solid var(--bd)", cursor:"pointer", transition:"all .15s" }} className="card-hv" onClick={() => setPage("fleet:"+f.id)}>
                    <div style={{ width:64, height:44, borderRadius:8, overflow:"hidden", flexShrink:0 }}>
                      <Img preset="card" src={f.img} alt={f.name + " helicopter exterior"} style={{ width:"100%", height:"100%", objectFit:"cover" }}/>
                    </div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontWeight:600, fontSize:13 }}>{f.name}</div>
                      <div style={{ fontSize:12, color:"var(--mu)", marginTop:1 }}>up to {f.seats} pax • {f.engine==="twin"?"Twin":"Single"}-engine</div>
                    </div>
                    <div style={{ display:"flex", alignItems:"center", gap:6, flexShrink:0 }} onClick={(e) => { e.stopPropagation(); openBooking({ aircraft:f }); }}>
                      <span style={{ fontSize:12, fontWeight:600, color:"var(--bl)" }}>Quote</span>
                      <ArrowRight size={12} color="var(--bl)"/>
                    </div>
                  </div>
                ))}
              </div>
              <p style={{ fontSize:12, color:"var(--mu2)", lineHeight:1.6 }}>{pricingPromise(__CMS?.SETTINGS?.fullPaymentOnlyDays).fine}</p>
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
              {pricingPromise(__CMS?.SETTINGS?.fullPaymentOnlyDays).points.map(f => (
                <div key={f.title} style={{ display:"flex", gap:14, alignItems:"flex-start" }}>
                  <div style={{ width:24, height:24, borderRadius:"50%", background:"var(--grbg)", color:"var(--gr)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, marginTop:2 }}><Check size={12}/></div>
                  <div>
                    <div style={{ fontWeight:600, fontSize:14, marginBottom:4 }}>{f.title}</div>
                    <div style={{ fontSize:13, color:"var(--mu)", lineHeight:1.6 }}>{f.body}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      </FadeIn>

      {/* USE CASES — data + lead split from src/lib/homepage-journeys.js, the
          SAME list the #ssr-content block in index.astro renders (single
          source, crawler and visitor can never diverge). Deliberately
          text-only cards (owner call, 2026-08-17): hierarchy comes from the
          lead line, not from images. journeyLead is lossless — lead + rest
          is the frozen text, character for character. */}
      <FadeIn>
      <section className="sec">
        <div className="wrap">
          <div style={{ textAlign:"center", marginBottom:40 }}>
            <div className="eyebrow">Typical journeys</div>
            <h2 className="h2">How our clients fly</h2>
          </div>
          <div className="jt-grid" tabIndex={0} role="region" aria-label="Typical journeys, scroll horizontally on small screens">
            {JOURNEYS.map(t => {
              const [lead, rest] = journeyLead(t.text);
              return (
                <div key={t.route} className="card jt-card">
                  <div style={{ display:"inline-block", alignSelf:"flex-start", padding:"3px 10px", borderRadius:100, background:"var(--blbg)", color:"var(--bl)", fontSize:11, fontWeight:700, marginBottom:14 }}>{t.type}</div>
                  <div style={{ fontSize:16, fontWeight:700, letterSpacing:"-.02em", color:"var(--ink)", lineHeight:1.45, marginBottom:10 }}>{lead}</div>
                  <p style={{ fontSize:13, color:"var(--mu)", lineHeight:1.7, marginBottom:18 }}>{rest}</p>
                  <div style={{ borderTop:"1px solid var(--bd)", paddingTop:12, marginTop:"auto" }}>
                    {t.routeSlug
                      ? <a href={"/route/" + t.routeSlug} style={{ fontWeight:700, fontSize:13, color:"var(--bl)", textDecoration:"none" }} onClick={(e) => { if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return; e.preventDefault(); setPage("route:" + t.routeSlug); document.getElementById("top")?.scrollIntoView(); }}>{t.route}</a>
                      : <div style={{ fontWeight:700, fontSize:13, color:"var(--bl)" }}>{t.route}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
      </FadeIn>

      {/* Newsletter */}
      <FadeIn>
      <section className="sec-sm" style={{ background:"var(--ink)" }}>
        <div className="wrap bk-main-grid" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:48, alignItems:"center" }}>
          <div>
            <h2 style={{ fontFamily:"var(--sans)", fontSize:26, fontWeight:800, letterSpacing:"-.04em", marginBottom:8, color:"white" }}>Stay connected</h2>
            <p style={{ fontSize:14, color:"rgba(255,255,255,.42)", lineHeight:1.7 }}>New routes, seasonal packages, and travel guides about private helicopter travel in Greece.</p>
          </div>
          <NewsletterForm source="homepage-hero"/>
        </div>
      </section>
      </FadeIn>
    </>
  );
}

/* TILE MAP (img-based, no external JS needed) */
const TS   = 256; // tile size px

function lng2tileFn(lng, z) { return (lng + 180) / 360 * Math.pow(2, z); }
function lat2tileFn(lat, z) {
  const r = lat * Math.PI / 180;
  return (1 - Math.log(Math.tan(r) + 1 / Math.cos(r)) / Math.PI) / 2 * Math.pow(2, z);
}
// Keep old helpers for other components using fixed ZOOM=12
const ZOOM = 12;

function TileMap({ pads, selected, onSelect, scheme, height = 230 }) {
  const col = scheme === "green" ? "#0A6640" : "#0D56A6";
  const mapRef = useRef(null);
  const mapInst = useRef(null);
  const markersRef = useRef([]);
  const initDone = useRef(false);
  const near = useNearViewport(mapRef);

  // Initialize map + add markers once the container nears the viewport — defers
  // the carto basemap tiles (+ the Leaflet lib) until just before scroll-in
  // instead of eagerly on mount while the map sits below the fold.
  useEffect(() => {
    if (!near) return;
    loadLeaflet().then(L => {
      if (!mapRef.current || mapInst.current) return;
      const cx = pads.length > 0
        ? { lat: pads.reduce((s,p)=>s+p.lat,0)/pads.length, lng: pads.reduce((s,p)=>s+p.lng,0)/pads.length }
        : { lat: 37.9, lng: 23.7 };
      const map = L.map(mapRef.current, { center: [cx.lat, cx.lng], zoom: 12, zoomControl: false, attributionControl: false });
      L.control.zoom({ position: "topright" }).addTo(map);
      attachMapLayers(map, L);
      mapInst.current = map;

      // Add all markers immediately on init
      pads.forEach(pad => {
        const icon = pad.type === "airport" ? airportIcon(L, false, col) : helipadIcon(L, false, col);
        const m = L.marker([pad.lat, pad.lng], { icon }).addTo(map);
        m.bindTooltip(pad.name, { direction: "top", offset: [0, -14] });
        m.on("click", () => onSelect(pad));
        m.getElement()?.setAttribute("aria-label", "Select helipad " + pad.name);
        markersRef.current.push(m);
      });

      if (pads.length > 1) {
        const bounds = L.latLngBounds(pads.map(p => [p.lat, p.lng]));
        map.fitBounds(bounds, { padding: [30, 30], maxZoom: 14 });
      }

      // Multiple invalidateSize calls to handle various rendering timings
      initDone.current = true;
      [100, 300, 600, 1200].forEach(ms => setTimeout(() => { if (mapInst.current) mapInst.current.invalidateSize(); }, ms));
    });
    return () => { if (mapInst.current) { mapInst.current.remove(); mapInst.current = null; } };
  }, [near]);

  // Viewport-based (vh-clamp) heights resize the container with the window —
  // keep Leaflet's internal size in sync so tiles keep covering the box.
  useEffect(() => {
    const onR = () => { if (mapInst.current) mapInst.current.invalidateSize(); };
    window.addEventListener("resize", onR);
    return () => window.removeEventListener("resize", onR);
  }, []);

  // Update marker styles on selection change (don't re-add, just update icons)
  useEffect(() => {
    if (!mapInst.current || !_leaflet.L || !initDone.current) return;
    const L = _leaflet.L;
    markersRef.current.forEach(m => m.remove());
    markersRef.current = [];
    pads.forEach(pad => {
      const isSel = selected?.id === pad.id;
      const icon = pad.type === "airport" ? airportIcon(L, isSel, col) : helipadIcon(L, isSel, col);
      const m = L.marker([pad.lat, pad.lng], { icon }).addTo(mapInst.current);
      m.bindTooltip(pad.name, { direction: "top", offset: [0, -14] });
      m.on("click", () => onSelect(pad));
      m.getElement()?.setAttribute("aria-label", "Select helipad " + pad.name);
      markersRef.current.push(m);
    });
    if (selected && mapInst.current) {
      mapInst.current.setView([selected.lat, selected.lng], mapInst.current.getZoom(), { animate: true });
    }
  }, [selected?.id]);

  // minHeight + width reserve the map box at its final size from first paint, so
  // the [100,300,600,1200]ms invalidateSize cascade (kept — Leaflet needs it for
  // tile correctness in animating/initially-collapsed containers) redraws tiles
  // INSIDE an already-sized box and never shifts surrounding content (CLS).
  return <div ref={mapRef} style={{ height, minHeight: height, width:"100%", borderRadius:0 }}/>;
}

export function HelipadPanel({ label, city, selected, onSelect, scheme, compact = false, allowedOperatorIds, servesPredicate = null, aircraft = null }) {
  const allPads = getHelipads(city);
  // Brief A — when a servesPredicate is supplied (helipads-first flow) it is the
  // authoritative pad filter: it already folds in route-pricing operators, the
  // pad-service exclusion list, and (for the arrival panel) the chosen
  // departure pad, so the offered pads are dead-end-free. Falls back to the
  // legacy allowedOperatorIds membership filter for any caller that doesn't
  // pass a predicate.
  const pads = servesPredicate
    ? allPads.filter(servesPredicate)
    : allowedOperatorIds?.length
    ? allPads.filter(h => {
        if (!h.operatorIds?.length) return true; // no restriction = open to all
        return h.operatorIds.some(opId => allowedOperatorIds.includes(opId));
      })
    : allPads;
  const col  = scheme === "green" ? "var(--gr)" : "var(--bl)";
  const bg   = scheme === "green" ? "var(--grbg)" : "var(--blbg)";
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => { const c = () => setIsMobile(window.innerWidth <= 640); c(); window.addEventListener("resize", c); return () => window.removeEventListener("resize", c); }, []);
  return (
    <div style={{ height:"100%", display:"flex", flexDirection:"column" }}>
      {!compact && <div style={{ fontWeight:700, fontSize:11.5, color:"var(--mu)", textTransform:"uppercase", letterSpacing:".08em", marginBottom:10 }}>{label}</div>}
      <div style={{ background:"white", border: compact ? "none" : "1px solid var(--bd)", borderRadius: compact ? 0 : 18, overflow:"hidden", flex:1, display:"flex", flexDirection:"column" }}>
        {/* City header */}
        <div style={{ padding: compact ? "8px 14px" : "11px 16px", borderBottom:"1px solid var(--bd)", display:"flex", alignItems:"center", gap:8 }}>
          <span style={{ fontSize:12, color:"var(--mu)" }}>
            {[pads.filter(p=>p.type!=="airport").length > 0 ? pads.filter(p=>p.type!=="airport").length + (pads.filter(p=>p.type!=="airport").length>1?" helipads":" helipad") : "", pads.filter(p=>p.type==="airport").length > 0 ? pads.filter(p=>p.type==="airport").length + (pads.filter(p=>p.type==="airport").length>1?" airports":" airport") : ""].filter(Boolean).join(" • ")}
          </span>
          {selected && !isMobile && <span className="tag" style={{ marginLeft:"auto", background:bg, color:col, fontSize:10 }}><Check size={9}/> {selected.name}</span>} </div>
        {/* Tile map -- full height on desktop, compact on mobile */}
        {/* Map height is viewport-capped (owner, 2026-07-07): the old fixed
            350/450px pushed the helipad LIST below the fold behind the sticky
            footer. clamp keeps >=2 list entries visible on a 1050px overlay
            AND a 667px phone; the box is reserved from first paint (no CLS). */}
        {(() => {
        const mapEl = !compact
          ? <TileMap pads={pads} selected={selected} onSelect={onSelect} scheme={scheme}/>
          : <TileMap pads={pads} selected={selected} onSelect={onSelect} scheme={scheme} height={isMobile ? "clamp(160px, 26vh, 260px)" : "clamp(180px, 30vh, 340px)"}/>;
        {/* Card list */}
        const listEl = (
        <div style={{ padding:10, display:"flex", flexDirection:"column", gap:6 }}>
          {pads.length === 0 && (
            <div style={{ padding:"20px 16px", textAlign:"center", color:"var(--mu)", fontSize:13 }}>
              No helipads available for this aircraft and operator combination.
            </div>
          )}
          {pads.map(h => {
            const isSel = selected?.id === h.id;
            return (
              <div key={h.id} role="button" tabIndex={0} onClick={() => onSelect(h)} onKeyDown={onKbdActivate} style={{ display:"flex", alignItems:"center", gap:12, padding:"13px 14px", minHeight:48, border:"1.5px solid " + (isSel?col:"var(--bd)"), borderRadius:12, cursor:"pointer", background:isSel?bg:"transparent", transition:"all .15s" }}>
              <div style={{ width:30, height:30, borderRadius:"50%", background:isSel?col:"var(--off2)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, transition:"background .2s" }}>
              {h.type === "airport" ? (
              <Plane size={13} color={isSel?"white":"var(--mu2)"}/>
              ) : (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke={isSel?"white":"var(--mu2)"} strokeWidth="2"/>
              <path d="M8 7v10M16 7v10M8 12h8" stroke={isSel?"white":"var(--mu2)"} strokeWidth="2.2" strokeLinecap="round"/>
              </svg>
              )}
              </div>
              <div style={{ flex:1, minWidth:0 }}>
              <div style={{ display:"flex", alignItems:"center", gap:6 }}>
              <div style={{ fontWeight:700, fontSize:13, color:isSel?"var(--ink)":"var(--ink2)" }}>{h.name}</div>
              {h.iata && <span style={{ fontSize:9, fontWeight:700, padding:"1px 5px", borderRadius:4, background:isSel?"rgba(0,0,0,.08)":"var(--off2)", color:isSel?"var(--mu)":"var(--mu2)", letterSpacing:".05em" }}>{h.iata}</span>} </div>
              {(() => { const rf = resolvedPadFee(h, aircraft); return rf > 0 && (
              <div style={{ display:"inline-flex", alignItems:"center", gap:4, marginTop:4, fontSize:10, fontWeight:700, padding:"2px 7px", borderRadius:100, background: isSel ? "rgba(0,0,0,.06)" : (h.type==="airport" ? "#FEF3C7" : "var(--blbg)"), color: isSel ? "var(--ink2)" : (h.type==="airport" ? "#92400E" : "var(--bl)") }}>
~+EUR {fmtPrice(rf)} {h.type==="airport" ? "airport fee" : "landing fee"}
              </div>
              ); })()}
              </div>
              {isSel ? <Check size={15} color={col}/> : <ChevronRight size={14} color="var(--mu2)"/>} </div>
            );
          })}
        </div>
        );
        {/* Mobile (compact): the LIST is the primary control and renders FIRST,
            the map below - conditional render (not CSS order) so DOM/focus
            order matches the visual order. Desktop order unchanged. */}
        return compact && isMobile ? <>{listEl}{mapEl}</> : <>{mapEl}{listEl}</>;
        })()}
      </div>
    </div>
  );
}

/* POLICY BADGE */
export function PolicyCard({ policy, compact = false, flightDate = null }) {
  if (!policy) return null;
  const { depositPct, weatherProtection, noShowNote, noShowRefundPct, operatorCancelNote, weatherNote, inFlightCancelNote } = policy;

  // Strict-filter tiers — no synthesis fallback. Hide entire card if Sanity
  // policy doesn't have valid entries (mirrors K1 server-side behavior).
  const resolvedTiers = getValidPolicyTiers(policy);
  if (resolvedTiers.length === 0) return null;

  // "Free cancellation" only when the policy actually REACHES a 100% refund tier.
  // The free-cancel threshold = the lowest hoursBeforeFlight among full-refund tiers
  // (cancel ≥ that many hours before → full refund). No 100% tier → no badge (e.g.
  // Bellavia 90/50/0 → none; HELISTAR 48h=100% → "Free cancel 48h+"). DISPLAY only —
  // the real refund tiers + the policy table below are unchanged.
  const fullRefundTiers = resolvedTiers.filter(t => t.refundPct >= 100);
  const freeCancelHours = fullRefundTiers.length ? Math.min(...fullRefundTiers.map(t => t.hoursBeforeFlight)) : null;
  const cancColor = freeCancelHours >= 72 ? "var(--gr)" : freeCancelHours >= 48 ? "#0369A1" : "#92400E";
  const cancBg    = freeCancelHours >= 72 ? "var(--grbg)" : freeCancelHours >= 48 ? "#E0F2FE" : "#FEF3C7";

  // Dynamic deposit vs full payment
  const fpDays = (__CMS?.SETTINGS?.fullPaymentOnlyDays) || 7;
  const flDate = flightDate ? new Date(flightDate) : null;
  const daysLeft = flDate ? Math.ceil((flDate - new Date()) / (1000*60*60*24)) : 999;
  const canDeposit = daysLeft >= fpDays;

  if (compact) return (
    <div style={{ display:"flex", flexWrap:"wrap", gap:5 }}>
      {freeCancelHours != null && (
        <span style={{ display:"inline-flex", alignItems:"center", gap:4, padding:"4px 10px", borderRadius:100, fontSize:11.5, fontWeight:600, background:cancBg, color:cancColor }}>
          <Check size={12} style={{flexShrink:0}}/> Free cancel {freeCancelHours}h+
        </span>
      )}
      {weatherProtection && (
        <span style={{ display:"inline-flex", alignItems:"center", gap:4, padding:"4px 10px", borderRadius:100, fontSize:11.5, fontWeight:600, background:"var(--grbg)", color:"var(--gr)" }}>
          <Shield size={12} style={{flexShrink:0}}/> Weather refund
        </span>
      )}
      <span style={{ display:"inline-flex", alignItems:"center", gap:4, padding:"4px 10px", borderRadius:100, fontSize:11.5, fontWeight:600, background: canDeposit ? "var(--blbg)" : "#FEF3C7", color: canDeposit ? "var(--bl)" : "#92400E" }}>
        <CreditCard size={12} style={{flexShrink:0}}/> {canDeposit ? depositPct + "% deposit" : "Full payment required"}
      </span>
    </div>
  );

  // Phase 2 G-3: clean bordered table, no rainbow pills, no rainbow text colors.
  // Reads as a financial document \u2014 matches the design used in emails + manage page.
  const feePct = (__CMS?.SETTINGS?.processingFeePct) ?? 3;
  const tierLabel = (t) => t.label || (t.refundPct >= 100 ? "Full refund (less " + feePct + "% fee)" : t.refundPct > 0 ? t.refundPct + "%" : "No refund");
  // P3 #24 — the processor-fee note is only relevant when a full (100%) refund
  // is reachable. Policies whose tiers never hit 100% (e.g. 80/50/0) hide it.
  const hasFullRefundTier = resolvedTiers.some(t => t.refundPct >= 100);

  return (
    <section style={{ border:"1px solid #E5E7EB", borderRadius:12, padding:"20px 24px", background:"#FFFFFF" }}>
      <h3 style={{ margin:"0 0 4px", fontSize:10, fontWeight:700, letterSpacing:".08em", color:"#797672", textTransform:"uppercase" }}>Cancellation policy</h3>
      {hasFullRefundTier && (
        <p style={{ margin:"0 0 14px", fontSize:12, color:"#797672", lineHeight:1.5 }}>A {feePct}% payment processor fee is deducted from full refunds only. Partial refunds (when a cancellation fee tier applies) are paid in full at the tier amount shown — no additional fee.</p>
      )}
      <table style={{ width:"100%", borderCollapse:"collapse", fontFamily:"system-ui,sans-serif" }}>
        <thead>
          <tr>
            <th style={{ textAlign:"left", padding:"8px 0", borderBottom:"1px solid #E5E7EB", fontSize:10, fontWeight:700, letterSpacing:".06em", textTransform:"uppercase", color:"#797672" }}>Timing</th>
            <th style={{ textAlign:"right", padding:"8px 0", borderBottom:"1px solid #E5E7EB", fontSize:10, fontWeight:700, letterSpacing:".06em", textTransform:"uppercase", color:"#797672" }}>Refund</th>
          </tr>
        </thead>
        <tbody>
          {resolvedTiers.map((t, i) => {
            const condition = i === 0
              ? "More than " + t.hoursBeforeFlight + " hours before flight"
              : t.hoursBeforeFlight === 0
              ? "Less than " + resolvedTiers[i-1].hoursBeforeFlight + " hours / no-show"
              : "Less than " + resolvedTiers[i-1].hoursBeforeFlight + " hours before flight";
            return (
              <tr key={i}>
                <td style={{ padding:"10px 0", fontSize:13, color:"#18191A", lineHeight:1.5, borderBottom:"1px solid #F2F0EB" }}>{condition}</td>
                <td style={{ padding:"10px 0", fontSize:13, color:"#18191A", textAlign:"right", fontWeight:600, borderBottom:"1px solid #F2F0EB" }}>{tierLabel(t)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p style={{ margin:"14px 0 0", fontSize:12, color:"#797672", lineHeight:1.5 }}>Weather cancellation: full refund or free rescheduling.</p>
      {operatorCancelNote && <p style={{ margin:"6px 0 0", fontSize:12, color:"#797672", lineHeight:1.5 }}>Cancellation by operator: {operatorCancelNote}</p>}
      {inFlightCancelNote && <p style={{ margin:"6px 0 0", fontSize:12, color:"#797672", lineHeight:1.5 }}>Flight has commenced: {inFlightCancelNote}</p>}
    </section>
  );
}

/* STEP BAR */
// Brief A — helipads BEFORE aircraft. Pads are chosen first so the aircraft
// list can be filtered to operators that serve both pads and the price shown
// is pad-exact. Custom-route keeps its own (helipad-less) page list below.
const STEPS        = ["Route","Helipads","Aircraft","Details","Payment"];
// Quote-flow variant: when the chosen aircraft has no fixed price (data.isQuoteRequest),
// the final step submits a charter request instead of charging the card.
export const QUOTE_STEPS  = ["Route","Helipads","Aircraft","Details","Submit request"];
export const STEP_PAGES   = ["route","helipads","search","booking-passengers","booking-payment"];
export const CUSTOM_STEPS      = ["Route","Aircraft","Details","Quote"];
export const CUSTOM_STEP_PAGES = ["route","search","booking-passengers","booking-custom-quote"];
export const CQ_STEPS          = ["Your request","Your details"];
export const CQ_STEP_PAGES     = ["route","booking-custom-quote"];

export function StepBar({ current, setPage, steps, stepPages, maxReached = 99 }) {
  const LABELS = steps || STEPS;
  const PAGES  = stepPages || STEP_PAGES;
  const [narrow, setNarrow] = useState(false);
  useEffect(() => {
    const check = () => setNarrow(window.innerWidth < 600);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  return (
    <div style={{ background:"white", borderBottom:"1px solid var(--bd)", position:"sticky", top:0, zIndex:10 }}>
      <div className="wrap">
        <div style={{ display:"flex", alignItems:"center", height:52 }}>
          {LABELS.map((label, i) => {
            const done   = i < current;
            const active = i === current;
            const canNav = i <= maxReached && i !== current;
            const showLabel = !narrow || active;
            return (
              <div key={label} style={{ display:"flex", alignItems:"center", flex: i < LABELS.length-1 ? "1 1 0" : "0 0 auto", minWidth:0 }}>
                <div onClick={() => canNav && setPage(PAGES[i])} style={{ display:"flex", alignItems:"center", gap: showLabel ? 8 : 0, cursor:canNav?"pointer":"default", padding:"4px 0", userSelect:"none", flexShrink:0, opacity: i > maxReached ? 0.4 : 1 }}>
                  <div style={{ width:26, height:26, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", fontWeight:800, fontSize:12, flexShrink:0, background: done ? "var(--gr)" : active ? "var(--bl)" : "var(--off2)", color: done||active ? "white" : "var(--mu2)", boxShadow: active ? "0 0 0 3px var(--blbg)" : done ? "0 0 0 3px var(--grbg)" : "none", transition:"all .2s", }}>

                    {done ? <Check size={12}/> : i+1} </div>
                  {showLabel && (
                    <span style={{ fontSize:13, fontWeight:600, whiteSpace:"nowrap", color: done ? "var(--gr)" : active ? "var(--bl)" : "var(--mu2)", transition:"color .2s", }}>{label}</span>

                  )}
                </div>
                {/* Connector line */}
                {i < LABELS.length-1 && (
                  <div style={{ flex:1, height:2, margin:"0 6px", borderRadius:2, minWidth:8, background: done ? "var(--gr)" : "var(--bd)", opacity: done ? 0.4 : 1, transition:"background .3s", }}/>

                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// Brief A — shown when no operator serves any pad in a city for this route
// (empty city / unserved combination). Never €0, never a dead empty list:
// the customer is told it is price-on-request and routed to a custom quote.


/* TRIP INFO / PRE-CHECK */

/* QUICK WEIGHT CHECK (inline on aircraft page) */

/* Unified sticky footer for all booking steps */
export function BookingFooter({ children, hint = null }) {
  return (
    <div style={{ position:"sticky", bottom:0, left:0, right:0, marginTop:"auto", background:"white", borderTop:"1px solid var(--bd)", boxShadow:"0 -4px 24px rgba(0,0,0,.1)", padding:"14px 20px calc(18px + env(safe-area-inset-bottom, 0px))", zIndex:50 }}>
      {/* optional gated-CTA hint ABOVE the button row: the sticky bar grows
          upward, so the CTA itself never shifts when the hint appears/leaves */}
      {hint}
      <div style={{ display:"flex", alignItems:"center", gap:12 }}>
        {children}
      </div>
    </div>
  );
}


/* AIRCRAFT INFO POPUP — opened from "View details" link on a booking-flow
 * card. Read-only inspection of an aircraft (photo, specs, cabin layout,
 * highlights, longDesc). The booking-flow's parent state is untouched —
 * the popup is purely an overlay. Closing or selecting from inside resumes
 * the underlying SearchPage exactly where the user left off. */

/* SEARCH / RESULTS */

export const NATIONALITIES = [
  "🇦🇫 Afghan","🇦🇱 Albanian","🇩🇿 Algerian","🇦🇩 Andorran","🇦🇴 Angolan","🇦🇬 Antiguan","🇦🇷 Argentine","🇦🇲 Armenian","🇦🇺 Australian","🇦🇹 Austrian",
  "🇦🇿 Azerbaijani","🇧🇸 Bahamian","🇧🇭 Bahraini","🇧🇩 Bangladeshi","🇧🇧 Barbadian","🇧🇾 Belarusian","🇧🇪 Belgian","🇧🇿 Belizean","🇧🇯 Beninese","🇧🇹 Bhutanese",
  "🇧🇴 Bolivian","🇧🇦 Bosnian","🇧🇼 Botswanan","🇧🇷 Brazilian","🇧🇳 Bruneian","🇧🇬 Bulgarian","🇧🇫 Burkinabe","🇧🇮 Burundian","🇰🇭 Cambodian","🇨🇲 Cameroonian",
  "🇨🇦 Canadian","🇨🇻 Cape Verdean","🇨🇫 Central African","🇹🇩 Chadian","🇨🇱 Chilean","🇨🇳 Chinese","🇨🇴 Colombian","🇰🇲 Comorian","🇨🇬 Congolese","🇨🇷 Costa Rican",
  "🇭🇷 Croatian","🇨🇺 Cuban","🇨🇾 Cypriot","🇨🇿 Czech","🇩🇰 Danish","🇩🇯 Djiboutian","🇩🇲 Dominican","🇪🇨 Ecuadorian","🇪🇬 Egyptian","🇸🇻 Salvadoran",
  "🇬🇶 Equatorial Guinean","🇪🇷 Eritrean","🇪🇪 Estonian","🇸🇿 Swazi","🇪🇹 Ethiopian","🇫🇯 Fijian","🇫🇮 Finnish","🇫🇷 French","🇬🇦 Gabonese","🇬🇲 Gambian",
  "🇬🇪 Georgian","🇩🇪 German","🇬🇭 Ghanaian","🇬🇷 Greek","🇬🇩 Grenadian","🇬🇹 Guatemalan","🇬🇳 Guinean","🇬🇾 Guyanese","🇭🇹 Haitian","🇭🇳 Honduran",
  "🇭🇺 Hungarian","🇮🇸 Icelandic","🇮🇳 Indian","🇮🇩 Indonesian","🇮🇷 Iranian","🇮🇶 Iraqi","🇮🇪 Irish","🇮🇱 Israeli","🇮🇹 Italian","🇨🇮 Ivorian",
  "🇯🇲 Jamaican","🇯🇵 Japanese","🇯🇴 Jordanian","🇰🇿 Kazakh","🇰🇪 Kenyan","🇰🇼 Kuwaiti","🇰🇬 Kyrgyz","🇱🇦 Lao","🇱🇻 Latvian","🇱🇧 Lebanese",
  "🇱🇾 Libyan","🇱🇮 Liechtensteiner","🇱🇹 Lithuanian","🇱🇺 Luxembourgish","🇲🇬 Malagasy","🇲🇼 Malawian","🇲🇾 Malaysian","🇲🇻 Maldivian","🇲🇱 Malian","🇲🇹 Maltese",
  "🇲🇽 Mexican","🇲🇩 Moldovan","🇲🇨 Monacan","🇲🇳 Mongolian","🇲🇪 Montenegrin","🇲🇦 Moroccan","🇲🇿 Mozambican","🇲🇲 Myanmar","🇳🇦 Namibian","🇳🇵 Nepali",
  "🇳🇱 Dutch","🇳🇿 New Zealander","🇳🇮 Nicaraguan","🇳🇬 Nigerian","🇲🇰 North Macedonian","🇳🇴 Norwegian","🇴🇲 Omani","🇵🇰 Pakistani","🇵🇦 Panamanian","🇵🇬 Papua New Guinean",
  "🇵🇾 Paraguayan","🇵🇪 Peruvian","🇵🇭 Filipino","🇵🇱 Polish","🇵🇹 Portuguese","🇶🇦 Qatari","🇷🇴 Romanian","🇷🇺 Russian","🇷🇼 Rwandan","🇸🇦 Saudi",
  "🇸🇳 Senegalese","🇷🇸 Serbian","🇸🇬 Singaporean","🇸🇰 Slovak","🇸🇮 Slovenian","🇿🇦 South African","🇰🇷 South Korean","🇪🇸 Spanish","🇱🇰 Sri Lankan","🇸🇩 Sudanese",
  "🇸🇪 Swedish","🇨🇭 Swiss","🇹🇼 Taiwanese","🇹🇿 Tanzanian","🇹🇭 Thai","🇹🇹 Trinidadian","🇹🇳 Tunisian","🇹🇷 Turkish","🇺🇬 Ugandan","🇺🇦 Ukrainian",
  "🇦🇪 Emirati","🇬🇧 British","🇺🇸 American","🇺🇾 Uruguayan","🇺🇿 Uzbek","🇻🇪 Venezuelan","🇻🇳 Vietnamese","🇾🇪 Yemeni","🇿🇲 Zambian","🇿🇼 Zimbabwean",
];

export const COUNTRIES = [
  "🇬🇷 Greece","🇩🇪 Germany","🇬🇧 United Kingdom","🇺🇸 United States","🇫🇷 France","🇮🇹 Italy","🇪🇸 Spain","🇳🇱 Netherlands","🇦🇹 Austria","🇧🇪 Belgium",
  "🇨🇭 Switzerland","🇸🇪 Sweden","🇳🇴 Norway","🇩🇰 Denmark","🇫🇮 Finland","🇵🇹 Portugal","🇮🇪 Ireland","🇵🇱 Poland","🇨🇿 Czech Republic","🇭🇺 Hungary",
  "🇷🇴 Romania","🇧🇬 Bulgaria","🇭🇷 Croatia","🇸🇮 Slovenia","🇸🇰 Slovakia","🇱🇹 Lithuania","🇱🇻 Latvia","🇪🇪 Estonia","🇨🇾 Cyprus","🇲🇹 Malta",
  "🇱🇺 Luxembourg","🇮🇸 Iceland","🇱🇮 Liechtenstein","🇲🇨 Monaco","🇦🇩 Andorra","🇷🇸 Serbia","🇲🇪 Montenegro","🇧🇦 Bosnia","🇲🇰 North Macedonia","🇦🇱 Albania",
  "🇹🇷 Turkey","🇮🇱 Israel","🇦🇪 UAE","🇸🇦 Saudi Arabia","🇶🇦 Qatar","🇰🇼 Kuwait","🇧🇭 Bahrain","🇴🇲 Oman","🇯🇴 Jordan","🇱🇧 Lebanon",
  "🇦🇺 Australia","🇳🇿 New Zealand","🇨🇦 Canada","🇯🇵 Japan","🇨🇳 China","🇰🇷 South Korea","🇹🇼 Taiwan","🇸🇬 Singapore","🇭🇰 Hong Kong","🇮🇳 India",
  "🇧🇷 Brazil","🇲🇽 Mexico","🇦🇷 Argentina","🇨🇱 Chile","🇨🇴 Colombia","🇷🇺 Russia","🇺🇦 Ukraine","🇬🇪 Georgia","🇪🇬 Egypt","🇿🇦 South Africa",
  "🇳🇬 Nigeria","🇰🇪 Kenya","🇲🇦 Morocco","🇹🇭 Thailand","🇮🇩 Indonesia","🇲🇾 Malaysia","🇵🇭 Philippines","🇻🇳 Vietnam","🇵🇰 Pakistan",
];

/* NATIONALITY PICKER — searchable dropdown with PickerSheet on mobile */

/* PHONE INPUT — country code prefix with flag + number */
let PHONE_CODES = [
  { flag:"🇬🇷", code:"+30", country:"Greece" },
  { flag:"🇩🇪", code:"+49", country:"Germany" },
  { flag:"🇬🇧", code:"+44", country:"UK" },
  { flag:"🇺🇸", code:"+1",  country:"USA" },
  { flag:"🇫🇷", code:"+33", country:"France" },
  { flag:"🇮🇹", code:"+39", country:"Italy" },
  { flag:"🇪🇸", code:"+34", country:"Spain" },
  { flag:"🇳🇱", code:"+31", country:"Netherlands" },
  { flag:"🇦🇹", code:"+43", country:"Austria" },
  { flag:"🇧🇪", code:"+32", country:"Belgium" },
  { flag:"🇨🇭", code:"+41", country:"Switzerland" },
  { flag:"🇸🇪", code:"+46", country:"Sweden" },
  { flag:"🇳🇴", code:"+47", country:"Norway" },
  { flag:"🇩🇰", code:"+45", country:"Denmark" },
  { flag:"🇫🇮", code:"+358",country:"Finland" },
  { flag:"🇵🇹", code:"+351",country:"Portugal" },
  { flag:"🇮🇪", code:"+353",country:"Ireland" },
  { flag:"🇵🇱", code:"+48", country:"Poland" },
  { flag:"🇨🇿", code:"+420",country:"Czech Rep." },
  { flag:"🇭🇺", code:"+36", country:"Hungary" },
  { flag:"🇷🇴", code:"+40", country:"Romania" },
  { flag:"🇧🇬", code:"+359",country:"Bulgaria" },
  { flag:"🇭🇷", code:"+385",country:"Croatia" },
  { flag:"🇸🇮", code:"+386",country:"Slovenia" },
  { flag:"🇸🇰", code:"+421",country:"Slovakia" },
  { flag:"🇨🇾", code:"+357",country:"Cyprus" },
  { flag:"🇲🇹", code:"+356",country:"Malta" },
  { flag:"🇷🇸", code:"+381",country:"Serbia" },
  { flag:"🇦🇱", code:"+355",country:"Albania" },
  { flag:"🇹🇷", code:"+90", country:"Turkey" },
  { flag:"🇮🇱", code:"+972",country:"Israel" },
  { flag:"🇦🇪", code:"+971",country:"UAE" },
  { flag:"🇸🇦", code:"+966",country:"Saudi Arabia" },
  { flag:"🇶🇦", code:"+974",country:"Qatar" },
  { flag:"🇰🇼", code:"+965",country:"Kuwait" },
  { flag:"🇦🇺", code:"+61", country:"Australia" },
  { flag:"🇨🇦", code:"+1",  country:"Canada" },
  { flag:"🇯🇵", code:"+81", country:"Japan" },
  { flag:"🇨🇳", code:"+86", country:"China" },
  { flag:"🇮🇳", code:"+91", country:"India" },
  { flag:"🇰🇷", code:"+82", country:"South Korea" },
  { flag:"🇧🇷", code:"+55", country:"Brazil" },
  { flag:"🇲🇽", code:"+52", country:"Mexico" },
  { flag:"🇷🇺", code:"+7",  country:"Russia" },
  { flag:"🇺🇦", code:"+380",country:"Ukraine" },
  { flag:"🇪🇬", code:"+20", country:"Egypt" },
  { flag:"🇿🇦", code:"+27", country:"South Africa" },
  { flag:"🇳🇬", code:"+234",country:"Nigeria" },
  { flag:"🇹🇭", code:"+66", country:"Thailand" },
  { flag:"🇸🇬", code:"+65", country:"Singapore" },
].sort((a, b) => a.country.localeCompare(b.country));
if (__CMS?.SETTINGS?.phoneCountryCodes?.length) PHONE_CODES = __CMS.SETTINGS.phoneCountryCodes;

export function PhoneInput({ value, onChange, error, style }) {
  const parsePhone = (v) => {
    if (!v) return { code:"+30", number:"" };
    for (const pc of PHONE_CODES) {
      if (v.startsWith(pc.code + " ") || v.startsWith(pc.code)) {
        return { code: pc.code, number: v.slice(pc.code.length).trim() };
      }
    }
    return { code:"+30", number: v };
  };
  const { code, number } = parsePhone(value);
  const [selCode, setSelCode] = useState(code);
  const [codeOpen, setCodeOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [isMobile, setMobile] = useState(false);
  const [codeQuery, setCodeQuery] = useState("");
  const ref = useRef(null);
  const codeInputRef = useRef(null);

  useEffect(() => { const c=()=>setMobile(window.innerWidth<=640); c(); window.addEventListener("resize",c); return ()=>window.removeEventListener("resize",c); }, []);
  useEffect(() => { const fn=e=>{if(ref.current&&!ref.current.contains(e.target))setCodeOpen(false);}; document.addEventListener("mousedown",fn); return ()=>document.removeEventListener("mousedown",fn); }, []);
  useEffect(() => { if(codeOpen&&!isMobile) setTimeout(()=>codeInputRef.current?.focus(),50); }, [codeOpen, isMobile]);

  const currentFlag = PHONE_CODES.find(p => p.code === selCode)?.flag || "🇬🇷";
  const borderColor = error ? "#C0392B" : focused ? "var(--bl)" : "transparent";
  const bg = "var(--off)";
  const shadow = focused && !error ? "0 0 0 3px rgba(13,86,166,.08)" : "none";

  const handleCodeChange = (pc) => { setSelCode(pc.code); setCodeOpen(false); setCodeQuery(""); onChange(pc.code + " " + number); };
  const handleNumberChange = (num) => { onChange(selCode + " " + num); };
  const filteredCodes = codeQuery ? PHONE_CODES.filter(pc => pc.country.toLowerCase().includes(codeQuery.toLowerCase()) || pc.code.includes(codeQuery)) : PHONE_CODES;

  return (
    <div ref={ref} style={{ display:"flex", gap:0, position:"relative", borderRadius:10, border:"1.5px solid "+borderColor, background:bg, boxShadow:shadow, transition:"all .15s", ...style }}>
      <button type="button" onClick={() => setCodeOpen(o => !o)} style={{ display:"flex", alignItems:"center", gap:4, padding:"0 10px", background:"transparent", border:"none", cursor:"pointer", fontSize:13, fontWeight:600, color:"var(--ink2)", flexShrink:0, minWidth:72 }}>
        <span style={{ fontSize:16 }}>{currentFlag}</span>
        <span>{selCode}</span>
        <ChevronDown size={10} color="var(--mu2)" style={{ transform:"rotate("+(codeOpen?180:0)+"deg)", transition:"transform .2s" }}/>
      </button>
      <div style={{ width:1, background:"var(--bd)", margin:"6px 0", flexShrink:0 }}/>
      <input type="tel" value={number} onChange={e => handleNumberChange(e.target.value)} onFocus={() => setFocused(true)} onBlur={() => setFocused(false)} placeholder="Phone number" style={{ border:"none", outline:"none", background:"transparent", flex:1, padding:"11px 14px", fontSize:16, color:"var(--ink2)", borderRadius:"0 10px 10px 0" }}/>
      {codeOpen && isMobile && (
        <PickerSheet onClose={() => { setCodeOpen(false); setCodeQuery(""); }} title="Country code">
          <div style={{padding:"4px 4px 8px"}}><input className="fi" autoFocus value={codeQuery} onChange={e=>setCodeQuery(e.target.value)} placeholder="Search country or code..." style={{fontSize:16,padding:"12px 14px",background:"var(--off)"}}/></div>
          <div style={{ maxHeight:"50vh", overflowY:"auto" }}>
            {filteredCodes.map(pc => (
              <div key={pc.code+pc.country} onClick={() => handleCodeChange(pc)} style={{ padding:"14px 16px", fontSize:16, cursor:"pointer", display:"flex", alignItems:"center", gap:10, background: pc.code===selCode?"var(--blbg)":"white", color: pc.code===selCode?"var(--bl)":"var(--ink)", borderBottom:"1px solid var(--bd)" }}>
                <span style={{ fontSize:20 }}>{pc.flag}</span>
                <span style={{ fontWeight:600, minWidth:50 }}>{pc.code}</span>
                <span style={{ color:"var(--mu)" }}>{pc.country}</span>
              </div>
            ))}
          </div>
        </PickerSheet>
      )}
      {codeOpen && !isMobile && (
        <div onMouseDown={e => e.stopPropagation()} style={{ position:"absolute", top:"calc(100% + 4px)", left:0, width:260, zIndex:1200, background:"white", border:"1px solid var(--bd)", borderRadius:12, boxShadow:"0 12px 40px rgba(0,0,0,.13)", overflow:"hidden" }}>
          <div style={{padding:"8px 8px 4px"}}><input ref={codeInputRef} className="fi" value={codeQuery} onChange={e=>setCodeQuery(e.target.value)} placeholder="Search..." style={{fontSize:13,padding:"8px 10px",background:"var(--off)"}}/></div>
          <div style={{ maxHeight:200, overflowY:"auto" }}>
          {filteredCodes.map(pc => (
            <div key={pc.code+pc.country} onMouseDown={e => e.preventDefault()} onClick={() => handleCodeChange(pc)} style={{ padding:"7px 12px", fontSize:13, cursor:"pointer", display:"flex", alignItems:"center", gap:8, background: pc.code===selCode?"var(--blbg)":"white", color: pc.code===selCode?"var(--bl)":"var(--ink)" }}>
              <span style={{ fontSize:15 }}>{pc.flag}</span>
              <span style={{ fontWeight:600, minWidth:40 }}>{pc.code}</span>
              <span style={{ color:"var(--mu)", fontSize:12 }}>{pc.country}</span>
            </div>
          ))}
          </div>
        </div>
      )}
    </div>
  );
}


// Distinct, field-level messages for the three blocking ID-upload outcomes.
// English to match the rest of the (English) checkout UI.
export const ID_UPLOAD_MESSAGES = {
  too_large: "This file is too large and couldn't be reduced enough. Please upload a smaller image or a PDF.",
  unsupported: "This file type isn't supported. Please upload a JPG, PNG or PDF (iPhone HEIC photos are converted automatically — if this keeps failing, export as JPG).",
  failed: "We couldn't process this file. Please try a different photo or upload it later via the link in your confirmation email.",
};


// Connecting-flight card — collapsible accordion shown in BookingPassengers
// for oneway / roundtrip bookings (sightseeing is suppressed upstream).
// Either a single self-contained card (oneway, with direction picker) or one
// of two stacked cards (roundtrip outbound + return). Field validation is
// per-card: when the toggle is on, all six fields are required; the parent
// component gates `canProceed` on this.
function ConnectingFlightCard({ on, setOn, cf, setCfField, label, subtitle, showDirection, attempted }) {
  const FL = { fontSize:11, fontWeight:700, letterSpacing:'.04em', textTransform:'uppercase', color:'var(--mu)', marginBottom:5 };
  const REQ = { color:'#C0392B', fontWeight:700 };
  const err = (key) => attempted && on && !String(cf[key] || '').trim();
  const fSt = (key) => ({ border: err(key) ? FIELD_ERROR_BORDER : '1.5px solid transparent', background: 'var(--off)' });
  return (
    <div style={{ borderTop:'1px solid var(--bd)', padding:16 }}>
      <label style={{ display:'flex', alignItems:'flex-start', gap:10, cursor:'pointer' }}>
        <input type="checkbox" checked={on} onChange={e => setOn(e.target.checked)} style={{ accentColor:'var(--bl)', width:15, height:15, marginTop:2, flexShrink:0 }}/>
        <div>
          <div style={{ fontWeight:600, fontSize:13 }}>{label}</div>
          {subtitle && <div style={{ fontSize:11, color:'var(--mu)', marginTop:2 }}>{subtitle}</div>}
        </div>
      </label>
      {on && (
        <div style={{ marginTop:14, display:'flex', flexDirection:'column', gap:10 }}>
          {showDirection && (
            <div>
              <div style={FL}>Direction <span style={REQ}>*</span></div>
              <div style={{ display:'flex', gap:14, fontSize:13, flexWrap:'wrap' }}>
                <label style={{ display:'flex', alignItems:'center', gap:6, cursor:'pointer' }}>
                  <input type="radio" checked={cf.direction === 'inbound'} onChange={() => setCfField('direction', 'inbound')} style={{ accentColor:'var(--bl)' }}/>
                  Arriving by airplane before the helicopter departs
                </label>
                <label style={{ display:'flex', alignItems:'center', gap:6, cursor:'pointer' }}>
                  <input type="radio" checked={cf.direction === 'outbound'} onChange={() => setCfField('direction', 'outbound')} style={{ accentColor:'var(--bl)' }}/>
                  Departing by airplane after the helicopter arrives
                </label>
              </div>
            </div>
          )}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(150px, 1fr))', gap:10 }}>
            <div>
              <div style={FL}>Airline <span style={REQ}>*</span></div>
              <input className="fi" style={fSt('airline')} value={cf.airline} onChange={e => setCfField('airline', e.target.value)} placeholder="e.g. British Airways" maxLength={100}/>
            </div>
            <div>
              <div style={FL}>Flight number <span style={REQ}>*</span></div>
              <input className="fi" style={fSt('flightNumber')} value={cf.flightNumber} onChange={e => setCfField('flightNumber', e.target.value)} placeholder="e.g. BA632" maxLength={20}/>
            </div>
          </div>
          <div>
            <div style={FL}>Airport <span style={REQ}>*</span></div>
            <input className="fi" style={fSt('airport')} value={cf.airport} onChange={e => setCfField('airport', e.target.value)} placeholder="e.g. Athens (ATH) or LHR" maxLength={100}/>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(150px, 1fr))', gap:10 }}>
            <div>
              <div style={FL}>Date <span style={REQ}>*</span></div>
              <DatePicker value={cf.date} onChange={v => setCfField('date', v)} min={TODAY} max={new Date(Date.now()+2*365*24*3600*1000).toISOString().split("T")[0]} error={err('date')} placeholder="Flight date"/>
            </div>
            <div>
              <div style={FL}>Time <span style={REQ}>*</span></div>
              <TimePicker value={cf.time} onChange={v => setCfField('time', v)} date={cf.date} placeholder="Flight time"/>
              {err('time') && <FieldError>Required</FieldError>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


/* BOOKING: EXTRAS */
export function getAvailableExtras(data) {
  const depId = data?.depPad?.id;
  const arrId = data?.arrPad?.id;
  return EXTRAS.filter(e => {
    if (!e.locationIds || e.locationIds.length === 0) return false;
    return e.locationIds.includes(depId) || e.locationIds.includes(arrId);
  });
}

// P2 #9 phase 3 — snapshot-based extras total. Each selection carries
// its own price (snapshotted at toggle time), so no catalog lookup is
// needed. leg controls the multiplier: 'both' = ×2 (per-leg roundtrip,
// commit 2), 'single'/'outbound'/'return' = ×1.
export function calcExtrasTotal(selections) {
  let total = 0;
  for (const sel of Object.values(selections || {})) {
    if (!sel) continue;
    const price = Number(sel.price) || 0;
    total += sel.leg === "both" ? price * 2 : price;
  }
  return total;
}

// ── P2 #9 phase 3 — inline aircraft-card operator extras ──────────────
// Operators that can fly this aircraft (active + aircraft in their fleet).
function getOperatorsForAircraft(aircraftId, operatorFleet) {
  if (!aircraftId || !Array.isArray(operatorFleet)) return [];
  return operatorFleet.filter(o =>
    o && o.status === 'active' && Array.isArray(o.fleet) && o.fleet.includes(aircraftId)
  );
}

// Merge active extras from every operator that flies this aircraft.
// Dedup by extra id — first occurrence wins (keeps ids stable).
function getAircraftExtras(aircraftId, operatorFleet) {
  const ops = getOperatorsForAircraft(aircraftId, operatorFleet);
  const seen = new Set();
  const out = [];
  for (const op of ops) {
    // P2 #9 phase 4 — operator extra `price` is the BROKER price; the
    // customer price is broker + commission markup.
    // Phase 1b — operator extras are now stored NET (excl. VAT); VAT applies via
    // the default brokerToCustomer. MUST mirror the server (computeExtrasTotal)
    // or the booking price-validator 400s. extras.price net-migrated at go-live.
    const rate = getEffectiveCommission(op, __CMS?.SETTINGS);
    for (const e of (op.extras || [])) {
      if (!e || !e.id || e.active === false || seen.has(e.id)) continue;
      seen.add(e.id);
      const brokerPrice = Number(e.price) || 0;
      // Prefer the server-authoritative customer price (public-dto, real commission)
      // so the funnel sends exactly what bookings-create validates — no
      // extra_price_mismatch for operators whose rate != the site default. Fall
      // back to the local calc only when customerPrice is absent (legacy payload).
      const customerPrice = (typeof e.customerPrice === 'number' && e.customerPrice > 0)
        ? e.customerPrice
        : brokerToCustomer(brokerPrice, rate);
      out.push({
        id: e.id, name: e.name || e.id, description: e.description || '',
        price: customerPrice, brokerPrice,
        kind: e.kind || 'service',
        perLeg: !!e.perLeg, applies: e.applies || 'either',
        locationIds: Array.isArray(e.locationIds) ? e.locationIds : [],
        operatorId: op._id,
      });
    }
  }
  return out;
}

// canBookPilotExtra + isExtraAvailableForItinerary moved to the shared pure module
// ../lib/extras-availability.js (imported above) so the funnel and the server-side
// alternatives resolver gate extras identically (single source of truth).

// True when the customer added the optional 2nd-pilot extra (kind='pilot')
// for this aircraft. Used by the payload check to count the second pilot's
// mass. Resolves the selected extra ids → their kind via the aircraft's
// extras list. Defensive: any missing input → false.
export function secondPilotChosen(data, aircraft) {
  if (!data || !aircraft || !aircraft.id) return false;
  const sels = data.extraSelections || {};
  const ids = Object.values(sels).map(s => s && s.id).filter(Boolean);
  if (ids.length === 0) return false;
  const extras = getAircraftExtras(aircraft.id, OPERATOR_FLEET);
  return ids.some(id => { const e = extras.find(x => x.id === id); return e && e.kind === 'pilot'; });
}

// Combined visibility filter for the inline aircraft-card extras section.
export function getVisibleExtras(aircraft, paxCount, itineraryHelipadIds, operatorFleet) {
  if (!aircraft || !aircraft.id) return [];
  return getAircraftExtras(aircraft.id, operatorFleet).filter(e => {
    if (e.kind === 'pilot' && !canBookPilotExtra(aircraft, paxCount)) return false;
    if (!isExtraAvailableForItinerary(e, itineraryHelipadIds)) return false;
    return true;
  });
}

// Self-contained selection snapshot for an extra at a given leg.
export function extraSelectionSnapshot(extra, leg) {
  return {
    id: extra.id, name: extra.name, price: Number(extra.price) || 0,
    brokerPrice: Number(extra.brokerPrice) || 0,
    kind: extra.kind || 'service', perLeg: !!extra.perLeg,
    operatorId: extra.operatorId || null, leg,
  };
}

// ── P2 #9 phase 3 commit 2 — roundtrip per-leg helpers ────────────────
// City of the extra's first location that sits on this itinerary.
function extraLocationCity(extra, fromCity, toCity) {
  for (const locId of (extra.locationIds || [])) {
    const loc = LOCATION_DB.find(l => l.id === locId);
    if (loc && (loc.city === fromCity || loc.city === toCity)) return loc.city;
  }
  return null;
}

// Which legs a per-leg extra is offered on for a roundtrip. Global extras
// and applies='either' → both legs. Direction-locked (applies 'departure'
// / 'arrival') → only the matching leg(s). 'departure' = the leg the
// customer departs FROM the location; 'arrival' = the leg they arrive AT
// it. Roundtrip A→B→A: outbound departs A / arrives B, return departs B /
// arrives A.
export function getRenderableLegs(extra, fromCity, toCity) {
  if (!extra.locationIds || extra.locationIds.length === 0) return ['outbound', 'return'];
  const applies = extra.applies || 'either';
  if (applies === 'either') return ['outbound', 'return'];
  const extraCity = extraLocationCity(extra, fromCity, toCity);
  const legs = [];
  for (const leg of ['outbound', 'return']) {
    const legFrom = leg === 'outbound' ? fromCity : toCity;
    const legTo   = leg === 'outbound' ? toCity : fromCity;
    if (applies === 'departure' && extraCity === legFrom) legs.push(leg);
    if (applies === 'arrival'   && extraCity === legTo)   legs.push(leg);
  }
  return legs.length ? legs : ['outbound', 'return'];
}

// Human label for one leg of a per-leg extra.
export function getLegLabel(extra, leg, fromCity, toCity) {
  const legName = leg === 'outbound' ? 'Outbound' : 'Return';
  if (!extra.locationIds || extra.locationIds.length === 0) return legName + ' flight';
  const extraCity = extraLocationCity(extra, fromCity, toCity);
  if (!extraCity) return legName + ' flight';
  const legFrom = leg === 'outbound' ? fromCity : toCity;
  const legTo   = leg === 'outbound' ? toCity : fromCity;
  if (extraCity === legFrom) return legName + ' — departure from ' + extraCity;
  if (extraCity === legTo)   return legName + ' — arrival in ' + extraCity;
  return legName + ' flight';
}


/* BOOKING: PAYMENT */

/* BOOKING: CONFIRM */

/* FLEET PAGE */

/* AIRCRAFT DETAIL PAGE */


/* DESTINATIONS PAGE */
export const FilterPill = ({ label, active, onClick }) => (
  <button onClick={onClick} style={{ padding:"7px 16px", borderRadius:100, fontSize:13, fontWeight:600, cursor:"pointer", transition:"all .15s", border:"1.5px solid " + (active ? "var(--bl)" : "var(--bd)"), background: active ? "var(--blbg)" : "white", color: active ? "var(--bl)" : "var(--mu)", textTransform:"capitalize" }}>
    {label}
  </button>
);


// Allowlist-based HTML sanitizer for CMS-authored content (blog articles,
// prohibited-items policy, etc.). Runs via DOMPurify — replaces a previous
// regex-based blocklist that had known bypass vectors (attribute-quoting
// tricks, <svg onload>, <object>, etc.). See SECURITY.md / audit B-A5.
const SANITIZE_CONFIG = {
  ALLOWED_TAGS: ['p','a','strong','em','b','i','ul','ol','li','h2','h3','h4','img','blockquote','code','pre','br','hr','span','div'],
  ALLOWED_ATTR: ['href','src','alt','title','target','rel','style'],
  ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):|\/|#)/i,
};
// Module-cached lazy loader (2026-05-19d) — keeps DOMPurify (~11KB)
// out of the homepage critical chain. First call triggers a dynamic
// import; subsequent calls reuse the cached promise/module.
let _dompurify = null;
let _dompurifyPromise = null;
function getDOMPurify() {
  if (_dompurify) return Promise.resolve(_dompurify);
  if (!_dompurifyPromise) {
    _dompurifyPromise = import("isomorphic-dompurify").then(m => {
      _dompurify = m.default;
      return _dompurify;
    });
  }
  return _dompurifyPromise;
}
// Renders sanitized HTML from CMS-authored content. While DOMPurify is
// still loading, renders an empty placeholder; once loaded, sanitizes
// the input and swaps in. Only mounts on /blog/* + /prohibited-items.
function SafeHtml({ html, style }) {
  const [clean, setClean] = useState("");
  useEffect(() => {
    let mounted = true;
    if (!html) { setClean(""); return; }
    getDOMPurify().then(dp => {
      if (mounted) setClean(dp.sanitize(html, SANITIZE_CONFIG));
    });
    return () => { mounted = false; };
  }, [html]);
  return <div style={style} dangerouslySetInnerHTML={{ __html: clean }} />;
}

// safeMdLink + MarkdownBody moved to src/lib/markdown-tokenizer.js +
// markdown-body.jsx (2026-06-15, for unit-testability). safeMdLink is
// imported at the top of this file; still used by mdToHtml() below.
// B-M1 audit (safe link schemes only) preserved verbatim in that module.

function mdToHtml(md) {
  if (!md) return "";
  return md
    .replace(/^### (.+)$/gm, '<h3 style="font-weight:700;font-size:18px;color:var(--ink);margin:28px 0 10px">$1</h3>')
    .replace(/^## (.+)$/gm, '<h2 style="font-weight:800;font-size:20px;color:var(--ink);margin:32px 0 12px;letter-spacing:-.02em">$1</h2>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_, text, url) => '<a href="' + safeMdLink(url) + '" style="color:var(--bl);font-weight:600" target="_blank" rel="noopener">' + text + '</a>')
    .replace(/^- (.+)$/gm, '<li style="margin-bottom:6px">$1</li>')
    .replace(/(<li[^>]*>.*<\/li>\n?)+/g, (m) => '<ul style="margin:12px 0 20px;padding-left:20px">' + m + '</ul>')
    .replace(/^(?!<[hul])(.*\S.*)$/gm, '<p class="body-p" style="margin-bottom:18px">$1</p>');
}

/* SAFETY PAGE */

/* ABOUT PAGE */
/* CONTACT PAGE */
// Cloudflare Turnstile — invisible bot protection
export function TurnstileWidget({ onToken }) {
  const ref = useRef(null);
  const widgetId = useRef(null);
  useEffect(() => {
    if (!TURNSTILE_KEY || !ref.current) return;
    const render = () => {
      if (widgetId.current != null) return;
      widgetId.current = window.turnstile.render(ref.current, {
        sitekey: TURNSTILE_KEY,
        callback: (token) => onToken(token),
        "error-callback": () => onToken(null),
        "expired-callback": () => { onToken(null); if (widgetId.current != null) window.turnstile.reset(widgetId.current); },
        size: "flexible",
        // The site has no dark mode — pin the widget to light so it never
        // follows the visitor's OS prefers-color-scheme (rendered black on our
        // light page). language:'en' pins it to the (English) site instead of
        // the browser locale (it was showing German "Erfolg!" to EN visitors).
        theme: "light",
        language: "en",
      });
    };
    if (window.turnstile) { render(); return; }
    if (!document.getElementById("turnstile-script")) {
      const s = document.createElement("script");
      s.id = "turnstile-script";
      s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      s.async = true;
      s.onload = render;
      document.head.appendChild(s);
    } else {
      const check = setInterval(() => { if (window.turnstile) { clearInterval(check); render(); } }, 100);
      return () => clearInterval(check);
    }
  }, []);
  if (!TURNSTILE_KEY) return null;
  return <div ref={ref}/>;
}



/* FOOTER */
export function Footer({ setPage, openBooking, reopenCookies }) {
  const go = (p) => { setPage(p); document.getElementById("top")?.scrollIntoView(); };

  // Popular routes — dynamic top 4 by recency-weighted booking score, with
  // hardcoded fallback when API has no signal yet (fresh deploys, etc.).
  const [topRouteSlugs, setTopRouteSlugs] = useState(null);
  useEffect(() => {
    loadRouteScores().then(scores => {
      const ranked = [...scores].sort((a, b) => b.score - a.score).filter(s => s.score > 0).slice(0, 4).map(s => s.slug);
      if (ranked.length >= 2) setTopRouteSlugs(ranked);
    }).catch(() => {});
  }, []);
  const popularRouteLinks = (topRouteSlugs || ['athens-mykonos', 'athens-santorini', 'athens-paros', 'mykonos-santorini'])
    .map(slug => {
      const r = SEO_ROUTES.find(x => x.slug === slug);
      const label = r ? r.from + ' → ' + r.to : slug.replace(/-/g, ' ');
      return { l: label, p: 'route:' + slug };
    });
  popularRouteLinks.push({ l: 'All routes', p: 'routes' });

  return (
    <footer>
      {/* CTA strip */}
      <div className="f-cta">
        <div className="wrap" style={{ display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:24 }}>
          <div>
            <div style={{ fontFamily:"var(--serif)", fontStyle:"italic", fontSize:"clamp(22px,3vw,32px)", color:"white", lineHeight:1.2, marginBottom:6 }}>{HP.ctaTitle}</div>
            <p style={{ fontSize:14, color:"rgba(255,255,255,.92)", maxWidth:420, lineHeight:1.7 }}>{HP.ctaSub}</p>
          </div>
          <div style={{ display:"flex", gap:12, flexWrap:"wrap" }}>
            <button className="btn btn-bl btn-lg" onClick={() => openBooking()} style={{ whiteSpace:"nowrap" }}>Request a flight <ArrowRight size={16}/></button>
            <button className="btn btn-lg" style={{ background:"rgba(255,255,255,.08)", color:"white", border:"1.5px solid rgba(255,255,255,.12)", whiteSpace:"nowrap" }} onClick={() => go("contact")}>
              Contact us
            </button>
          </div>
        </div>
      </div>

      {/* Main footer */}
      <div className="f-main">
        <div className="wrap">
          <div className="fg">
            {/* Brand column */}
            <div>
              <div className="flogo" dangerouslySetInnerHTML={{ __html: fullLogoSvgOnDark({ height: 36 }) }} />
              <p className="fdesc">Helicopter charter platform across Greece. Connecting you with EASA-certified operators for private transfers, island hopping, and VIP travel.</p>
              <div className="f-contact">
                <a href={"mailto:" + CONTACT_EMAIL}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M22 7l-10 7L2 7"/></svg>
                  {CONTACT_EMAIL}
                </a>
              </div>
              <div className="fsocs">
                {[["instagram","Instagram",<Instagram size={14}/>,__CMS?.SETTINGS?.socialLinks?.instagram],["facebook","Facebook",<Facebook size={14}/>,__CMS?.SETTINGS?.socialLinks?.facebook],["linkedin","LinkedIn",<Linkedin size={14}/>,__CMS?.SETTINGS?.socialLinks?.linkedin]].filter(([,,,url]) => url).map(([name,label,icon,url]) => (
                  <a key={name} href={url} target="_blank" rel="noopener noreferrer" className="fsoc" aria-label={label} style={{ color:"inherit", textDecoration:"none" }}>{icon}</a>
                ))}
              </div>
              {/* Newsletter — compact 1-line form. Same NewsletterForm as the
                  homepage section (live success/duplicate/error states). */}
              <div style={{ marginTop:20 }}>
                <div style={{ fontSize:11, fontWeight:700, letterSpacing:".08em", textTransform:"uppercase", color:"rgba(255,255,255,.65)", marginBottom:10 }}>Stay connected</div>
                <NewsletterForm source="footer"/>
              </div>
            </div>

            {/* Link columns */}
            {[
              { h:"Services", links:[
                { l:"Island Transfers",    p:"charter-island" },
                { l:"Yacht Transfers",     p:"charter-yacht" },
                { l:"Sightseeing Flights", p:"charter-sightseeing" },
                { l:"VIP & Corporate",     p:"charter-vip" },
                { l:"Airport Connections", p:"charter-airport" },
                { l:"Custom Itineraries",  p:"charter-custom" },
              ]},
              { h:"Destinations", links:[
                { l:"Mykonos",    p:"dest:mykonos" },
                { l:"Santorini",  p:"dest:santorini" },
                { l:"Paros",      p:"dest:paros" },
                { l:"Corfu",      p:"dest:corfu" },
                { l:"Rhodes",     p:"dest:rhodes" },
                { l:"All destinations", p:"destinations" },
                { l:"Hotel transfers", p:"hotels" },
              ]},
              { h:"Popular Routes", links: popularRouteLinks },
              { h:"Company", links:[
                { l:"How it works",   p:"how-it-works" },
                { l:"About us",      p:"about" },
                { l:"Our Fleet",     p:"fleet" },
                { l:"Safety",        p:"safety" },
                { l:"Journal",       p:"blog" },
                { l:"Contact",       p:"contact" },
              ]},
            ].map(col => (
              <div key={col.h}>
                <div className="fch">{col.h}</div>
                <div className="flinks">
                  {col.links.map(l => (
                    <button key={l.l} className="flink" onClick={() => go(l.p)}>{l.l}</button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Trust strip */}
          <div className="f-trust">
            <div className="f-trust-badge">
              <Shield size={15}/> EASA-certified operators only
            </div>
            <div className="f-trust-badge">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
              Full insurance on every flight
            </div>
            <div className="f-trust-badge">
              <Clock size={15}/> Same-day booking response
            </div>
            <div className="f-trust-badge">
              <CreditCard size={15}/> No hidden fees — transparent pricing
            </div>
          </div>

          {/* Bottom bar */}
          <div className="fbot">
            <div className="fcopy">&copy; {new Date().getFullYear()} {BRAND.name} &mdash; {__CMS?.SETTINGS?.footerText || 'Astraios Aviation SMPC \u2014 Athens, Greece'}</div>
            <div className="fbl">
              {[{l:"Privacy",p:"privacy"},{l:"Terms",p:"terms"},{l:"Prohibited Items",p:"prohibited"},{l:"Cookies",p:"cookies"}].map(({l,p}) => (
                <button key={l} className="fbll" onClick={() => go(p)}>{l}</button>
              ))}
              <button className="fbll" onClick={reopenCookies}>Cookie settings</button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

// MarkdownBody (the shared legal-page renderer) now lives in
// src/lib/markdown-body.jsx (imported at top). Parsing is split into the
// pure, unit-tested tokenizer in src/lib/markdown-tokenizer.js.

// CMS sections renderer for page documents (Safety, About, etc.)
export function CMSSections({ sections }) {
  if (!sections?.length) return null;
  return sections.map((s, i) => (
    <section key={i} style={{ padding:"56px 0", borderBottom: i < sections.length-1 ? "1px solid var(--bd)" : "none" }}>
      <div className="wrap">
        {s.eyebrow && <div className="eyebrow" style={{ marginBottom:10 }}>{s.eyebrow}</div>}
        {s.title && <h2 style={{ fontWeight:800, fontSize:"clamp(22px,3vw,32px)", letterSpacing:"-.04em", marginBottom:16 }}>{s.title}</h2>}
        {s.body && <div style={{ maxWidth:720, fontSize:15, lineHeight:1.85, color:"var(--mu)" }}><MarkdownBody text={s.body}/></div>}
        {s.items?.length > 0 && (
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(250px, 1fr))", gap:20, marginTop:24 }}>
            {s.items.map((item, j) => (
              <div key={j} style={{ padding:"20px 24px", background:"var(--off)", borderRadius:14, border:"1px solid var(--bd)" }}>
                {item.icon && <div style={{ fontSize:24, marginBottom:8 }}>{renderIcon(item.icon, 24, "var(--bl)", item.iconImage)}</div>}
                {item.value && <div style={{ fontWeight:800, fontSize:28, letterSpacing:"-.03em", color:"var(--bl)", marginBottom:4 }}>{item.value}</div>}
                {item.title && <div style={{ fontWeight:700, fontSize:14, marginBottom:4 }}>{item.title}</div>}
                {item.body && <div style={{ fontSize:13, color:"var(--mu)", lineHeight:1.6 }}>{item.body}</div>}
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  ));
}

// -- HOW IT WORKS PAGE ------------------------------------------------------
// Real process explanation (not a homepage placeholder). The copy is a fixed
// product-string set; only the hero image stays CMS-overridable for flexibility.


// ProhibitedItemsContent moved to Sanity CMS (legalPage slug "prohibited",
// 2026-06-15). Rendered via MarkdownBody on /prohibited (LegalPages.jsx) and
// in the checkout LegalModal \u2014 same CMS source as terms/privacy/cookies.

/* APP ROOT */
/* BOOKING: CUSTOM QUOTE */
/* PAYMENT RETURN — shown after Mollie redirect back */



/* BOOKING ROUTE STEP (overlay step 1) */

/* BOOKING OVERLAY */
const BOOKING_PAGES = new Set(["route","helipads","search","booking-passengers","booking-extras","booking-payment","booking-confirm","booking-custom-quote"]);

// -- BOOKING SUMMARY SIDEBAR --------------------------------------------------


// TermsContent + PrivacyContent moved to Sanity CMS (legalPage slugs
// "terms" / "privacy", 2026-06-15). The checkout LegalModal now renders
// __CMS.LEGAL[type].bodyMarkdown via MarkdownBody — single CMS source for
// all three legal modals (terms / privacy / prohibited).


// -- SEO ROUTE PAIRS -------------------------------------------------------
// SEO_ROUTES declared near other data arrays, populated from CMS

// -- SEO CONTENT STORAGE (persisted in Sanity) ------------------------------
// Legacy generator docs bake a literal "from EUR ∞" into their prose (metaDesc /
// faqs / keyFacts). Resolve the SAME real minimum the page's price surfaces
// already use (route.cardFromPrice / Athens→dest / Athens→hotel-city mins) and
// sanitize the doc before it enters the cache, so SPA meta (useSEO), visible
// FAQ answers and key facts never render "∞". Read-only display guard.
const _seoPriceMinFor = (type, slug) => {
  try {
    if (type === "route") return routeFromPriceMin((SEO_ROUTES.find(r => r.slug === slug) || {}).cardFromPrice);
    if (type === "dest") {
      const d = DESTS.find(x => (x.name || "").toLowerCase().replace(/\s+/g, "-") === slug);
      return d ? getDestFromAthensMin(d.name) : null;
    }
    if (type === "hotel") {
      const h = HOTELS.find(x => x.id === slug);
      return (h && h.city) ? routesFromPriceMin(SEO_ROUTES.filter(r => r.from === "Athens" && r.to === h.city)) : null;
    }
  } catch { /* CMS globals not ready — sentence-drop fallback */ }
  return null;
};
const _seoCache = {};
export const loadSEO = async (type, slug) => {
  const key = type + ":" + slug;
  if (_seoCache[key]) return _seoCache[key];
  try {
    const r = await fetch("/api/seo/load?type=" + encodeURIComponent(type) + "&slug=" + encodeURIComponent(slug));
    if (!r.ok) return null;
    const raw = await r.json();
    const data = raw ? sanitizeSeoContentDoc(raw, _seoPriceMinFor(type, slug)) : raw;
    if (data) _seoCache[key] = data;
    return data;
  } catch { return null; }
};

// -- POPULARITY TRACKING (live from Sanity bookings via /api/popularity)
//
// 2026-05-20 — Vercel cron-audit follow-up. The previous in-memory cache
// (5-min TTL) deduplicated repeat calls AFTER a response settled, but
// roughly 4-9 components useEffect-mount roughly simultaneously and all
// see `_popCache === null` before the first response returns — producing
// the burst pattern observed in Vercel logs (4 hits in the same second).
// Added an in-flight Promise pointer so concurrent callers await the
// same fetch; only the first triggers a network round-trip.
let _popCache = null;
let _popCacheTime = 0;
let _popInflight = null;
const _loadPopularity = async () => {
  const now = Date.now();
  if (_popCache && (now - _popCacheTime) < 5 * 60 * 1000) return _popCache;
  if (_popInflight) return _popInflight;
  _popInflight = (async () => {
    try {
      const res = await fetch('/api/popularity');
      if (!res.ok) throw new Error('API error');
      _popCache = await res.json();
      _popCacheTime = Date.now();
      return _popCache;
    } catch {
      return { destinations: [], routes: [], aircraft: [] };
    } finally {
      _popInflight = null;
    }
  })();
  return _popInflight;
};
export const loadRouteScores = async () => {
  const pop = await _loadPopularity();
  const scoreMap = {};
  (pop.routes || []).forEach(r => { scoreMap[r.slug] = r.score; });
  return SEO_ROUTES.map(r => ({ slug: r.slug, score: scoreMap[r.slug] || 0 }));
};
export const loadDestScores = async () => {
  const pop = await _loadPopularity();
  const scoreMap = {};
  (pop.destinations || []).forEach(d => { scoreMap[d.city] = d.score; });
  return DESTS.filter(d => d.name !== "Athens").map(d => ({ city: d.name, score: scoreMap[d.name] || 0 }));
};
export const loadAcScores = async (from, to) => {
  const pop = await _loadPopularity();
  const scoreMap = {};
  if (from && to) {
    // Route-specific scores ONLY (directional city pair). The old silent
    // fallback to GLOBAL scores made a booking on a DIFFERENT route surface
    // as "most booked on this route" (owner bug 2026-07-07, Athens->Mykonos
    // data shown on Athens->Elafonissos). No route data => all zeros.
    (pop.routeAircraft || []).filter(a => a.from === from && a.to === to).forEach(a => { scoreMap[a.id] = { score: a.score, count: a.count || 0 }; });
  } else {
    (pop.aircraft || []).forEach(a => { scoreMap[a.id] = { score: a.score, count: a.count || 0 }; });
  }
  return FLEET.map(f => ({ id: f.id, score: scoreMap[f.id]?.score || 0, count: scoreMap[f.id]?.count || 0 }));
};


// -- SAMPLE SEO CONTENT (preloaded so pages render without API call) --------
const SAMPLE_DEST = {};
const SAMPLE_ROUTE = {};

// Patch loadSEO to fall back to sample data

// -- CHARTER SERVICE PAGES --------------------------------------------------
// Content moved to src/lib/charter-services-fallback.js (2026-05-19j) so
// Astro SSR (resolveEntity in src/lib/jsonld.js) and the React client share
// a single source of truth. Spread into a mutable `let` so the __CMS override
// below can still replace it wholesale when Sanity ships a populated map.
export let CHARTER_SERVICES = { ...CHARTER_SERVICES_FALLBACK };
// CMS override: charter services
if (__CMS && Object.keys(__CMS.CHARTER_SERVICES).length) CHARTER_SERVICES = __CMS.CHARTER_SERVICES;


// -- ALL ROUTES PAGE --------------------------------------------------------
export const Pill = ({ label, active, onClick }) => (
  <button onClick={onClick} style={{ padding:"6px 16px", borderRadius:100, fontSize:13, fontWeight:600, cursor:"pointer", transition:"all .15s", border:"1.5px solid "+(active?"white":"rgba(255,255,255,.35)"), background:active?"white":"transparent", color:active?"var(--bl)":"rgba(255,255,255,.92)" }}>
    {label}
  </button>
);


// -- HOTELS PAGE ------------------------------------------------------------

// -- HOTEL MAP -- shows helipad pin(s) + hotel pin --------------------------

function HotelMap({ hotel, pads, primaryIds }) {
  const mapRef = useRef(null);
  const mapInst = useRef(null);
  const near = useNearViewport(mapRef);

  // Defer Leaflet init + carto basemap tiles until the map nears the viewport
  // (eager on mount before this; map sits below the fold on hotel pages).
  useEffect(() => {
    if (!near) return;
    loadLeaflet().then(L => {
      if (!mapRef.current || mapInst.current) return;
      const allPts = [[hotel.lat, hotel.lng], ...(pads || []).map(p => [p.lat, p.lng])];
      const bounds = L.latLngBounds(allPts);
      const map = L.map(mapRef.current, { center: bounds.getCenter(), zoom: 13, zoomControl: false, attributionControl: false });
      L.control.zoom({ position: "topright" }).addTo(map);
      attachMapLayers(map, L);
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 14 });

      // Hotel pin — pulsing dot with permanent pill label until first zoom (Track 4F)
      const hotelMarker = L.marker([hotel.lat, hotel.lng], { icon: hotelIcon(L), zIndexOffset: 100 }).addTo(map);
      hotelMarker.bindTooltip(hotel.name, { direction: "right", permanent: true, offset: [25, 0], className: "hotel-pin-label" });
      hotelMarker.getElement()?.setAttribute("aria-label", hotel.name);
      let labelHidden = false;
      map.on('zoomstart', () => {
        if (labelHidden) return;
        hotelMarker.unbindTooltip();
        hotelMarker.bindTooltip(hotel.name, { direction: "top", offset: [0, -28] });
        labelHidden = true;
      });

      // Helipad / airport pins — primary green, context gray (Track 4A)
      const hasPrimary = (id) => primaryIds ? (primaryIds.has ? primaryIds.has(id) : primaryIds.includes(id)) : false;
      const direct = isDirectHelipad(hotel);
      (pads || []).forEach(p => {
        const isPrimary = hasPrimary(p.id);
        const col = isPrimary ? "#059669" : "#797672";
        const icon = p.type === "airport" ? airportIcon(L, false, col) : helipadIcon(L, false, col);
        let tip = p.name;
        if (isPrimary) {
          if (direct) tip += " (direct)";
          else if (hotel.transferMin) tip += " (~" + hotel.transferMin + " min transfer)";
          else tip += " (primary)";
        }
        const pm = L.marker([p.lat, p.lng], { icon }).bindTooltip(tip, { direction: "top", offset: [0, -14] }).addTo(map);
        pm.getElement()?.setAttribute("aria-label", tip);
      });

      mapInst.current = map;
      [100, 300, 600, 1200].forEach(ms => setTimeout(() => { if (mapInst.current) mapInst.current.invalidateSize(); }, ms));
    });
    return () => { if (mapInst.current) { mapInst.current.remove(); mapInst.current = null; } };
  }, [near]);

  return (
    <div style={{ borderRadius:16, overflow:"hidden", border:"1px solid var(--bd)", height:300 }}>
      <div ref={mapRef} style={{ height:300, minHeight:300, width:"100%" }}/>
    </div>
  );
}


// -- SIGHTSEEING TOUR DETAIL PAGE -------------------------------------------

// -- HOTEL DETAIL PAGE ------------------------------------------------------
function HotelPage({ hotelId, setPage, openBooking }) {
  const h = HOTELS.find(x => x.id === hotelId);
  if (!h) return null;

  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [routeScoreMap, setRouteScoreMap] = useState({});

  useEffect(() => {
    loadSEO("hotel", hotelId).then(c => { setContent(c); setLoading(false); });
  }, [hotelId]);

  useEffect(() => {
    loadRouteScores().then(scores => {
      const m = {}; scores.forEach(s => { m[s.slug] = s.score; });
      setRouteScoreMap(m);
    }).catch(() => {});
  }, []);

  const dest     = DESTS.find(d => d.name === h.city);
  // Hotel's curated primary helipads (from Sanity ref array) — city-agnostic:
  // Amanzoe in Kranidi references Porto-Heli's helipad; old city-match filter missed it.
  const hotelPads = customerVisiblePads(LOCATION_DB.filter(p => (h.helipads || []).includes(p.id)));
  // Top 5 nearest enabled pads with GPS, for the map — context + primary mix (Track 4A).
  // Gated on the CANONICAL served flag (hasServingActiveOperator), the SAME predicate
  // customerVisiblePads uses for the pad LIST — so the HotelMap markers never pin an
  // unserved pad (e.g. ZZZZHEL) the list already hides. hasActiveOperator (operatorIds
  // ∩ active) over-counted unserved bases and slipped them onto the map.
  const nearestPads = (h.lat != null && h.lng != null)
    ? LOCATION_DB
        .filter(p => p.enabled !== false && p.hasServingActiveOperator && p.lat != null && p.lng != null)
        .map(p => ({ ...p, _dist: haversineKm(p, h) }))
        .filter(p => p._dist != null)
        .sort((a, b) => a._dist - b._dist)
        .slice(0, 5)
    : [];
  const primaryPadIds = new Set(h.helipads || []);
  const destRoute = SEO_ROUTES
    .filter(r => r.to === h.city)
    .slice()
    .sort((a, b) => (routeScoreMap[b.slug] || 0) - (routeScoreMap[a.slug] || 0))
    .slice(0, 3);
  const nearby    = HOTELS.filter(x => x.city === h.city && x.id !== h.id).slice(0,3);
  const direct    = isDirectHelipad(h);
  const c = content || {};

  return (
    <div style={{ paddingTop:"var(--nav)", background:"white" }}>
      {/* Hero */}
      <div className="ph-hero-rich">
        <div style={{ position:"absolute", inset:0 }}>
          {/* Sanity-first — hotel doc images win over seoContent (2026-05-19c). */}
          <Img preset="hero" src={h.heroImg || h.img || c.heroImage} alt={h.name + " — helicopter transfer arrival view"} style={{ width:"100%", height:"100%", objectFit:"cover" }}/>
          <div style={{ position:"absolute", inset:0, background:"linear-gradient(to top, rgba(5,20,50,.88) 0%, rgba(5,20,50,.5) 45%, rgba(5,20,50,.3) 100%)" }}/>
        </div>
        <div className="wrap" style={{ position:"relative", padding:"0 36px 48px", width:"100%", boxSizing:"border-box" }}>
          {/* Breadcrumb */}
          <div style={{ display:"flex", gap:6, alignItems:"center", marginBottom:20, fontSize:12, color:"rgba(255,255,255,.65)" }}>
            <span onClick={() => setPage("home")} style={{ cursor:"pointer" }}>Home</span>
            <ChevronDown size={10} style={{ transform:"rotate(-90deg)" }}/>
            <span onClick={() => setPage("hotels")} style={{ cursor:"pointer" }}>Hotels</span>
            <ChevronDown size={10} style={{ transform:"rotate(-90deg)" }}/>
            <span onClick={() => setPage("hotels")} style={{ cursor:"pointer" }}>{h.city}</span>
            <ChevronDown size={10} style={{ transform:"rotate(-90deg)" }}/>
            <span style={{ color:"white" }}>{h.name}</span>
          </div>

          <div className="eyebrow" style={{ color:"rgba(255,255,255,.7)", marginBottom:10 }}>{h.city} - Hotels</div>
          <div style={{ display:"flex", gap:10, marginBottom:14, flexWrap:"wrap" }}>
            <span style={{ background:direct?"var(--gr)":"rgba(255,255,255,.2)", color:"white", fontSize:12, fontWeight:700, padding:"4px 12px", borderRadius:100 }}>
              {direct ? <><svg width="11" height="11" viewBox="0 0 11 11" fill="none" style={{display:"inline-block",verticalAlign:"middle"}}><circle cx="5.5" cy="5.5" r="4.4" stroke="white" strokeWidth="1.2"/><line x1="3.7" y1="3.6" x2="3.7" y2="7.4" stroke="white" strokeWidth="1.3" strokeLinecap="round"/><line x1="7.3" y1="3.6" x2="7.3" y2="7.4" stroke="white" strokeWidth="1.3" strokeLinecap="round"/><line x1="3.7" y1="5.5" x2="7.3" y2="5.5" stroke="white" strokeWidth="1.3" strokeLinecap="round"/></svg> Direct helipad</> : <><svg width="11" height="11" viewBox="0 0 24 24" fill="white" style={{display:"inline-block",verticalAlign:"middle"}}><path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.85 7h10.29l1.08 3.11H5.77L6.85 7zM19 17H5v-5h14v5z"/><circle cx="7.5" cy="14.5" r="1.5"/><circle cx="16.5" cy="14.5" r="1.5"/></svg> {h.transferMin ? h.transferMin + " min" : "Transfer"}</>} </span>
            <span style={{ background:"rgba(255,255,255,.15)", color:"white", fontSize:12, fontWeight:600, padding:"4px 12px", borderRadius:100 }}>{h.cat}</span>
            <span style={{ background:"rgba(255,255,255,.15)", color:"white", fontSize:12, fontWeight:600, padding:"4px 12px", borderRadius:100 }}>{"★".repeat(Math.min(h.stars,5))}</span>
          </div>

          <h2 style={{ fontFamily:"var(--sans)", fontWeight:800, fontSize:"clamp(30px,5vw,54px)", letterSpacing:"-.05em", color:"white", lineHeight:1.05, marginBottom:16, maxWidth:680 }}>
            {h.name}
          </h2>
          <p style={{ fontSize:16, color:"rgba(255,255,255,.7)", maxWidth:520, lineHeight:1.7, marginBottom:28 }}>
            {[h.city, h.rooms, direct ? "Helicopter transfer direct to property" : (h.transferMin ? "Helicopter transfer in " + h.transferMin + " min" : "Helicopter transfer available")].filter(Boolean).join(" - ")}
          </p>
          <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
            <button className="btn btn-lg" style={{ background:"white", color:"var(--bl)", fontWeight:700 }} onClick={() => openBooking({ to: h.city })}>
              Book helicopter to {h.name} <ArrowRight size={16}/>
            </button>
          </div>
        </div>
      </div>

      {/* Stats strip */}
      <div style={{ background:"var(--bl)" }}>
        <div className="wrap rpad">
          <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)" }} className="ac-grid stats-strip-4">
            {(() => {
              const athensHub = HUBS.find(hub => hub.name === "Athens");
              const fromAthensLabel = (() => {
                if (dest?.time === "Hub") return "Hub";
                if (athensHub && h.lat != null && h.lng != null) {
                  const label = flightTimeLabel({ lat: athensHub.lat, lng: athensHub.lng }, { lat: h.lat, lng: h.lng });
                  if (label) return label;
                }
                return dest?.time || "—";
              })();
              return [
                ["Category", h.cat || "—"],
                ["Rooms", h.rooms || "—"],
                ["Transfer", direct ? "Direct helipad" : (h.transferMin ? h.transferMin + " min from helipad" : "Helicopter transfer")],
                ["From Athens", fromAthensLabel],
              ];
            })().map(([l,v],i) => (
              <div key={l} style={{ padding:"16px 20px", textAlign:"center", borderRight:i<3?"1px solid rgba(255,255,255,.1)":"none" }}>
                <div style={{ fontSize:10, color:"rgba(255,255,255,.85)", textTransform:"uppercase", letterSpacing:".1em", marginBottom:5 }}>{l}</div>
                <div style={{ fontWeight:700, fontSize:16, color:"white" }}>{v}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="wrap rpad" style={{ paddingTop:44 }}>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 300px", gap:52, alignItems:"start" }} className="bk-main-grid">

          {/* Content */}
          <div>
            {/* About */}
            <div style={{ paddingBottom:44 }}>
              <div className="eyebrow" style={{ marginBottom:10 }}>About the hotel</div>
              <h2 style={{ fontWeight:800, fontSize:21, letterSpacing:"-.04em", marginBottom:16 }}>Why stay at {h.name}?</h2>
              <p className="body-p" style={{ marginBottom:20 }}>{c.intro || h.desc}</p>
              {c.whyChoose && <p className="body-p" style={{ marginBottom:16 }}>{c.whyChoose}</p>} <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                {(h.notable || '').split(" - ").filter(Boolean).map(tag => (
                  <span key={tag} style={{ fontSize:13, fontWeight:600, color:"var(--mu)", background:"var(--off)", padding:"5px 12px", borderRadius:8, border:"1px solid var(--bd)" }}>{tag}</span>
                ))}
              </div>
            </div>

            {/* Helipad / transfer info */}
            <div style={{ padding:"44px 0", borderTop:"1px solid var(--bd)" }}>
              <div className="eyebrow" style={{ marginBottom:10 }}>Helicopter transfer</div>
              <h2 style={{ fontWeight:800, fontSize:21, letterSpacing:"-.04em", marginBottom:16 }}>
                {direct ? "Direct helipad on property" : "Helipad transfer to " + h.name}
              </h2>
              <div style={{ padding:24, borderRadius:16, border:"2px solid " + (direct?"var(--gr)":"var(--blmid)"), background:direct?"var(--grbg)":"var(--blbg)", marginBottom:20, display:"flex", gap:14, alignItems:"flex-start" }}>
                <span style={{ flexShrink:0, color:direct?"var(--gr)":"var(--bl)", display:"flex", alignItems:"center", marginTop:2 }}>
                  {direct
                    ? <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8"/><line x1="8.5" y1="8.5" x2="8.5" y2="15.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/><line x1="15.5" y1="8.5" x2="15.5" y2="15.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/><line x1="8.5" y1="12" x2="15.5" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
                    : <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.85 7h10.29l1.08 3.11H5.77L6.85 7zM19 17H5v-5h14v5z" fill="currentColor"/><circle cx="7.5" cy="14.5" r="1.5" fill="currentColor"/><circle cx="16.5" cy="14.5" r="1.5" fill="currentColor"/></svg>
                  }
                </span>
                <p style={{ fontSize:14, color:"var(--mu)", lineHeight:1.75, margin:0, flex:1 }}>
                  {c.transferDetail || (direct
                    ? "Upon arrival at " + h.name + ", your helicopter lands directly on the property's private helipad -- no ground transfer, no waiting. Our team coordinates directly with the hotel's helipad team for a seamless arrival."
                    : (h.transferMin
                        ? "Upon arrival at " + h.name + ", your helicopter lands at the nearest approved helipad and a private transfer vehicle takes you to the property in approximately " + h.transferMin + " minutes. We arrange the full journey end-to-end."
                        : "Upon arrival at " + h.name + ", your helicopter lands at the nearest approved helipad and a private transfer vehicle takes you to the property. We arrange the full journey end-to-end.")
                  )}
                </p>
              </div>

              {/* Map showing helipad + hotel */}
              {h.lat && (
                <div style={{ marginBottom:20 }}>
                  <HotelMap hotel={h} pads={nearestPads} primaryIds={primaryPadIds}/>
                </div>
              )}

              {/* Landing pads */}
              {hotelPads.length > 0 && (
                <>
                  <h3 style={{ fontWeight:700, fontSize:15, marginBottom:12 }}>Landing location{hotelPads.length>1?"s":""}</h3>
                  <div style={{ display:"flex", flexDirection:"column", gap:8, marginBottom:20 }}>
                    {hotelPads.map(p => (
                      <div key={p.id} style={{ display:"flex", gap:14, padding:"14px 16px", background:"var(--off)", borderRadius:12, border:"1px solid var(--bd)" }}>
                        <span style={{ color:"var(--bl)", display:"flex", alignItems:"center" }}>{p.type==="airport"?<svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M21 16v-2l-8-5V3.5C13 2.67 12.33 2 11.5 2S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z" fill="currentColor"/></svg>:<svg width="18" height="18" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8"/><line x1="8.5" y1="8.5" x2="8.5" y2="15.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/><line x1="15.5" y1="8.5" x2="15.5" y2="15.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/><line x1="8.5" y1="12" x2="15.5" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>}</span>
                        <div>
                          <div style={{ fontWeight:700, fontSize:14 }}>{p.name}{p.iata&&<span style={{ fontSize:11, color:"var(--bl)", fontWeight:700, marginLeft:8, background:"var(--blbg)", padding:"1px 5px", borderRadius:4 }}>{p.iata}</span>}</div>
                          <div style={{ fontSize:12, color:"var(--mu)", marginTop:2 }}>{p.addr}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Flight times from hubs (Phase 3C + Track 4E distance column) */}
            {(() => {
              if (!HUBS.length || h.lat == null || h.lng == null) return null;
              const target = { lat: h.lat, lng: h.lng };
              const nearbyHubs = HUBS
                .map(hub => ({ ...hub, _dist: haversineKm({ lat: hub.lat, lng: hub.lng }, target) }))
                .filter(hub => hub._dist != null && hub._dist >= 10);
              if (nearbyHubs.length === 0) return null;
              return (
                <div style={{ padding:"44px 0", borderTop:"1px solid var(--bd)" }}>
                  <div className="eyebrow" style={{ marginBottom:10 }}>Helicopter access</div>
                  <h2 style={{ fontWeight:800, fontSize:"clamp(18px,2.5vw,22px)", letterSpacing:"-.04em", marginBottom:16 }}>
                    Flight times from hubs
                  </h2>
                  <div style={{ borderRadius:16, overflow:"hidden", border:"1px solid var(--bd)" }}>
                    <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
                      <thead>
                        <tr style={{ background:"var(--off)" }}>
                          <th scope="col" style={{ padding:"12px 14px", textAlign:"left", fontWeight:700, fontSize:12, color:"var(--mu)", borderBottom:"1px solid var(--bd)" }}>Hub</th>
                          <th scope="col" style={{ padding:"12px 14px", textAlign:"left", fontWeight:700, fontSize:12, color:"var(--mu)", borderBottom:"1px solid var(--bd)" }}>IATA</th>
                          <th scope="col" style={{ padding:"12px 14px", textAlign:"right", fontWeight:700, fontSize:12, color:"var(--mu)", borderBottom:"1px solid var(--bd)" }}>Distance</th>
                          <th scope="col" style={{ padding:"12px 14px", textAlign:"right", fontWeight:700, fontSize:12, color:"var(--mu)", borderBottom:"1px solid var(--bd)" }}>Flight time</th>
                        </tr>
                      </thead>
                      <tbody>
                        {nearbyHubs.map((hub, i) => {
                          const mins = flightTimeLabel({ lat: hub.lat, lng: hub.lng }, target);
                          return (
                            <tr key={hub._id} style={{ background: i%2===0 ? "white" : "var(--off)" }}>
                              <td style={{ padding:"11px 14px", fontWeight:600, color:"var(--ink)", borderBottom:"1px solid var(--bd)" }}>{hub.name}</td>
                              <td style={{ padding:"11px 14px", color:"var(--mu)", fontFamily:"ui-monospace,monospace", fontSize:12, borderBottom:"1px solid var(--bd)" }}>{hub.iata || ""}</td>
                              <td style={{ padding:"11px 14px", textAlign:"right", color:"var(--mu)", fontFamily:"ui-monospace,monospace", fontSize:12, borderBottom:"1px solid var(--bd)" }}>{distLabel(hub._dist)}</td>
                              <td style={{ padding:"11px 14px", textAlign:"right", color:"var(--bl)", fontWeight:700, borderBottom:"1px solid var(--bd)" }}>{mins || "—"}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <p style={{ fontSize:11, color:"var(--mu2)", marginTop:10, fontStyle:"italic" }}>
                    Approximate straight-line times at ~220 km/h cruise + 5 min overhead. Actual time varies with wind and ATC routing.
                  </p>
                </div>
              );
            })()}

            {/* Routes to this city */}
            {destRoute.length > 0 && (
              <div style={{ padding:"44px 0", borderTop:"1px solid var(--bd)" }}>
                <div className="eyebrow" style={{ marginBottom:10 }}>How to get here</div>
                <h2 style={{ fontWeight:800, fontSize:21, letterSpacing:"-.04em", marginBottom:16 }}>
                  Flights and transfers to {h.city}
                </h2>
                <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                  {destRoute.map(r => {
                    const hotelPadId = h.helipads?.[0];
                    const hotelPad   = hotelPadId ? LOCATION_DB.find(p => p.id === hotelPadId) : null;
                    return (
                      <SubRouteCard key={r.slug} r={r} openBooking={() => openBooking({ from: r.from, to: r.to, ...(hotelPad ? { arrPad: hotelPad } : {}) })} extra={hotelPad ? <span style={{ color:"var(--gr)", marginLeft:4 }}> · {hotelPad.name}</span> : null} compact/>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Best time to visit (from seoContent) */}
            {c.bestTime && (
              <div style={{ padding:"44px 0", borderTop:"1px solid var(--bd)" }}>
                <div className="eyebrow" style={{ marginBottom:10 }}>When to visit</div>
                <h2 style={{ fontWeight:800, fontSize:"clamp(18px,2.5vw,22px)", letterSpacing:"-.04em", marginBottom:16 }}>Best time to fly to {h.name}</h2>
                <p className="body-p">{c.bestTime}</p>
              </div>
            )}

            {/* FAQs (from seoContent) */}
            {/* Key facts — structural rows from SYSTEM data (shared builder,
                same as SSR); soft generated rows appended. */}
            <KeyFactsGrid facts={mergeKeyFacts(buildHotelSystemKeyFacts(h, { fleet: FLEET, locations: LOCATION_DB, operators: OPERATOR_FLEET }), c.keyFacts)}/>

            {c.faqs && c.faqs.length > 0 && (
              <div style={{ padding:"44px 0", borderTop:"1px solid var(--bd)" }}>
                <div className="eyebrow" style={{ marginBottom:10 }}>Common questions</div>
                <h2 style={{ fontWeight:800, fontSize:"clamp(18px,2.5vw,22px)", letterSpacing:"-.04em", marginBottom:20 }}>FAQs &mdash; {h.name}</h2>
                {c.faqs.filter(f => f && f.q && f.a).map((f,i) => <FaqItem key={i} q={f.q} a={f.a}/>)}
              </div>
            )}

            {/* Other hotels nearby */}
            {nearby.length > 0 && (
              <div style={{ padding:"44px 0", borderTop:"1px solid var(--bd)" }}>
                <div className="eyebrow" style={{ marginBottom:10 }}>Also in {h.city}</div>
                <h2 style={{ fontWeight:800, fontSize:21, letterSpacing:"-.04em", marginBottom:20 }}>Other hotels we serve</h2>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:12 }} className="fleet-grid">
                  {nearby.map(n => (
                    <div key={n.id} className="card card-hv" style={{ overflow:"hidden", cursor:"pointer" }} onClick={() => setPage("hotel:"+n.id)}>
                      <div style={{ height:100, overflow:"hidden" }}>
                        <Img preset="card" src={n.img} alt={n.name + " — helicopter charter destination"} style={{ width:"100%", height:"100%", objectFit:"cover" }}/>
                      </div>
                      <div style={{ padding:"12px 14px" }}>
                        <div style={{ fontWeight:700, fontSize:13 }}>{n.name}</div>
                        <div style={{ fontSize:11, color:"var(--mu)", marginTop:2 }}>{n.cat}</div>
                        <div style={{ fontSize:11, color:isDirectHelipad(n)?"var(--gr)":"var(--mu2)", marginTop:4 }}>
                          {isDirectHelipad(n) ? <><svg width="11" height="11" viewBox="0 0 11 11" fill="none" style={{display:"inline-block",verticalAlign:"middle"}}><circle cx="5.5" cy="5.5" r="4.2" stroke="var(--gr)" strokeWidth="1.2"/><line x1="3.8" y1="3.7" x2="3.8" y2="7.3" stroke="var(--gr)" strokeWidth="1.3" strokeLinecap="round"/><line x1="7.2" y1="3.7" x2="7.2" y2="7.3" stroke="var(--gr)" strokeWidth="1.3" strokeLinecap="round"/><line x1="3.8" y1="5.5" x2="7.2" y2="5.5" stroke="var(--gr)" strokeWidth="1.3" strokeLinecap="round"/></svg> Direct helipad</> : <><svg width="11" height="11" viewBox="0 0 24 24" fill="var(--mu)" style={{display:"inline-block",verticalAlign:"middle"}}><path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.85 7h10.29l1.08 3.11H5.77L6.85 7zM19 17H5v-5h14v5z"/><circle cx="7.5" cy="14.5" r="1.5"/><circle cx="16.5" cy="14.5" r="1.5"/></svg> {n.transferMin ? n.transferMin + " min" : "Transfer"}</>} </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sticky sidebar */}
          <div style={{ display:"flex", flexDirection:"column", gap:16, position:"sticky", top:"calc(var(--nav) + 20px)" }}>
            {/* Book CTA */}
            <div style={{ background:"var(--bl)", borderRadius:16, padding:"24px 20px" }}>
              <div style={{ fontWeight:800, fontSize:16, color:"white", marginBottom:6 }}>Fly to {h.name}</div>
              <p style={{ fontSize:13, color:"rgba(255,255,255,.85)", lineHeight:1.65, marginBottom:16 }}>{direct ? "Direct helipad at hotel" : (h.transferMin ? h.transferMin + " min transfer from helipad" : "Helicopter transfer from helipad")}</p>
              <button className="btn" style={{ background:"white", color:"var(--bl)", fontWeight:700, width:"100%", justifyContent:"center" }} onClick={() => openBooking({ from:"Athens", to:h.city })}>
                Get a quote <ArrowRight size={14}/>
              </button>
            </div>

            {/* Hotel facts */}
            <div className="card" style={{ padding:20 }}>
              <div style={{ fontSize:11, fontWeight:700, letterSpacing:".08em", textTransform:"uppercase", color:"var(--mu)", marginBottom:14 }}>Hotel details</div>
              {[
                ["Category", "\u2605".repeat(Math.min(h.stars || 0, 5)).trim() + (h.cat ? (Math.min(h.stars || 0, 5) > 0 ? " " : "") + h.cat : "") || "—"],
                ["Location", h.city ? h.city + ", Greece" : "—"],
                ["Rooms", h.rooms || "—"],
                ["Helipad", direct ? "Direct helipad" : (h.transferMin ? h.transferMin + " min transfer" : "Contact us")],
                ["Nearest landing", hotelPads.length > 0 ? hotelPads[0].name : "Contact us"],
              ].map(([l,v]) => (
                <div key={l} style={{ display:"flex", justifyContent:"space-between", padding:"8px 0", borderBottom:"1px solid var(--bd)", fontSize:13 }}>
                  <span style={{ color:"var(--mu)" }}>{l}</span>
                  <span style={{ fontWeight:600, color:"var(--ink)", textAlign:"right", maxWidth:"55%" }}>{v}</span>
                </div>
              ))}
            </div>

            {/* Trust */}
            <div className="card" style={{ padding:20 }}>
              {[
                {icon:<Shield size={13} color="var(--gr)"/>, text:"EASA-certified operators"},
                {icon:<Check size={13} color="var(--gr)"/>, text:"Fixed pricing \u2014 no surprises"},
                {icon:<Zap size={13} color="var(--gr)"/>, text:"Fast response times"},
                {icon:<CreditCard size={13} color="var(--gr)"/>, text:"Weather refund guarantee"},
              ].map(t => (
                <div key={t.text} style={{ display:"flex", gap:10, alignItems:"center", padding:"7px 0", borderBottom:"1px solid var(--bd)", fontSize:13 }}>
                  {t.icon} <span>{t.text}</span>
                </div>
              ))}
            </div>

            {/* Contact */}
            <div className="card" style={{ padding:20 }}>
              <div style={{ fontSize:13, color:"var(--mu)", lineHeight:1.6, marginBottom:14 }}>Need a custom transfer to {h.name}?</div>
              <button className="btn btn-ol btn-sm" style={{ width:"100%" }} onClick={() => setPage("contact")}>Contact us</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom:"1px solid var(--bd)" }}>
      <button onClick={() => setOpen(o => !o)} style={{ width:"100%", textAlign:"left", padding:"16px 0", background:"none", border:"none", cursor:"pointer", display:"flex", justifyContent:"space-between", alignItems:"center", gap:16 }}>
        <span style={{ fontWeight:600, fontSize:15, color:"var(--ink)" }}>{q}</span>
        <ChevronDown size={16} color="var(--mu)" style={{ flexShrink:0, transform:open?"rotate(180deg)":"none", transition:"transform .2s" }}/>
      </button>
      {open && <div className="body-p" style={{ paddingBottom:16 }}>{a}</div>} </div>
  );
}

// -- DESTINATION SEO PAGE ---------------------------------------------------

function DestinationSEOPage({ dest, setPage, openBooking }) {
  const [content, setContent]   = useState(null);
  const [loading, setLoading]   = useState(true);
  const [selPad, setSelPad]     = useState(null);
  const [routeScoreMap, setRouteScoreMap] = useState({});
  const slug = (dest.name || '').toLowerCase().replace(/\s+/g,"-");

  const destPads   = customerVisiblePads(LOCATION_DB.filter(p => p.city === dest.name));
  const destHotels = HOTELS.filter(h => h.city === dest.name);
  const allPads    = LOCATION_DB.filter(p => p.city === dest.name);
  const airports   = destPads.filter(p => p.type === "airport");
  const helipads   = destPads.filter(p => p.type === "helipad");
  // Sort by recency-weighted booking popularity; pre-launch / no-bookings
  // case is a no-op (all scores 0) and falls back to natural SEO_ROUTES order.
  // Cap at 8 for layout sanity — most destinations have <8 routes anyway.
  const destRoutes = SEO_ROUTES
    .filter(r => r.to === dest.name || r.from === dest.name)
    .slice()
    .sort((a, b) => (routeScoreMap[b.slug] || 0) - (routeScoreMap[a.slug] || 0))
    .slice(0, 8);
  const related    = [...new Set(destRoutes.map(r => r.to === dest.name ? r.from : r.to))]
    .map(n => DESTS.find(d => d.name === n)).filter(Boolean).slice(0,4);

  useEffect(() => {
    loadSEO("dest", slug).then(c => { setContent(c); setLoading(false); });
  }, [slug]);

  useEffect(() => {
    loadRouteScores().then(scores => {
      const m = {}; scores.forEach(s => { m[s.slug] = s.score; });
      setRouteScoreMap(m);
    }).catch(() => {});
  }, []);

  const schemaLD = JSON.stringify({
    "@context":"https://schema.org","@graph":[
      {"@type":"BreadcrumbList","itemListElement":[
        {"@type":"ListItem","position":1,"name":"Home","item":"https://www.heligreece.com"},
        {"@type":"ListItem","position":2,"name":"Destinations","item":"https://www.heligreece.com/destinations"},
        {"@type":"ListItem","position":3,"name":dest.name,"item":"https://www.heligreece.com/dest/"+slug},
      ]},
      content && content.faqs && content.faqs.length > 0 ? {
        "@type":"FAQPage",
        "mainEntity":(content.faqs||[]).filter(f => f && f.q && f.a).map(f => ({"@type":"Question","name":f.q,"acceptedAnswer":{"@type":"Answer","text":f.a}}))
      } : null,
      // Service (not Product) + finite-guarded offers — never price:0.
      {"@type":"Service","name":"Private Helicopter Charter to "+dest.name,
        "provider":{"@id":"https://www.heligreece.com/#org"},
        ...(getDestFromAthensMin(dest.name) > 0 ? {"offers":{"@type":"Offer","priceCurrency":"EUR","price":getDestFromAthensMin(dest.name),"availability":"https://schema.org/InStock"}} : {})},
    ].filter(Boolean)
  });

  if (loading) return <SkeletonPage/>;

  const c = content || {};

  return (
    <div style={{ paddingTop:"var(--nav)", background:"white" }}>
      <div data-schema-ld={schemaLD} style={{ display:"none" }}/>

      {/* HERO */}
      <div className="ph-hero-rich">
        <div style={{ position:"absolute", inset:0 }}>
          {/* Sanity-first (2026-05-19c) — destination doc heroImage/image
              win over auto-generated seoContent. Was reversed; the AI
              content generator wrote a Pexels URL into seoContent.heroImage
              that hid the manually-uploaded Sanity image. */}
          <Img preset="hero" src={dest.heroImg || dest.img || c.heroImage} alt={dest.name + " from the air — helicopter charter destination"} style={{ width:"100%", height:"100%", objectFit:"cover" }}/>
          <div style={{ position:"absolute", inset:0, background:"linear-gradient(to top, rgba(5,20,50,.88) 0%, rgba(5,20,50,.5) 45%, rgba(5,20,50,.3) 100%)" }}/>
        </div>
        <div className="wrap" style={{ position:"relative", padding:"0 36px 48px", width:"100%", boxSizing:"border-box" }}>
          {/* Breadcrumb */}
          <div style={{ display:"flex", gap:6, alignItems:"center", marginBottom:20, fontSize:12, color:"rgba(255,255,255,.65)" }}>
            <span onClick={() => setPage("home")} style={{ cursor:"pointer" }}>Home</span>
            <ChevronDown size={10} style={{ transform:"rotate(-90deg)" }}/>
            <span onClick={() => setPage("destinations")} style={{ cursor:"pointer" }}>Destinations</span>
            <ChevronDown size={10} style={{ transform:"rotate(-90deg)" }}/>
            <span style={{ color:"white" }}>{dest.name}</span>
          </div>
          <div className="eyebrow" style={{ color:"rgba(255,255,255,.7)", marginBottom:10 }}>{dest.tag} - Greece</div>
          <h2 style={{ fontFamily:"var(--sans)", fontWeight:800, fontSize:"clamp(30px,5vw,54px)", letterSpacing:"-.05em", color:"white", lineHeight:1.05, marginBottom:16, maxWidth:680 }}>
            {c.h1 || "Private Helicopter to " + dest.name}
          </h2>
          <p style={{ fontSize:16, color:"rgba(255,255,255,.7)", maxWidth:520, lineHeight:1.7, marginBottom:28 }}>
            {c.metaDesc || "Full aircraft charter. " + (dest.time === "Hub" ? "" : (destFlightLabel(dest) !== "—" ? destFlightLabel(dest) + " from Athens. " : "")) + (getDestFromAthensMin(dest.name) ? "From EUR " + fmtPrice(getDestFromAthensMin(dest.name)) + "." : "")}
          </p>
          <div style={{ display:"flex", gap:10, flexWrap:"wrap", alignItems:"center" }}>
            <button className="btn btn-lg" style={{ background:"white", color:"var(--bl)", fontWeight:700 }} onClick={() => openBooking({ from:"Athens", to:dest.name })}>
              Book helicopter to {dest.name} <ArrowRight size={16}/>
            </button>
            <div style={{ color:"rgba(255,255,255,.65)", fontSize:13, display:"flex", gap:6, alignItems:"center" }}>
              <Shield size={13}/> HCAA-licensed operators - Full insurance </div>
          </div>
        </div>
      </div>

      {/* STATS STRIP */}
      <div style={{ background:"var(--bl)" }}>
        <div className="wrap rpad">
          <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)" }} className="ac-grid stats-strip-4">
            {[
              ["Flight time", destFlightLabel(dest)],
              ["From price", getDestFromAthensMin(dest.name) ? "EUR "+fmtPrice(getDestFromAthensMin(dest.name)) : "Price on request"],
              ["Landing options", destPads.length + " in "+dest.name],
              ["Aircraft types", visibleMarketingFleet().length + " available"],
            ].map(([l,v],i) => (
              <div key={l} style={{ padding:"16px 20px", textAlign:"center", borderRight: i<3 ? "1px solid rgba(255,255,255,.1)" : "none" }}>
                <div style={{ fontSize:10, color:"rgba(255,255,255,.85)", textTransform:"uppercase", letterSpacing:".1em", marginBottom:5 }}>{l}</div>
                <div style={{ fontWeight:700, fontSize:16, color:"white" }}>{v}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* MAIN 2-COLUMN LAYOUT */}
      <div className="wrap rpad" style={{ paddingTop:44 }}>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 300px", gap:52, alignItems:"start" }} className="bk-main-grid">

          {/* LEFT: CONTENT */}
          <div>

            {/* Intro */}
            {c.intro && (
              <div style={{ paddingBottom:44 }}>
                <p className="body-p" style={{ maxWidth:640 }}>{c.intro}</p>
              </div>
            )}


            {/* HELIPADS SECTION */}
            {destPads.length > 0 && (
              <div style={{ padding:"44px 0", borderTop:"1px solid var(--bd)" }}>
                <div className="eyebrow" style={{ marginBottom:10 }}>Landing locations</div>
                <h2 style={{ fontWeight:800, fontSize:"clamp(18px,2.5vw,22px)", letterSpacing:"-.04em", marginBottom:8 }}>
                  Helipads and airports in {dest.name}
                </h2>
                <p style={{ fontSize:14, color:"var(--mu)", marginBottom:24, lineHeight:1.7 }}>
                  We operate into {destPads.length} approved landing location{destPads.length!==1?"s":""} in {dest.name}.
                  Click a marker to see details. Your exact landing point is confirmed at booking based on your accommodation.
                </p>

                {/* Map + list side by side */}
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:20, marginBottom:0 }} className="form-grid-2">
                  {/* Map */}
                  <div style={{ borderRadius:16, overflow:"hidden", border:"1px solid var(--bd)", height:260 }}>
                    <TileMap pads={destPads} selected={selPad} onSelect={setSelPad} scheme="blue"/>
                  </div>
                  {/* Helipad list */}
                  <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                    {airports.map(pad => (
                      <div key={pad.id} role="button" tabIndex={0} aria-pressed={selPad?.id===pad.id} onKeyDown={onKbdActivate} onClick={() => setSelPad(pad)} style={{ display:"flex", gap:12, padding:"12px 14px", borderRadius:12, border:"1.5px solid " + (selPad?.id===pad.id?"var(--bl)":"var(--bd)"), cursor:"pointer", background:selPad?.id===pad.id?"var(--blbg)":"white", transition:"all .15s" }}>
                        <div style={{ width:34, height:34, borderRadius:8, background:"var(--blbg)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                          <Plane size={15} color="var(--bl)"/>
                        </div>
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ fontWeight:700, fontSize:13, display:"flex", alignItems:"center", gap:6 }}>
                            {pad.name}
                            {pad.iata && <span style={{ fontSize:10, fontWeight:700, color:"var(--bl)", background:"var(--blbg)", padding:"1px 6px", borderRadius:4 }}>{pad.iata}</span>} </div>
                          {pad.addr && <div style={{ fontSize:11, color:"var(--mu)", marginTop:2, lineHeight:1.4 }}>{pad.addr}</div>}
                        </div>
                        <div style={{ fontSize:10, fontWeight:600, color:"var(--bl)", background:"var(--blbg)", padding:"2px 7px", borderRadius:5, height:"fit-content", flexShrink:0 }}>Airport</div>
                      </div>
                    ))}
                    {helipads.map(pad => (
                      <div key={pad.id} role="button" tabIndex={0} aria-pressed={selPad?.id===pad.id} onKeyDown={onKbdActivate} onClick={() => setSelPad(pad)} style={{ display:"flex", gap:12, padding:"12px 14px", borderRadius:12, border:"1.5px solid " + (selPad?.id===pad.id?"var(--gr)":"var(--bd)"), cursor:"pointer", background:selPad?.id===pad.id?"var(--grbg)":"white", transition:"all .15s" }}>
                        <div style={{ width:34, height:34, borderRadius:8, background:"var(--grbg)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="var(--gr)" strokeWidth="1.8"/><line x1="8.5" y1="8" x2="8.5" y2="16" stroke="var(--gr)" strokeWidth="2.2" strokeLinecap="round"/><line x1="15.5" y1="8" x2="15.5" y2="16" stroke="var(--gr)" strokeWidth="2.2" strokeLinecap="round"/><line x1="8.5" y1="12" x2="15.5" y2="12" stroke="var(--gr)" strokeWidth="2.2" strokeLinecap="round"/></svg>
                        </div>
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ fontWeight:700, fontSize:13 }}>{pad.name}</div>
                          {pad.addr && <div style={{ fontSize:11, color:"var(--mu)", marginTop:2, lineHeight:1.4 }}>{pad.addr}</div>}
                        </div>
                        <div style={{ fontSize:10, fontWeight:600, color:"var(--gr)", background:"var(--grbg)", padding:"2px 7px", borderRadius:5, height:"fit-content", flexShrink:0 }}>Helipad</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Selected pad detail */}
                {selPad && (
                  <div style={{ marginTop:12, padding:"14px 16px", borderRadius:12, background:"var(--off)", border:"1px solid var(--bd)", display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:12 }}>
                    <div>
                      <div style={{ fontWeight:700, fontSize:14 }}>{selPad.name}</div>
                      <div style={{ fontSize:12, color:"var(--mu)", marginTop:2 }}>{selPad.addr}</div>
                      <div style={{ fontSize:11, color:"var(--mu2)", fontFamily:"monospace", marginTop:4 }}>
                        {selPad.lat}N, {selPad.lng}E
                      </div>
                    </div>
                    <button className="btn btn-bl btn-sm" onClick={() => openBooking({ from:"Athens", to:dest.name })}>
                      Book landing here <ArrowRight size={12}/>
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* WHY HELICOPTER */}
            {c.whyHeli && (
              <div style={{ padding:"44px 0", borderTop:"1px solid var(--bd)" }}>
                <div className="eyebrow" style={{ marginBottom:10 }}>Why fly private</div>
                <h2 style={{ fontWeight:800, fontSize:"clamp(18px,2.5vw,22px)", letterSpacing:"-.04em", marginBottom:16 }}>
                  Why take a helicopter to {dest.name}?
                </h2>
                <p className="body-p" style={{ marginBottom:20 }}>{c.whyHeli}</p>
              </div>
            )}

            {/* TRANSPORT COMPARISON */}
            {dest.time !== "Hub" && (
              <div style={{ padding:"44px 0", borderTop:"1px solid var(--bd)" }}>
                <div className="eyebrow" style={{ marginBottom:10 }}>How it compares</div>
                <h2 style={{ fontWeight:800, fontSize:"clamp(18px,2.5vw,22px)", letterSpacing:"-.04em", marginBottom:20 }}>
                  Helicopter vs ferry vs flight to {dest.name}
                </h2>
                <div style={{ borderRadius:16, overflow:"hidden", border:"1px solid var(--bd)" }}>
                  <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
                    <thead>
                      <tr style={{ background:"var(--off)" }}>
                        {["","Helicopter","Fast ferry","Commercial flight"].map((h,i) => (
                          <th key={h} scope="col" style={{ padding:"12px 16px", textAlign:i===0?"left":"center", fontWeight:700, fontSize:12, color:i===1?"var(--bl)":"var(--mu)", borderBottom:"1px solid var(--bd)", background:i===1?"var(--blbg)":"transparent" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        ["Total journey time", destFlightLabel(dest), "2.5-8h including port", "3-4h including airport"],
                        ["Privacy", "Full aircraft -- private", "Public deck", "Economy cabin"],
                        ["Flexibility", "Your schedule", "Fixed timetable", "Fixed timetable"],
                        ["Luggage", "Up to 10 bags", "Unlimited", "Carry-on + 1 checked"],
                        ["Scenic experience", "Aerial views included", "Sea level", "Nothing to see"],
                        ["Weather risk", "Refund if cancelled", "Can be rough", "Low"],
                      ].map(([label, heli, ferry, flight],i) => (
                        <tr key={label} style={{ background:i%2===0?"white":"var(--off)" }}>
                          <th scope="row" style={{ padding:"11px 16px", fontWeight:600, color:"var(--ink)", borderBottom:"1px solid var(--bd)", textAlign:"left" }}>{label}</th>
                          <td style={{ padding:"11px 16px", textAlign:"center", color:"var(--bl)", fontWeight:600, borderBottom:"1px solid var(--bd)", background:"rgba(13,86,166,.04)" }}>{heli}</td>
                          <td style={{ padding:"11px 16px", textAlign:"center", color:"var(--mu)", borderBottom:"1px solid var(--bd)" }}>{ferry}</td>
                          <td style={{ padding:"11px 16px", textAlign:"center", color:"var(--mu)", borderBottom:"1px solid var(--bd)" }}>{flight}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Flight times from hubs (Phase 3C + Track 4E distance column) */}
            {(() => {
              const destLoc = (dest.lat != null && dest.lng != null) ? { lat: dest.lat, lng: dest.lng } : cityLocation(dest.name);
              if (!HUBS.length || !destLoc?.lat || !destLoc?.lng) return null;
              const target = { lat: destLoc.lat, lng: destLoc.lng };
              const nearbyHubs = HUBS
                .map(hub => ({ ...hub, _dist: haversineKm({ lat: hub.lat, lng: hub.lng }, target) }))
                .filter(hub => hub._dist != null && hub._dist >= 10);
              if (nearbyHubs.length === 0) return null;
              return (
                <div style={{ padding:"44px 0", borderTop:"1px solid var(--bd)" }}>
                  <div className="eyebrow" style={{ marginBottom:10 }}>Helicopter access</div>
                  <h2 style={{ fontWeight:800, fontSize:"clamp(18px,2.5vw,22px)", letterSpacing:"-.04em", marginBottom:16 }}>
                    Flight times from hubs
                  </h2>
                  <div style={{ borderRadius:16, overflow:"hidden", border:"1px solid var(--bd)" }}>
                    <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
                      <thead>
                        <tr style={{ background:"var(--off)" }}>
                          <th scope="col" style={{ padding:"12px 14px", textAlign:"left", fontWeight:700, fontSize:12, color:"var(--mu)", borderBottom:"1px solid var(--bd)" }}>Hub</th>
                          <th scope="col" style={{ padding:"12px 14px", textAlign:"left", fontWeight:700, fontSize:12, color:"var(--mu)", borderBottom:"1px solid var(--bd)" }}>IATA</th>
                          <th scope="col" style={{ padding:"12px 14px", textAlign:"right", fontWeight:700, fontSize:12, color:"var(--mu)", borderBottom:"1px solid var(--bd)" }}>Distance</th>
                          <th scope="col" style={{ padding:"12px 14px", textAlign:"right", fontWeight:700, fontSize:12, color:"var(--mu)", borderBottom:"1px solid var(--bd)" }}>Flight time</th>
                        </tr>
                      </thead>
                      <tbody>
                        {nearbyHubs.map((hub, i) => {
                          const mins = flightTimeLabel({ lat: hub.lat, lng: hub.lng }, target);
                          return (
                            <tr key={hub._id} style={{ background: i%2===0 ? "white" : "var(--off)" }}>
                              <td style={{ padding:"11px 14px", fontWeight:600, color:"var(--ink)", borderBottom:"1px solid var(--bd)" }}>{hub.name}</td>
                              <td style={{ padding:"11px 14px", color:"var(--mu)", fontFamily:"ui-monospace,monospace", fontSize:12, borderBottom:"1px solid var(--bd)" }}>{hub.iata || ""}</td>
                              <td style={{ padding:"11px 14px", textAlign:"right", color:"var(--mu)", fontFamily:"ui-monospace,monospace", fontSize:12, borderBottom:"1px solid var(--bd)" }}>{distLabel(hub._dist)}</td>
                              <td style={{ padding:"11px 14px", textAlign:"right", color:"var(--bl)", fontWeight:700, borderBottom:"1px solid var(--bd)" }}>{mins || "—"}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <p style={{ fontSize:11, color:"var(--mu2)", marginTop:10, fontStyle:"italic" }}>
                    Approximate straight-line times at ~220 km/h cruise + 5 min overhead. Actual time varies with wind and ATC routing.
                  </p>
                </div>
              );
            })()}

            {/* AIRCRAFT */}
            <div style={{ padding:"44px 0", borderTop:"1px solid var(--bd)" }}>
              <div className="eyebrow" style={{ marginBottom:10 }}>Your aircraft</div>
              <h2 style={{ fontWeight:800, fontSize:"clamp(18px,2.5vw,22px)", letterSpacing:"-.04em", marginBottom:8 }}>
                Choose your helicopter to {dest.name}
              </h2>
              <p style={{ fontSize:14, color:"var(--mu)", marginBottom:20 }}>All prices are for the full aircraft -- no shared seats. Select based on group size and luggage.</p>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(2,1fr)", gap:12 }} className="form-grid-2">
                {dedupeRouteCards(SEO_ROUTES.find(r => r.from === "Athens" && r.to === dest.name), FLEET).map(g => {
                  // DEDUP one card per (model x operator) against the Athens->dest route,
                  // mirroring the route page: f = a VISIBLE representative instance, p =
                  // the group MIN route.cardFromPrice for that model (incl. hidden). No
                  // engine recompute — read-only over the precomputed cardFromPrice.
                  const f = g.rep;
                  const p = g.fromPrice;
                  return (
                    <div key={g.model + '|' + (g.operatorId || '')} className="card card-hv" style={{ padding:0, overflow:"hidden", transition:"all .15s", cursor:"pointer" }} onClick={() => setPage("fleet:"+f.id)}>
                    <div style={{ aspectRatio:"4/3", width:"100%", overflow:"hidden", position:"relative", background:"var(--off)" }}>
                      <Img preset="card" src={f.img} alt={f.name + " helicopter exterior"} style={{ width:"100%", height:"100%", objectFit:"contain" }}/>
                    </div>
                    <div style={{ padding:"14px 18px 18px" }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:10 }}>
                    <div>
                    <div style={{ fontWeight:800, fontSize:14 }}>{f.name}</div>
                    <div style={{ fontSize:12, color:"var(--mu)", marginTop:2 }}>{f.type}</div>
                    </div>
                    <div style={{ textAlign:"right" }}>
                    <div style={{ fontWeight:800, fontSize:18, color:"var(--bl)", letterSpacing:"-.03em" }}>
                    {routeCardFromLabel(p)}
                    </div>
                    <div style={{ fontSize:10, color:"var(--mu2)" }}>full aircraft</div>
                    </div>
                    </div>
                    <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:14 }}>
                    {[[<Users size={11}/>,f.seats+" pax"], [<Briefcase size={11}/>,<BagsLine>{f.maxLuggage} bags</BagsLine>], [<Route size={11}/>,f.range]].map(([ic,tag],i) => (
                    <span key={i} style={{ display:"inline-flex", alignItems:"center", gap:4, fontSize:11, fontWeight:600, color:"var(--mu)", background:"var(--off)", padding:"3px 8px", borderRadius:6 }}>{ic}{tag}</span>
                    ))}
                    </div>
                    <button className="btn btn-ol btn-sm" style={{ width:"100%", justifyContent:"center" }} onClick={(e) => { e.stopPropagation(); openBooking({ from:"Athens", to:dest.name }); }}>
                    Book {f.name} <ArrowRight size={12}/>
                    </button>
                    </div></div>
                  );
                })}
              </div>
            </div>

            {/* BEST TIME */}
            {c.bestTime && (
              <div style={{ padding:"44px 0", borderTop:"1px solid var(--bd)" }}>
                <div className="eyebrow" style={{ marginBottom:10 }}>When to go</div>
                <h2 style={{ fontWeight:800, fontSize:"clamp(18px,2.5vw,22px)", letterSpacing:"-.04em", marginBottom:16 }}>
                  Best time to visit {dest.name} by helicopter
                </h2>
                {/* Season pills */}
                <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10, marginBottom:20 }} className="ac-grid stats-strip-4">
                  {[["Spring","Apr-May","Quiet, warm, great light"],["Summer","Jun-Sep","Peak season, hot, busy"],["Autumn","Oct-Nov","Warm sea, fewer crowds"],["Winter","Dec-Mar","Off-peak, peaceful"]].map(([s,m,d]) => (
                    <div key={s} style={{ padding:"14px 12px", borderRadius:12, background:"var(--off)", border:"1px solid var(--bd)", textAlign:"center" }}>
                      <div style={{ fontWeight:700, fontSize:13, marginBottom:3 }}>{s}</div>
                      <div style={{ fontSize:12, color:"var(--bl)", fontWeight:600, marginBottom:6 }}>{m}</div>
                      <div style={{ fontSize:11, color:"var(--mu)", lineHeight:1.5 }}>{d}</div>
                    </div>
                  ))}
                </div>
                <p className="body-p">{c.bestTime}</p>
              </div>
            )}

            {/* ROUTES */}
            {destRoutes.length > 0 && (
              <div style={{ padding:"44px 0", borderTop:"1px solid var(--bd)" }}>
                <div className="eyebrow" style={{ marginBottom:10 }}>Direct connections</div>
                <h2 style={{ fontWeight:800, fontSize:"clamp(18px,2.5vw,22px)", letterSpacing:"-.04em", marginBottom:20 }}>
                  Flights and transfers to {dest.name}
                </h2>
                <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                  {destRoutes.map(r => {
                    const img = DESTS.find(d => d.name === (r.to === dest.name ? r.from : r.to))?.img;
                    return (
                      <SubRouteCard key={r.slug} r={r} setPage={setPage} openBooking={openBooking}/>
                    );
                  })}
                </div>
              </div>
            )}

            {/* FAQs */}
            {/* Key facts — structural rows from SYSTEM data (shared builder,
                same as SSR); soft generated rows appended. */}
            <KeyFactsGrid facts={mergeKeyFacts(buildDestSystemKeyFacts(dest, { fleet: FLEET, locations: LOCATION_DB, operators: OPERATOR_FLEET, fromAthensMin: getDestFromAthensMin(dest.name) }), c.keyFacts)}/>

            <ReviewsSection reviews={publishedReviewsFor("dest", dest, REVIEWS)}/>

            {c.faqs && c.faqs.length > 0 && (
              <div style={{ padding:"44px 0", borderTop:"1px solid var(--bd)" }}>
                <div className="eyebrow" style={{ marginBottom:10 }}>Common questions</div>
                <h2 style={{ fontWeight:800, fontSize:"clamp(18px,2.5vw,22px)", letterSpacing:"-.04em", marginBottom:20 }}>
                  Frequently asked questions about helicopter charters to {dest.name}
                </h2>
                {c.faqs.filter(f => f && f.q && f.a).map((f,i) => <FaqItem key={i} q={f.q} a={f.a}/>)} </div>
            )}

            {/* RELATED DESTINATIONS */}
            {related.length > 0 && (
              <div style={{ padding:"44px 0", borderTop:"1px solid var(--bd)" }}>
                <div className="eyebrow" style={{ marginBottom:10 }}>Also popular</div>
                <h2 style={{ fontWeight:800, fontSize:"clamp(18px,2.5vw,22px)", letterSpacing:"-.04em", marginBottom:20 }}>
                  Other destinations
                </h2>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12 }} className="ac-grid">
                  {related.map(d => (
                    <div key={d.name} className="card card-hv" style={{ overflow:"hidden", cursor:"pointer" }} onClick={() => setPage("dest:"+(d.name || '').toLowerCase().replace(/\s+/g,"-"))}>
                      <div style={{ height:90, overflow:"hidden" }}>
                        <Img preset="card" src={d.img} alt={d.name + " — helicopter charter destination"} style={{ width:"100%", height:"100%", objectFit:"cover" }}/>
                      </div>
                      <div style={{ padding:"10px 12px" }}>
                        <div style={{ fontWeight:700, fontSize:13 }}>{d.name}</div>
                        <div style={{ fontSize:11, color:"var(--mu)", marginTop:2 }}>{(() => { const l = destFlightLabel(d); return (l === "Hub" || l === "—") ? l : l + " from Athens"; })()}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* STICKY SIDEBAR */}
          <div style={{ display:"flex", flexDirection:"column", gap:16, position:"sticky", top:"calc(var(--nav) + 20px)" }}>
            {/* Book CTA */}
            <div style={{ background:"var(--bl)", borderRadius:16, padding:"24px 20px" }}>
              <div style={{ fontWeight:800, fontSize:16, color:"white", marginBottom:6 }}>Fly to {dest.name}</div>
              <p style={{ fontSize:13, color:"rgba(255,255,255,.85)", lineHeight:1.65, marginBottom:16 }}>{destFlightLabel(dest)} from Athens. Full aircraft, fixed pricing.</p>
              <button className="btn" style={{ background:"white", color:"var(--bl)", fontWeight:700, width:"100%", justifyContent:"center" }} onClick={() => openBooking({ from:"Athens", to:dest.name })}>
                Get a quote <ArrowRight size={14}/>
              </button>
            </div>

            {/* Key facts */}
            <div className="card" style={{ padding:20 }}>
              <div style={{ fontSize:11, fontWeight:700, letterSpacing:".08em", textTransform:"uppercase", color:"var(--mu)", marginBottom:14 }}>Key facts</div>
              {[
                ["Region", dest.tag],
                ["Flight time", destFlightLabel(dest)],
                ["Helipads", destPads.length + " landing options"],
                ["Hotels we serve", destHotels.length > 0 ? destHotels.length + " luxury hotels" : "Contact us"],
                ["Aircraft", "4 types available"],
              ].map(([l,v]) => (
                <div key={l} style={{ display:"flex", justifyContent:"space-between", padding:"8px 0", borderBottom:"1px solid var(--bd)", fontSize:13 }}>
                  <span style={{ color:"var(--mu)" }}>{l}</span>
                  <span style={{ fontWeight:600, color:"var(--ink)" }}>{v}</span>
                </div>
              ))}
            </div>

            {/* Trust */}
            <div className="card" style={{ padding:20 }}>
              <div style={{ fontSize:11, fontWeight:700, letterSpacing:".08em", textTransform:"uppercase", color:"var(--mu)", marginBottom:14 }}>Why book with us</div>
              {[
                {icon:<Shield size={13} color="var(--gr)"/>, text:"EASA-certified operators"},
                {icon:<Check size={13} color="var(--gr)"/>, text:"Fixed pricing — no surprises"},
                {icon:<Zap size={13} color="var(--gr)"/>, text:"Fast response times"},
                {icon:<CreditCard size={13} color="var(--gr)"/>, text:"Weather refund guarantee"},
              ].map(t => (
                <div key={t.text} style={{ display:"flex", gap:10, alignItems:"center", padding:"7px 0", borderBottom:"1px solid var(--bd)", fontSize:13 }}>
                  {t.icon} <span>{t.text}</span>
                </div>
              ))}
            </div>

            {/* Contact */}
            <div className="card" style={{ padding:20 }}>
              <div style={{ fontSize:11, fontWeight:700, letterSpacing:".08em", textTransform:"uppercase", color:"var(--mu)", marginBottom:10 }}>Need help?</div>
              <p style={{ fontSize:13, color:"var(--mu)", lineHeight:1.6, marginBottom:14 }}>Our team can plan your trip to {dest.name} down to the last detail.</p>
              <button className="btn btn-ol btn-sm" style={{ width:"100%" }} onClick={() => setPage("contact")}>Contact us</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// -- ROUTE SEO PAGE ---------------------------------------------------------
const PadList = ({ pads, selPad, setSelPad, city }) => (
  <div style={{ border:"1px solid var(--bd)", borderRadius:16, overflow:"hidden" }}>
    <TileMap pads={pads} selected={selPad} onSelect={setSelPad} scheme="blue"/>
    <div style={{ display:"flex", flexDirection:"column", gap:8, padding:10 }}>
      {pads.map(pad => (
        <div key={pad.id} role="button" tabIndex={0} aria-pressed={selPad?.id===pad.id} onKeyDown={onKbdActivate} onClick={() => setSelPad(pad)} style={{ display:"flex", gap:10, padding:"11px 13px", borderRadius:10, border:"1.5px solid "+(selPad?.id===pad.id?"var(--bl)":"var(--bd)"), cursor:"pointer", background:selPad?.id===pad.id?"var(--blbg)":"white", transition:"all .15s" }}>
          <span style={{ color:"var(--bl)", display:"flex", flexShrink:0 }}>{pad.type==="airport"?<svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M21 16v-2l-8-5V3.5C13 2.67 12.33 2 11.5 2S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z" fill="currentColor"/></svg>:<svg width="18" height="18" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8"/><line x1="8.5" y1="8.5" x2="8.5" y2="15.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/><line x1="15.5" y1="8.5" x2="15.5" y2="15.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/><line x1="8.5" y1="12" x2="15.5" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>}</span>
          <div>
            <div style={{ fontWeight:700, fontSize:13 }}>{pad.name}{pad.iata&&<span style={{ fontSize:10, color:"var(--bl)", fontWeight:700, marginLeft:6, background:"var(--blbg)", padding:"1px 5px", borderRadius:4 }}>{pad.iata}</span>}</div>
            <div style={{ fontSize:11, color:"var(--mu)", marginTop:2 }}>{pad.addr}</div>
          </div>
        </div>
      ))}
      {pads.length === 0 && <div style={{ fontSize:13, color:"var(--mu)", padding:16 }}>Contact us for landing options in {city}.</div>}
    </div>
  </div>
);

function RouteSEOPage({ route, setPage, openBooking }) {
  // LCP fix — when the page was server-rendered for THIS route, [...slug].astro
  // injects its seoContent as window.__ROUTE_SEO__ so the hero renders on the
  // first React paint instead of waiting on loadSEO()'s skeleton + fetch. Only
  // the landed route matches (slug-gated); SPA navigations to other routes fall
  // through to the async loadSEO path below. The useEffect still runs loadSEO
  // (same network call as before, now non-blocking) to warm the shared cache
  // and adopt the canonical /api/seo/load shape — the fields the page reads
  // (h1, intro, routeHighlights, practicalInfo, keyFacts, faqs) are identical
  // in both, so there is no visible change when it resolves.
  const injectedSeo = (typeof window !== "undefined" && window.__ROUTE_SEO__ && window.__ROUTE_SEO__.slug === route.slug)
    ? window.__ROUTE_SEO__.content : null;
  const [content, setContent] = useState(injectedSeo);
  const [loading, setLoading] = useState(!injectedSeo);
  const [selFromPad, setSelFromPad] = useState(null);
  const [selToPad, setSelToPad]     = useState(null);

  const fromPads = customerVisiblePads(LOCATION_DB.filter(p => p.city === route.from));
  const toPads   = customerVisiblePads(LOCATION_DB.filter(p => p.city === route.to));
  const related  = SEO_ROUTES.filter(r => r.slug !== route.slug && (r.from===route.from||r.to===route.to||r.from===route.to||r.to===route.from)).slice(0,4);

  useEffect(() => {
    loadSEO("route", route.slug).then(c => { setContent(c); setLoading(false); });
  }, [route.slug]);

  if (loading) return <SkeletonPage/>;

  const c = content || {};

  return (
    <div style={{ paddingTop:"var(--nav)", background:"white" }}>

      {/* HERO */}
      <div className="ph-hero-rich">
        <div style={{ position:"absolute", inset:0 }}>
          {/* Sanity-first — route doc + destination doc images win over
              auto-generated seoContent (2026-05-19c). */}
          <RouteSplitImage r={route} variant="hero"/> <div style={{ position:"absolute", inset:0, background:"linear-gradient(to top, rgba(5,20,50,.88) 0%, rgba(5,20,50,.5) 45%, rgba(5,20,50,.3) 100%)" }}/>
        </div>
        <div className="wrap" style={{ position:"relative", padding:"0 36px 48px", width:"100%", boxSizing:"border-box" }}>
          <div style={{ display:"flex", gap:6, alignItems:"center", marginBottom:20, fontSize:12, color:"rgba(255,255,255,.65)" }}>
            <span onClick={() => setPage("home")} style={{ cursor:"pointer" }}>Home</span>
            <ChevronDown size={10} style={{ transform:"rotate(-90deg)" }}/>
            <span onClick={() => setPage("routes")} style={{ cursor:"pointer" }}>Routes</span>
            <ChevronDown size={10} style={{ transform:"rotate(-90deg)" }}/>
            <span style={{ color:"white" }}>{route.from} to {route.to}</span>
          </div>
          <div className="eyebrow" style={{ color:"rgba(255,255,255,.7)", marginBottom:10 }}>Private Helicopter Charter - Greece</div>
          <h2 style={{ fontFamily:"var(--sans)", fontWeight:800, fontSize:"clamp(30px,5vw,54px)", letterSpacing:"-.05em", color:"white", lineHeight:1.05, marginBottom:16, maxWidth:680 }}>
            {c.h1 || "Private Helicopter from "+route.from+" to "+route.to}
          </h2>
          <div style={{ display:"flex", gap:16, marginBottom:20, flexWrap:"wrap" }}>
            {[fmtFlightShort(routeFlightMin(route)), "Up to "+FLEET.reduce((m,f)=>Math.max(m,f.seats),0)+" passengers", routeCardFromLabel(getRouteFromPriceMin(route.from,route.to))].map(s => (
              <div key={s} style={{ color:"rgba(255,255,255,.7)", fontSize:13, display:"flex", gap:6, alignItems:"center" }}>
                <div style={{ width:4, height:4, borderRadius:"50%", background:"rgba(255,255,255,.65)", flexShrink:0 }}/>
                {s}
              </div>
            ))}
          </div>
          <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
            <button className="btn btn-lg" style={{ background:"white", color:"var(--bl)", fontWeight:700 }} onClick={() => openBooking({ from:route.from, to:route.to })}>
              Book this route <ArrowRight size={16}/>
            </button>
          </div>
        </div>
      </div>

      {/* Stats strip */}
      <div style={{ background:"var(--bl)" }}>
        <div className="wrap rpad">
          <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)" }} className="ac-grid stats-strip-4">
            {[
              ["Flight time", fmtFlightShort(routeFlightMin(route))],
              ["From price", routeFromPriceAmountLabel(route.cardFromPrice)],
              ["Dep. helipads", fromPads.length+" in "+route.from],
              ["Arr. helipads", toPads.length+" in "+route.to],
            ].map(([l,v],i) => (
              <div key={l} style={{ padding:"16px 20px", textAlign:"center", borderRight:i<3?"1px solid rgba(255,255,255,.1)":"none" }}>
                <div style={{ fontSize:10, color:"rgba(255,255,255,.85)", textTransform:"uppercase", letterSpacing:".1em", marginBottom:5 }}>{l}</div>
                <div style={{ fontWeight:700, fontSize:16, color:"white" }}>{v}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="wrap rpad" style={{ paddingTop:44 }}>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 300px", gap:52, alignItems:"start" }} className="bk-main-grid">
          <div>

            {/* Intro */}
            {c.intro && (
              <div style={{ paddingBottom:44 }}>
                <p className="body-p">{c.intro}</p>
              </div>
            )}

            {/* Route highlights */}
            {c.routeHighlights && (
              <div style={{ padding:"44px 0", borderTop:"1px solid var(--bd)" }}>
                <div className="eyebrow" style={{ marginBottom:10 }}>The journey</div>
                <h2 style={{ fontWeight:800, fontSize:"clamp(18px,2.5vw,22px)", letterSpacing:"-.04em", marginBottom:16 }}>What you see from the air</h2>
                <p className="body-p">{c.routeHighlights}</p>
              </div>
            )}

            {/* Departure helipads */}
            {fromPads.length > 0 && (
              <div style={{ padding:"44px 0", borderTop:"1px solid var(--bd)" }}>
                <div className="eyebrow" style={{ marginBottom:10 }}>Where you depart</div>
                <h2 style={{ fontWeight:800, fontSize:"clamp(18px,2.5vw,22px)", letterSpacing:"-.04em", marginBottom:8 }}>
                  Departure locations in {route.from}
                </h2>
                <p style={{ fontSize:14, color:"var(--mu)", marginBottom:20 }}>
                  We operate from {fromPads.length} approved location{fromPads.length!==1?"s":""} in {route.from}. Your exact departure point is confirmed at booking.
                </p>
                <PadList pads={fromPads} selPad={selFromPad} setSelPad={setSelFromPad} city={route.from}/>
              </div>
            )}

            {/* Arrival helipads */}
            {toPads.length > 0 && (
              <div style={{ padding:"44px 0", borderTop:"1px solid var(--bd)" }}>
                <div className="eyebrow" style={{ marginBottom:10 }}>Where you land</div>
                <h2 style={{ fontWeight:800, fontSize:"clamp(18px,2.5vw,22px)", letterSpacing:"-.04em", marginBottom:8 }}>
                  Arrival locations in {route.to}
                </h2>
                <p style={{ fontSize:14, color:"var(--mu)", marginBottom:20 }}>
                  We operate into {toPads.length} approved landing location{toPads.length!==1?"s":""} in {route.to}.
                </p>
                <PadList pads={toPads} selPad={selToPad} setSelPad={setSelToPad} city={route.to}/>
              </div>
            )}

            {/* Aircraft */}
            <div style={{ padding:"44px 0", borderTop:"1px solid var(--bd)" }}>
              <div className="eyebrow" style={{ marginBottom:10 }}>Your aircraft</div>
              <h2 style={{ fontWeight:800, fontSize:"clamp(18px,2.5vw,22px)", letterSpacing:"-.04em", marginBottom:8 }}>
                Aircraft available on this route
              </h2>
              <p style={{ fontSize:14, color:"var(--mu)", marginBottom:20 }}>Full private charter. No shared seats. All prices include VAT.</p>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(2,1fr)", gap:12 }} className="form-grid-2">
                {dedupeRouteCards(route, FLEET).map(g => {
                  // DEDUP: one card per (model x operator). f = a VISIBLE representative
                  // instance (image/name/specs/link); p = the group MIN cardFromPrice
                  // INCLUDING hidden instances (so the cheapest card === the route hero).
                  const f = g.rep;
                  const p = g.fromPrice;
                  return (
                    <div key={g.model + '|' + (g.operatorId || '')} className="card card-hv" style={{ padding:0, overflow:"hidden", cursor:"pointer" }} onClick={() => setPage("fleet:"+f.id)}>
                    <div style={{ aspectRatio:"4/3", width:"100%", overflow:"hidden", position:"relative", background:"var(--off)" }}>
                      <Img preset="card" src={f.img} alt={f.name + " helicopter exterior"} style={{ width:"100%", height:"100%", objectFit:"contain" }}/>
                    </div>
                    <div style={{ padding:"14px 18px 18px" }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:8 }}>
                    <div>
                    <div style={{ fontWeight:800, fontSize:14 }}>{f.name}</div>
                    <div style={{ fontSize:12, color:"var(--mu)" }}>{f.type}</div>
                    </div>
                    <div style={{ fontWeight:800, fontSize:17, color:"var(--bl)" }}>{routeCardFromLabel(p)}</div>
                    </div>
                    <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:12 }}>
                    {[[<Users size={11}/>,f.seats+" pax"], [<Briefcase size={11}/>,<BagsLine>{f.maxLuggage} bags</BagsLine>], [<Gauge size={11}/>,fmtFlightShort(calcFlightMin(route.from,route.to,f.id)||routeFlightMin(route))]].map(([ic,t],i) => (
                    <span key={i} style={{ display:"inline-flex", alignItems:"center", gap:4, fontSize:11, fontWeight:600, color:"var(--mu)", background:"var(--off)", padding:"2px 7px", borderRadius:5 }}>{ic}{t}</span>
                    ))}
                    </div>
                    <button className="btn btn-ol btn-sm" style={{ width:"100%", justifyContent:"center" }} onClick={(e) => { e.stopPropagation(); openBooking({ from:route.from, to:route.to }); }}>
                    Select {f.name} <ArrowRight size={12}/>
                    </button>
                    </div></div>
                  );
                })}
              </div>
            </div>

            {/* Practical info */}
            {c.practicalInfo && (
              <div style={{ padding:"44px 0", borderTop:"1px solid var(--bd)" }}>
                <div className="eyebrow" style={{ marginBottom:10 }}>On the day</div>
                <h2 style={{ fontWeight:800, fontSize:"clamp(18px,2.5vw,22px)", letterSpacing:"-.04em", marginBottom:16 }}>Practical information</h2>
                <p className="body-p">{c.practicalInfo}</p>
              </div>
            )}

            {/* FAQs */}
            {/* Key facts — STRUCTURAL rows (Departure / Arrival options /
                Flight time / Price) from SYSTEM data via the shared builder
                (system-key-facts.js, same function as SSR); soft generated
                rows appended, structural duplicates dropped. */}
            <KeyFactsGrid facts={mergeKeyFacts(buildRouteSystemKeyFacts(route, { fleet: FLEET, locations: LOCATION_DB, operators: OPERATOR_FLEET }), c.keyFacts)}/>

            <ReviewsSection reviews={publishedReviewsFor("route", route, REVIEWS)}/>

            {c.faqs && c.faqs.length > 0 && (
              <div style={{ padding:"44px 0", borderTop:"1px solid var(--bd)" }}>
                <div className="eyebrow" style={{ marginBottom:10 }}>Common questions</div>
                <h2 style={{ fontWeight:800, fontSize:"clamp(18px,2.5vw,22px)", letterSpacing:"-.04em", marginBottom:20 }}>
                  FAQs -- helicopter from {route.from} to {route.to}
                </h2>
                {c.faqs.filter(f => f && f.q && f.a).map((f,i) => <FaqItem key={i} q={f.q} a={f.a}/>)} </div>
            )}

            {/* Hotels at destination */}
            {getHotelsForRoute(route.to).length > 0 && (
              <div style={{ padding:"44px 0", borderTop:"1px solid var(--bd)" }}>
                <div className="eyebrow" style={{ marginBottom:10 }}>Where to stay</div>
                <h2 style={{ fontWeight:800, fontSize:"clamp(18px,2.5vw,22px)", letterSpacing:"-.04em", marginBottom:8 }}>
                  Top hotels in {route.to}
                </h2>
                <p style={{ fontSize:14, color:"var(--mu)", marginBottom:20 }}>
                  We arrange helicopter transfers directly to these properties -- including hotels with private helipads.
                </p>
                <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
                  {getHotelsForRoute(route.to).map(h => (
                    <div key={h.id} className="card" style={{ padding:"16px 18px", display:"flex", gap:14, alignItems:"center" }}>
                      <div style={{ width:60, height:60, borderRadius:10, overflow:"hidden", flexShrink:0 }}>
                        <Img preset="card" src={h.img} alt={h.name + " — helicopter transfer hotel"} style={{ width:"100%", height:"100%", objectFit:"cover" }}/>
                      </div>
                      <div style={{ flex:1 }}>
                        <div style={{ fontWeight:700, fontSize:14, marginBottom:3 }}>{h.name}</div>
                        <div style={{ fontSize:12, color:"var(--mu)", marginBottom:5 }}>{[("★".repeat(Math.min(h.stars || 0, 5))), h.cat].filter(Boolean).join(" - ")}</div>
                        <div style={{ display:"inline-flex", alignItems:"center", gap:5, padding:"2px 8px", borderRadius:100, fontSize:11, fontWeight:600, background: isDirectHelipad(h) ? "var(--grbg)" : "var(--off)", color:      isDirectHelipad(h) ? "var(--gr)" : "var(--mu)" }}>
                          {isDirectHelipad(h) ? <><svg width="11" height="11" viewBox="0 0 11 11" fill="none" style={{display:"inline-block",verticalAlign:"middle"}}><circle cx="5.5" cy="5.5" r="4.2" stroke="var(--gr)" strokeWidth="1.2"/><line x1="3.8" y1="3.7" x2="3.8" y2="7.3" stroke="var(--gr)" strokeWidth="1.3" strokeLinecap="round"/><line x1="7.2" y1="3.7" x2="7.2" y2="7.3" stroke="var(--gr)" strokeWidth="1.3" strokeLinecap="round"/><line x1="3.8" y1="5.5" x2="7.2" y2="5.5" stroke="var(--gr)" strokeWidth="1.3" strokeLinecap="round"/></svg> Direct helipad</> : <><svg width="11" height="11" viewBox="0 0 24 24" fill="var(--mu)" style={{display:"inline-block",verticalAlign:"middle"}}><path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.85 7h10.29l1.08 3.11H5.77L6.85 7zM19 17H5v-5h14v5z"/><circle cx="7.5" cy="14.5" r="1.5"/><circle cx="16.5" cy="14.5" r="1.5"/></svg> {h.transferMin ? h.transferMin + " min" : "Transfer"}</>} </div>
                      </div>
                      <button className="btn btn-ol btn-sm" onClick={() => openBooking({ to: route.to })}>
                        Fly here <ArrowRight size={12}/>
                      </button>
                    </div>
                  ))}
                </div>
                <button className="btn btn-ol btn-sm" style={{ marginTop:14 }} onClick={() => setPage("hotels")}>
                  All hotels in {route.to} <ArrowRight size={12}/>
                </button>
              </div>
            )}

            {/* Related routes */}
            {related.length > 0 && (
              <div style={{ padding:"44px 0", borderTop:"1px solid var(--bd)" }}>
                <div className="eyebrow" style={{ marginBottom:10 }}>More routes</div>
                <h2 style={{ fontWeight:800, fontSize:"clamp(18px,2.5vw,22px)", letterSpacing:"-.04em", marginBottom:16 }}>Related helicopter routes</h2>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(2,1fr)", gap:10, paddingTop:4 }} className="form-grid-2">
                  {related.map(r => (
                    <SubRouteCard key={r.slug} r={r} setPage={setPage} openBooking={openBooking}/>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sticky sidebar */}
          <div style={{ display:"flex", flexDirection:"column", gap:16, position:"sticky", top:"calc(var(--nav) + 20px)" }}>
            {/* Book CTA */}
            <div style={{ background:"var(--bl)", borderRadius:16, padding:"24px 20px" }}>
              <div style={{ fontWeight:800, fontSize:16, color:"white", marginBottom:6 }}>{route.from} to {route.to}</div>
              <p style={{ fontSize:13, color:"rgba(255,255,255,.85)", lineHeight:1.65, marginBottom:16 }}>{fmtFlightShort(routeFlightMin(route))} • Full aircraft charter</p>
              <button className="btn" style={{ background:"white", color:"var(--bl)", fontWeight:700, width:"100%", justifyContent:"center" }} onClick={() => openBooking({ from:route.from, to:route.to })}>
                Get a quote <ArrowRight size={14}/>
              </button>
            </div>

            {/* Route facts */}
            <div className="card" style={{ padding:20 }}>
              <div style={{ fontSize:11, fontWeight:700, letterSpacing:".08em", textTransform:"uppercase", color:"var(--mu)", marginBottom:14 }}>Route details</div>
              {[
                ["Flight time", fmtFlightShort(routeFlightMin(route))],
                ["From", route.from + " (" + fromPads.length + " helipads)"],
                ["To", route.to + " (" + toPads.length + " helipads)"],
                ["Aircraft", "4 types available"],
                ["Charter type", "Full aircraft"],
              ].map(([l,v]) => (
                <div key={l} style={{ display:"flex", justifyContent:"space-between", padding:"8px 0", borderBottom:"1px solid var(--bd)", fontSize:13 }}>
                  <span style={{ color:"var(--mu)" }}>{l}</span>
                  <span style={{ fontWeight:600, color:"var(--ink)", textAlign:"right", maxWidth:"55%" }}>{v}</span>
                </div>
              ))}
            </div>

            {/* Trust */}
            <div className="card" style={{ padding:20 }}>
              {[
                {icon:<Shield size={13} color="var(--gr)"/>, text:"EASA-certified operators"},
                {icon:<Check size={13} color="var(--gr)"/>, text:"Fixed pricing — no surprises"},
                {icon:<Zap size={13} color="var(--gr)"/>, text:"Fast response times"},
                {icon:<CreditCard size={13} color="var(--gr)"/>, text:"Weather refund guarantee"},
              ].map(t => (
                <div key={t.text} style={{ display:"flex", gap:10, alignItems:"center", padding:"7px 0", borderBottom:"1px solid var(--bd)", fontSize:13 }}>
                  {t.icon} <span>{t.text}</span>
                </div>
              ))}
            </div>

            {/* Contact */}
            <div className="card" style={{ padding:20 }}>
              <div style={{ fontSize:13, color:"var(--mu)", lineHeight:1.6, marginBottom:14 }}>Questions about this route?</div>
              <button className="btn btn-ol btn-sm" style={{ width:"100%" }} onClick={() => setPage("contact")}>Contact us</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


// -- SEO META MANAGEMENT ------------------------------------------------------
const SITE = {
  name:    __CMS?.SETTINGS?.siteName  || BRAND.name,
  tagline: __CMS?.SETTINGS?.tagline   || "Private Helicopter Charter Greece",
  url:     __CMS?.SETTINGS?.siteUrl   || "https://www.heligreece.com",
  logo:    __CMS?.SETTINGS?.logoUrl   || "https://www.heligreece.com/logo.png",
  // ogImage from Sanity site settings; falls back to brand logo (no Unsplash).
  image:   __CMS?.SETTINGS?.ogImage   || __CMS?.SETTINGS?.logoUrl || "https://www.heligreece.com/logo.png",
  phone:   __CMS?.SETTINGS?.phone     || "",
  email:   CONTACT_EMAIL,
  address: { street: __CMS?.SETTINGS?.address?.street || "Ellinikon Heliport", city: __CMS?.SETTINGS?.address?.city || "Athens", country: __CMS?.SETTINGS?.address?.country || "GR", zip: __CMS?.SETTINGS?.zip || "16777" },
  geo:     { lat: __CMS?.SETTINGS?.geoLat || 37.893, lng: __CMS?.SETTINGS?.geoLng || 23.726 },
};

const _PM_DEFAULTS = {
  home:         { title:"Private Helicopter Charter Greece | " + BRAND.name, desc:"Book a private helicopter charter across Greece. Athens to Mykonos in 28 min, Santorini in 42 min. Full aircraft, HCAA-licensed operators." },
  fleet:        { title:"Our Helicopter Fleet | Private single- & twin-engine charters in Greece | " + BRAND.name, desc:"Charter a private helicopter across the Greek islands — single- and twin-engine aircraft, full aircraft with no shared seats, EASA-certified and HCAA-licensed operators." },
  destinations: { title:"Helicopter Destinations in Greece | " + BRAND.name, desc:"Fly to 20+ Greek island destinations by private helicopter. Mykonos, Santorini, Corfu, Rhodes and more. Instant online booking." },
  routes:       { title:"Helicopter Routes Greece | Athens to Islands | " + BRAND.name, desc:"42 private helicopter routes across Greece. Island-to-island connections, Athens hub routes, and Cyclades loops. View flight times and prices." },
  hotels:       { title:"Luxury Hotel Helicopter Transfers Greece | " + BRAND.name, desc:"Private helicopter transfers to Greece's top luxury hotels. Katikies, Grace Santorini, Amanzoe and 20 more. Direct helipad connections." },
  "how-it-works": { title:"How It Works | " + BRAND.name, desc:"Browse all available charter helicopters in Greece with real prices. Book in minutes. No risk payment - you're only charged when your flight is confirmed." },
  blog:         { title:"Blog | Helicopter Travel in Greece | " + BRAND.name, desc:"Tips, guides and stories about private helicopter travel across the Greek islands." },
  safety:       { title:"Safety | HCAA-Licensed Operators | " + BRAND.name, desc:"Safety is our priority. All charter operators are HCAA and EASA certified. Full insurance, 5-step pre-flight checks, weather protection policy." },
  about:        { title:"About " + BRAND.name + " | Private Helicopter Charter Greece", desc:BRAND.name + " is a private helicopter charter broker based in Athens, Greece. HCAA-licensed operators, EASA-compliant fleet." },
  contact:      { title:"Contact | " + BRAND.name, desc:"Get in touch with our team. We respond as soon as possible. Email or our contact form." },
  privacy:      { title:"Privacy Policy | " + BRAND.name, desc:"How we collect and use your personal data at " + BRAND.name + "." },
  terms:        { title:"Terms of Service | " + BRAND.name, desc:"Terms and conditions for helicopter charter bookings made via " + BRAND.name + "." },
  cookies:      { title:"Cookie Policy | " + BRAND.name, desc:"Cookie policy for heligreece.com." },
};
// CMS page meta overrides from siteSettings.pageMeta array
const _PM_CMS = (__CMS?.SETTINGS?.pageMeta || []).reduce((acc, p) => { if (p.page) acc[p.page] = { title: p.title || _PM_DEFAULTS[p.page]?.title, desc: p.desc || _PM_DEFAULTS[p.page]?.desc }; return acc; }, {});
// NOTE (2026-07-08): the merged PAGE_META lookup is gone — useSEO now derives
// every head value from the SAME buildMetaTags chain the SSR uses; only the
// owner-curated _PM_CMS overrides (siteSettings.pageMeta) still apply on top.
// _PM_DEFAULTS remains as the fallback title/desc pool _PM_CMS composes from.

// Maps a SPA page key to the /api/seo/load (type, slug) pair for the detail
// types whose curated seoContent doc lives OUTSIDE the public payload. Shares
// loadSEO's _seoCache with the page components (one fetch per page).
const _seoDocFor = (page) => {
  if (page.startsWith("dest:"))    return { type: "dest",     slug: page.slice(5) };
  if (page.startsWith("route:"))   return { type: "route",    slug: page.slice(6) };
  if (page.startsWith("hotel:"))   return { type: "hotel",    slug: page.slice(6) };
  if (page.startsWith("fleet:"))   return { type: "aircraft", slug: page.slice(6) };
  if (page.startsWith("charter-")) return { type: "charter",  slug: page };
  return null;
};
// /api/seo/load aliases metaDesc as metaDescription — normalise so
// buildMetaTags (which reads .metaDesc) sees the override.
const _normSeoDoc = (c) => (c ? { ...c, metaDesc: c.metaDesc || c.metaDescription } : null);

// HYDRATION GUARD (2026-07-08) — on a fresh deep-link load the SSR head
// already carries the CORRECT per-URL meta (buildMetaTags in index.astro /
// [...slug].astro: title, description, canonical, og:*, twitter:*, robots,
// @graph JSON-LD). The SPA previously overwrote it on mount with generic
// homepage values and a broken category canonical ("/dest/") because the
// call site truncated the page key to "dest:"/"route:"/"blog:". The guard
// skips EVERY head write while the SPA is still on the page it hydrated on
// (including async re-runs on that page) — head updates happen only after a
// real SPA-internal navigation, and from then on for every page (navigating
// back to the first page updates correctly too).
let _seoFirstPage = null;
let _seoNavigated = false;

function useSEO(page, seoData) {
  useEffect(() => {
    if (!_seoNavigated) {
      if (_seoFirstPage === null) _seoFirstPage = page;
      if (page === _seoFirstPage) return; // hydration: the SSR head is the truth
      _seoNavigated = true;
    }

    // SINGLE SOURCE OF TRUTH — the same resolveEntity + buildMetaTags chain
    // the SSR head uses, over the same payload (window.__SANITY_DATA__ is the
    // sanitised fetchAllSiteData object both index.astro and [...slug].astro
    // inject). No payload → leave the existing head untouched (never write
    // defaults over correct values).
    const raw = (typeof window !== "undefined") ? window.__SANITY_DATA__ : null;
    if (!raw) return;

    const path = pageToPath(page);
    const { entity, entityType } = resolveEntity(path, raw);

    // Helper to set/create a meta tag
    const setMeta = (sel, attr, val) => {
      let el = document.querySelector(sel);
      if (!el) { el = document.createElement("meta"); document.head.appendChild(el); }
      el.setAttribute(attr, val);
    };
    const setNamed = (name, content) => { setMeta(`meta[name="${name}"]`, "name", name); document.querySelector(`meta[name="${name}"]`).setAttribute("content", content); };
    const setProp  = (prop, content) => { setMeta(`meta[property="${prop}"]`, "property", prop); document.querySelector(`meta[property="${prop}"]`).setAttribute("content", content); };

    const apply = (seoContent) => {
      const meta = buildMetaTags({ path, siteUrl: SITE.url, settings: raw.settings, allData: raw, entity, entityType, seoContent });
      // Owner-curated per-page CMS overrides (siteSettings.pageMeta) still win
      // on plain page keys (empty in production today; kept as the CMS hook).
      const pm = _PM_CMS[page];
      const title = (pm && pm.title) || meta.title;
      const desc  = (pm && pm.desc)  || meta.description;

      document.title = title;
      setNamed("description", desc);
      setNamed("robots", meta.robots);
      setNamed("theme-color", "#0D56A6");

      // Open Graph — og:image/twitter:image only change when a page-specific
      // source exists (never downgraded to a placeholder).
      setProp("og:title", title);
      setProp("og:description", desc);
      setProp("og:url", meta.canonical);
      setProp("og:type", meta.ogType || "website");
      setProp("og:site_name", SITE.name);
      if (meta.ogImage) setProp("og:image", meta.ogImage);

      // Twitter Card — summary_large_image only when a share image exists
      // (mirrors the SSR heads).
      setNamed("twitter:card", meta.ogImage ? "summary_large_image" : "summary");
      setNamed("twitter:title", title);
      setNamed("twitter:description", desc);
      if (meta.ogImage) setNamed("twitter:image", meta.ogImage);

      // Canonical — always the exact absolute detail URL from buildMetaTags
      // (never a category fallback like "/dest/").
      let canonEl = document.querySelector('link[rel="canonical"]');
      if (!canonEl) { canonEl = document.createElement("link"); canonEl.rel = "canonical"; document.head.appendChild(canonEl); }
      canonEl.href = meta.canonical;

      // JSON-LD (SPA-side #ld-json block, unchanged mechanism)
      const schema = buildSchema(page, title, desc, meta.ogImage || SITE.image, meta.canonical, seoContent || seoData);
      let ldEl = document.getElementById("ld-json");
      if (!ldEl) { ldEl = document.createElement("script"); ldEl.id = "ld-json"; ldEl.type = "application/ld+json"; document.head.appendChild(ldEl); }
      ldEl.textContent = JSON.stringify(schema);
    };

    // First pass runs synchronously with whatever seoContent is already at
    // hand (shared cache / caller) — these values are ALREADY page-specific
    // (right title pattern, right canonical), so no generic intermediate state
    // ever appears. The async pass only refines with the curated doc.
    const doc = _seoDocFor(page);
    const cacheKey = doc ? doc.type + ":" + doc.slug : null;
    apply(_normSeoDoc((cacheKey && _seoCache[cacheKey]) || seoData));
    let stale = false;
    if (doc && !(cacheKey && _seoCache[cacheKey])) {
      loadSEO(doc.type, doc.slug).then(c => { if (!stale && c) apply(_normSeoDoc(c)); }).catch(() => {});
    }
    return () => { stale = true; };
  }, [page, seoData?.metaTitle]);
}

function buildSchema(page, title, desc, image, url, seoData) {
  const org = {
    "@type": "Organization",
    "@id": SITE.url + "/#org",
    "name": SITE.name,
    "url": SITE.url,
    "logo": SITE.logo,
    ...(SITE.phone ? {"telephone": SITE.phone} : {}),
    "email": SITE.email,
    "address": { "@type":"PostalAddress", "streetAddress":SITE.address.street, "addressLocality":SITE.address.city, "addressCountry":SITE.address.country },
  };

  const graph = [org];

  // WebSite (always)
  graph.push({ "@type":"WebSite", "@id":SITE.url+"/#website", "url":SITE.url, "name":SITE.name, "inLanguage":"en-GB", "publisher":{"@id":SITE.url+"/#org"} });

  // WebPage (always)
  const webpage = { "@type":"WebPage", "@id":url, "url":url, "name":title, "description":desc, "inLanguage":"en-GB", "isPartOf":{"@id":SITE.url+"/#website"} };
  graph.push(webpage);

  // BreadcrumbList — emit ONLY where the SSR JSON-LD (buildJsonLd in
  // [...slug].astro / index.astro) does NOT already emit one, so the page never
  // ships TWO conflicting BreadcrumbLists (GSC "Missing field item" / duplicate).
  // SSR owns every DETAIL page (dest:/route:/hotel:/blog:/fleet:<id>) + the static
  // about/safety/contact/how-it-works pages; the SPA keeps the LIST/legal pages
  // (hotels, fleet, routes, destinations, blog, terms, privacy, cookies) where SSR
  // emits none. Home has no breadcrumb on either side.
  const ssrOwnsBreadcrumb = /^(dest|route|hotel|blog|fleet):/.test(page)
    || page === "about" || page === "safety" || page === "contact" || page === "how-it-works";
  if (page !== "home" && !ssrOwnsBreadcrumb) {
    const crumbs = [{ "@type":"ListItem", "position":1, "name":"Home", "item":SITE.url }];
    if (page.startsWith("dest:")) {
      crumbs.push({ "@type":"ListItem", "position":2, "name":"Destinations", "item":SITE.url+"/destinations" });
      crumbs.push({ "@type":"ListItem", "position":3, "name":seoData?.h1 || page.slice(5) });
    } else if (page.startsWith("route:")) {
      crumbs.push({ "@type":"ListItem", "position":2, "name":"Routes", "item":SITE.url+"/routes" });
      crumbs.push({ "@type":"ListItem", "position":3, "name":title });
    } else if (page.startsWith("hotel:")) {
      crumbs.push({ "@type":"ListItem", "position":2, "name":"Hotels", "item":SITE.url+"/hotels" });
      crumbs.push({ "@type":"ListItem", "position":3, "name":title });
    } else if (page.startsWith("blog:")) {
      crumbs.push({ "@type":"ListItem", "position":2, "name":"Journal", "item":SITE.url+"/blog" });
      crumbs.push({ "@type":"ListItem", "position":3, "name":title });
    } else {
      const labels = { fleet:"Fleet", destinations:"Destinations", routes:"Routes", hotels:"Hotels", safety:"Safety", about:"About", contact:"Contact", blog:"Journal", terms:"Terms", privacy:"Privacy", cookies:"Cookies" };
      crumbs.push({ "@type":"ListItem", "position":2, "name":labels[page]||title });
    }
    // GSC flags a BreadcrumbList itemListElement that lacks `item` ("missing field
    // item"). Non-final crumbs already carry their section URL; give the FINAL
    // crumb the canonical page URL too (item is optional on the last item per
    // Google's spec, but supplying it removes the warning).
    if (crumbs.length && !crumbs[crumbs.length - 1].item) crumbs[crumbs.length - 1].item = url;
    graph.push({ "@type":"BreadcrumbList", "itemListElement":crumbs });
  }

  // Homepage: LocalBusiness + FAQ
  if (page === "home") {
    graph.push({
      "@type": ["LocalBusiness","TravelAgency"],
      "@id": SITE.url + "/#business",
      "name": SITE.name,
      "description": desc,
      "url": SITE.url,
      ...(SITE.phone ? {"telephone": SITE.phone} : {}),
      "email": SITE.email,
      "image": image,
      "priceRange": "€€€€",
      "currenciesAccepted": "EUR",
      "paymentAccepted": "Credit Card",
      "address": { "@type":"PostalAddress", "streetAddress":SITE.address.street, "addressLocality":SITE.address.city, "addressCountry":SITE.address.country },
      "geo": { "@type":"GeoCoordinates", "latitude":SITE.geo.lat, "longitude":SITE.geo.lng },
      "openingHoursSpecification": [
        { "@type":"OpeningHoursSpecification", "dayOfWeek":["Monday","Tuesday","Wednesday","Thursday","Friday"], "opens":"08:00", "closes":"21:00" },
        { "@type":"OpeningHoursSpecification", "dayOfWeek":"Saturday", "opens":"09:00", "closes":"18:00" },
        { "@type":"OpeningHoursSpecification", "dayOfWeek":"Sunday", "opens":"10:00", "closes":"17:00" },
      ],
      "sameAs": ["https://www.instagram.com/greekhelicopters", "https://www.facebook.com/greekhelicopters"],
      "hasOfferCatalog": {
        "@type":"OfferCatalog",
        "name":"Helicopter Charter Services",
        "itemListElement": [
          { "@type":"Offer", "itemOffered":{ "@type":"Service", "name":"Island Transfer by Helicopter", "description":"Private helicopter transfers between Greek islands and Athens" } },
          { "@type":"Offer", "itemOffered":{ "@type":"Service", "name":"Sightseeing Helicopter Flights", "description":"Scenic helicopter tours over the Greek islands" } },
          { "@type":"Offer", "itemOffered":{ "@type":"Service", "name":"VIP Airport Helicopter Transfer", "description":"Private helicopter airport connections in Greece" } },
        ],
      },
    });
    // Homepage FAQ
    graph.push({ "@type":"FAQPage", "mainEntity":[
      { "@type":"Question", "name":"How much does a helicopter charter in Greece cost?", "acceptedAnswer":{ "@type":"Answer", "text":"Pricing is per aircraft, not per seat, and depends on the route, the aircraft and the helipads. Each route page shows its current from-price, or you can request a quote in under a minute. Because you charter the whole aircraft, groups of 3-5 often find it comparable to business-class ferry tickets." } },
      { "@type":"Question", "name":"How far in advance should I book?", "acceptedAnswer":{ "@type":"Answer", "text":"We recommend booking 2-3 weeks ahead during peak season (July-August). Off-season, 3-5 days is usually sufficient. Same-day bookings are possible subject to aircraft availability." } },
      { "@type":"Question", "name":"What is the cancellation policy?", "acceptedAnswer":{ "@type":"Answer", "text":"Each operator has their own cancellation policy with flexible refund tiers. The earlier you cancel, the higher the refund. A 3% card-processing fee is retained on full refunds only — partial-refund tiers are paid in full at the tier amount shown. Weather cancellations receive a full refund or free rebook. The exact cancellation tiers are displayed during booking." } },
      { "@type":"Question", "name":"How long is the flight from Athens to Mykonos?", "acceptedAnswer":{ "@type":"Answer", "text":"Athens to Mykonos takes approximately 28 minutes by helicopter. Compare this to 2.5-5 hours by ferry or 45 minutes by commercial flight (plus airport time)." } },
      { "@type":"Question", "name":"Are the helicopters safe?", "acceptedAnswer":{ "@type":"Answer", "text":"We work exclusively with HCAA-licensed, EASA-compliant operators. All aircraft are maintained under EASA Part-145 programmes. Pilots have minimum 1,500 hours total flight time. Weather protection is included on all bookings." } },
    ]});
  }

  // Fleet page: a helicopter charter is a SERVICE (route-dependent / on-request
  // pricing), not a fixed-price Product — a Product without a valid offer always
  // fails Google's "offers, review or aggregateRating required" rule (GSC: the
  // fleet "from" price is not reliably available here, so the AggregateOffer had
  // an EMPTY lowPrice → invalid). @type Service removes that requirement and
  // matches the SSR buildProduct (jsonld.js). A real "from" price still adds a
  // valid AggregateOffer; when none exists the Service is valid with no offers
  // (never an empty/zero placeholder price).
  if (page === "fleet") {
    FLEET.forEach(f => {
      const low = Number(f.resolvedPrice || f.price) || null;
      graph.push({
        "@type": "Service",
        "name": f.name + " Helicopter Charter",
        ...(f.desc ? { "description": f.desc } : {}),
        "serviceType": "Helicopter charter",
        "provider": { "@id": SITE.url + "/#org" },
        "areaServed": { "@type":"Country", "name":"Greece" },
        "category": "Helicopter Charter",
        ...(low > 0 ? { "offers": { "@type":"AggregateOffer", "priceCurrency":"EUR", "lowPrice":low, "offerCount":SEO_ROUTES.length || 1, "availability":"https://schema.org/InStock" } } : {}),
      });
    });
  }

  // Destination SEO page
  if (page && page.startsWith && page.startsWith("dest:") && seoData) {
    const destName = seoData.h1 || title;
    graph.push({
      "@type": "Service",
      "name": "Helicopter Charter to " + destName,
      "provider": { "@id": SITE.url + "/#org" },
      "areaServed": { "@type":"Place", "name":destName + ", Greece" },
      "description": desc,
      "url": url,
      // Finite-guarded: a real price emits a real Offer; no price emits NO
      // offers block (never a fabricated price:0 placeholder).
      ...((Number.isFinite(Number(seoData.price)) && Number(seoData.price) > 0) ? { "offers": { "@type":"Offer", "priceCurrency":"EUR", "price":Number(seoData.price), "availability":"https://schema.org/InStock" } } : {}),
    });
    if (seoData.faq && seoData.faq.length) {
      graph.push({ "@type":"FAQPage", "mainEntity": seoData.faq.map(q => ({ "@type":"Question", "name":q.q, "acceptedAnswer":{ "@type":"Answer", "text":q.a } })) });
    }
  }

  // Route SEO page
  if (page && page.startsWith && page.startsWith("route:") && seoData) {
    graph.push({
      "@type": "Service",
      "name": title,
      "provider": { "@id": SITE.url + "/#org" },
      "description": desc,
      "url": url,
      ...(seoData.price ? {"offers":{ "@type":"Offer", "priceCurrency":"EUR", "price":seoData.price, "availability":"https://schema.org/InStock" }} : {}),
    });
    if (seoData.faq && seoData.faq.length) {
      graph.push({ "@type":"FAQPage", "mainEntity": seoData.faq.map(q => ({ "@type":"Question", "name":q.q, "acceptedAnswer":{ "@type":"Answer", "text":q.a } })) });
    }
  }

  // Blog article
  if (page && page.startsWith && page.startsWith("blog:")) {
    const slug = page.slice(5);
    const blog = BLOGS.find(b => b.slug === slug);
    if (blog) {
      graph.push({
        "@type": "Article",
        "headline": blog.title,
        "description": blog.exc,
        "author": { "@type":"Organization", "name":blog.author || SITE.name },
        "publisher": { "@id": SITE.url + "/#org" },
        "datePublished": blog.date,
        "image": blog.img || image,
        "mainEntityOfPage": url,
      });
    }
  }

  return { "@context":"https://schema.org", "@graph":graph };
}


// ============================================================
// ANALYTICS — loaded after cookie consent via the built-in banner
let CLARITY_ID = "";
let GA_MEASUREMENT_ID = "";
let GADS_CONVERSION_ID = "";
let GADS_CONVERSION_LABEL = "";
export let TURNSTILE_KEY = "";
let CRISP_ID = "";
if (__CMS) {
  if (__CMS.SETTINGS.clarityId)           CLARITY_ID = __CMS.SETTINGS.clarityId;
  if (__CMS.SETTINGS.gaMeasurementId)     GA_MEASUREMENT_ID = __CMS.SETTINGS.gaMeasurementId;
  if (__CMS.SETTINGS.gadsConversionId)    GADS_CONVERSION_ID = __CMS.SETTINGS.gadsConversionId;
  if (__CMS.SETTINGS.gadsConversionLabel) GADS_CONVERSION_LABEL = __CMS.SETTINGS.gadsConversionLabel;
  if (__CMS.SETTINGS.turnstileSiteKey)    TURNSTILE_KEY = __CMS.SETTINGS.turnstileSiteKey;
  if (__CMS.SETTINGS.crispWebsiteId)      CRISP_ID = __CMS.SETTINGS.crispWebsiteId;
}

// Landing search string, captured at module eval — i.e. what the address bar
// carried when THIS document loaded, before the mount-time replaceState or any
// SPA pushState could touch it. Feeds the gclid/utm page_location recovery in
// initAnalytics (adsPageLocationOverride); never persisted, dies with the page.
const LANDING_SEARCH = typeof window !== "undefined" ? window.location.search : "";

function initAnalytics() {
  if (window._analyticsLoaded) return;
  window._analyticsLoaded = true;
  // NOTE: consent signals are owned solely by applyConsent(), which runs
  // immediately before this and maps all six Consent-Mode v2 signals
  // (incl. advertising → ad_user_data/ad_personalization/ad_storage). This
  // function must NOT issue its own consent update — the old partial update
  // here clobbered functionality_storage/personalization_storage to granted
  // on analytics-only consent (too permissive). Script-loading only below.
  if (CLARITY_ID) {
    (function(c,l,a,r,i,t,y){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
    t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
    y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
    })(window,document,"clarity","script",CLARITY_ID);
  }
  if (GA_MEASUREMENT_ID || GADS_CONVERSION_ID) {
    const gtagId = GA_MEASUREMENT_ID || GADS_CONVERSION_ID;
    const s = document.createElement("script");
    s.async = true;
    s.src = "https://www.googletagmanager.com/gtag/js?id=" + gtagId;
    document.head.appendChild(s);
    window.gtag("js", new Date());
    // gclid/utm recovery: GA4 loads only after analytics consent, so its first
    // hit can miss the click params when the user navigated away from the
    // landing URL before accepting the banner (the [page] pushState carries no
    // query). Override page_location with the CURRENT url + the landing ads
    // params in exactly that case; null in every other (organic landing, or
    // the url still carries the params — then gtag reads the real URL).
    const adsPageLocation = adsPageLocationOverride(LANDING_SEARCH, window.location.href);
    if (GA_MEASUREMENT_ID)    window.gtag("config", GA_MEASUREMENT_ID, { anonymize_ip: true, ...(adsPageLocation ? { page_location: adsPageLocation } : {}) });
    if (GADS_CONVERSION_ID)   window.gtag("config", GADS_CONVERSION_ID);
  }
}

// Google Consent Mode v2 defaults — set BEFORE any scripts load
function initConsentDefaults() {
  window.dataLayer = window.dataLayer || [];
  window.gtag = function(){ window.dataLayer.push(arguments); };
  window.gtag("consent", "default", {
    ad_personalization: "denied",
    ad_storage: "denied",
    ad_user_data: "denied",
    analytics_storage: "denied",
    functionality_storage: "denied",
    personalization_storage: "denied",
    security_storage: "granted",
  });
  window.gtag("set", "ads_data_redaction", true);
}
initConsentDefaults();

// Crisp Live Chat — loaded from CMS crispWebsiteId. Gated behind the
// "Functional" consent toggle in the built-in banner.
function initCrisp() {
  if (!CRISP_ID || window.$crisp) return;
  const stored = localStorage.getItem("cookie-consent");
  if (stored) {
    try {
      const parsed = JSON.parse(stored);
      if (parsed.consent?.functional || parsed.choice === "all") loadCrispScript();
    } catch(e) {}
  }
}
function loadCrispScript() {
  if (window.$crisp) return;
  window.$crisp = [];
  window.CRISP_WEBSITE_ID = CRISP_ID;
  const s = document.createElement("script");
  s.src = "https://client.crisp.chat/l.js";
  s.async = true;
  document.head.appendChild(s);
  window.$crisp.push(["config", "color:theme", "blue"]);
  // crisp_message_sent — the USER sent a chat message (not opening the widget).
  // Registered exactly ONCE: loadCrispScript() early-returns when window.$crisp
  // already exists, so remounts / React Strict-Mode double-effects can't add a
  // second handler. This lives INSIDE loadCrispScript (called only on Functional
  // consent), so the event respects the same consent coupling as Crisp itself.
  // Only the FIRST sent message per browser session becomes an event (a
  // sessionStorage flag), so a 20-message conversation is one lead, not 20. No
  // message content ever enters the dataLayer — event name only.
  window.$crisp.push(["on", "message:sent", () => {
    try {
      const ss = window.sessionStorage;
      if (ss && ss.getItem("gh_crisp_msg_sent")) return;
      if (ss) ss.setItem("gh_crisp_msg_sent", "1");
    } catch (e) { /* Safari private mode → fire without dedup */ }
    track("crisp_message_sent", { chat_source: "crisp" });
  }]);
}

export function trackBookingConversion(value, currency = "EUR", transactionId) {
  // Silent no-op until BOTH Google Ads IDs are filled in Sanity site settings —
  // a send_to with an empty label would be an invalid conversion target.
  if (!window.gtag || !GADS_CONVERSION_ID || !GADS_CONVERSION_LABEL) return;
  window.gtag("event", "conversion", {
    send_to: GADS_CONVERSION_ID + "/" + GADS_CONVERSION_LABEL,
    value, currency,
    // transaction_id lets Google Ads dedupe retries/reloads of the same booking.
    ...(transactionId ? { transaction_id: transactionId } : {}),
  });
}

export function track(event, params = {}) {
  if (typeof window === "undefined") return; // SSR-safe: never touch window on the server
  if (window.gtag) window.gtag("event", event, params);
  if (window.clarity) window.clarity("set", event, JSON.stringify(params).slice(0,255));
  // ONE dataLayer entry per call — the gtag() arguments call above (["event",
  // name, params]). That single entry is consumed by BOTH GA4 (arguments path)
  // AND GTM's gtag interop, which surfaces it as a Custom Event named `event`.
  // DO NOT re-add a second explicit object entry (an {event, ...params} push):
  // GTM would then see the SAME logical event twice and fire every bound tag
  // TWICE (the double-push bug — reverted here; 64cc5bc added it in the mistaken
  // belief the arguments entry was invisible to GTM). Clarity is untouched.
}

// Gated-CTA feedback (shared by funnel steps — route / aircraft / details):
// find the FIRST invalid field in source order, fire gated_cta_click once per
// step visit (trackedRef dedup, field KEYS only — never values), scroll it to
// center and focus it when it is a form control. The caller keeps its own
// marking mechanism (setErrors / attempted / gatedAttempt); this helper never
// mutates state and never advances the step.
export function gatedCtaFeedback(step, fields, trackedRef) {
  const first = (fields || []).find(f => !f.isValid);
  if (!first) return null;
  if (trackedRef && !trackedRef.current) {
    trackedRef.current = true;
    track("gated_cta_click", { step, missing: first.key });
  }
  const el = first.el ? first.el.current
    : (typeof document !== "undefined" && first.anchorId ? document.getElementById(first.anchorId) : null);
  if (el) {
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    if (typeof el.focus === "function" && /^(INPUT|SELECT|TEXTAREA)$/.test(el.tagName || "")) el.focus({ preventScroll: true });
  }
  return first.key;
}

// Built-in cookie banner — applies consent choices and loads scripts based
// on granular preferences (analytics + advertising + functional). The
// advertising category drives Consent-Mode v2 ad_storage / ad_user_data /
// ad_personalization plus personalization_storage; rebound from functional
// to advertising in Q-GTM-CONSENT-BUG (personalization_storage is
// advertising-adjacent per Google's spec, not chat/functional).
function applyConsent(consent) {
  if (window.gtag) {
    window.gtag("consent", "update", {
      analytics_storage:       consent.analytics   ? "granted" : "denied",
      ad_storage:              consent.advertising ? "granted" : "denied",
      ad_user_data:            consent.advertising ? "granted" : "denied",
      ad_personalization:      consent.advertising ? "granted" : "denied",
      personalization_storage: consent.advertising ? "granted" : "denied",
      functionality_storage:   consent.functional  ? "granted" : "denied",
    });
  }
  if (consent.analytics) initAnalytics();
  if (consent.functional) initCrisp();
  // Microsoft Clarity consent — parallels the GA4 analytics_storage signal above.
  // Clarity's project has cookie consent enabled, so the tag (loaded by
  // initAnalytics on grant) stays consent-pending and records nothing / sets no
  // _clck/_clsk cookies until clarity('consent') is called. window.clarity is the
  // queue stub the Clarity snippet defines synchronously inside initAnalytics, so
  // this enqueues even before the tag finishes loading. On deny/withdraw it sends
  // clarity('consent', false) (mirrors the GA4 denied path); on an initial deny
  // Clarity was never loaded, so the guard is a no-op — recording only with
  // analytics consent, exactly like GA4 (GDPR-consistent).
  if (window.clarity) window.clarity("consent", !!consent.analytics);
}

function CookieConsent({ setPage, visible, setVisible }) {
  const [showDetails, setShowDetails] = useState(false);
  // GDPR / DSGVO Art. 6(1)(a) + Art. 7: non-essential cookies require an
  // explicit, affirmative opt-in. Defaults MUST be unchecked. User clicks
  // "Accept all" or toggles per-category + "Save settings" to opt in.
  const [analytics, setAnalytics] = useState(false);
  const [advertising, setAdvertising] = useState(false);
  const [functional, setFunctional] = useState(false);
  if (!visible) return null;

  const save = (mode) => {
    const consent = {
      analytics:   mode === "all" ? true : mode === "necessary" ? false : analytics,
      advertising: mode === "all" ? true : mode === "necessary" ? false : advertising,
      functional:  mode === "all" ? true : mode === "necessary" ? false : functional,
    };
    // Version "2.0" added in Q-GTM-CONSENT-BUG when the Advertising toggle
    // was introduced. Restore-on-mount triggers a re-prompt for any record
    // with version != "2.0".
    const record = { choice: mode, consent, timestamp: new Date().toISOString(), version: "2.0" };
    try { localStorage.setItem("cookie-consent", JSON.stringify(record)); } catch(e) {}
    applyConsent(consent);
    try { fetch("/api/consent-log", { method: "POST", headers: {"Content-Type":"application/json"}, body: JSON.stringify(record) }).catch(() => {}); } catch(e) {}
    setVisible(false);
  };

  const Toggle = ({ on, set }) => (
    <button onClick={() => set(!on)} aria-label="Toggle" style={{ width:42, height:24, borderRadius:12, background: on ? "var(--bl)" : "var(--bd2)", border:"none", cursor:"pointer", position:"relative", transition:"background .2s", flexShrink:0 }}>
      <div style={{ width:18, height:18, borderRadius:9, background:"white", boxShadow:"0 1px 3px rgba(0,0,0,.2)", position:"absolute", top:3, left: on ? 21 : 3, transition:"left .2s" }}/>
    </button>
  );

  return (
    <div style={{ position:"fixed", bottom:0, left:0, right:0, zIndex:400, padding:"0 16px 16px", pointerEvents:"none" }}>
      <div style={{ maxWidth:520, marginLeft:"auto", background:"white", borderRadius:16, boxShadow:"0 8px 40px rgba(0,0,0,.18)", border:"1px solid var(--bd)", padding: showDetails ? "24px 24px 20px" : "20px 24px", pointerEvents:"auto" }}>
        {!showDetails ? (
          <>
            <div style={{ display:"flex", gap:14, alignItems:"flex-start" }}>
              <div style={{ flex:1 }}>
                <div style={{ fontWeight:700, fontSize:14, marginBottom:6, color:"var(--ink)" }}>We use cookies</div>
                <p style={{ fontSize:13, color:"var(--mu)", lineHeight:1.6, marginBottom:14 }}>
                  Essential cookies keep the booking flow working. Optional cookies enable analytics, advertising attribution, and live chat — each opt-in is independent.{" "}
                  <span style={{ color:"var(--bl)", cursor:"pointer", fontWeight:600 }} onClick={() => setShowDetails(true)}>Customise</span>
                </p>
              </div>
            </div>
            <div style={{ display:"flex", gap:8 }}>
              <button className="btn btn-bl btn-sm" style={{ flex:1, justifyContent:"center", fontSize:13 }} onClick={() => save("all")}>Accept all</button>
              <button className="btn btn-ol btn-sm" style={{ flex:1, justifyContent:"center", fontSize:13 }} onClick={() => save("necessary")}>Necessary only</button>
            </div>
          </>
        ) : (
          <>
            <div style={{ fontWeight:700, fontSize:14, marginBottom:12, color:"var(--ink)" }}>Cookie settings</div>
            <div style={{ marginBottom:16, display:"flex", flexDirection:"column", gap:14 }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <div>
                  <div style={{ fontWeight:600, fontSize:13, color:"var(--ink)" }}>Strictly necessary</div>
                  <div style={{ fontSize:12, color:"var(--mu)" }}>Booking flow, session, security</div>
                </div>
                <span style={{ fontSize:11, fontWeight:700, color:"var(--gr)", background:"var(--grbg)", padding:"3px 10px", borderRadius:100 }}>Always on</span>
              </div>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <div>
                  <div style={{ fontWeight:600, fontSize:13, color:"var(--ink)" }}>Analytics</div>
                  <div style={{ fontSize:12, color:"var(--mu)" }}>Google Analytics, Microsoft Clarity</div>
                </div>
                <Toggle on={analytics} set={setAnalytics}/>
              </div>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <div>
                  <div style={{ fontWeight:600, fontSize:13, color:"var(--ink)" }}>Advertising</div>
                  <div style={{ fontSize:12, color:"var(--mu)" }}>Google Ads conversion tracking, future Meta Pixel</div>
                </div>
                <Toggle on={advertising} set={setAdvertising}/>
              </div>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <div>
                  <div style={{ fontWeight:600, fontSize:13, color:"var(--ink)" }}>Functional</div>
                  <div style={{ fontSize:12, color:"var(--mu)" }}>Crisp live chat</div>
                </div>
                <Toggle on={functional} set={setFunctional}/>
              </div>
            </div>
            <div style={{ fontSize:12, color:"var(--mu)", marginBottom:14 }}>
              Read our full <span style={{ color:"var(--bl)", cursor:"pointer", fontWeight:600 }} onClick={() => { setVisible(false); setPage("cookies"); }}>Cookie Policy</span>
            </div>
            <div style={{ display:"flex", gap:8 }}>
              <button className="btn btn-bl btn-sm" style={{ flex:1, justifyContent:"center", fontSize:13 }} onClick={() => save("custom")}>Save preferences</button>
              <button className="btn btn-ol btn-sm" style={{ flex:1, justifyContent:"center", fontSize:13 }} onClick={() => save("all")}>Accept all</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// URL ↔ Page mapping for deep linking & SEO
export function pageToPath(pg) {
  if (pg === "home") return "/";
  if (pg.startsWith("dest:")) return "/dest/" + pg.slice(5);
  if (pg.startsWith("route:")) return "/route/" + pg.slice(6);
  if (pg.startsWith("hotel:")) return "/hotel/" + pg.slice(6);
  if (pg.startsWith("blog:")) return "/blog/" + pg.slice(5);
  if (pg.startsWith("fleet:")) return "/fleet/" + pg.slice(6);
  if (pg.startsWith("tour:")) return "/tour/" + pg.slice(5);
  return "/" + pg;
}
function pathToPage(path) {
  const p = path.replace(/\/+$/, "") || "/";
  if (p === "/") return "home";
  if (p.startsWith("/dest/")) return "dest:" + p.slice(6);
  if (p.startsWith("/route/")) return "route:" + p.slice(7);
  if (p.startsWith("/hotel/")) return "hotel:" + p.slice(7);
  if (p.startsWith("/blog/")) return "blog:" + p.slice(6);
  if (p.startsWith("/fleet/")) return "fleet:" + p.slice(7);
  if (p.startsWith("/tour/")) return "tour:" + p.slice(6);
  // Old standalone SEO Content Lab (removed) — force its legacy deep link to
  // the SPA 404 view instead of falling through to Home.
  if (p === "/admin-content") return "not-found";
  return p.slice(1); // e.g. "/fleet" → "fleet"
}

// Shown when a deep-linked detail page references a CMS document that
// doesn't exist (deleted route / destination / hotel / aircraft). Without
// this, control fell through to the Home component — Google then indexed
// the 200-status blank "not found" as Home content (audit B-A4).

export default function App() {

  const [page,    setPage]    = useState(() => pathToPage(window.location.pathname));
  const [data,    setData]    = useState({});
  const [bkOpen,  setBkOpen]  = useState(false);
  const [bkPage,  setBkPage]  = useState("route");
  const [bkData,  setBkData]  = useState({});
  const [swReset, setSwReset] = useState(0);

  // Reliably hide the SSR static-hero overlay once React has committed its first
  // render (the React hero/intro is now on screen, identical to the overlay).
  // The mount scripts also flip body.react-loaded via double-rAF, but rAF starves
  // when the main thread is saturated by hydration + third-party scripts, leaving
  // the overlay (with non-interactive static CTAs) on top for seconds. This effect
  // fires at commit time, independent of paint scheduling — whichever sets the
  // class first wins (adding it twice is harmless). Flash-free: App()'s first
  // render already has the hero+intro (synchronous __ROUTE_SEO__/entity data).
  useEffect(() => { document.body.classList.add('react-loaded'); }, []);

  // Handle Mollie payment return URL (?payment=complete&ref=GH/XXXXX)
  const [paymentReturn, setPaymentReturn] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const payment = params.get("payment");
      const ref = params.get("ref");
      // charged=1 (set by /api/payments/return for quote-accept = full direct
      // charge) → show "Payment received" copy; absent (direct booking pre-auth/
      // hold) → "Authorisation confirmed". Display-only.
      const charged = params.get("charged") === "1";
      // value/currency/from/to/aircraft are appended by /api/payments/return
      // ONLY on the Mollie-verified authorized|paid redirect — they feed the
      // Ads conversion + GA4 purchase below. Absent on cancelled/pending/
      // fallback returns and on old links → tracking stays silent.
      if (payment && ref) return {
        status: payment, ref, charged,
        value: params.get("value"),
        from: params.get("from") || "",
        to: params.get("to") || "",
        aircraft: params.get("aircraft") || "",
      };
    } catch(e) {}
    return null;
  });

  // Google Ads conversion + GA4 purchase — fired HERE (server-verified payment
  // return) and nowhere else: the funnel redirects to Mollie before any success
  // state, so a funnel-side fire never happened for paid bookings. Guarded to
  // once per booking ref via sessionStorage ('gh_tracked_' + ref — the same key
  // schema the old BookingConfirm guard used), so a reload of the return URL
  // never double-fires. No value param (old link / non-verified return) → no-op.
  useEffect(() => {
    if (!paymentReturn) return;
    let storage = null;
    try { storage = window.sessionStorage; } catch(e) {} // Safari private mode
    fireReturnConversionOnce(paymentReturn, {
      storage,
      trackConversion: trackBookingConversion,
      // gtag alone: its arguments push feeds BOTH GA4 and GTM's gtag interop
      // (one Custom Event 'purchase'). No separate dataLayer seam — a second
      // object push would double-fire the 'GH Booking Purchase' tag.
      gtag: window.gtag || null,
    });
  }, [paymentReturn]);
  const CONSENT_EXPIRY_DAYS = (__CMS?.SETTINGS?.consentExpiryDays) || 180;
  const [cookieOpen, setCookieOpen] = useState(() => {
    try {
      const stored = localStorage.getItem("cookie-consent");
      if (stored) {
        const parsed = JSON.parse(stored);
        const { timestamp, consent, version } = parsed;
        const age = (Date.now() - new Date(timestamp).getTime()) / (1000 * 60 * 60 * 24);
        if (age > CONSENT_EXPIRY_DAYS) {
          localStorage.removeItem("cookie-consent");
          return true;
        }
        // Q-GTM-CONSENT-BUG (2026-05-08): version bumped "1.0" → "2.0" when
        // the Advertising toggle was added. v1.0 records have no opinion on
        // advertising consent — re-prompt rather than guess. Catches:
        //   - any record with version != "2.0" (incl. missing version)
        //   - any record whose consent object lacks the advertising boolean
        // The legacy `choice === "all"` fast-path is gone — universal
        // re-prompt covers it. Returning users will see the banner once.
        if (version !== "2.0" || !consent || typeof consent.advertising !== "boolean") {
          localStorage.removeItem("cookie-consent");
          return true;
        }
        setTimeout(() => applyConsent(consent), 0);
        return false;
      }
    } catch(e) {}
    return true;
  });

  // Initialize Crisp live chat (consent-gated: only loads after user accepts)
  useEffect(() => { initCrisp(); }, []);

  // Hide Crisp chat bubble when booking overlay is open
  useEffect(() => {
    if (!window.$crisp) return;
    try { window.$crisp.push(["do", (bkOpen || cookieOpen) ? "chat:hide" : "chat:show"]); } catch(e) {}
  }, [bkOpen, cookieOpen]);

  // Scroll to top on every page change + update URL
  useEffect(() => {
    if ("scrollRestoration" in window.history) window.history.scrollRestoration = "manual";
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    window.scrollTo(0, 0);
    document.getElementById("top")?.scrollIntoView();
    const root = document.getElementById("app-root");
    if (root) root.scrollTop = 0;
    requestAnimationFrame(() => {
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      window.scrollTo(0, 0);
    });
    // Update URL bar (skip booking pages)
    const BOOKING_INTERNAL = new Set(["booking-passengers","booking-extras","booking-payment","booking-confirm","booking-custom-quote","helipads","search"]);
    if (!BOOKING_INTERNAL.has(page)) {
      const newPath = pageToPath(page);
      if (window.location.pathname !== newPath) {
        window.history.pushState({ page }, "", newPath);
      }
    }
  }, [page]);

  // Browser back/forward button support
  useEffect(() => {
    const onPop = (e) => {
      const pg = e.state?.page || pathToPage(window.location.pathname);
      setPage(pg);
    };
    window.addEventListener("popstate", onPop);
    // Set initial state. Full current URL (path + query + hash), NOT pathname
    // only — this line only exists to seed history.state.page for onPop, and
    // the old pathname-only form silently stripped ?gclid/utm_* BEFORE GA4
    // (consent-gated, loads later) sent its first hit → every Ads session was
    // classified direct/organic. The deliberate ?payment param cleanup on
    // navigation (handleSetPage) is a separate, unchanged path.
    window.history.replaceState({ page }, "", window.location.pathname + window.location.search + window.location.hash);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // Track page views
  useEffect(() => {
    const pageName = page.startsWith("dest:") ? "destination" : page.startsWith("route:") ? "route" : page.startsWith("hotel:") ? "hotel" : page.startsWith("charter-") ? "charter_service" : page;
    const slug = page.includes(":") ? page.split(":")[1] : page.startsWith("charter-") ? page : "";
    track("page_view", { page_name: pageName, page_slug: slug });
  }, [page]);

  // Dynamic SEO -- update title/meta/schema on every page change
  // FULL page key (2026-07-08) — the former truncation to "dest:"/"route:"/
  // "blog:" stripped the slug, which made useSEO build the broken category
  // canonical ("/dest/") and lose every page-specific value on deep links.
  useSEO(page, data?.seoContent || null);

  // openBooking -- called from anywhere in the site
  const openBooking = (initData = {}, startPage = null) => {
    // Always start fresh — don't merge with old booking data
    const merged = { ...initData };

    // Default from -> Athens if only "to" is given
    if (!merged.from && merged.to) merged.from = "Athens";

    // Always start at route selection (step 1) unless explicitly overridden
    let page = startPage || "route";
    // Custom route goes directly to quote form
    if (merged.tripType === "customroute") page = "booking-custom-quote";

    setBkData(merged);
    setBkPage(page);
    setBkOpen(true);
    // Track where the booking was initiated from. `source` = the ORIGIN page-key
    // (home / route:<slug> / dest:<slug> / hotel:<slug> / tour:<slug> / fleet / …)
    // via the SAME pathToPage the page-state is seeded with, read fresh from
    // window.location. It intentionally does NOT use the local `page` var above —
    // that is the booking START step ("route"/"booking-custom-quote"), not the
    // origin page. (Previously referenced an identifier defined nowhere →
    // ReferenceError on every open since the initial commit; the event never
    // reached GA4/Clarity.)
    track("booking_open", { source: typeof window !== "undefined" ? pathToPage(window.location.pathname) : "", from: merged.from || "", to: merged.to || "", aircraft: merged.aircraft?.name || "" });
  };

  const closeBooking = () => {
    // GA4: track where the customer abandoned
    if (window.gtag && bkPage !== "booking-confirm") {
      const stepNames = { route:"select_route", helipads:"select_helipads", search:"select_aircraft", "booking-passengers":"enter_passengers", "booking-extras":"select_extras", "booking-payment":"enter_payment", "booking-custom-quote":"custom_quote" };
      window.gtag("event", "booking_abandon", { abandon_step: stepNames[bkPage] || bkPage, from: bkData?.from || "", to: bkData?.to || "", aircraft: bkData?.aircraft?.name || "" });
    }
    setBkOpen(false);
    setBkData({});
    setBkPage("route");
    setSwReset(c => c + 1);
  };

  // Override setPage: booking pages open overlay, others navigate normally
  const handleSetPage = (p) => {
    // If the Mollie payment-return overlay is showing, ANY navigation must
    // dismiss it first — otherwise render() keeps early-returning
    // <PaymentReturnPage> and the page stays frozen on the success card while
    // only the URL changes (the [page] effect still pushStates). Nav, Footer and
    // MobileMenu all route through handleSetPage, so clearing it here fixes every
    // entry point. Also strip the ?payment/?ref/?charged query (plus the
    // value/currency/from/to/aircraft conversion-tracking params — replaceState
    // drops the ENTIRE search string) so a mid-navigation reload can't re-read
    // it into paymentReturn and resurrect the overlay or re-enter the tracking
    // path (which is additionally ref-guarded via sessionStorage).
    if (paymentReturn) {
      setPaymentReturn(null);
      if (window.location.search) {
        window.history.replaceState({}, "", window.location.pathname + window.location.hash);
      }
    }
    if (BOOKING_PAGES.has(p)) {
      setBkPage(p);
      setBkOpen(true);
    } else {
      setPage(p);
      setBkOpen(false);
      document.getElementById("top")?.scrollIntoView();
    }
  };

  // Home onSearch also needs to open overlay
  const handleHomeSearch = (searchData) => {
    setBkData(prev => ({ ...searchData, paxData: prev.paxData }));
    // Brief A — helipads BEFORE aircraft: the home-page search opens the
    // overlay at the Helipads step (was: straight to aircraft). Custom routes
    // (and forced custom quotes) keep their helipad-less flow.
    setBkPage((searchData.tripType === "customroute" || searchData.forceCustomQuote) ? "booking-custom-quote" : "helipads");
    setBkOpen(true);
    track("route_search", { trip_type: searchData.tripType, from: searchData.from || "", to: searchData.to || "", passengers: (searchData.adults||0) + (searchData.children||0) + (searchData.infants||0) });
  };

  const render = () => {
    // Payment return from Mollie
    if (paymentReturn) {
      return <PaymentReturnPage status={paymentReturn.status} bookingRef={paymentReturn.ref} charged={paymentReturn.charged} setPage={(p) => { setPaymentReturn(null); window.history.replaceState({}, "", "/"); handleSetPage(p); }}/>;
    }
    // Destination SEO page: "dest:slug"
    if (page.startsWith("hotel:")) {
      const hid = page.slice(6);
      const hotel = HOTELS.find(h => h.id === hid);
      if (hotel) return <HotelPage hotelId={hid} setPage={handleSetPage} openBooking={openBooking}/>;
      return <NotFoundPage kind="Hotel" listingLabel="Browse all hotels" listingPage="hotels" setPage={handleSetPage}/>;
    }
    if (page.startsWith("charter-")) {
      // The SSR #charter-static-hero fixes the LCP (server-paints the hero image,
      // preload-bound) but NOT the CLS: CharterServicePage is a lazy chunk, so the
      // footer still shifts when the chunk swaps in under the overlay (Chrome scores
      // the shift even while it is occluded). So charter keeps relying on the shared
      // min-height:100vh Suspense fallback below — measured CLS 0.002 with it vs
      // 0.777 without — it is required, not redundant.
      return (<CharterServicePage serviceId={page} setPage={handleSetPage} openBooking={openBooking}/>);
    }
    if (page.startsWith("dest:")) {
      const slug = page.slice(5);
      const dest = DESTS.find(d => (d.name || '').toLowerCase().replace(/\s+/g,"-") === slug);
      if (dest) return <DestinationSEOPage dest={dest} setPage={handleSetPage} openBooking={openBooking}/>;
      return <NotFoundPage kind="Destination" listingLabel="Browse all destinations" listingPage="destinations" setPage={handleSetPage}/>;
    }
    // Route SEO page: "route:slug"
    if (page.startsWith("route:")) {
      const slug = page.slice(6);
      const route = SEO_ROUTES.find(r => r.slug === slug);
      if (route) return <RouteSEOPage route={route} setPage={handleSetPage} openBooking={openBooking}/>;
      return <NotFoundPage kind="Route" listingLabel="Browse all routes" listingPage="routes" setPage={handleSetPage}/>;
    }
    // Blog article page: "blog:slug" — BlogPage itself handles the "article
    // not found" state by rendering the listing, which is an acceptable UX
    // fallback (no dedicated 404 page).
    if (page.startsWith("blog:")) {
      const slug = page.slice(5);
      return (
        <Suspense fallback={<div style={{ minHeight:"100vh", paddingTop:"var(--nav)", textAlign:"center", padding:"80px 20px", color:"var(--mu)" }}>Loading article…</div>}>
          <BlogPage setPage={handleSetPage} openBooking={openBooking} activeSlug={slug} deps={{ BLOGS, IMG, Img, SafeHtml, mdToHtml }}/>
        </Suspense>
      );
    }
    // Aircraft detail page: "fleet:id"
    if (page.startsWith("fleet:")) {
      const acId = page.slice(6);
      const ac = FLEET.find(f => f.id === acId);
      if (ac) return <AircraftDetailPage aircraft={ac} setPage={handleSetPage} openBooking={openBooking}/>;
      return <NotFoundPage kind="Aircraft" listingLabel="Browse our fleet" listingPage="fleet" setPage={handleSetPage}/>;
    }
    // Sightseeing tour detail page: "tour:id"
    if (page.startsWith("tour:")) {
      const tid = page.slice(5);
      const tour = SIGHTSEEING_TOURS.find(t => t.id === tid);
      if (tour) return <TourDetailPage tour={tour} setPage={handleSetPage} openBooking={openBooking}/>;
      return <NotFoundPage kind="Tour" listingLabel="Browse all sightseeing tours" listingPage="charter-sightseeing" setPage={handleSetPage}/>;
    }
    switch (page) {
      case "home":         return <Home         openBooking={openBooking} setPage={handleSetPage} setData={setData} data={data} onSearch={handleHomeSearch} swReset={swReset}/>;
      case "fleet":        return <FleetPage    setPage={handleSetPage} setData={setData} openBooking={openBooking}/>;
      case "destinations": return <DestinationsPage setData={setData} setPage={handleSetPage} openBooking={openBooking}/>;
      case "blog":         return (
        <Suspense fallback={<div style={{ minHeight:"100vh", paddingTop:"var(--nav)", textAlign:"center", padding:"80px 20px", color:"var(--mu)" }}>Loading Journal…</div>}>
          <BlogPage setPage={handleSetPage} openBooking={openBooking} activeSlug={null} deps={{ BLOGS, IMG, Img, SafeHtml, mdToHtml }}/>
        </Suspense>
      );
      case "safety":       return <SafetyPage setPage={handleSetPage} openBooking={openBooking}/>;
      case "contact":      return <ContactPage setPage={handleSetPage} openBooking={openBooking}/>;
      case "about":        return <AboutPage setPage={handleSetPage} openBooking={openBooking}/>;
      case "routes":       return <RoutesPage setPage={handleSetPage} openBooking={openBooking}/>;
      case "hotels":       return <HotelsPage setPage={handleSetPage} openBooking={openBooking}/>;
      case "how-it-works": return <HowItWorksPage setPage={handleSetPage} openBooking={openBooking}/>;
      case "privacy":
      case "terms":
      case "cookies":
      case "prohibited":   return (
        <Suspense fallback={<div style={{ minHeight:"100vh", paddingTop:"var(--nav)", textAlign:"center", padding:"80px 20px", color:"var(--mu)" }}>Loading…</div>}>
          <LegalPages which={page === "prohibited" ? "prohibited" : page} deps={{ CMS: __CMS, MarkdownBody }}/>
        </Suspense>
      );
      case "not-found":    return <NotFoundPage kind="Page" listingLabel="Go to homepage" listingPage="home" setPage={handleSetPage}/>;
      default:             return <Home         openBooking={openBooking} setPage={handleSetPage} setData={setData} data={data} onSearch={handleHomeSearch} swReset={swReset}/>;
    }
  };

  return (
    <>
      <style>{FONTS}{CSS}</style>
      <div id="app-root" style={{ minHeight:"100vh", display:"flex", flexDirection:"column", background:"white" }}>
        <div id="top"/>
        <Nav page={page} setPage={handleSetPage} openBooking={openBooking}/>
        <main style={{ flex:1, background:"white" }}><Suspense fallback={<div style={{ minHeight:"100vh", paddingTop:"var(--nav)", textAlign:"center", padding:"80px 20px", color:"var(--mu)" }}>Loading…</div>}>{render()}</Suspense></main>
        <Footer setPage={handleSetPage} openBooking={openBooking} reopenCookies={() => setCookieOpen(true)}/>
      </div>
      <BackToTop/>
      <CookieConsent setPage={handleSetPage} visible={cookieOpen} setVisible={setCookieOpen}/>
      {bkOpen && <Suspense fallback={<div style={{position:"fixed",inset:0,zIndex:1000,background:"#0F1419"}} />}><BookingOverlay open={bkOpen} onClose={closeBooking} page={bkPage} setPage={(p) => { if (BOOKING_PAGES.has(p)) { setBkPage(p); } else { setBkOpen(false); setPage(p); } }} data={bkData} setData={setBkData} /></Suspense>}
    </>
  );
}
